'use strict';
export const validate = validate10;
export default validate10;
const schema11 = {
	$schema: 'http://json-schema.org/schema',
	$ref: '#/definitions/BlueprintV2Declaration',
	definitions: {
		BlueprintV2Declaration: { $ref: '#/definitions/V2Schema.BlueprintV2' },
		'V2Schema.BlueprintV2': {
			type: 'object',
			properties: {
				version: {
					type: 'number',
					const: 2,
					description:
						'Not a generic \'number\' type – this schema is specifically for Blueprints v2. Version 1 had no "version" field and versions 3, 4, 5, etc will be different from version 2.',
				},
				$schema: {
					anyOf: [
						{ $ref: '#/definitions/DataSources.URLReference' },
						{
							$ref: '#/definitions/DataSources.ExecutionContextPath',
						},
					],
					description: 'JSON Schema URL.',
				},
				blueprintMeta: {
					type: 'object',
					properties: {
						name: { type: 'string' },
						description: { type: 'string' },
						moreInfo: { type: 'string' },
						version: { type: 'string' },
						authors: { type: 'array', items: { type: 'string' } },
						homepage: {
							$ref: '#/definitions/DataSources.URLReference',
						},
						donateLink: {
							$ref: '#/definitions/DataSources.URLReference',
						},
						tags: { type: 'array', items: { type: 'string' } },
						license: { type: 'string' },
					},
					additionalProperties: false,
				},
				applicationOptions: {
					type: 'object',
					properties: {
						'wordpress-playground': {
							type: 'object',
							properties: {
								landingPage: {
									type: 'string',
									description:
										'The first page the user is redirected to once the Playground is loaded and the Blueprint is executed.',
									default: '/wp-admin',
								},
								login: {
									anyOf: [
										{ type: 'boolean' },
										{
											type: 'object',
											properties: {
												username: { type: 'string' },
												password: { type: 'string' },
											},
											required: ['username', 'password'],
											additionalProperties: false,
										},
									],
									description:
										'Whether to log the user in after the Blueprint is executed. If true, the user is logged in as "admin".',
									default: false,
								},
								networkAccess: {
									type: 'boolean',
									description:
										'Whether to allow the site to access the network.',
									default: false,
								},
								loadPhpExtensions: {
									type: 'array',
									items: { type: 'string', const: 'intl' },
									description:
										'Optional PHP extensions to load in the Playground runtime before executing the Blueprint. Extensions omitted from this list are not disabled.',
								},
							},
							additionalProperties: false,
							description:
								'Options for the WordPress Playground.',
						},
					},
					required: ['wordpress-playground'],
					additionalProperties: false,
					description:
						'Divergence from Blueprints v1:\n\nThere are no `landingPage` or `login` top-level properties. Instead, Blueprint v2 introduces a dedicated top-level `applicationOptions` property for declaring options or opinions for different application contexts.\n\nTo keep Blueprints portable and focused on site creation, this specification only allows a small set of Playground-specific options. Other environments cannot declare additional options. Future versions of this specification may allow additional options – they will be discussed on a case-by-case basis.',
				},
				contentBaseline: {
					anyOf: [
						{ type: 'string', const: 'keep-all' },
						{ type: 'string', const: 'empty' },
						{
							type: 'string',
							enum: ['posts', 'pages'],
							description:
								'Content types created by a vanilla WordPress installation and controlled by `contentBaseline`.',
						},
						{
							type: 'array',
							items: {
								type: 'string',
								enum: ['posts', 'pages', 'comments'],
								description:
									'Content types created by a vanilla WordPress installation and controlled by `contentBaseline`.',
							},
							minItems: 1,
							uniqueItems: true,
							allOf: [
								{
									if: { contains: { const: 'comments' } },
									then: {
										allOf: [
											{ contains: { const: 'posts' } },
											{ contains: { const: 'pages' } },
										],
									},
								},
							],
						},
					],
					description:
						'The content from a vanilla WordPress installation to retain before applying the rest of the Blueprint. `keep-all` leaves the installation unchanged, `empty` removes its posts, pages, and comments, a content type retains only that type, and a list retains the selected content types.\n\nThis policy runs only when the current invocation creates vanilla WordPress. It is skipped when applying the Blueprint to an existing site, so it cannot erase content from that site. It is not valid when `wordpressVersion` is "none". Metadata and relationships follow their parent content. Empty content tables have their sequences reset so subsequent imports receive the identifiers they would on a site created without default content.\n\nComments can only be retained together with both posts and pages because the schema cannot know which type contains their parent records.',
					default: 'keep-all',
				},
				usersBaseline: {
					type: 'string',
					enum: ['keep-all', 'empty'],
					description:
						'The users from a vanilla WordPress installation to retain before applying the rest of the Blueprint. `keep-all` retains the administrator created by WordPress, while `empty` removes it before creating the users declared by this Blueprint.\n\nEmpty user tables have their sequences reset before those users are created.\n\nAn empty user baseline requires an empty content baseline so removing the installation administrator cannot silently delete or orphan authored content. It also requires at least one declared administrator, ensuring the resulting WordPress site remains manageable.\n\nLike `contentBaseline`, this policy is skipped when applying the Blueprint to an existing site and is not valid when `wordpressVersion` is "none".',
					default: 'keep-all',
				},
				siteLanguage: {
					type: 'string',
					description:
						'Sets the WPLANG constant and downloads any missing translations for WordPress core and all the installed plugins and themes. If you need a fine-grained control over the translations, use imperative steps in the `additionalStepsAfterExecution` array.',
					default: 'en_US',
				},
				siteOptions: {
					type: 'object',
					additionalProperties: {
						type: [
							'string',
							'boolean',
							'number',
							'array',
							'object',
						],
					},
					properties: {
						blogname: {
							type: 'string',
							description:
								'Site title.\n\nExample: "Adam\'s Movies"',
							default: 'My WordPress Site',
						},
						timezone_string: {
							type: 'string',
							description: 'Example: "Poland/Warsaw"',
							default: 'UTC',
						},
						permalink_structure: {
							anyOf: [
								{ type: 'string' },
								{ type: 'boolean', const: false },
							],
							description:
								'Site permalink structure. If present and different from the current permalink structure, the Blueprint runner will run `$wp_rewrite->flush_rules();`. If you only want to set this option without flushing the rules, use an explicit `additionalStepsAfterExecution` step.\n\nExample: "/%year%/%monthnum%/%postname%/" or false for no pretty permalinks.',
							default: '/%postname%/',
						},
						siteUrl: false,
					},
					description:
						'Site options. In WordPress, the values are PHP-serializable, but Blueprints are intentionally restricted to an even stricter subset of those, that are JSON-serializable. This is to prevent passing JavaScript Date objects and similar.\n\nThe runner **MUST** use the WordPress `update_option` function to store the siteOptions values defined in this property as WordPress options. Lists and objects are passed to `update_option` as PHP arrays.\n\nSite options example:\n\n```json {     "blogname": "Adam\'s Movies",     "timezone": "Poland/Warsaw",     "gutenberg-experiments": { \t\t\t\'gutenberg-custom-dataviews\': true, \t\t\t\'gutenberg-new-posts-dashboard\': true, \t\t\t\'gutenberg-quick-edit-dataviews\': true \t\t} } ```',
				},
				constants: {
					type: 'object',
					additionalProperties: {
						type: ['boolean', 'string', 'number'],
					},
					properties: {
						WP_DEBUG: { type: 'boolean' },
						WP_DEBUG_LOG: { type: 'boolean' },
						WP_DEBUG_DISPLAY: { type: 'boolean' },
						SCRIPT_DEBUG: { type: 'boolean' },
					},
					description:
						'Constants to define in the wp-config.php file.\n\nThe runner may overwrite the define() calls in the wp-config.php file on the target site. It assumes the wp-config.php file at the Blueprint Execution Target is writable.',
				},
				wordpressVersion: {
					anyOf: [
						{ $ref: '#/definitions/DataSources.WordPressVersion' },
						{ $ref: '#/definitions/DataSources.DataReference' },
						{
							type: 'object',
							properties: {
								min: {
									$ref: '#/definitions/DataSources.WordPressVersionConstraintVersion',
								},
								max: {
									$ref: '#/definitions/DataSources.WordPressVersionConstraintVersion',
								},
								preferred: {
									$ref: '#/definitions/DataSources.WordPressVersionPreferredVersion',
									default: 'latest',
								},
							},
							required: ['min'],
							additionalProperties: false,
						},
					],
					description:
						'WordPress version to install or require.\n\nA string selects the version for a newly created site. A branch such as `6.8` selects the newest available release in that branch. Strings are selection hints and do not reject an existing site. `"none"` boots the PHP runtime without downloading WordPress or initializing its database.\n\nAn object declares compatibility bounds. The runner chooses the newest available release within those bounds for a new site and verifies an existing site\'s installed version against them. `preferred` influences new-site selection without narrowing compatibility.\n\nA data reference supplies the WordPress files for a newly created site.',
					default: 'latest',
				},
				phpVersion: {
					anyOf: [
						{ $ref: '#/definitions/DataSources.PHPVersion' },
						{
							type: 'object',
							properties: {
								min: {
									$ref: '#/definitions/DataSources.PHPVersionConstraintVersion',
								},
								recommended: {
									$ref: '#/definitions/DataSources.PHPVersionConstraintVersion',
								},
								max: {
									$ref: '#/definitions/DataSources.PHPVersionConstraintVersion',
								},
							},
							additionalProperties: false,
						},
					],
					description:
						"The PHP version required for this Blueprint to work.\n\nIn runtimes where we set up the runtime, such as Playground and wp-env, the runner will choose a version compatible with this constraint.\n\nIn other environments, this is used for validation. The Blueprint engine will throw an error if the currently running PHP version doesn't match this constraint.",
					default:
						'"8.0". Changing the default value will bump the Blueprint version.',
				},
				activeTheme: {
					anyOf: [
						{
							$ref: '#/definitions/DataSources.ThemeDirectoryReference',
						},
						{ $ref: '#/definitions/DataSources.DataReference' },
						{
							type: 'object',
							properties: {
								source: {
									anyOf: [
										{
											$ref: '#/definitions/DataSources.ThemeDirectoryReference',
										},
										{
											$ref: '#/definitions/DataSources.DataReference',
										},
									],
								},
								importStarterContent: {
									type: 'boolean',
									description:
										"Whether to import the theme's starter content after installing it.",
								},
								targetDirectoryName: {
									type: 'string',
									description:
										'An explicit directory name within wp-content/themes to install the theme at. If not provided, it will be inferred from the theme source.',
									pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
								},
								onError: {
									type: 'string',
									enum: ['skip-theme', 'throw'],
									description:
										"Sometimes it's fine when a theme fails to install.",
									default: 'throw',
								},
								ifAlreadyInstalled: {
									type: 'string',
									enum: ['overwrite', 'skip', 'error'],
									description:
										'How to handle a theme that is already installed.',
									default: 'overwrite',
								},
								humanReadableName: {
									type: 'string',
									description:
										'Human-readable name of the theme for the progress bar.\n\nFor example, with the following Blueprint:\n\n```json {     "themes": [         {             "source": "https://github.com/Automattic/adventurer/archive/refs/heads/beta.zip",             "humanReadableName": "Adventurer"         }     ] } ```\n\nThe progress bar will show "Installing Adventurer theme" instead of "Installing https://github.com/Automattic/adventurer/archive/refs/heads/beta.zip".',
								},
							},
							required: ['source'],
							additionalProperties: false,
						},
					],
					description:
						'The theme to install and also activate.\n\n> Why not support an `active` property in the `themes` array?\n\nBecause an `"active"` property would have to default to `false` for themes while it defaults to `true` for plugins. That\'s error-prone and confusing.',
				},
				themes: {
					type: 'array',
					items: {
						anyOf: [
							{
								$ref: '#/definitions/DataSources.ThemeDirectoryReference',
							},
							{ $ref: '#/definitions/DataSources.DataReference' },
							{
								type: 'object',
								properties: {
									source: {
										anyOf: [
											{
												$ref: '#/definitions/DataSources.ThemeDirectoryReference',
											},
											{
												$ref: '#/definitions/DataSources.DataReference',
											},
										],
									},
									importStarterContent: {
										type: 'boolean',
										description:
											"Whether to import the theme's starter content after installing it.",
									},
									targetDirectoryName: {
										type: 'string',
										description:
											'An explicit directory name within wp-content/themes to install the theme at. If not provided, it will be inferred from the theme source.',
										pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
									},
									onError: {
										type: 'string',
										enum: ['skip-theme', 'throw'],
										description:
											"Sometimes it's fine when a theme fails to install.",
										default: 'throw',
									},
									ifAlreadyInstalled: {
										type: 'string',
										enum: ['overwrite', 'skip', 'error'],
										description:
											'How to handle a theme that is already installed.',
										default: 'overwrite',
									},
									humanReadableName: {
										type: 'string',
										description:
											'Human-readable name of the theme for the progress bar.\n\nFor example, with the following Blueprint:\n\n```json {     "themes": [         {             "source": "https://github.com/Automattic/adventurer/archive/refs/heads/beta.zip",             "humanReadableName": "Adventurer"         }     ] } ```\n\nThe progress bar will show "Installing Adventurer theme" instead of "Installing https://github.com/Automattic/adventurer/archive/refs/heads/beta.zip".',
									},
								},
								required: ['source'],
								additionalProperties: false,
							},
						],
					},
					description:
						'Installed themes to install without activating them.\n\nExample:\n\n```json {     "version": 2,     "themes": [         "stylish-press-theme",         "adventurer@4.6.0",         {             "source": "https://github.com/richtabor/kanso/archive/refs/heads/main.zip",             "targetDirectoryName": "kanso"         }     ] } ```',
				},
				plugins: {
					type: 'array',
					items: {
						anyOf: [
							{ $ref: '#/definitions/DataSources.DataReference' },
							{
								$ref: '#/definitions/DataSources.PluginDirectoryReference',
							},
							{
								type: 'object',
								properties: {
									source: {
										anyOf: [
											{
												$ref: '#/definitions/DataSources.DataReference',
											},
											{
												$ref: '#/definitions/DataSources.PluginDirectoryReference',
											},
										],
									},
									active: {
										type: 'boolean',
										description:
											'Whether to activate the plugin.',
										default: 'true.',
									},
									activationOptions: {
										type: 'object',
										additionalProperties: {
											anyOf: [
												{ type: 'string' },
												{ type: 'boolean' },
												{ type: 'number' },
												{
													type: 'array',
													items: {
														$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
													},
												},
												{
													type: 'object',
													additionalProperties: {
														$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
													},
												},
											],
										},
										description:
											'Parameters to pass to the plugin during activation.\n\nThese options are stored in a site option that the plugin can access during its activation hook. The option name is:\n\n```php \'blueprint_activation_\' . plugin_basename( __FILE__ ) ```\n\nThis ensures uniqueness even when multiple versions of the same plugin exist. This is similar to how the `register_activation_hook` function requires the plugin file path as its first argument.\n\nThe Blueprint runner will remove the option after activating the plugin.\n\nExample:\n\nIn the Blueprint: ```json {     "source": "woocommerce",     "activationOptions": {         "storeCity": "Wrocław",         "storeCountry": "Poland",         "storePostalCode": "53-607"     } } ```\n\nIn the plugin\'s activation hook:\n\n```php register_activation_hook( __FILE__, function( $network_wide ) {     // Get the activation options from the transient     $option_name = \'blueprint_activation_\' . plugin_basename( __FILE__ );     $blueprint_activation_options = get_option( $option_name ) ?? [];\n\n    if ( $blueprint_activation_options ) {         $store_city = $blueprint_activation_options[\'storeCity\'] ?? \'\';         $store_country = $blueprint_activation_options[\'storeCountry\'] ?? \'\';         $store_postal_code = $blueprint_activation_options[\'storePostalCode\'] ?? \'\';\n\n        // ...do something with the options...     }\n\n    // Continue with normal activation... } ); ```',
									},
									targetDirectoryName: {
										type: 'string',
										description:
											'An explicit directory name within wp-content/plugins to install the plugin at. If not provided, it will be inferred from the plugin source.',
										pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
									},
									onError: {
										type: 'string',
										enum: ['skip-plugin', 'throw'],
										description:
											"Sometimes it's fine when a plugin fails to install.\n\nUse-case:   Compatibility testing. A Blueprint may install WordPress nightly with   a number of plugins to test. Some of those plugins may not yet be compatible   with the latest version of WordPress. This is something to take not of,   but not a strong reason to fail the entire Blueprint installation.",
										default: 'throw',
									},
									ifAlreadyInstalled: {
										type: 'string',
										enum: ['overwrite', 'skip', 'error'],
										description:
											'How to handle a plugin that is already installed.',
										default: 'overwrite',
									},
									humanReadableName: {
										type: 'string',
										description:
											'Human-readable name of the plugin for the progress bar.\n\nFor example, with the following Blueprint:\n\n```json {     "plugins": [         {             "source": "https://github.com/Automattic/jetpack/archive/refs/heads/beta.zip",             "humanReadableName": "Jetpack Beta"         }     ] } ```\n\nThe progress bar will show "Installing Jetpack Beta plugin" instead of "Installing https://github.com/Automattic/jetpack/archive/refs/heads/beta.zip".',
									},
								},
								required: ['source'],
								additionalProperties: false,
							},
						],
					},
					description:
						'A list of plugins to install and activate.\n\nExample:\n\n```json {     "version": 2,     "plugins": [         "jetpack",         "akismet@6.4.3",         "./query-monitor.php",         "./code-block.zip",         {             "source": "https://github.com/woocommerce/woocommerce/archive/refs/heads/6.4.3.zip",             "active": false         }     ] } ```',
				},
				muPlugins: {
					type: 'array',
					items: { $ref: '#/definitions/DataSources.DataReference' },
					description:
						'A list of mu-plugins to install.\n\nExample:\n\n```json {     "version": 2,     "muPlugins": [         {             "filename": "addFilter-0.php",             "content": "<?php add_action( \'requests-requests.before_request\', function( &$url ) {\\n$url = \'https://playground.wordpress.net/cors-proxy.php?\' . $url;\\n} );"         }     ] } ```',
				},
				postTypes: {
					type: 'object',
					additionalProperties: {
						anyOf: [
							{
								type: 'object',
								properties: {
									label: {
										type: 'string',
										description:
											"Name of the post type shown in the menu. Usually plural. Default is value of $labels['name'].",
									},
									labels: {
										type: 'object',
										additionalProperties: {
											type: 'string',
										},
										properties: {
											name: {
												type: 'string',
												description:
													"General name for the post type, usually plural. Default is 'Posts' / 'Pages'.",
											},
											singular_name: {
												type: 'string',
												description:
													"Name for one object of this post type. Default is 'Post' / 'Page'.",
											},
											add_new: {
												type: 'string',
												description:
													"Label for adding a new item. Default is 'Add New' / 'Add New'.",
											},
											add_new_item: {
												type: 'string',
												description:
													"Label for adding a new singular item. Default is 'Add New Post' / 'Add New Page'.",
											},
											edit_item: {
												type: 'string',
												description:
													"Label for editing a singular item. Default is 'Edit Post' / 'Edit Page'.",
											},
											new_item: {
												type: 'string',
												description:
													"Label for the new item page title. Default is 'New Post' / 'New Page'.",
											},
											view_item: {
												type: 'string',
												description:
													"Label for viewing a singular item. Default is 'View Post' / 'View Page'.",
											},
											view_items: {
												type: 'string',
												description:
													"Label for viewing post type archives. Default is 'View Posts' / 'View Pages'.",
											},
											search_items: {
												type: 'string',
												description:
													"Label for searching plural items. Default is 'Search Posts' / 'Search Pages'.",
											},
											not_found: {
												type: 'string',
												description:
													"Label used when no items are found. Default is 'No posts found' / 'No pages found'.",
											},
											not_found_in_trash: {
												type: 'string',
												description:
													"Label used when no items are in the Trash. Default is 'No posts found in Trash' / 'No pages found in Trash'.",
											},
											parent_item_colon: {
												type: 'string',
												description:
													"Label used to prefix parents of hierarchical items. Default is 'Parent Page:'.",
											},
											all_items: {
												type: 'string',
												description:
													"Label to signify all items in a submenu link. Default is 'All Posts' / 'All Pages'.",
											},
											archives: {
												type: 'string',
												description:
													"Label for archives in nav menus. Default is 'Post Archives' / 'Page Archives'.",
											},
											attributes: {
												type: 'string',
												description:
													"Label for the attributes meta box. Default is 'Post Attributes' / 'Page Attributes'.",
											},
											insert_into_item: {
												type: 'string',
												description:
													"Label for the media frame button. Default is 'Insert into post' / 'Insert into page'.",
											},
											uploaded_to_this_item: {
												type: 'string',
												description:
													"Label for the media frame filter. Default is 'Uploaded to this post' / 'Uploaded to this page'.",
											},
											featured_image: {
												type: 'string',
												description:
													"Label for the featured image meta box title. Default is 'Featured image'.",
											},
											set_featured_image: {
												type: 'string',
												description:
													"Label for setting the featured image. Default is 'Set featured image'.",
											},
											remove_featured_image: {
												type: 'string',
												description:
													"Label for removing the featured image. Default is 'Remove featured image'.",
											},
											use_featured_image: {
												type: 'string',
												description:
													"Label in the media frame for using a featured image. Default is 'Use as featured image'.",
											},
											menu_name: {
												type: 'string',
												description:
													'Label for the menu name. Default is the same as name.',
											},
											filter_items_list: {
												type: 'string',
												description:
													"Label for the table views hidden heading. Default is 'Filter posts list' / 'Filter pages list'.",
											},
											filter_by_date: {
												type: 'string',
												description:
													"Label for the date filter in list tables. Default is 'Filter by date'.",
											},
											items_list_navigation: {
												type: 'string',
												description:
													"Label for the table pagination hidden heading. Default is 'Posts list navigation' / 'Pages list navigation'.",
											},
											items_list: {
												type: 'string',
												description:
													"Label for the table hidden heading. Default is 'Posts list' / 'Pages list'.",
											},
											item_published: {
												type: 'string',
												description:
													"Label used when an item is published. Default is 'Post published.' / 'Page published.'",
											},
											item_published_privately: {
												type: 'string',
												description:
													"Label used when an item is published with private visibility. Default is 'Post published privately.' / 'Page published privately.'",
											},
											item_reverted_to_draft: {
												type: 'string',
												description:
													"Label used when an item is switched to a draft. Default is 'Post reverted to draft.' / 'Page reverted to draft.'",
											},
											item_trashed: {
												type: 'string',
												description:
													"Label used when an item is moved to Trash. Default is 'Post trashed.' / 'Page trashed.'",
											},
											item_scheduled: {
												type: 'string',
												description:
													"Label used when an item is scheduled for publishing. Default is 'Post scheduled.' / 'Page scheduled.'",
											},
											item_updated: {
												type: 'string',
												description:
													"Label used when an item is updated. Default is 'Post updated.' / 'Page updated.'",
											},
											item_link: {
												type: 'string',
												description:
													"Title for a navigation link block variation. Default is 'Post Link' / 'Page Link'.",
											},
											item_link_description: {
												type: 'string',
												description:
													"Description for a navigation link block variation. Default is 'A link to a post.' / 'A link to a page.'",
											},
										},
										description:
											'An array of labels for this post type. If not set, post labels are inherited for non-hierarchical types and page labels for hierarchical ones\n\nThe labels documented for WordPress 6.8 are listed below, and this type also supports an arbitrary set of labels to support future WordPress releases.',
									},
									description: {
										type: 'string',
										description:
											'A short descriptive summary of what the post type is.',
									},
									public: {
										type: 'boolean',
										description:
											'Whether a post type is intended for use publicly either via the admin interface or by front-end users. While the default settings of $exclude_from_search, $publicly_queryable, $show_ui, and $show_in_nav_menus are inherited from $public, each does not rely on this relationship and controls a very specific intention. Default false.',
									},
									hierarchical: {
										type: 'boolean',
										description:
											'Whether the post type is hierarchical (e.g. page). Default false.',
									},
									exclude_from_search: {
										type: 'boolean',
										description:
											'Whether to exclude posts with this post type from front end search results. Default is the opposite value of $public.',
									},
									publicly_queryable: {
										type: 'boolean',
										description:
											'Whether queries can be performed on the front end for the post type as part of parse_request(). Endpoints would include:\n* ?post_type={post_type_key}\n* ?{post_type_key}={single_post_slug}\n* ?{post_type_query_var}={single_post_slug} If not set, the default is inherited from $public.',
									},
									show_ui: {
										type: 'boolean',
										description:
											'Whether to generate and allow a UI for managing this post type in the admin. Default is value of $public.',
									},
									show_in_menu: {
										type: ['boolean', 'string'],
										description:
											"Where to show the post type in the admin menu. To work, $show_ui must be true. If true, the post type is shown in its own top level menu. If false, no menu is shown. If a string of an existing top level menu ('tools.php' or 'edit.php?post_type=page', for example), the post type will be placed as a sub-menu of that. Default is value of $show_ui.",
									},
									show_in_admin_bar: {
										type: 'boolean',
										description:
											'Makes this post type available via the admin bar. Default is value of $show_in_menu.',
									},
									show_in_nav_menus: {
										type: 'boolean',
										description:
											'Makes this post type available for selection in navigation menus. Default is value of $public.',
									},
									show_in_rest: {
										type: 'boolean',
										description:
											'Whether to include the post type in the REST API. Set this to true for the post type to be available in the block editor.',
									},
									rest_base: {
										type: 'string',
										description:
											'To change the base URL of REST API route. Default is $post_type.',
									},
									rest_namespace: {
										type: 'string',
										description:
											'To change the namespace URL of REST API route. Default is wp/v2.',
									},
									rest_controller_class: {
										type: 'string',
										description:
											"REST API controller class name. Default is 'WP_REST_Posts_Controller'.",
									},
									menu_icon: {
										type: 'string',
										description:
											"The URL to the icon to be used for this menu. Pass a base64-encoded SVG using a data URI, which will be colored to match the color scheme — this should begin with 'data:image/svg+xml;base64,'. Pass the name of a Dashicons helper class to use a font icon, e.g. 'dashicons-chart-pie'. Pass 'none' to leave div.wp-menu-image empty so an icon can be added via CSS. Defaults to use the posts icon.",
									},
									menu_position: {
										type: ['string', 'number'],
										description:
											'The position in the menu order the post type should appear. To work, $show_in_menu must be true. Default null (at the bottom).',
									},
									rename_capabilities: {
										type: 'boolean',
										description:
											'Whether to rename the capabilities for this post type.',
									},
									singular_capability_name: {
										type: 'string',
										description:
											'The singular capability name for this post type.',
									},
									plural_capability_name: {
										type: 'string',
										description:
											'The plural capability name for this post type.',
									},
									taxonomies: {
										type: 'array',
										items: { type: 'string' },
										description:
											'An array of taxonomy identifiers that will be registered for the post type. Taxonomies can be registered later with register_taxonomy() or register_taxonomy_for_object_type().',
									},
									query_var_name: {
										type: 'string',
										description:
											'The query var name for this post type.',
									},
									register_meta_box_cb: {
										type: 'string',
										description:
											'Provide a callback function that sets up the meta boxes for the edit form. Do remove_meta_box() and add_meta_box() calls in the callback. Default null.',
									},
									enter_title_here: {
										type: 'string',
										description:
											'Custom text for the "Enter title here" placeholder in the title field.',
									},
									capability_type: {
										anyOf: [
											{ type: 'string' },
											{
												type: 'array',
												items: { type: 'string' },
												minItems: 2,
												maxItems: 2,
											},
										],
										description:
											"The string to use to build the read, edit, and delete capabilities. May be passed as an array to allow for alternative plurals when using this argument as a base to construct the capabilities, e.g. array('story', 'stories'). Default 'post'.",
									},
									capabilities: {
										type: 'object',
										additionalProperties: {
											type: 'string',
										},
										description:
											'Array of capabilities for this post type. $capability_type is used as a base to construct capabilities by default. See get_post_type_capabilities().',
									},
									map_meta_cap: {
										type: 'boolean',
										description:
											'Whether to use the internal default meta capability handling. Default false.',
									},
									supports: {
										allOf: [
											{
												type: 'array',
												items: {
													type: 'string',
													enum: [
														'title',
														'editor',
														'author',
														'thumbnail',
														'excerpt',
														'trackbacks',
														'custom-fields',
														'comments',
														'revisions',
														'page-attributes',
														'post-formats',
													],
												},
											},
											{
												type: 'array',
												items: { type: 'string' },
											},
										],
										description:
											"Core feature(s) the post type supports. Serves as an alias for calling add_post_type_support() directly.\n\nCore features include 'title', 'editor', 'comments', 'revisions', 'trackbacks', 'author', 'excerpt', 'page-attributes', 'thumbnail', 'custom-fields', and 'post-formats'.\n\nAdditionally, the 'revisions' feature dictates whether the post type will store revisions, the 'autosave' feature dictates whether the post type will be autosaved, and the 'comments' feature dictates whether the comments count will show on the edit screen.\n\nFor backward compatibility reasons, adding 'editor' support implies 'autosave' support too.\n\nA feature can also be specified as an array of arguments to provide additional information about supporting that feature.\n\nExample: array( 'my_feature', array( 'field' => 'value' ) ).\n\nIf false, no features will be added. Default is an array containing 'title' and 'editor'.",
									},
									has_archive: {
										type: ['boolean', 'string'],
										description:
											'Whether there should be post type archives, or if a string, the archive slug to use. Will generate the proper rewrite rules if $rewrite is enabled. Default false.',
									},
									rewrite: {
										anyOf: [
											{ type: 'boolean' },
											{
												type: 'object',
												properties: {
													slug: { type: 'string' },
													with_front: {
														type: 'boolean',
													},
													pages: { type: 'boolean' },
													feeds: { type: 'boolean' },
													ep_mask: { type: 'number' },
												},
												additionalProperties: false,
											},
										],
										description:
											'Triggers the handling of rewrites for this post type. To prevent rewrite, set to false. Defaults to true, using $post_type as slug. To specify rewrite rules, an array can be passed with any of these keys:\n- slug (string): Customize the permastruct slug. Defaults to $post_type key.\n- with_front (bool): Whether the permastruct should be prepended with WP_Rewrite::$front. Default true.\n- feeds (bool): Whether the feed permastruct should be built for this post type. Default is value of $has_archive.\n- pages (bool): Whether the permastruct should provide for pagination. Default true.\n- ep_mask (int): Endpoint mask to assign. If not specified and permalink_epmask is set, inherits from $permalink_epmask.   If not specified and permalink_epmask is not set, defaults to EP_PERMALINK.',
									},
									query_var: {
										type: ['boolean', 'string'],
										description:
											'Sets the query_var key for this post type. Defaults to $post_type key. If false, a post type cannot be loaded at ?{query_var}={post_slug}. If specified as a string, the query ?{query_var_string}={post_slug} will be valid.',
									},
									can_export: {
										type: 'boolean',
										description:
											'Whether to allow this post type to be exported. Default true.',
									},
									delete_with_user: {
										type: 'boolean',
										description:
											"Whether to delete posts of this type when deleting a user. If true, posts of this type belonging to the user will be moved to Trash when the user is deleted. If false, posts of this type belonging to the user will *not* be trashed or deleted. If not set (the default), posts are trashed if post type supports the 'author' feature. Otherwise posts are not trashed or deleted. Default null.",
									},
									template: {
										type: 'array',
										items: {
											type: 'array',
											minItems: 2,
											items: [
												{ type: 'string' },
												{
													type: 'object',
													additionalProperties: {
														anyOf: [
															{ type: 'string' },
															{ type: 'boolean' },
															{ type: 'number' },
															{
																type: 'array',
																items: {
																	$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																},
															},
															{
																type: 'object',
																additionalProperties:
																	{
																		$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																	},
															},
														],
													},
												},
											],
											maxItems: 2,
										},
										description:
											'Array of blocks to use as the default initial state for an editor session. Each item should be an array containing block name and optional attributes.',
									},
									template_lock: {
										type: ['string', 'boolean'],
										enum: ['all', 'insert', false],
										description:
											"Whether the block template should be locked if $template is set. If set to 'all', the user is unable to insert new blocks, move existing blocks and delete blocks. If set to 'insert', the user is able to move existing blocks but is unable to insert new blocks and delete blocks. Default false.",
									},
								},
								additionalProperties: false,
							},
							{
								$ref: '#/definitions/DataSources.ExecutionContextPath',
							},
						],
					},
					propertyNames: { pattern: '^[a-z0-9_-]{1,20}$' },
					description:
						"Very basic schema for defining custom post types.\n\nIMPORTANT: Using this property requires an explicit inclusion of the `secure-custom-fields` plugin. If it's missing, the Blueprint runner will throw an error.\n\nSee https://github.com/WordPress/blueprints-library/issues/32 for more context.",
				},
				fonts: {
					type: 'object',
					additionalProperties: {
						anyOf: [
							{
								$ref: '#/definitions/DataSources.FileDataReference',
							},
							{
								type: 'object',
								properties: {
									$schema: {
										type: 'string',
										description:
											'JSON schema URI for font-collection.json.',
									},
									font_families: {
										type: 'array',
										items: {
											type: 'object',
											properties: {
												font_family_settings: {
													type: 'object',
													properties: {
														name: {
															type: 'string',
															description:
																'Name of the font family preset, translatable.',
														},
														slug: {
															type: 'string',
															description:
																'Kebab-case unique identifier for the font family preset.',
														},
														fontFamily: {
															type: 'string',
															description:
																'CSS font-family value.',
														},
														preview: {
															type: 'string',
															description:
																'URL to a preview image of the font family.',
														},
														fontFace: {
															type: 'array',
															items: {
																type: 'object',
																properties: {
																	preview: {
																		type: 'string',
																		description:
																			'URL to a preview image of the font.',
																	},
																	fontFamily:
																		{
																			type: 'string',
																			description:
																				'CSS font-family value.',
																		},
																	fontStyle: {
																		type: 'string',
																		description:
																			'CSS font-style value.',
																	},
																	fontWeight:
																		{
																			type: [
																				'string',
																				'number',
																			],
																			description:
																				'List of available font weights, separated by a space.',
																		},
																	fontDisplay:
																		{
																			type: 'string',
																			enum: [
																				'auto',
																				'block',
																				'fallback',
																				'swap',
																				'optional',
																			],
																			description:
																				'CSS font-display value.',
																		},
																	src: {
																		anyOf: [
																			{
																				$ref: '#/definitions/DataSources.FileDataReference',
																			},
																			{
																				type: 'array',
																				items: {
																					$ref: '#/definitions/DataSources.FileDataReference',
																				},
																			},
																		],
																		description:
																			'Paths or URLs to the font files.',
																	},
																	fontStretch:
																		{
																			type: 'string',
																			description:
																				'CSS font-stretch value.',
																		},
																	ascentOverride:
																		{
																			type: 'string',
																			description:
																				'CSS ascent-override value.',
																		},
																	descentOverride:
																		{
																			type: 'string',
																			description:
																				'CSS descent-override value.',
																		},
																	fontVariant:
																		{
																			type: 'string',
																			description:
																				'CSS font-variant value.',
																		},
																	fontFeatureSettings:
																		{
																			type: 'string',
																			description:
																				'CSS font-feature-settings value.',
																		},
																	fontVariationSettings:
																		{
																			type: 'string',
																			description:
																				'CSS font-variation-settings value.',
																		},
																	lineGapOverride:
																		{
																			type: 'string',
																			description:
																				'CSS line-gap-override value.',
																		},
																	sizeAdjust:
																		{
																			type: 'string',
																			description:
																				'CSS size-adjust value.',
																		},
																	unicodeRange:
																		{
																			type: 'string',
																			description:
																				'CSS unicode-range value.',
																		},
																},
																required: [
																	'fontFamily',
																	'src',
																],
																additionalProperties: false,
																description:
																	'Font face settings with added preview property.',
															},
															description:
																'Array of font-face definitions.',
														},
													},
													required: [
														'name',
														'slug',
														'fontFamily',
													],
													additionalProperties: false,
													description:
														'Font family settings with added preview property.',
												},
												categories: {
													type: 'array',
													items: { type: 'string' },
													description:
														'Array of category slugs.',
												},
											},
											required: ['font_family_settings'],
											additionalProperties: false,
										},
										description:
											'Array of font families ready to be installed.',
									},
								},
								required: ['font_families'],
								additionalProperties: false,
								description:
									'Font collection schema for WordPress Font Library.',
							},
						],
					},
					description:
						'A list of fonts to register in the site\'s Font Library.\n\nExample:\n\n```json fonts: {     "open-sans": "https://example.com/fonts/open-sans.woff2",     "roboto": "./wp-content/fonts/roboto.woff2" } ```\n\nOr using the full font collection schema:\n\n```json fonts: {     "my-collection": {         "font_families": [             {                 "font_family_settings": {                     "name": "Open Sans",                     "slug": "open-sans",                     "fontFamily": "Open Sans",                     "preview": "https://example.com/previews/open-sans.png",                     "fontFace": [                         {                             "fontFamily": "Open Sans",                             "fontWeight": "400",                             "fontStyle": "normal",                             "src": "./wp-content/fonts/open-sans-regular.woff2"                         }                     ]                 },                 "categories": ["sans-serif"]             }         ]     } } ```',
				},
				media: {
					type: 'array',
					items: {
						anyOf: [
							{
								$ref: '#/definitions/DataSources.FileDataReference',
							},
							{
								type: 'object',
								properties: {
									source: {
										$ref: '#/definitions/DataSources.FileDataReference',
									},
									title: { type: 'string' },
									description: { type: 'string' },
									alt: { type: 'string' },
									caption: { type: 'string' },
								},
								required: ['source'],
								additionalProperties: false,
							},
						],
					},
					description:
						'A list of media files to upload to the WordPress Media Library – in formats supported by the WordPress Media Library.\n\nExample:\n\n```json media: [     "https://example.com/images/hero.jpg",     "./wp-content/uploads/2024/01/logo.png",     {         "source": "https://example.com/videos/intro.mp4",         "title": "Introduction Video",         "description": "A brief introduction to our company",         "alt": "Company introduction video"     },     {         "source": "./wp-content/uploads/2024/01/brochure.pdf",         "title": "Product Brochure",         "description": "Detailed information about our products"     } ] ```',
				},
				content: {
					type: 'array',
					items: {
						type: 'object',
						discriminator: { propertyName: 'type' },
						required: ['type'],
						oneOf: [
							{
								type: 'object',
								properties: {
									type: {
										type: 'string',
										const: 'mysql-dump',
									},
									source: {
										anyOf: [
											{
												$ref: '#/definitions/DataSources.FileDataReference',
											},
											{
												type: 'array',
												items: {
													$ref: '#/definitions/DataSources.FileDataReference',
												},
											},
										],
									},
								},
								required: ['type', 'source'],
								additionalProperties: false,
							},
							{
								type: 'object',
								additionalProperties: false,
								properties: {
									urlsMode: {
										type: 'string',
										enum: ['rewrite', 'preserve'],
										description:
											'Whether to rewrite the hrefs in the remote site\'s content URLs in the WXR file from the remote site domain to the current site domain\'s (and path etc).\n\nPossible values:\n\n* "rewrite" – Rewrite the hrefs to the current site domain\'s (and path etc).\n* "preserve" – Preserve the hrefs as they are.',
										default: '"rewrite".',
									},
									urlsMap: {
										type: 'object',
										additionalProperties: {
											$ref: '#/definitions/DataSources.URLReference',
										},
										description:
											'A mapping of base URLs to rewrite.',
										propertyNames: {
											$ref: '#/definitions/DataSources.URLReference',
										},
									},
									type: { type: 'string', const: 'posts' },
									source: {
										anyOf: [
											{
												$ref: '#/definitions/DataSources.FileDataReference',
											},
											{
												type: 'object',
												properties: {
													post_author: {
														type: 'number',
														description:
															'Username of the post author.\n\nIf missing, the default value will be resolved in the following order until one is available:\n\n* Default user defined in the runner configuration.\n* The first administrator in the database.\n* The first user in the database.\n* A newly created user.\n\nThe aggressive resolution is necessary because post_author is NOT NULL in the database schema.',
													},
													post_date: {
														type: 'string',
														description:
															"The date of the post in UTC. Accepts format 'YYYY-MM-DD HH:MM:SS'. Can be used to schedule future posts (when used with post_status: 'future').",
														default: 'Current time',
													},
													post_content: {
														type: 'string',
														description:
															'The main post content. Can contain HTML, shortcodes, etc. While technically optional, posts are usually expected to have content.',
														default: '',
													},
													post_title: {
														type: 'string',
														description:
															'The post title.',
													},
													post_excerpt: {
														type: 'string',
														description:
															'The post excerpt. Default empty.',
													},
													post_status: {
														type: 'string',
														enum: [
															'publish',
															'pending',
															'draft',
															'auto-draft',
															'future',
															'private',
															'inherit',
															'trash',
														],
														description:
															'The post status',
													},
													post_type: {
														type: 'string',
														description:
															"The post type (e.g., 'post', 'page'). Default 'post'.",
													},
													comment_status: {
														type: 'string',
														enum: [
															'open',
															'closed',
														],
														description:
															"Whether comments are allowed ('open' or 'closed').",
														default: "'open'.",
													},
													post_password: {
														type: 'string',
														description:
															'A password to protect access. Default empty.',
													},
													post_name: {
														type: 'string',
														description:
															'The URL slug. Default sanitized post_title for new posts.',
													},
													post_parent_name: {
														type: 'string',
														description:
															'Post parent name for hierarchical post types (e.g., pages). Default empty.',
													},
													menu_order: {
														type: 'number',
														description:
															'Menu order within a post type. Default 0.',
													},
													post_mime_type: {
														type: 'string',
														description:
															'MIME type for attachments. Default empty.',
													},
													guid: {
														type: 'string',
														description:
															'Global Unique ID. Default empty.',
													},
													post_category: {
														type: 'array',
														items: {
															type: 'string',
														},
														description:
															"Array of category slugs. Defaults to the site's default category.",
													},
													post_tags: {
														type: 'array',
														items: {
															type: 'string',
														},
														description:
															'Array of tag names. Default empty.',
													},
													tax_input: {
														type: 'object',
														additionalProperties: {
															type: 'array',
															items: {
																type: 'string',
															},
														},
														description:
															'Taxonomy terms keyed by taxonomy name. For hierarchical taxonomies: array of term names. For non-hierarchical: array of term names or slugs.\n\nExamples: ```json tax_input: {   // For hierarchical taxonomies like categories   "category": ["Books", "Fiction", "Science Fiction"],\n\n  // For non-hierarchical taxonomies like tags   "post_tag": ["bestseller", "featured", "summer-reading"],\n\n  // For custom taxonomies   "genre": ["mystery", "thriller"],   "author": ["Jane Doe", "John Smith"] } ```',
													},
													meta_input: {
														type: 'object',
														additionalProperties: {
															anyOf: [
																{
																	type: 'string',
																},
																{
																	type: 'boolean',
																},
																{
																	type: 'number',
																},
																{
																	type: 'array',
																	items: {
																		$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																	},
																},
																{
																	type: 'object',
																	additionalProperties:
																		{
																			$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																		},
																},
															],
														},
														description:
															'Post meta keyed by meta key to value. Default empty.\n\nExamples: ```json meta_input: {   // Simple values   "price": "19.99",   "in_stock": true,   "stock": 42,\n\n  // Array values   "related_products": [123, 456, 789],   "product_colors": ["red", "blue", "green"],\n\n  // Object values   "_product_attributes": {     "color": {       "name": "Color",       "value": "Red",       "position": 0,       "visible": true     }   },   "seo_data": {     "title": "Custom SEO Title",     "description": "Custom meta description",     "keywords": ["product", "featured"]   } } ```',
													},
													page_template: {
														type: 'string',
														description:
															"Specifies the page template file to use. This parameter only applies if post_type is 'page'. For other post types, it's ignored. Provide the template filename (e.g., 'template-contact.php'). Include subdirectory if applicable (e.g., 'templates/full-width.php'). To set a template for non-page post types, use meta_input with key '_wp_page_template'.",
														default: '',
													},
												},
												required: ['post_title'],
												additionalProperties: false,
												description:
													'Post data type. It is inspired by the wp_insert_post() arguments, but it diverges from it in a few ways.',
											},
											{
												type: 'array',
												items: {
													anyOf: [
														{
															$ref: '#/definitions/DataSources.FileDataReference',
														},
														{
															type: 'object',
															properties: {
																post_author: {
																	type: 'number',
																	description:
																		'Username of the post author.\n\nIf missing, the default value will be resolved in the following order until one is available:\n\n* Default user defined in the runner configuration.\n* The first administrator in the database.\n* The first user in the database.\n* A newly created user.\n\nThe aggressive resolution is necessary because post_author is NOT NULL in the database schema.',
																},
																post_date: {
																	type: 'string',
																	description:
																		"The date of the post in UTC. Accepts format 'YYYY-MM-DD HH:MM:SS'. Can be used to schedule future posts (when used with post_status: 'future').",
																	default:
																		'Current time',
																},
																post_content: {
																	type: 'string',
																	description:
																		'The main post content. Can contain HTML, shortcodes, etc. While technically optional, posts are usually expected to have content.',
																	default: '',
																},
																post_title: {
																	type: 'string',
																	description:
																		'The post title.',
																},
																post_excerpt: {
																	type: 'string',
																	description:
																		'The post excerpt. Default empty.',
																},
																post_status: {
																	type: 'string',
																	enum: [
																		'publish',
																		'pending',
																		'draft',
																		'auto-draft',
																		'future',
																		'private',
																		'inherit',
																		'trash',
																	],
																	description:
																		'The post status',
																},
																post_type: {
																	type: 'string',
																	description:
																		"The post type (e.g., 'post', 'page'). Default 'post'.",
																},
																comment_status:
																	{
																		type: 'string',
																		enum: [
																			'open',
																			'closed',
																		],
																		description:
																			"Whether comments are allowed ('open' or 'closed').",
																		default:
																			"'open'.",
																	},
																post_password: {
																	type: 'string',
																	description:
																		'A password to protect access. Default empty.',
																},
																post_name: {
																	type: 'string',
																	description:
																		'The URL slug. Default sanitized post_title for new posts.',
																},
																post_parent_name:
																	{
																		type: 'string',
																		description:
																			'Post parent name for hierarchical post types (e.g., pages). Default empty.',
																	},
																menu_order: {
																	type: 'number',
																	description:
																		'Menu order within a post type. Default 0.',
																},
																post_mime_type:
																	{
																		type: 'string',
																		description:
																			'MIME type for attachments. Default empty.',
																	},
																guid: {
																	type: 'string',
																	description:
																		'Global Unique ID. Default empty.',
																},
																post_category: {
																	type: 'array',
																	items: {
																		type: 'string',
																	},
																	description:
																		"Array of category slugs. Defaults to the site's default category.",
																},
																post_tags: {
																	type: 'array',
																	items: {
																		type: 'string',
																	},
																	description:
																		'Array of tag names. Default empty.',
																},
																tax_input: {
																	type: 'object',
																	additionalProperties:
																		{
																			type: 'array',
																			items: {
																				type: 'string',
																			},
																		},
																	description:
																		'Taxonomy terms keyed by taxonomy name. For hierarchical taxonomies: array of term names. For non-hierarchical: array of term names or slugs.\n\nExamples: ```json tax_input: {   // For hierarchical taxonomies like categories   "category": ["Books", "Fiction", "Science Fiction"],\n\n  // For non-hierarchical taxonomies like tags   "post_tag": ["bestseller", "featured", "summer-reading"],\n\n  // For custom taxonomies   "genre": ["mystery", "thriller"],   "author": ["Jane Doe", "John Smith"] } ```',
																},
																meta_input: {
																	type: 'object',
																	additionalProperties:
																		{
																			anyOf: [
																				{
																					type: 'string',
																				},
																				{
																					type: 'boolean',
																				},
																				{
																					type: 'number',
																				},
																				{
																					type: 'array',
																					items: {
																						$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																					},
																				},
																				{
																					type: 'object',
																					additionalProperties:
																						{
																							$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																						},
																				},
																			],
																		},
																	description:
																		'Post meta keyed by meta key to value. Default empty.\n\nExamples: ```json meta_input: {   // Simple values   "price": "19.99",   "in_stock": true,   "stock": 42,\n\n  // Array values   "related_products": [123, 456, 789],   "product_colors": ["red", "blue", "green"],\n\n  // Object values   "_product_attributes": {     "color": {       "name": "Color",       "value": "Red",       "position": 0,       "visible": true     }   },   "seo_data": {     "title": "Custom SEO Title",     "description": "Custom meta description",     "keywords": ["product", "featured"]   } } ```',
																},
																page_template: {
																	type: 'string',
																	description:
																		"Specifies the page template file to use. This parameter only applies if post_type is 'page'. For other post types, it's ignored. Provide the template filename (e.g., 'template-contact.php'). Include subdirectory if applicable (e.g., 'templates/full-width.php'). To set a template for non-page post types, use meta_input with key '_wp_page_template'.",
																	default: '',
																},
															},
															required: [
																'post_title',
															],
															additionalProperties: false,
															description:
																'Post data type. It is inspired by the wp_insert_post() arguments, but it diverges from it in a few ways.',
														},
													],
												},
											},
										],
									},
								},
								required: ['source', 'type'],
							},
							{
								type: 'object',
								properties: {
									type: { type: 'string', const: 'wxr' },
								},
								required: ['type'],
								oneOf: [
									{
										type: 'object',
										additionalProperties: false,
										properties: {
											authorsMode: {
												type: 'string',
												const: 'map',
												description:
													'Map remote authors to existing local authors.',
											},
											authorsMap: {
												type: 'object',
												additionalProperties: {
													type: 'string',
												},
											},
											urlsMode: {
												type: 'string',
												enum: ['rewrite', 'preserve'],
												description:
													'Whether to rewrite the hrefs in the remote site\'s content URLs in the WXR file from the remote site domain to the current site domain\'s (and path etc).\n\nPossible values:\n\n* "rewrite" – Rewrite the hrefs to the current site domain\'s (and path etc).\n* "preserve" – Preserve the hrefs as they are.',
												default: '"rewrite".',
											},
											urlsMap: {
												type: 'object',
												additionalProperties: {
													$ref: '#/definitions/DataSources.URLReference',
												},
												description:
													'A mapping of base URLs to rewrite.',
												propertyNames: {
													$ref: '#/definitions/DataSources.URLReference',
												},
											},
											type: {
												type: 'string',
												const: 'wxr',
											},
											source: {
												anyOf: [
													{
														$ref: '#/definitions/DataSources.FileDataReference',
													},
													{
														type: 'array',
														items: {
															$ref: '#/definitions/DataSources.FileDataReference',
														},
													},
												],
											},
											staticAssets: {
												type: 'string',
												enum: ['fetch', 'hotlink'],
												description:
													'Static assets handling.\n\nPossible values:\n\n* "fetch" – Fetch the static assets and save them to the local filesystem.\n* "hotlink" – Hotlink the static assets from the remote site.',
												default: '"fetch".',
											},
											defaultAuthorUsername: {
												type: 'string',
												description:
													'The default author to use when `mode` is "default-author".',
												default: '"admin".',
											},
											importUsers: {
												type: 'boolean',
												description:
													'Whether to import users from the remote site.',
												default: 'false.',
											},
											importComments: {
												type: 'boolean',
												description:
													'Whether to import comments from the remote site.',
												default: 'false.',
											},
										},
										required: [
											'authorsMap',
											'authorsMode',
											'source',
											'type',
										],
									},
									{
										type: 'object',
										additionalProperties: false,
										properties: {
											authorsMode: {
												type: 'string',
												enum: [
													'create',
													'default-author',
												],
												description:
													'How to handle authors that don\'t exist on the current site.\n\nPossible values:\n\n* "create" – Create a new author.\n* "default-author" – Use the default author.',
												default: '"create".',
											},
											authorsMap: {
												type: 'object',
												additionalProperties: {
													type: 'string',
												},
											},
											urlsMode: {
												type: 'string',
												enum: ['rewrite', 'preserve'],
												description:
													'Whether to rewrite the hrefs in the remote site\'s content URLs in the WXR file from the remote site domain to the current site domain\'s (and path etc).\n\nPossible values:\n\n* "rewrite" – Rewrite the hrefs to the current site domain\'s (and path etc).\n* "preserve" – Preserve the hrefs as they are.',
												default: '"rewrite".',
											},
											urlsMap: {
												type: 'object',
												additionalProperties: {
													$ref: '#/definitions/DataSources.URLReference',
												},
												description:
													'A mapping of base URLs to rewrite.',
												propertyNames: {
													$ref: '#/definitions/DataSources.URLReference',
												},
											},
											type: {
												type: 'string',
												const: 'wxr',
											},
											source: {
												anyOf: [
													{
														$ref: '#/definitions/DataSources.FileDataReference',
													},
													{
														type: 'array',
														items: {
															$ref: '#/definitions/DataSources.FileDataReference',
														},
													},
												],
											},
											staticAssets: {
												type: 'string',
												enum: ['fetch', 'hotlink'],
												description:
													'Static assets handling.\n\nPossible values:\n\n* "fetch" – Fetch the static assets and save them to the local filesystem.\n* "hotlink" – Hotlink the static assets from the remote site.',
												default: '"fetch".',
											},
											defaultAuthorUsername: {
												type: 'string',
												description:
													'The default author to use when `mode` is "default-author".',
												default: '"admin".',
											},
											importUsers: {
												type: 'boolean',
												description:
													'Whether to import users from the remote site.',
												default: 'false.',
											},
											importComments: {
												type: 'boolean',
												description:
													'Whether to import comments from the remote site.',
												default: 'false.',
											},
										},
										required: ['source', 'type'],
									},
								],
							},
						],
					},
				},
				users: {
					type: 'array',
					items: {
						type: 'object',
						properties: {
							username: { type: 'string' },
							email: { type: 'string' },
							role: { type: 'string' },
							meta: {
								type: 'object',
								additionalProperties: { type: 'string' },
							},
						},
						required: ['username', 'email', 'role', 'meta'],
						additionalProperties: false,
					},
				},
				roles: {
					type: 'array',
					items: {
						type: 'object',
						properties: {
							name: { type: 'string' },
							capabilities: {
								type: 'object',
								additionalProperties: { type: 'string' },
							},
						},
						required: ['name', 'capabilities'],
						additionalProperties: false,
					},
				},
				additionalStepsAfterExecution: {
					type: 'array',
					items: {
						type: 'object',
						discriminator: { propertyName: 'step' },
						required: ['step'],
						oneOf: [
							{
								type: 'object',
								properties: {
									step: {
										type: 'string',
										const: 'activatePlugin',
									},
									pluginPath: {
										type: 'string',
										description:
											'Path to the plugin directory as absolute path (/wordpress/wp-content/plugins/plugin-name); or the plugin entry file relative to the plugins directory (plugin-name/plugin-name.php).',
									},
									humanReadableName: {
										type: 'string',
										description:
											'Human-readable name of the plugin for the progress bar.\n\nFor example, with the following Blueprint:\n\n```json {     "steps": [         {             "step": "activatePlugin",             "pluginPath": "wordpress-seo/wp-seo.php",             "humanReadableName": "Yoast SEO"         }     ] } ```\n\nThe progress bar will show "Activating Yoast SEO" instead of "Activating wordpress-seo/wp-seo.php".',
									},
								},
								required: ['step', 'pluginPath'],
								additionalProperties: false,
								description: '}}}',
							},
							{
								type: 'object',
								properties: {
									step: {
										type: 'string',
										const: 'activateTheme',
									},
									themeDirectoryName: {
										type: 'string',
										description:
											'The name of the theme directory inside wp-content/themes/',
									},
									humanReadableName: {
										type: 'string',
										description:
											'Human-readable name of the theme for the progress bar.\n\nFor example, with the following Blueprint:\n\n```json {     "steps": [         {             "step": "activateTheme",             "themeDirectoryName": "twentytwentythree",             "humanReadableName": "Twenty Twenty-Three"         }     ] } ```\n\nThe progress bar will show "Activating Twenty Twenty-Three" instead of "Activating twentytwentythree".',
									},
								},
								required: ['step', 'themeDirectoryName'],
								additionalProperties: false,
							},
							{
								type: 'object',
								properties: {
									step: { type: 'string', const: 'cp' },
									fromPath: { type: 'string' },
									toPath: { type: 'string' },
								},
								required: ['step', 'fromPath', 'toPath'],
								additionalProperties: false,
							},
							{
								type: 'object',
								properties: {
									step: {
										type: 'string',
										const: 'defineConstants',
									},
									constants: {
										type: 'object',
										additionalProperties: {
											type: [
												'boolean',
												'string',
												'number',
											],
										},
										properties: {
											WP_DEBUG: { type: 'boolean' },
											WP_DEBUG_LOG: { type: 'boolean' },
											WP_DEBUG_DISPLAY: {
												type: 'boolean',
											},
											SCRIPT_DEBUG: { type: 'boolean' },
										},
									},
								},
								required: ['step', 'constants'],
								additionalProperties: false,
							},
							{
								type: 'object',
								properties: {
									step: {
										type: 'string',
										const: 'enableMultisite',
										description:
											'Converts the target WordPress installation into a multisite network.',
									},
								},
								required: ['step'],
								additionalProperties: false,
							},
							{
								type: 'object',
								properties: {
									step: {
										type: 'string',
										const: 'importContent',
									},
									content: {
										type: 'array',
										items: {
											anyOf: [
												{
													type: 'object',
													properties: {
														type: {
															type: 'string',
															const: 'mysql-dump',
														},
														source: {
															anyOf: [
																{
																	$ref: '#/definitions/DataSources.FileDataReference',
																},
																{
																	type: 'array',
																	items: {
																		$ref: '#/definitions/DataSources.FileDataReference',
																	},
																},
															],
														},
													},
													required: [
														'type',
														'source',
													],
													additionalProperties: false,
												},
												{
													type: 'object',
													additionalProperties: false,
													properties: {
														urlsMode: {
															type: 'string',
															enum: [
																'rewrite',
																'preserve',
															],
															description:
																'Whether to rewrite the hrefs in the remote site\'s content URLs in the WXR file from the remote site domain to the current site domain\'s (and path etc).\n\nPossible values:\n\n* "rewrite" – Rewrite the hrefs to the current site domain\'s (and path etc).\n* "preserve" – Preserve the hrefs as they are.',
															default:
																'"rewrite".',
														},
														urlsMap: {
															type: 'object',
															additionalProperties:
																{
																	$ref: '#/definitions/DataSources.URLReference',
																},
															description:
																'A mapping of base URLs to rewrite.',
															propertyNames: {
																$ref: '#/definitions/DataSources.URLReference',
															},
														},
														type: {
															type: 'string',
															const: 'posts',
														},
														source: {
															anyOf: [
																{
																	$ref: '#/definitions/DataSources.FileDataReference',
																},
																{
																	type: 'object',
																	properties:
																		{
																			post_author:
																				{
																					type: 'number',
																					description:
																						'Username of the post author.\n\nIf missing, the default value will be resolved in the following order until one is available:\n\n* Default user defined in the runner configuration.\n* The first administrator in the database.\n* The first user in the database.\n* A newly created user.\n\nThe aggressive resolution is necessary because post_author is NOT NULL in the database schema.',
																				},
																			post_date:
																				{
																					type: 'string',
																					description:
																						"The date of the post in UTC. Accepts format 'YYYY-MM-DD HH:MM:SS'. Can be used to schedule future posts (when used with post_status: 'future').",
																					default:
																						'Current time',
																				},
																			post_content:
																				{
																					type: 'string',
																					description:
																						'The main post content. Can contain HTML, shortcodes, etc. While technically optional, posts are usually expected to have content.',
																					default:
																						'',
																				},
																			post_title:
																				{
																					type: 'string',
																					description:
																						'The post title.',
																				},
																			post_excerpt:
																				{
																					type: 'string',
																					description:
																						'The post excerpt. Default empty.',
																				},
																			post_status:
																				{
																					type: 'string',
																					enum: [
																						'publish',
																						'pending',
																						'draft',
																						'auto-draft',
																						'future',
																						'private',
																						'inherit',
																						'trash',
																					],
																					description:
																						'The post status',
																				},
																			post_type:
																				{
																					type: 'string',
																					description:
																						"The post type (e.g., 'post', 'page'). Default 'post'.",
																				},
																			comment_status:
																				{
																					type: 'string',
																					enum: [
																						'open',
																						'closed',
																					],
																					description:
																						"Whether comments are allowed ('open' or 'closed').",
																					default:
																						"'open'.",
																				},
																			post_password:
																				{
																					type: 'string',
																					description:
																						'A password to protect access. Default empty.',
																				},
																			post_name:
																				{
																					type: 'string',
																					description:
																						'The URL slug. Default sanitized post_title for new posts.',
																				},
																			post_parent_name:
																				{
																					type: 'string',
																					description:
																						'Post parent name for hierarchical post types (e.g., pages). Default empty.',
																				},
																			menu_order:
																				{
																					type: 'number',
																					description:
																						'Menu order within a post type. Default 0.',
																				},
																			post_mime_type:
																				{
																					type: 'string',
																					description:
																						'MIME type for attachments. Default empty.',
																				},
																			guid: {
																				type: 'string',
																				description:
																					'Global Unique ID. Default empty.',
																			},
																			post_category:
																				{
																					type: 'array',
																					items: {
																						type: 'string',
																					},
																					description:
																						"Array of category slugs. Defaults to the site's default category.",
																				},
																			post_tags:
																				{
																					type: 'array',
																					items: {
																						type: 'string',
																					},
																					description:
																						'Array of tag names. Default empty.',
																				},
																			tax_input:
																				{
																					type: 'object',
																					additionalProperties:
																						{
																							type: 'array',
																							items: {
																								type: 'string',
																							},
																						},
																					description:
																						'Taxonomy terms keyed by taxonomy name. For hierarchical taxonomies: array of term names. For non-hierarchical: array of term names or slugs.\n\nExamples: ```json tax_input: {   // For hierarchical taxonomies like categories   "category": ["Books", "Fiction", "Science Fiction"],\n\n  // For non-hierarchical taxonomies like tags   "post_tag": ["bestseller", "featured", "summer-reading"],\n\n  // For custom taxonomies   "genre": ["mystery", "thriller"],   "author": ["Jane Doe", "John Smith"] } ```',
																				},
																			meta_input:
																				{
																					type: 'object',
																					additionalProperties:
																						{
																							anyOf: [
																								{
																									type: 'string',
																								},
																								{
																									type: 'boolean',
																								},
																								{
																									type: 'number',
																								},
																								{
																									type: 'array',
																									items: {
																										$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																									},
																								},
																								{
																									type: 'object',
																									additionalProperties:
																										{
																											$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																										},
																								},
																							],
																						},
																					description:
																						'Post meta keyed by meta key to value. Default empty.\n\nExamples: ```json meta_input: {   // Simple values   "price": "19.99",   "in_stock": true,   "stock": 42,\n\n  // Array values   "related_products": [123, 456, 789],   "product_colors": ["red", "blue", "green"],\n\n  // Object values   "_product_attributes": {     "color": {       "name": "Color",       "value": "Red",       "position": 0,       "visible": true     }   },   "seo_data": {     "title": "Custom SEO Title",     "description": "Custom meta description",     "keywords": ["product", "featured"]   } } ```',
																				},
																			page_template:
																				{
																					type: 'string',
																					description:
																						"Specifies the page template file to use. This parameter only applies if post_type is 'page'. For other post types, it's ignored. Provide the template filename (e.g., 'template-contact.php'). Include subdirectory if applicable (e.g., 'templates/full-width.php'). To set a template for non-page post types, use meta_input with key '_wp_page_template'.",
																					default:
																						'',
																				},
																		},
																	required: [
																		'post_title',
																	],
																	additionalProperties: false,
																	description:
																		'Post data type. It is inspired by the wp_insert_post() arguments, but it diverges from it in a few ways.',
																},
																{
																	type: 'array',
																	items: {
																		anyOf: [
																			{
																				$ref: '#/definitions/DataSources.FileDataReference',
																			},
																			{
																				type: 'object',
																				properties:
																					{
																						post_author:
																							{
																								type: 'number',
																								description:
																									'Username of the post author.\n\nIf missing, the default value will be resolved in the following order until one is available:\n\n* Default user defined in the runner configuration.\n* The first administrator in the database.\n* The first user in the database.\n* A newly created user.\n\nThe aggressive resolution is necessary because post_author is NOT NULL in the database schema.',
																							},
																						post_date:
																							{
																								type: 'string',
																								description:
																									"The date of the post in UTC. Accepts format 'YYYY-MM-DD HH:MM:SS'. Can be used to schedule future posts (when used with post_status: 'future').",
																								default:
																									'Current time',
																							},
																						post_content:
																							{
																								type: 'string',
																								description:
																									'The main post content. Can contain HTML, shortcodes, etc. While technically optional, posts are usually expected to have content.',
																								default:
																									'',
																							},
																						post_title:
																							{
																								type: 'string',
																								description:
																									'The post title.',
																							},
																						post_excerpt:
																							{
																								type: 'string',
																								description:
																									'The post excerpt. Default empty.',
																							},
																						post_status:
																							{
																								type: 'string',
																								enum: [
																									'publish',
																									'pending',
																									'draft',
																									'auto-draft',
																									'future',
																									'private',
																									'inherit',
																									'trash',
																								],
																								description:
																									'The post status',
																							},
																						post_type:
																							{
																								type: 'string',
																								description:
																									"The post type (e.g., 'post', 'page'). Default 'post'.",
																							},
																						comment_status:
																							{
																								type: 'string',
																								enum: [
																									'open',
																									'closed',
																								],
																								description:
																									"Whether comments are allowed ('open' or 'closed').",
																								default:
																									"'open'.",
																							},
																						post_password:
																							{
																								type: 'string',
																								description:
																									'A password to protect access. Default empty.',
																							},
																						post_name:
																							{
																								type: 'string',
																								description:
																									'The URL slug. Default sanitized post_title for new posts.',
																							},
																						post_parent_name:
																							{
																								type: 'string',
																								description:
																									'Post parent name for hierarchical post types (e.g., pages). Default empty.',
																							},
																						menu_order:
																							{
																								type: 'number',
																								description:
																									'Menu order within a post type. Default 0.',
																							},
																						post_mime_type:
																							{
																								type: 'string',
																								description:
																									'MIME type for attachments. Default empty.',
																							},
																						guid: {
																							type: 'string',
																							description:
																								'Global Unique ID. Default empty.',
																						},
																						post_category:
																							{
																								type: 'array',
																								items: {
																									type: 'string',
																								},
																								description:
																									"Array of category slugs. Defaults to the site's default category.",
																							},
																						post_tags:
																							{
																								type: 'array',
																								items: {
																									type: 'string',
																								},
																								description:
																									'Array of tag names. Default empty.',
																							},
																						tax_input:
																							{
																								type: 'object',
																								additionalProperties:
																									{
																										type: 'array',
																										items: {
																											type: 'string',
																										},
																									},
																								description:
																									'Taxonomy terms keyed by taxonomy name. For hierarchical taxonomies: array of term names. For non-hierarchical: array of term names or slugs.\n\nExamples: ```json tax_input: {   // For hierarchical taxonomies like categories   "category": ["Books", "Fiction", "Science Fiction"],\n\n  // For non-hierarchical taxonomies like tags   "post_tag": ["bestseller", "featured", "summer-reading"],\n\n  // For custom taxonomies   "genre": ["mystery", "thriller"],   "author": ["Jane Doe", "John Smith"] } ```',
																							},
																						meta_input:
																							{
																								type: 'object',
																								additionalProperties:
																									{
																										anyOf: [
																											{
																												type: 'string',
																											},
																											{
																												type: 'boolean',
																											},
																											{
																												type: 'number',
																											},
																											{
																												type: 'array',
																												items: {
																													$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																												},
																											},
																											{
																												type: 'object',
																												additionalProperties:
																													{
																														$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																													},
																											},
																										],
																									},
																								description:
																									'Post meta keyed by meta key to value. Default empty.\n\nExamples: ```json meta_input: {   // Simple values   "price": "19.99",   "in_stock": true,   "stock": 42,\n\n  // Array values   "related_products": [123, 456, 789],   "product_colors": ["red", "blue", "green"],\n\n  // Object values   "_product_attributes": {     "color": {       "name": "Color",       "value": "Red",       "position": 0,       "visible": true     }   },   "seo_data": {     "title": "Custom SEO Title",     "description": "Custom meta description",     "keywords": ["product", "featured"]   } } ```',
																							},
																						page_template:
																							{
																								type: 'string',
																								description:
																									"Specifies the page template file to use. This parameter only applies if post_type is 'page'. For other post types, it's ignored. Provide the template filename (e.g., 'template-contact.php'). Include subdirectory if applicable (e.g., 'templates/full-width.php'). To set a template for non-page post types, use meta_input with key '_wp_page_template'.",
																								default:
																									'',
																							},
																					},
																				required:
																					[
																						'post_title',
																					],
																				additionalProperties: false,
																				description:
																					'Post data type. It is inspired by the wp_insert_post() arguments, but it diverges from it in a few ways.',
																			},
																		],
																	},
																},
															],
														},
													},
													required: [
														'source',
														'type',
													],
												},
												{
													type: 'object',
													additionalProperties: false,
													properties: {
														authorsMode: {
															type: 'string',
															const: 'map',
															description:
																'Map remote authors to existing local authors.',
														},
														authorsMap: {
															type: 'object',
															additionalProperties:
																{
																	type: 'string',
																},
														},
														urlsMode: {
															type: 'string',
															enum: [
																'rewrite',
																'preserve',
															],
															description:
																'Whether to rewrite the hrefs in the remote site\'s content URLs in the WXR file from the remote site domain to the current site domain\'s (and path etc).\n\nPossible values:\n\n* "rewrite" – Rewrite the hrefs to the current site domain\'s (and path etc).\n* "preserve" – Preserve the hrefs as they are.',
															default:
																'"rewrite".',
														},
														urlsMap: {
															type: 'object',
															additionalProperties:
																{
																	$ref: '#/definitions/DataSources.URLReference',
																},
															description:
																'A mapping of base URLs to rewrite.',
															propertyNames: {
																$ref: '#/definitions/DataSources.URLReference',
															},
														},
														type: {
															type: 'string',
															const: 'wxr',
														},
														source: {
															anyOf: [
																{
																	$ref: '#/definitions/DataSources.FileDataReference',
																},
																{
																	type: 'array',
																	items: {
																		$ref: '#/definitions/DataSources.FileDataReference',
																	},
																},
															],
														},
														staticAssets: {
															type: 'string',
															enum: [
																'fetch',
																'hotlink',
															],
															description:
																'Static assets handling.\n\nPossible values:\n\n* "fetch" – Fetch the static assets and save them to the local filesystem.\n* "hotlink" – Hotlink the static assets from the remote site.',
															default: '"fetch".',
														},
														defaultAuthorUsername: {
															type: 'string',
															description:
																'The default author to use when `mode` is "default-author".',
															default: '"admin".',
														},
														importUsers: {
															type: 'boolean',
															description:
																'Whether to import users from the remote site.',
															default: 'false.',
														},
														importComments: {
															type: 'boolean',
															description:
																'Whether to import comments from the remote site.',
															default: 'false.',
														},
													},
													required: [
														'authorsMap',
														'authorsMode',
														'source',
														'type',
													],
												},
												{
													type: 'object',
													additionalProperties: false,
													properties: {
														authorsMode: {
															type: 'string',
															enum: [
																'create',
																'default-author',
															],
															description:
																'How to handle authors that don\'t exist on the current site.\n\nPossible values:\n\n* "create" – Create a new author.\n* "default-author" – Use the default author.',
															default:
																'"create".',
														},
														authorsMap: {
															type: 'object',
															additionalProperties:
																{
																	type: 'string',
																},
														},
														urlsMode: {
															type: 'string',
															enum: [
																'rewrite',
																'preserve',
															],
															description:
																'Whether to rewrite the hrefs in the remote site\'s content URLs in the WXR file from the remote site domain to the current site domain\'s (and path etc).\n\nPossible values:\n\n* "rewrite" – Rewrite the hrefs to the current site domain\'s (and path etc).\n* "preserve" – Preserve the hrefs as they are.',
															default:
																'"rewrite".',
														},
														urlsMap: {
															type: 'object',
															additionalProperties:
																{
																	$ref: '#/definitions/DataSources.URLReference',
																},
															description:
																'A mapping of base URLs to rewrite.',
															propertyNames: {
																$ref: '#/definitions/DataSources.URLReference',
															},
														},
														type: {
															type: 'string',
															const: 'wxr',
														},
														source: {
															anyOf: [
																{
																	$ref: '#/definitions/DataSources.FileDataReference',
																},
																{
																	type: 'array',
																	items: {
																		$ref: '#/definitions/DataSources.FileDataReference',
																	},
																},
															],
														},
														staticAssets: {
															type: 'string',
															enum: [
																'fetch',
																'hotlink',
															],
															description:
																'Static assets handling.\n\nPossible values:\n\n* "fetch" – Fetch the static assets and save them to the local filesystem.\n* "hotlink" – Hotlink the static assets from the remote site.',
															default: '"fetch".',
														},
														defaultAuthorUsername: {
															type: 'string',
															description:
																'The default author to use when `mode` is "default-author".',
															default: '"admin".',
														},
														importUsers: {
															type: 'boolean',
															description:
																'Whether to import users from the remote site.',
															default: 'false.',
														},
														importComments: {
															type: 'boolean',
															description:
																'Whether to import comments from the remote site.',
															default: 'false.',
														},
													},
													required: [
														'source',
														'type',
													],
												},
											],
										},
									},
								},
								required: ['step', 'content'],
								additionalProperties: false,
							},
							{
								type: 'object',
								properties: {
									step: {
										type: 'string',
										const: 'importMedia',
									},
									media: {
										type: 'array',
										items: {
											anyOf: [
												{
													$ref: '#/definitions/DataSources.FileDataReference',
												},
												{
													type: 'object',
													properties: {
														source: {
															$ref: '#/definitions/DataSources.FileDataReference',
														},
														title: {
															type: 'string',
														},
														description: {
															type: 'string',
														},
														alt: { type: 'string' },
														caption: {
															type: 'string',
														},
													},
													required: ['source'],
													additionalProperties: false,
												},
											],
										},
									},
								},
								required: ['step', 'media'],
								additionalProperties: false,
							},
							{
								type: 'object',
								properties: {
									step: {
										type: 'string',
										const: 'importThemeStarterContent',
									},
									themeSlug: {
										type: 'string',
										description:
											'The name of the theme to import content from.',
									},
								},
								required: ['step'],
								additionalProperties: false,
							},
							{
								type: 'object',
								additionalProperties: false,
								properties: {
									source: {
										anyOf: [
											{
												$ref: '#/definitions/DataSources.DataReference',
											},
											{
												$ref: '#/definitions/DataSources.PluginDirectoryReference',
											},
										],
									},
									active: {
										type: 'boolean',
										description:
											'Whether to activate the plugin.',
										default: 'true.',
									},
									activationOptions: {
										type: 'object',
										additionalProperties: {
											anyOf: [
												{ type: 'string' },
												{ type: 'boolean' },
												{ type: 'number' },
												{
													type: 'array',
													items: {
														$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
													},
												},
												{
													type: 'object',
													additionalProperties: {
														$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
													},
												},
											],
										},
										description:
											'Parameters to pass to the plugin during activation.\n\nThese options are stored in a site option that the plugin can access during its activation hook. The option name is:\n\n```php \'blueprint_activation_\' . plugin_basename( __FILE__ ) ```\n\nThis ensures uniqueness even when multiple versions of the same plugin exist. This is similar to how the `register_activation_hook` function requires the plugin file path as its first argument.\n\nThe Blueprint runner will remove the option after activating the plugin.\n\nExample:\n\nIn the Blueprint: ```json {     "source": "woocommerce",     "activationOptions": {         "storeCity": "Wrocław",         "storeCountry": "Poland",         "storePostalCode": "53-607"     } } ```\n\nIn the plugin\'s activation hook:\n\n```php register_activation_hook( __FILE__, function( $network_wide ) {     // Get the activation options from the transient     $option_name = \'blueprint_activation_\' . plugin_basename( __FILE__ );     $blueprint_activation_options = get_option( $option_name ) ?? [];\n\n    if ( $blueprint_activation_options ) {         $store_city = $blueprint_activation_options[\'storeCity\'] ?? \'\';         $store_country = $blueprint_activation_options[\'storeCountry\'] ?? \'\';         $store_postal_code = $blueprint_activation_options[\'storePostalCode\'] ?? \'\';\n\n        // ...do something with the options...     }\n\n    // Continue with normal activation... } ); ```',
									},
									targetDirectoryName: {
										type: 'string',
										description:
											'An explicit directory name within wp-content/plugins to install the plugin at. If not provided, it will be inferred from the plugin source.',
										pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
									},
									onError: {
										type: 'string',
										enum: ['skip-plugin', 'throw'],
										description:
											"Sometimes it's fine when a plugin fails to install.\n\nUse-case:   Compatibility testing. A Blueprint may install WordPress nightly with   a number of plugins to test. Some of those plugins may not yet be compatible   with the latest version of WordPress. This is something to take not of,   but not a strong reason to fail the entire Blueprint installation.",
										default: 'throw',
									},
									ifAlreadyInstalled: {
										type: 'string',
										enum: ['overwrite', 'skip', 'error'],
										description:
											'How to handle a plugin that is already installed.',
										default: 'overwrite',
									},
									humanReadableName: {
										type: 'string',
										description:
											'Human-readable name of the plugin for the progress bar.\n\nFor example, with the following Blueprint:\n\n```json {     "plugins": [         {             "source": "https://github.com/Automattic/jetpack/archive/refs/heads/beta.zip",             "humanReadableName": "Jetpack Beta"         }     ] } ```\n\nThe progress bar will show "Installing Jetpack Beta plugin" instead of "Installing https://github.com/Automattic/jetpack/archive/refs/heads/beta.zip".',
									},
									step: {
										type: 'string',
										const: 'installPlugin',
									},
								},
								required: ['source', 'step'],
							},
							{
								type: 'object',
								additionalProperties: false,
								properties: {
									source: {
										anyOf: [
											{
												$ref: '#/definitions/DataSources.ThemeDirectoryReference',
											},
											{
												$ref: '#/definitions/DataSources.DataReference',
											},
										],
									},
									importStarterContent: {
										type: 'boolean',
										description:
											"Whether to import the theme's starter content after installing it.",
									},
									targetDirectoryName: {
										type: 'string',
										description:
											'An explicit directory name within wp-content/themes to install the theme at. If not provided, it will be inferred from the theme source.',
										pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
									},
									onError: {
										type: 'string',
										enum: ['skip-theme', 'throw'],
										description:
											"Sometimes it's fine when a theme fails to install.",
										default: 'throw',
									},
									ifAlreadyInstalled: {
										type: 'string',
										enum: ['overwrite', 'skip', 'error'],
										description:
											'How to handle a theme that is already installed.',
										default: 'overwrite',
									},
									humanReadableName: {
										type: 'string',
										description:
											'Human-readable name of the theme for the progress bar.\n\nFor example, with the following Blueprint:\n\n```json {     "themes": [         {             "source": "https://github.com/Automattic/adventurer/archive/refs/heads/beta.zip",             "humanReadableName": "Adventurer"         }     ] } ```\n\nThe progress bar will show "Installing Adventurer theme" instead of "Installing https://github.com/Automattic/adventurer/archive/refs/heads/beta.zip".',
									},
									step: {
										type: 'string',
										const: 'installTheme',
									},
									active: {
										type: 'boolean',
										description:
											'Whether to activate the theme after installing it.\n\nThis is not a part of the theme definition. Only the step can explicitly provide this option. The default value is `true`.',
									},
								},
								required: ['source', 'step'],
							},
							{
								type: 'object',
								properties: {
									step: { type: 'string', const: 'mkdir' },
									path: { type: 'string' },
								},
								required: ['step', 'path'],
								additionalProperties: false,
							},
							{
								type: 'object',
								properties: {
									step: { type: 'string', const: 'mv' },
									fromPath: { type: 'string' },
									toPath: { type: 'string' },
								},
								required: ['step', 'fromPath', 'toPath'],
								additionalProperties: false,
							},
							{
								type: 'object',
								properties: {
									step: { type: 'string', const: 'rm' },
									path: { type: 'string' },
								},
								required: ['step', 'path'],
								additionalProperties: false,
							},
							{
								type: 'object',
								properties: {
									step: { type: 'string', const: 'rmdir' },
									path: { type: 'string' },
								},
								required: ['step', 'path'],
								additionalProperties: false,
							},
							{
								type: 'object',
								properties: {
									step: {
										type: 'string',
										const: 'resetData',
									},
									contentTypes: {
										type: 'array',
										items: {
											type: 'string',
											enum: [
												'posts',
												'pages',
												'comments',
											],
											description:
												'Content types created by a vanilla WordPress installation and controlled by `contentBaseline`.',
										},
										description:
											'Content types to remove. When omitted, all posts, pages, custom post types, and comments are removed.',
									},
								},
								required: ['step'],
								additionalProperties: false,
							},
							{
								type: 'object',
								properties: {
									step: { type: 'string', const: 'runPHP' },
									code: {
										$ref: '#/definitions/DataSources.FileDataReference',
										description: 'The PHP file to execute.',
									},
									env: {
										type: 'object',
										additionalProperties: {
											type: 'string',
										},
										description:
											'Environment variables to set for this run.',
									},
								},
								required: ['step', 'code'],
								additionalProperties: false,
							},
							{
								type: 'object',
								properties: {
									step: { type: 'string', const: 'runSQL' },
									source: {
										$ref: '#/definitions/DataSources.FileDataReference',
									},
								},
								required: ['step', 'source'],
								additionalProperties: false,
							},
							{
								type: 'object',
								properties: {
									step: {
										type: 'string',
										const: 'setSiteLanguage',
									},
									language: {
										type: 'string',
										description:
											"The language to set, e.g. 'en_US'",
									},
								},
								required: ['step', 'language'],
								additionalProperties: false,
								description:
									'Sets the site language and download translations for WordPress core and all the installed plugins and themes.',
							},
							{
								type: 'object',
								properties: {
									step: {
										type: 'string',
										const: 'setSiteOptions',
									},
									options: {
										type: 'object',
										additionalProperties: {
											anyOf: [
												{ type: 'string' },
												{ type: 'boolean' },
												{ type: 'number' },
												{
													type: 'array',
													items: {
														$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
													},
												},
												{
													type: 'object',
													additionalProperties: {
														$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
													},
												},
											],
										},
									},
								},
								required: ['step', 'options'],
								additionalProperties: false,
							},
							{
								type: 'object',
								properties: {
									step: { type: 'string', const: 'unzip' },
									zipFile: {
										$ref: '#/definitions/DataSources.FileDataReference',
										description:
											'The zip file resource to extract.',
									},
									extractToPath: {
										type: 'string',
										description:
											'The path to extract the zip file to inside the virtual filesystem.',
									},
								},
								required: ['step', 'zipFile', 'extractToPath'],
								additionalProperties: false,
								description:
									'Unzips a file. While this step is not strictly necessary, it is very convenient for:\n\n* Working with GitHub releases that output doubly zipped data.\n* Preprocessing zipped data before using them in the Blueprint.',
							},
							{
								type: 'object',
								properties: {
									step: { type: 'string', const: 'wp-cli' },
									command: { type: 'string' },
									wpCliPath: { type: 'string' },
								},
								required: ['step', 'command'],
								additionalProperties: false,
							},
							{
								type: 'object',
								properties: {
									step: {
										type: 'string',
										const: 'writeFiles',
									},
									files: {
										type: 'object',
										additionalProperties: {
											$ref: '#/definitions/DataSources.DataReference',
										},
									},
								},
								required: ['step', 'files'],
								additionalProperties: false,
							},
						],
					},
				},
			},
			required: ['version'],
			additionalProperties: false,
			allOf: [
				{
					if: {
						properties: { usersBaseline: { const: 'empty' } },
						required: ['usersBaseline'],
					},
					then: {
						properties: {
							contentBaseline: { const: 'empty' },
							users: {
								type: 'array',
								contains: {
									type: 'object',
									properties: {
										role: { const: 'administrator' },
									},
									required: ['role'],
								},
							},
						},
						required: ['contentBaseline', 'users'],
					},
				},
				{
					if: {
						properties: { wordpressVersion: { const: 'none' } },
						required: ['wordpressVersion'],
					},
					then: {
						properties: {
							contentBaseline: false,
							usersBaseline: false,
						},
					},
				},
			],
		},
		'DataSources.URLReference': {
			type: 'string',
			description:
				'A reference to a HTTP or HTTPS URL.\n\nThe URLs are parsed using the WHATWG URL standard, which means they can optionally contain usernames and passwords if needed.',
			format: 'whatwg-http-url',
		},
		'DataSources.ExecutionContextPath': {
			type: 'string',
			description:
				'A reference to a file in the Blueprint Execution Context – see the main proposal document for more context.\n\nThe path must start with either ./ or / to distinguish it from a plugin or theme slug. Regardless of the prefix (./ or /), the path is relative to the Blueprint Execution Context root:\n\n* Relative paths (./) are relative to the location of blueprint.json file.\n* Absolute paths (/) are chrooted at the Blueprint Execution Context root which is   still the directory where blueprint.json is located.\n\nIt is not possible to escape the Blueprint Execution Context via "../" sequences.',
			pattern: '^(?!.*(?:^|/)\\.\\.(?:/|$))(?:\\./|/).*$',
		},
		'DataSources.WordPressVersion': {
			description:
				'WordPress version, e.g. "latest", "beta", "trunk", "none", "6.4", "6.4.3", "6.8-RC1", or "6.7-beta2".\n\nThese refer to slugs of specific WordPress releases as listed in the first table column on https://wordpress.org/download/releases/. "none" is not a release. It means the Blueprint runs PHP without installing WordPress.\n\nThe WordPressVersion type is only meaningful in the top-level `wordpressVersion` property.',
			type: 'string',
			pattern:
				'^(?:latest|beta|trunk|nightly|none|\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?)$',
		},
		'DataSources.SimpleVersionExpression': {
			type: 'string',
			pattern: '^(?:latest|\\d+\\.\\d+(?:\\.\\d+)?)$',
		},
		'DataSources.DataReference': {
			anyOf: [
				{ $ref: '#/definitions/DataSources.URLReference' },
				{ $ref: '#/definitions/DataSources.ExecutionContextPath' },
				{ $ref: '#/definitions/DataSources.InlineFile' },
				{ $ref: '#/definitions/DataSources.InlineDirectory' },
				{ $ref: '#/definitions/DataSources.GitPath' },
			],
			description: 'A union of all general data reference types.',
		},
		'DataSources.InlineFile': {
			type: 'object',
			properties: {
				filename: {
					type: 'string',
					pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
				},
				content: { type: 'string' },
			},
			required: ['filename', 'content'],
			additionalProperties: false,
			description:
				'A file that is inlined within the Blueprint JSON document.\n\nExample:\n\n```json {     "filename": "index.php",     "content": "<?php echo \'Hello, world!\'; ?>" } ```',
		},
		'DataSources.InlineDirectory': {
			type: 'object',
			properties: {
				directoryName: {
					type: 'string',
					pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
				},
				files: {
					type: 'object',
					additionalProperties: {
						anyOf: [
							{ type: 'string' },
							{
								$ref: '#/definitions/DataSources.NestedInlineDirectory',
							},
						],
					},
					propertyNames: { pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$' },
				},
			},
			required: ['directoryName', 'files'],
			additionalProperties: false,
			description:
				'A directory that is inlined within the Blueprint JSON document.\n\nExample:\n\n```json {     "directoryName": "my-directory",     "files": {         "index.php": "<?php echo \'Hello, world!\'; ?>",         "my-sub-directory": {             "files": {                 "index.php": "<?php echo \'Hello, world!\'; ?>"             }         }     } } ```',
		},
		'DataSources.NestedInlineDirectory': {
			type: 'object',
			properties: {
				files: {
					type: 'object',
					additionalProperties: {
						anyOf: [
							{ type: 'string' },
							{
								$ref: '#/definitions/DataSources.NestedInlineDirectory',
							},
						],
					},
					propertyNames: { pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$' },
				},
			},
			required: ['files'],
			additionalProperties: false,
			description:
				'A child directory inside an inline directory.\n\nIts directory name comes from the parent `files` record key.',
		},
		'DataSources.GitPath': {
			type: 'object',
			properties: {
				gitRepository: {
					$ref: '#/definitions/DataSources.URLReference',
					description:
						'A HTTP or HTTPS URL of the remote git repository.',
				},
				ref: {
					type: 'string',
					description:
						'A branch name, commit hash, or tag name.\n\nDefaults to HEAD.',
				},
				pathInRepository: {
					type: 'string',
					description:
						'A path inside the git repository this data reference points to.\n\nDefaults to the root of the repository.',
					pattern: '^(?!.*(?:^|/)\\.\\.(?:/|$)).*$',
				},
			},
			required: ['gitRepository'],
			additionalProperties: false,
			description: 'A reference to a remote git repository.',
		},
		'DataSources.WordPressVersionConstraintVersion': {
			type: 'string',
			pattern: '^\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?$',
		},
		'DataSources.ComparableVersionExpression': {
			type: 'string',
			pattern: '^\\d+\\.\\d+(?:\\.\\d+)?$',
		},
		'DataSources.WordPressVersionPreferredVersion': {
			type: 'string',
			pattern:
				'^(?:latest|\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?)$',
		},
		'DataSources.PHPVersion': {
			description:
				'PHP version, e.g. "8.1", "8.1.3", or "next".\n\nThese refer to PHP versions as listed in https://www.php.net/releases/. `next` previews the php-src development branch and is currently supported by the web runtime only.\n\nThe PHPVersion type is only meaningful in the top-level `phpVersion` property.',
			type: 'string',
			pattern: '^(?:latest|next|\\d+\\.\\d+(?:\\.\\d+)?)$',
		},
		'DataSources.PHPVersionConstraintVersion': {
			type: 'string',
			pattern: '^(?:latest|\\d+\\.\\d+(?:\\.\\d+)?)$',
		},
		'DataSources.ThemeDirectoryReference': {
			anyOf: [
				{ $ref: '#/definitions/DataSources.Slug' },
				{ type: 'string' },
			],
			description:
				'Theme directory reference, e.g. "twentytwentythree", "adventurer@4.6.0", or "twentytwentyfour@latest".\n\nThese refer to specific theme slugs in the WordPress.org theme repository.\n\nFor example, a reference to "adventurer" means the Adventurer theme as seen on https://wordpress.org/themes/adventurer/.\n\nThese references are only meaningful in:\n\n* The top-level `themes` array\n* The `installTheme` imperative step',
		},
		'DataSources.Slug': {
			type: 'string',
			description:
				'A WordPress.org directory slug.\n\nSlugs are intentionally treated as opaque strings. Playground should not reject future WordPress.org slug formats just because they do not match the directory conventions common today.',
		},
		'DataSources.PluginDirectoryReference': {
			anyOf: [
				{ $ref: '#/definitions/DataSources.Slug' },
				{ type: 'string' },
			],
			description:
				'Plugin directory reference, e.g. "jetpack", "jetpack@6.4", or "akismet@6.4.3".\n\nThese refer to a specific plugin slugs in the WordPress.org plugin repository.\n\nFor example, a reference to "wordpress-seo" means the Yoast SEO plugin as seen on https://wordpress.org/plugins/wordpress-seo/.\n\nThe Plugin Directory Reference are only meaningful in:\n\n* The top-level `plugins` array\n* The `installPlugin` imperative step',
		},
		'alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845':
			{
				anyOf: [
					{ type: 'string' },
					{ type: 'boolean' },
					{ type: 'number' },
					{
						type: 'array',
						items: {
							$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
						},
					},
					{
						type: 'object',
						additionalProperties: {
							$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
						},
					},
				],
			},
		'DataSources.FileDataReference': {
			anyOf: [
				{ $ref: '#/definitions/DataSources.URLReference' },
				{ $ref: '#/definitions/DataSources.ExecutionContextPath' },
				{ $ref: '#/definitions/DataSources.TargetSitePath' },
				{ $ref: '#/definitions/DataSources.InlineFile' },
			],
			description: 'A data reference that must resolve to a single file.',
		},
		'DataSources.TargetSitePath': {
			type: 'string',
			description:
				'A path within the target WordPress site, relative to the WordPress root directory. For example, site:wp-content/uploads/2024/01/image.jpg.\n\nUnlike an execution-context path, this path is resolved from the mutable target filesystem when the consuming step runs. Earlier steps may therefore create the referenced file. The runner must keep the path inside the target WordPress root; it never names a file on the host filesystem.',
			pattern:
				'^site:(?!\\/*$)(?!\\.\\.(?:/|$))(?!.*\\/\\.\\.(?:/|$)).+$',
		},
	},
};
const schema12 = {
	type: 'object',
	properties: {
		version: {
			type: 'number',
			const: 2,
			description:
				'Not a generic \'number\' type – this schema is specifically for Blueprints v2. Version 1 had no "version" field and versions 3, 4, 5, etc will be different from version 2.',
		},
		$schema: {
			anyOf: [
				{ $ref: '#/definitions/DataSources.URLReference' },
				{ $ref: '#/definitions/DataSources.ExecutionContextPath' },
			],
			description: 'JSON Schema URL.',
		},
		blueprintMeta: {
			type: 'object',
			properties: {
				name: { type: 'string' },
				description: { type: 'string' },
				moreInfo: { type: 'string' },
				version: { type: 'string' },
				authors: { type: 'array', items: { type: 'string' } },
				homepage: { $ref: '#/definitions/DataSources.URLReference' },
				donateLink: { $ref: '#/definitions/DataSources.URLReference' },
				tags: { type: 'array', items: { type: 'string' } },
				license: { type: 'string' },
			},
			additionalProperties: false,
		},
		applicationOptions: {
			type: 'object',
			properties: {
				'wordpress-playground': {
					type: 'object',
					properties: {
						landingPage: {
							type: 'string',
							description:
								'The first page the user is redirected to once the Playground is loaded and the Blueprint is executed.',
							default: '/wp-admin',
						},
						login: {
							anyOf: [
								{ type: 'boolean' },
								{
									type: 'object',
									properties: {
										username: { type: 'string' },
										password: { type: 'string' },
									},
									required: ['username', 'password'],
									additionalProperties: false,
								},
							],
							description:
								'Whether to log the user in after the Blueprint is executed. If true, the user is logged in as "admin".',
							default: false,
						},
						networkAccess: {
							type: 'boolean',
							description:
								'Whether to allow the site to access the network.',
							default: false,
						},
						loadPhpExtensions: {
							type: 'array',
							items: { type: 'string', const: 'intl' },
							description:
								'Optional PHP extensions to load in the Playground runtime before executing the Blueprint. Extensions omitted from this list are not disabled.',
						},
					},
					additionalProperties: false,
					description: 'Options for the WordPress Playground.',
				},
			},
			required: ['wordpress-playground'],
			additionalProperties: false,
			description:
				'Divergence from Blueprints v1:\n\nThere are no `landingPage` or `login` top-level properties. Instead, Blueprint v2 introduces a dedicated top-level `applicationOptions` property for declaring options or opinions for different application contexts.\n\nTo keep Blueprints portable and focused on site creation, this specification only allows a small set of Playground-specific options. Other environments cannot declare additional options. Future versions of this specification may allow additional options – they will be discussed on a case-by-case basis.',
		},
		contentBaseline: {
			anyOf: [
				{ type: 'string', const: 'keep-all' },
				{ type: 'string', const: 'empty' },
				{
					type: 'string',
					enum: ['posts', 'pages'],
					description:
						'Content types created by a vanilla WordPress installation and controlled by `contentBaseline`.',
				},
				{
					type: 'array',
					items: {
						type: 'string',
						enum: ['posts', 'pages', 'comments'],
						description:
							'Content types created by a vanilla WordPress installation and controlled by `contentBaseline`.',
					},
					minItems: 1,
					uniqueItems: true,
					allOf: [
						{
							if: { contains: { const: 'comments' } },
							then: {
								allOf: [
									{ contains: { const: 'posts' } },
									{ contains: { const: 'pages' } },
								],
							},
						},
					],
				},
			],
			description:
				'The content from a vanilla WordPress installation to retain before applying the rest of the Blueprint. `keep-all` leaves the installation unchanged, `empty` removes its posts, pages, and comments, a content type retains only that type, and a list retains the selected content types.\n\nThis policy runs only when the current invocation creates vanilla WordPress. It is skipped when applying the Blueprint to an existing site, so it cannot erase content from that site. It is not valid when `wordpressVersion` is "none". Metadata and relationships follow their parent content. Empty content tables have their sequences reset so subsequent imports receive the identifiers they would on a site created without default content.\n\nComments can only be retained together with both posts and pages because the schema cannot know which type contains their parent records.',
			default: 'keep-all',
		},
		usersBaseline: {
			type: 'string',
			enum: ['keep-all', 'empty'],
			description:
				'The users from a vanilla WordPress installation to retain before applying the rest of the Blueprint. `keep-all` retains the administrator created by WordPress, while `empty` removes it before creating the users declared by this Blueprint.\n\nEmpty user tables have their sequences reset before those users are created.\n\nAn empty user baseline requires an empty content baseline so removing the installation administrator cannot silently delete or orphan authored content. It also requires at least one declared administrator, ensuring the resulting WordPress site remains manageable.\n\nLike `contentBaseline`, this policy is skipped when applying the Blueprint to an existing site and is not valid when `wordpressVersion` is "none".',
			default: 'keep-all',
		},
		siteLanguage: {
			type: 'string',
			description:
				'Sets the WPLANG constant and downloads any missing translations for WordPress core and all the installed plugins and themes. If you need a fine-grained control over the translations, use imperative steps in the `additionalStepsAfterExecution` array.',
			default: 'en_US',
		},
		siteOptions: {
			type: 'object',
			additionalProperties: {
				type: ['string', 'boolean', 'number', 'array', 'object'],
			},
			properties: {
				blogname: {
					type: 'string',
					description: 'Site title.\n\nExample: "Adam\'s Movies"',
					default: 'My WordPress Site',
				},
				timezone_string: {
					type: 'string',
					description: 'Example: "Poland/Warsaw"',
					default: 'UTC',
				},
				permalink_structure: {
					anyOf: [
						{ type: 'string' },
						{ type: 'boolean', const: false },
					],
					description:
						'Site permalink structure. If present and different from the current permalink structure, the Blueprint runner will run `$wp_rewrite->flush_rules();`. If you only want to set this option without flushing the rules, use an explicit `additionalStepsAfterExecution` step.\n\nExample: "/%year%/%monthnum%/%postname%/" or false for no pretty permalinks.',
					default: '/%postname%/',
				},
				siteUrl: false,
			},
			description:
				'Site options. In WordPress, the values are PHP-serializable, but Blueprints are intentionally restricted to an even stricter subset of those, that are JSON-serializable. This is to prevent passing JavaScript Date objects and similar.\n\nThe runner **MUST** use the WordPress `update_option` function to store the siteOptions values defined in this property as WordPress options. Lists and objects are passed to `update_option` as PHP arrays.\n\nSite options example:\n\n```json {     "blogname": "Adam\'s Movies",     "timezone": "Poland/Warsaw",     "gutenberg-experiments": { \t\t\t\'gutenberg-custom-dataviews\': true, \t\t\t\'gutenberg-new-posts-dashboard\': true, \t\t\t\'gutenberg-quick-edit-dataviews\': true \t\t} } ```',
		},
		constants: {
			type: 'object',
			additionalProperties: { type: ['boolean', 'string', 'number'] },
			properties: {
				WP_DEBUG: { type: 'boolean' },
				WP_DEBUG_LOG: { type: 'boolean' },
				WP_DEBUG_DISPLAY: { type: 'boolean' },
				SCRIPT_DEBUG: { type: 'boolean' },
			},
			description:
				'Constants to define in the wp-config.php file.\n\nThe runner may overwrite the define() calls in the wp-config.php file on the target site. It assumes the wp-config.php file at the Blueprint Execution Target is writable.',
		},
		wordpressVersion: {
			anyOf: [
				{ $ref: '#/definitions/DataSources.WordPressVersion' },
				{ $ref: '#/definitions/DataSources.DataReference' },
				{
					type: 'object',
					properties: {
						min: {
							$ref: '#/definitions/DataSources.WordPressVersionConstraintVersion',
						},
						max: {
							$ref: '#/definitions/DataSources.WordPressVersionConstraintVersion',
						},
						preferred: {
							$ref: '#/definitions/DataSources.WordPressVersionPreferredVersion',
							default: 'latest',
						},
					},
					required: ['min'],
					additionalProperties: false,
				},
			],
			description:
				'WordPress version to install or require.\n\nA string selects the version for a newly created site. A branch such as `6.8` selects the newest available release in that branch. Strings are selection hints and do not reject an existing site. `"none"` boots the PHP runtime without downloading WordPress or initializing its database.\n\nAn object declares compatibility bounds. The runner chooses the newest available release within those bounds for a new site and verifies an existing site\'s installed version against them. `preferred` influences new-site selection without narrowing compatibility.\n\nA data reference supplies the WordPress files for a newly created site.',
			default: 'latest',
		},
		phpVersion: {
			anyOf: [
				{ $ref: '#/definitions/DataSources.PHPVersion' },
				{
					type: 'object',
					properties: {
						min: {
							$ref: '#/definitions/DataSources.PHPVersionConstraintVersion',
						},
						recommended: {
							$ref: '#/definitions/DataSources.PHPVersionConstraintVersion',
						},
						max: {
							$ref: '#/definitions/DataSources.PHPVersionConstraintVersion',
						},
					},
					additionalProperties: false,
				},
			],
			description:
				"The PHP version required for this Blueprint to work.\n\nIn runtimes where we set up the runtime, such as Playground and wp-env, the runner will choose a version compatible with this constraint.\n\nIn other environments, this is used for validation. The Blueprint engine will throw an error if the currently running PHP version doesn't match this constraint.",
			default:
				'"8.0". Changing the default value will bump the Blueprint version.',
		},
		activeTheme: {
			anyOf: [
				{ $ref: '#/definitions/DataSources.ThemeDirectoryReference' },
				{ $ref: '#/definitions/DataSources.DataReference' },
				{
					type: 'object',
					properties: {
						source: {
							anyOf: [
								{
									$ref: '#/definitions/DataSources.ThemeDirectoryReference',
								},
								{
									$ref: '#/definitions/DataSources.DataReference',
								},
							],
						},
						importStarterContent: {
							type: 'boolean',
							description:
								"Whether to import the theme's starter content after installing it.",
						},
						targetDirectoryName: {
							type: 'string',
							description:
								'An explicit directory name within wp-content/themes to install the theme at. If not provided, it will be inferred from the theme source.',
							pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
						},
						onError: {
							type: 'string',
							enum: ['skip-theme', 'throw'],
							description:
								"Sometimes it's fine when a theme fails to install.",
							default: 'throw',
						},
						ifAlreadyInstalled: {
							type: 'string',
							enum: ['overwrite', 'skip', 'error'],
							description:
								'How to handle a theme that is already installed.',
							default: 'overwrite',
						},
						humanReadableName: {
							type: 'string',
							description:
								'Human-readable name of the theme for the progress bar.\n\nFor example, with the following Blueprint:\n\n```json {     "themes": [         {             "source": "https://github.com/Automattic/adventurer/archive/refs/heads/beta.zip",             "humanReadableName": "Adventurer"         }     ] } ```\n\nThe progress bar will show "Installing Adventurer theme" instead of "Installing https://github.com/Automattic/adventurer/archive/refs/heads/beta.zip".',
						},
					},
					required: ['source'],
					additionalProperties: false,
				},
			],
			description:
				'The theme to install and also activate.\n\n> Why not support an `active` property in the `themes` array?\n\nBecause an `"active"` property would have to default to `false` for themes while it defaults to `true` for plugins. That\'s error-prone and confusing.',
		},
		themes: {
			type: 'array',
			items: {
				anyOf: [
					{
						$ref: '#/definitions/DataSources.ThemeDirectoryReference',
					},
					{ $ref: '#/definitions/DataSources.DataReference' },
					{
						type: 'object',
						properties: {
							source: {
								anyOf: [
									{
										$ref: '#/definitions/DataSources.ThemeDirectoryReference',
									},
									{
										$ref: '#/definitions/DataSources.DataReference',
									},
								],
							},
							importStarterContent: {
								type: 'boolean',
								description:
									"Whether to import the theme's starter content after installing it.",
							},
							targetDirectoryName: {
								type: 'string',
								description:
									'An explicit directory name within wp-content/themes to install the theme at. If not provided, it will be inferred from the theme source.',
								pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
							},
							onError: {
								type: 'string',
								enum: ['skip-theme', 'throw'],
								description:
									"Sometimes it's fine when a theme fails to install.",
								default: 'throw',
							},
							ifAlreadyInstalled: {
								type: 'string',
								enum: ['overwrite', 'skip', 'error'],
								description:
									'How to handle a theme that is already installed.',
								default: 'overwrite',
							},
							humanReadableName: {
								type: 'string',
								description:
									'Human-readable name of the theme for the progress bar.\n\nFor example, with the following Blueprint:\n\n```json {     "themes": [         {             "source": "https://github.com/Automattic/adventurer/archive/refs/heads/beta.zip",             "humanReadableName": "Adventurer"         }     ] } ```\n\nThe progress bar will show "Installing Adventurer theme" instead of "Installing https://github.com/Automattic/adventurer/archive/refs/heads/beta.zip".',
							},
						},
						required: ['source'],
						additionalProperties: false,
					},
				],
			},
			description:
				'Installed themes to install without activating them.\n\nExample:\n\n```json {     "version": 2,     "themes": [         "stylish-press-theme",         "adventurer@4.6.0",         {             "source": "https://github.com/richtabor/kanso/archive/refs/heads/main.zip",             "targetDirectoryName": "kanso"         }     ] } ```',
		},
		plugins: {
			type: 'array',
			items: {
				anyOf: [
					{ $ref: '#/definitions/DataSources.DataReference' },
					{
						$ref: '#/definitions/DataSources.PluginDirectoryReference',
					},
					{
						type: 'object',
						properties: {
							source: {
								anyOf: [
									{
										$ref: '#/definitions/DataSources.DataReference',
									},
									{
										$ref: '#/definitions/DataSources.PluginDirectoryReference',
									},
								],
							},
							active: {
								type: 'boolean',
								description: 'Whether to activate the plugin.',
								default: 'true.',
							},
							activationOptions: {
								type: 'object',
								additionalProperties: {
									anyOf: [
										{ type: 'string' },
										{ type: 'boolean' },
										{ type: 'number' },
										{
											type: 'array',
											items: {
												$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
											},
										},
										{
											type: 'object',
											additionalProperties: {
												$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
											},
										},
									],
								},
								description:
									'Parameters to pass to the plugin during activation.\n\nThese options are stored in a site option that the plugin can access during its activation hook. The option name is:\n\n```php \'blueprint_activation_\' . plugin_basename( __FILE__ ) ```\n\nThis ensures uniqueness even when multiple versions of the same plugin exist. This is similar to how the `register_activation_hook` function requires the plugin file path as its first argument.\n\nThe Blueprint runner will remove the option after activating the plugin.\n\nExample:\n\nIn the Blueprint: ```json {     "source": "woocommerce",     "activationOptions": {         "storeCity": "Wrocław",         "storeCountry": "Poland",         "storePostalCode": "53-607"     } } ```\n\nIn the plugin\'s activation hook:\n\n```php register_activation_hook( __FILE__, function( $network_wide ) {     // Get the activation options from the transient     $option_name = \'blueprint_activation_\' . plugin_basename( __FILE__ );     $blueprint_activation_options = get_option( $option_name ) ?? [];\n\n    if ( $blueprint_activation_options ) {         $store_city = $blueprint_activation_options[\'storeCity\'] ?? \'\';         $store_country = $blueprint_activation_options[\'storeCountry\'] ?? \'\';         $store_postal_code = $blueprint_activation_options[\'storePostalCode\'] ?? \'\';\n\n        // ...do something with the options...     }\n\n    // Continue with normal activation... } ); ```',
							},
							targetDirectoryName: {
								type: 'string',
								description:
									'An explicit directory name within wp-content/plugins to install the plugin at. If not provided, it will be inferred from the plugin source.',
								pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
							},
							onError: {
								type: 'string',
								enum: ['skip-plugin', 'throw'],
								description:
									"Sometimes it's fine when a plugin fails to install.\n\nUse-case:   Compatibility testing. A Blueprint may install WordPress nightly with   a number of plugins to test. Some of those plugins may not yet be compatible   with the latest version of WordPress. This is something to take not of,   but not a strong reason to fail the entire Blueprint installation.",
								default: 'throw',
							},
							ifAlreadyInstalled: {
								type: 'string',
								enum: ['overwrite', 'skip', 'error'],
								description:
									'How to handle a plugin that is already installed.',
								default: 'overwrite',
							},
							humanReadableName: {
								type: 'string',
								description:
									'Human-readable name of the plugin for the progress bar.\n\nFor example, with the following Blueprint:\n\n```json {     "plugins": [         {             "source": "https://github.com/Automattic/jetpack/archive/refs/heads/beta.zip",             "humanReadableName": "Jetpack Beta"         }     ] } ```\n\nThe progress bar will show "Installing Jetpack Beta plugin" instead of "Installing https://github.com/Automattic/jetpack/archive/refs/heads/beta.zip".',
							},
						},
						required: ['source'],
						additionalProperties: false,
					},
				],
			},
			description:
				'A list of plugins to install and activate.\n\nExample:\n\n```json {     "version": 2,     "plugins": [         "jetpack",         "akismet@6.4.3",         "./query-monitor.php",         "./code-block.zip",         {             "source": "https://github.com/woocommerce/woocommerce/archive/refs/heads/6.4.3.zip",             "active": false         }     ] } ```',
		},
		muPlugins: {
			type: 'array',
			items: { $ref: '#/definitions/DataSources.DataReference' },
			description:
				'A list of mu-plugins to install.\n\nExample:\n\n```json {     "version": 2,     "muPlugins": [         {             "filename": "addFilter-0.php",             "content": "<?php add_action( \'requests-requests.before_request\', function( &$url ) {\\n$url = \'https://playground.wordpress.net/cors-proxy.php?\' . $url;\\n} );"         }     ] } ```',
		},
		postTypes: {
			type: 'object',
			additionalProperties: {
				anyOf: [
					{
						type: 'object',
						properties: {
							label: {
								type: 'string',
								description:
									"Name of the post type shown in the menu. Usually plural. Default is value of $labels['name'].",
							},
							labels: {
								type: 'object',
								additionalProperties: { type: 'string' },
								properties: {
									name: {
										type: 'string',
										description:
											"General name for the post type, usually plural. Default is 'Posts' / 'Pages'.",
									},
									singular_name: {
										type: 'string',
										description:
											"Name for one object of this post type. Default is 'Post' / 'Page'.",
									},
									add_new: {
										type: 'string',
										description:
											"Label for adding a new item. Default is 'Add New' / 'Add New'.",
									},
									add_new_item: {
										type: 'string',
										description:
											"Label for adding a new singular item. Default is 'Add New Post' / 'Add New Page'.",
									},
									edit_item: {
										type: 'string',
										description:
											"Label for editing a singular item. Default is 'Edit Post' / 'Edit Page'.",
									},
									new_item: {
										type: 'string',
										description:
											"Label for the new item page title. Default is 'New Post' / 'New Page'.",
									},
									view_item: {
										type: 'string',
										description:
											"Label for viewing a singular item. Default is 'View Post' / 'View Page'.",
									},
									view_items: {
										type: 'string',
										description:
											"Label for viewing post type archives. Default is 'View Posts' / 'View Pages'.",
									},
									search_items: {
										type: 'string',
										description:
											"Label for searching plural items. Default is 'Search Posts' / 'Search Pages'.",
									},
									not_found: {
										type: 'string',
										description:
											"Label used when no items are found. Default is 'No posts found' / 'No pages found'.",
									},
									not_found_in_trash: {
										type: 'string',
										description:
											"Label used when no items are in the Trash. Default is 'No posts found in Trash' / 'No pages found in Trash'.",
									},
									parent_item_colon: {
										type: 'string',
										description:
											"Label used to prefix parents of hierarchical items. Default is 'Parent Page:'.",
									},
									all_items: {
										type: 'string',
										description:
											"Label to signify all items in a submenu link. Default is 'All Posts' / 'All Pages'.",
									},
									archives: {
										type: 'string',
										description:
											"Label for archives in nav menus. Default is 'Post Archives' / 'Page Archives'.",
									},
									attributes: {
										type: 'string',
										description:
											"Label for the attributes meta box. Default is 'Post Attributes' / 'Page Attributes'.",
									},
									insert_into_item: {
										type: 'string',
										description:
											"Label for the media frame button. Default is 'Insert into post' / 'Insert into page'.",
									},
									uploaded_to_this_item: {
										type: 'string',
										description:
											"Label for the media frame filter. Default is 'Uploaded to this post' / 'Uploaded to this page'.",
									},
									featured_image: {
										type: 'string',
										description:
											"Label for the featured image meta box title. Default is 'Featured image'.",
									},
									set_featured_image: {
										type: 'string',
										description:
											"Label for setting the featured image. Default is 'Set featured image'.",
									},
									remove_featured_image: {
										type: 'string',
										description:
											"Label for removing the featured image. Default is 'Remove featured image'.",
									},
									use_featured_image: {
										type: 'string',
										description:
											"Label in the media frame for using a featured image. Default is 'Use as featured image'.",
									},
									menu_name: {
										type: 'string',
										description:
											'Label for the menu name. Default is the same as name.',
									},
									filter_items_list: {
										type: 'string',
										description:
											"Label for the table views hidden heading. Default is 'Filter posts list' / 'Filter pages list'.",
									},
									filter_by_date: {
										type: 'string',
										description:
											"Label for the date filter in list tables. Default is 'Filter by date'.",
									},
									items_list_navigation: {
										type: 'string',
										description:
											"Label for the table pagination hidden heading. Default is 'Posts list navigation' / 'Pages list navigation'.",
									},
									items_list: {
										type: 'string',
										description:
											"Label for the table hidden heading. Default is 'Posts list' / 'Pages list'.",
									},
									item_published: {
										type: 'string',
										description:
											"Label used when an item is published. Default is 'Post published.' / 'Page published.'",
									},
									item_published_privately: {
										type: 'string',
										description:
											"Label used when an item is published with private visibility. Default is 'Post published privately.' / 'Page published privately.'",
									},
									item_reverted_to_draft: {
										type: 'string',
										description:
											"Label used when an item is switched to a draft. Default is 'Post reverted to draft.' / 'Page reverted to draft.'",
									},
									item_trashed: {
										type: 'string',
										description:
											"Label used when an item is moved to Trash. Default is 'Post trashed.' / 'Page trashed.'",
									},
									item_scheduled: {
										type: 'string',
										description:
											"Label used when an item is scheduled for publishing. Default is 'Post scheduled.' / 'Page scheduled.'",
									},
									item_updated: {
										type: 'string',
										description:
											"Label used when an item is updated. Default is 'Post updated.' / 'Page updated.'",
									},
									item_link: {
										type: 'string',
										description:
											"Title for a navigation link block variation. Default is 'Post Link' / 'Page Link'.",
									},
									item_link_description: {
										type: 'string',
										description:
											"Description for a navigation link block variation. Default is 'A link to a post.' / 'A link to a page.'",
									},
								},
								description:
									'An array of labels for this post type. If not set, post labels are inherited for non-hierarchical types and page labels for hierarchical ones\n\nThe labels documented for WordPress 6.8 are listed below, and this type also supports an arbitrary set of labels to support future WordPress releases.',
							},
							description: {
								type: 'string',
								description:
									'A short descriptive summary of what the post type is.',
							},
							public: {
								type: 'boolean',
								description:
									'Whether a post type is intended for use publicly either via the admin interface or by front-end users. While the default settings of $exclude_from_search, $publicly_queryable, $show_ui, and $show_in_nav_menus are inherited from $public, each does not rely on this relationship and controls a very specific intention. Default false.',
							},
							hierarchical: {
								type: 'boolean',
								description:
									'Whether the post type is hierarchical (e.g. page). Default false.',
							},
							exclude_from_search: {
								type: 'boolean',
								description:
									'Whether to exclude posts with this post type from front end search results. Default is the opposite value of $public.',
							},
							publicly_queryable: {
								type: 'boolean',
								description:
									'Whether queries can be performed on the front end for the post type as part of parse_request(). Endpoints would include:\n* ?post_type={post_type_key}\n* ?{post_type_key}={single_post_slug}\n* ?{post_type_query_var}={single_post_slug} If not set, the default is inherited from $public.',
							},
							show_ui: {
								type: 'boolean',
								description:
									'Whether to generate and allow a UI for managing this post type in the admin. Default is value of $public.',
							},
							show_in_menu: {
								type: ['boolean', 'string'],
								description:
									"Where to show the post type in the admin menu. To work, $show_ui must be true. If true, the post type is shown in its own top level menu. If false, no menu is shown. If a string of an existing top level menu ('tools.php' or 'edit.php?post_type=page', for example), the post type will be placed as a sub-menu of that. Default is value of $show_ui.",
							},
							show_in_admin_bar: {
								type: 'boolean',
								description:
									'Makes this post type available via the admin bar. Default is value of $show_in_menu.',
							},
							show_in_nav_menus: {
								type: 'boolean',
								description:
									'Makes this post type available for selection in navigation menus. Default is value of $public.',
							},
							show_in_rest: {
								type: 'boolean',
								description:
									'Whether to include the post type in the REST API. Set this to true for the post type to be available in the block editor.',
							},
							rest_base: {
								type: 'string',
								description:
									'To change the base URL of REST API route. Default is $post_type.',
							},
							rest_namespace: {
								type: 'string',
								description:
									'To change the namespace URL of REST API route. Default is wp/v2.',
							},
							rest_controller_class: {
								type: 'string',
								description:
									"REST API controller class name. Default is 'WP_REST_Posts_Controller'.",
							},
							menu_icon: {
								type: 'string',
								description:
									"The URL to the icon to be used for this menu. Pass a base64-encoded SVG using a data URI, which will be colored to match the color scheme — this should begin with 'data:image/svg+xml;base64,'. Pass the name of a Dashicons helper class to use a font icon, e.g. 'dashicons-chart-pie'. Pass 'none' to leave div.wp-menu-image empty so an icon can be added via CSS. Defaults to use the posts icon.",
							},
							menu_position: {
								type: ['string', 'number'],
								description:
									'The position in the menu order the post type should appear. To work, $show_in_menu must be true. Default null (at the bottom).',
							},
							rename_capabilities: {
								type: 'boolean',
								description:
									'Whether to rename the capabilities for this post type.',
							},
							singular_capability_name: {
								type: 'string',
								description:
									'The singular capability name for this post type.',
							},
							plural_capability_name: {
								type: 'string',
								description:
									'The plural capability name for this post type.',
							},
							taxonomies: {
								type: 'array',
								items: { type: 'string' },
								description:
									'An array of taxonomy identifiers that will be registered for the post type. Taxonomies can be registered later with register_taxonomy() or register_taxonomy_for_object_type().',
							},
							query_var_name: {
								type: 'string',
								description:
									'The query var name for this post type.',
							},
							register_meta_box_cb: {
								type: 'string',
								description:
									'Provide a callback function that sets up the meta boxes for the edit form. Do remove_meta_box() and add_meta_box() calls in the callback. Default null.',
							},
							enter_title_here: {
								type: 'string',
								description:
									'Custom text for the "Enter title here" placeholder in the title field.',
							},
							capability_type: {
								anyOf: [
									{ type: 'string' },
									{
										type: 'array',
										items: { type: 'string' },
										minItems: 2,
										maxItems: 2,
									},
								],
								description:
									"The string to use to build the read, edit, and delete capabilities. May be passed as an array to allow for alternative plurals when using this argument as a base to construct the capabilities, e.g. array('story', 'stories'). Default 'post'.",
							},
							capabilities: {
								type: 'object',
								additionalProperties: { type: 'string' },
								description:
									'Array of capabilities for this post type. $capability_type is used as a base to construct capabilities by default. See get_post_type_capabilities().',
							},
							map_meta_cap: {
								type: 'boolean',
								description:
									'Whether to use the internal default meta capability handling. Default false.',
							},
							supports: {
								allOf: [
									{
										type: 'array',
										items: {
											type: 'string',
											enum: [
												'title',
												'editor',
												'author',
												'thumbnail',
												'excerpt',
												'trackbacks',
												'custom-fields',
												'comments',
												'revisions',
												'page-attributes',
												'post-formats',
											],
										},
									},
									{
										type: 'array',
										items: { type: 'string' },
									},
								],
								description:
									"Core feature(s) the post type supports. Serves as an alias for calling add_post_type_support() directly.\n\nCore features include 'title', 'editor', 'comments', 'revisions', 'trackbacks', 'author', 'excerpt', 'page-attributes', 'thumbnail', 'custom-fields', and 'post-formats'.\n\nAdditionally, the 'revisions' feature dictates whether the post type will store revisions, the 'autosave' feature dictates whether the post type will be autosaved, and the 'comments' feature dictates whether the comments count will show on the edit screen.\n\nFor backward compatibility reasons, adding 'editor' support implies 'autosave' support too.\n\nA feature can also be specified as an array of arguments to provide additional information about supporting that feature.\n\nExample: array( 'my_feature', array( 'field' => 'value' ) ).\n\nIf false, no features will be added. Default is an array containing 'title' and 'editor'.",
							},
							has_archive: {
								type: ['boolean', 'string'],
								description:
									'Whether there should be post type archives, or if a string, the archive slug to use. Will generate the proper rewrite rules if $rewrite is enabled. Default false.',
							},
							rewrite: {
								anyOf: [
									{ type: 'boolean' },
									{
										type: 'object',
										properties: {
											slug: { type: 'string' },
											with_front: { type: 'boolean' },
											pages: { type: 'boolean' },
											feeds: { type: 'boolean' },
											ep_mask: { type: 'number' },
										},
										additionalProperties: false,
									},
								],
								description:
									'Triggers the handling of rewrites for this post type. To prevent rewrite, set to false. Defaults to true, using $post_type as slug. To specify rewrite rules, an array can be passed with any of these keys:\n- slug (string): Customize the permastruct slug. Defaults to $post_type key.\n- with_front (bool): Whether the permastruct should be prepended with WP_Rewrite::$front. Default true.\n- feeds (bool): Whether the feed permastruct should be built for this post type. Default is value of $has_archive.\n- pages (bool): Whether the permastruct should provide for pagination. Default true.\n- ep_mask (int): Endpoint mask to assign. If not specified and permalink_epmask is set, inherits from $permalink_epmask.   If not specified and permalink_epmask is not set, defaults to EP_PERMALINK.',
							},
							query_var: {
								type: ['boolean', 'string'],
								description:
									'Sets the query_var key for this post type. Defaults to $post_type key. If false, a post type cannot be loaded at ?{query_var}={post_slug}. If specified as a string, the query ?{query_var_string}={post_slug} will be valid.',
							},
							can_export: {
								type: 'boolean',
								description:
									'Whether to allow this post type to be exported. Default true.',
							},
							delete_with_user: {
								type: 'boolean',
								description:
									"Whether to delete posts of this type when deleting a user. If true, posts of this type belonging to the user will be moved to Trash when the user is deleted. If false, posts of this type belonging to the user will *not* be trashed or deleted. If not set (the default), posts are trashed if post type supports the 'author' feature. Otherwise posts are not trashed or deleted. Default null.",
							},
							template: {
								type: 'array',
								items: {
									type: 'array',
									minItems: 2,
									items: [
										{ type: 'string' },
										{
											type: 'object',
											additionalProperties: {
												anyOf: [
													{ type: 'string' },
													{ type: 'boolean' },
													{ type: 'number' },
													{
														type: 'array',
														items: {
															$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
														},
													},
													{
														type: 'object',
														additionalProperties: {
															$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
														},
													},
												],
											},
										},
									],
									maxItems: 2,
								},
								description:
									'Array of blocks to use as the default initial state for an editor session. Each item should be an array containing block name and optional attributes.',
							},
							template_lock: {
								type: ['string', 'boolean'],
								enum: ['all', 'insert', false],
								description:
									"Whether the block template should be locked if $template is set. If set to 'all', the user is unable to insert new blocks, move existing blocks and delete blocks. If set to 'insert', the user is able to move existing blocks but is unable to insert new blocks and delete blocks. Default false.",
							},
						},
						additionalProperties: false,
					},
					{ $ref: '#/definitions/DataSources.ExecutionContextPath' },
				],
			},
			propertyNames: { pattern: '^[a-z0-9_-]{1,20}$' },
			description:
				"Very basic schema for defining custom post types.\n\nIMPORTANT: Using this property requires an explicit inclusion of the `secure-custom-fields` plugin. If it's missing, the Blueprint runner will throw an error.\n\nSee https://github.com/WordPress/blueprints-library/issues/32 for more context.",
		},
		fonts: {
			type: 'object',
			additionalProperties: {
				anyOf: [
					{ $ref: '#/definitions/DataSources.FileDataReference' },
					{
						type: 'object',
						properties: {
							$schema: {
								type: 'string',
								description:
									'JSON schema URI for font-collection.json.',
							},
							font_families: {
								type: 'array',
								items: {
									type: 'object',
									properties: {
										font_family_settings: {
											type: 'object',
											properties: {
												name: {
													type: 'string',
													description:
														'Name of the font family preset, translatable.',
												},
												slug: {
													type: 'string',
													description:
														'Kebab-case unique identifier for the font family preset.',
												},
												fontFamily: {
													type: 'string',
													description:
														'CSS font-family value.',
												},
												preview: {
													type: 'string',
													description:
														'URL to a preview image of the font family.',
												},
												fontFace: {
													type: 'array',
													items: {
														type: 'object',
														properties: {
															preview: {
																type: 'string',
																description:
																	'URL to a preview image of the font.',
															},
															fontFamily: {
																type: 'string',
																description:
																	'CSS font-family value.',
															},
															fontStyle: {
																type: 'string',
																description:
																	'CSS font-style value.',
															},
															fontWeight: {
																type: [
																	'string',
																	'number',
																],
																description:
																	'List of available font weights, separated by a space.',
															},
															fontDisplay: {
																type: 'string',
																enum: [
																	'auto',
																	'block',
																	'fallback',
																	'swap',
																	'optional',
																],
																description:
																	'CSS font-display value.',
															},
															src: {
																anyOf: [
																	{
																		$ref: '#/definitions/DataSources.FileDataReference',
																	},
																	{
																		type: 'array',
																		items: {
																			$ref: '#/definitions/DataSources.FileDataReference',
																		},
																	},
																],
																description:
																	'Paths or URLs to the font files.',
															},
															fontStretch: {
																type: 'string',
																description:
																	'CSS font-stretch value.',
															},
															ascentOverride: {
																type: 'string',
																description:
																	'CSS ascent-override value.',
															},
															descentOverride: {
																type: 'string',
																description:
																	'CSS descent-override value.',
															},
															fontVariant: {
																type: 'string',
																description:
																	'CSS font-variant value.',
															},
															fontFeatureSettings:
																{
																	type: 'string',
																	description:
																		'CSS font-feature-settings value.',
																},
															fontVariationSettings:
																{
																	type: 'string',
																	description:
																		'CSS font-variation-settings value.',
																},
															lineGapOverride: {
																type: 'string',
																description:
																	'CSS line-gap-override value.',
															},
															sizeAdjust: {
																type: 'string',
																description:
																	'CSS size-adjust value.',
															},
															unicodeRange: {
																type: 'string',
																description:
																	'CSS unicode-range value.',
															},
														},
														required: [
															'fontFamily',
															'src',
														],
														additionalProperties: false,
														description:
															'Font face settings with added preview property.',
													},
													description:
														'Array of font-face definitions.',
												},
											},
											required: [
												'name',
												'slug',
												'fontFamily',
											],
											additionalProperties: false,
											description:
												'Font family settings with added preview property.',
										},
										categories: {
											type: 'array',
											items: { type: 'string' },
											description:
												'Array of category slugs.',
										},
									},
									required: ['font_family_settings'],
									additionalProperties: false,
								},
								description:
									'Array of font families ready to be installed.',
							},
						},
						required: ['font_families'],
						additionalProperties: false,
						description:
							'Font collection schema for WordPress Font Library.',
					},
				],
			},
			description:
				'A list of fonts to register in the site\'s Font Library.\n\nExample:\n\n```json fonts: {     "open-sans": "https://example.com/fonts/open-sans.woff2",     "roboto": "./wp-content/fonts/roboto.woff2" } ```\n\nOr using the full font collection schema:\n\n```json fonts: {     "my-collection": {         "font_families": [             {                 "font_family_settings": {                     "name": "Open Sans",                     "slug": "open-sans",                     "fontFamily": "Open Sans",                     "preview": "https://example.com/previews/open-sans.png",                     "fontFace": [                         {                             "fontFamily": "Open Sans",                             "fontWeight": "400",                             "fontStyle": "normal",                             "src": "./wp-content/fonts/open-sans-regular.woff2"                         }                     ]                 },                 "categories": ["sans-serif"]             }         ]     } } ```',
		},
		media: {
			type: 'array',
			items: {
				anyOf: [
					{ $ref: '#/definitions/DataSources.FileDataReference' },
					{
						type: 'object',
						properties: {
							source: {
								$ref: '#/definitions/DataSources.FileDataReference',
							},
							title: { type: 'string' },
							description: { type: 'string' },
							alt: { type: 'string' },
							caption: { type: 'string' },
						},
						required: ['source'],
						additionalProperties: false,
					},
				],
			},
			description:
				'A list of media files to upload to the WordPress Media Library – in formats supported by the WordPress Media Library.\n\nExample:\n\n```json media: [     "https://example.com/images/hero.jpg",     "./wp-content/uploads/2024/01/logo.png",     {         "source": "https://example.com/videos/intro.mp4",         "title": "Introduction Video",         "description": "A brief introduction to our company",         "alt": "Company introduction video"     },     {         "source": "./wp-content/uploads/2024/01/brochure.pdf",         "title": "Product Brochure",         "description": "Detailed information about our products"     } ] ```',
		},
		content: {
			type: 'array',
			items: {
				type: 'object',
				discriminator: { propertyName: 'type' },
				required: ['type'],
				oneOf: [
					{
						type: 'object',
						properties: {
							type: { type: 'string', const: 'mysql-dump' },
							source: {
								anyOf: [
									{
										$ref: '#/definitions/DataSources.FileDataReference',
									},
									{
										type: 'array',
										items: {
											$ref: '#/definitions/DataSources.FileDataReference',
										},
									},
								],
							},
						},
						required: ['type', 'source'],
						additionalProperties: false,
					},
					{
						type: 'object',
						additionalProperties: false,
						properties: {
							urlsMode: {
								type: 'string',
								enum: ['rewrite', 'preserve'],
								description:
									'Whether to rewrite the hrefs in the remote site\'s content URLs in the WXR file from the remote site domain to the current site domain\'s (and path etc).\n\nPossible values:\n\n* "rewrite" – Rewrite the hrefs to the current site domain\'s (and path etc).\n* "preserve" – Preserve the hrefs as they are.',
								default: '"rewrite".',
							},
							urlsMap: {
								type: 'object',
								additionalProperties: {
									$ref: '#/definitions/DataSources.URLReference',
								},
								description:
									'A mapping of base URLs to rewrite.',
								propertyNames: {
									$ref: '#/definitions/DataSources.URLReference',
								},
							},
							type: { type: 'string', const: 'posts' },
							source: {
								anyOf: [
									{
										$ref: '#/definitions/DataSources.FileDataReference',
									},
									{
										type: 'object',
										properties: {
											post_author: {
												type: 'number',
												description:
													'Username of the post author.\n\nIf missing, the default value will be resolved in the following order until one is available:\n\n* Default user defined in the runner configuration.\n* The first administrator in the database.\n* The first user in the database.\n* A newly created user.\n\nThe aggressive resolution is necessary because post_author is NOT NULL in the database schema.',
											},
											post_date: {
												type: 'string',
												description:
													"The date of the post in UTC. Accepts format 'YYYY-MM-DD HH:MM:SS'. Can be used to schedule future posts (when used with post_status: 'future').",
												default: 'Current time',
											},
											post_content: {
												type: 'string',
												description:
													'The main post content. Can contain HTML, shortcodes, etc. While technically optional, posts are usually expected to have content.',
												default: '',
											},
											post_title: {
												type: 'string',
												description: 'The post title.',
											},
											post_excerpt: {
												type: 'string',
												description:
													'The post excerpt. Default empty.',
											},
											post_status: {
												type: 'string',
												enum: [
													'publish',
													'pending',
													'draft',
													'auto-draft',
													'future',
													'private',
													'inherit',
													'trash',
												],
												description: 'The post status',
											},
											post_type: {
												type: 'string',
												description:
													"The post type (e.g., 'post', 'page'). Default 'post'.",
											},
											comment_status: {
												type: 'string',
												enum: ['open', 'closed'],
												description:
													"Whether comments are allowed ('open' or 'closed').",
												default: "'open'.",
											},
											post_password: {
												type: 'string',
												description:
													'A password to protect access. Default empty.',
											},
											post_name: {
												type: 'string',
												description:
													'The URL slug. Default sanitized post_title for new posts.',
											},
											post_parent_name: {
												type: 'string',
												description:
													'Post parent name for hierarchical post types (e.g., pages). Default empty.',
											},
											menu_order: {
												type: 'number',
												description:
													'Menu order within a post type. Default 0.',
											},
											post_mime_type: {
												type: 'string',
												description:
													'MIME type for attachments. Default empty.',
											},
											guid: {
												type: 'string',
												description:
													'Global Unique ID. Default empty.',
											},
											post_category: {
												type: 'array',
												items: { type: 'string' },
												description:
													"Array of category slugs. Defaults to the site's default category.",
											},
											post_tags: {
												type: 'array',
												items: { type: 'string' },
												description:
													'Array of tag names. Default empty.',
											},
											tax_input: {
												type: 'object',
												additionalProperties: {
													type: 'array',
													items: { type: 'string' },
												},
												description:
													'Taxonomy terms keyed by taxonomy name. For hierarchical taxonomies: array of term names. For non-hierarchical: array of term names or slugs.\n\nExamples: ```json tax_input: {   // For hierarchical taxonomies like categories   "category": ["Books", "Fiction", "Science Fiction"],\n\n  // For non-hierarchical taxonomies like tags   "post_tag": ["bestseller", "featured", "summer-reading"],\n\n  // For custom taxonomies   "genre": ["mystery", "thriller"],   "author": ["Jane Doe", "John Smith"] } ```',
											},
											meta_input: {
												type: 'object',
												additionalProperties: {
													anyOf: [
														{ type: 'string' },
														{ type: 'boolean' },
														{ type: 'number' },
														{
															type: 'array',
															items: {
																$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
															},
														},
														{
															type: 'object',
															additionalProperties:
																{
																	$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																},
														},
													],
												},
												description:
													'Post meta keyed by meta key to value. Default empty.\n\nExamples: ```json meta_input: {   // Simple values   "price": "19.99",   "in_stock": true,   "stock": 42,\n\n  // Array values   "related_products": [123, 456, 789],   "product_colors": ["red", "blue", "green"],\n\n  // Object values   "_product_attributes": {     "color": {       "name": "Color",       "value": "Red",       "position": 0,       "visible": true     }   },   "seo_data": {     "title": "Custom SEO Title",     "description": "Custom meta description",     "keywords": ["product", "featured"]   } } ```',
											},
											page_template: {
												type: 'string',
												description:
													"Specifies the page template file to use. This parameter only applies if post_type is 'page'. For other post types, it's ignored. Provide the template filename (e.g., 'template-contact.php'). Include subdirectory if applicable (e.g., 'templates/full-width.php'). To set a template for non-page post types, use meta_input with key '_wp_page_template'.",
												default: '',
											},
										},
										required: ['post_title'],
										additionalProperties: false,
										description:
											'Post data type. It is inspired by the wp_insert_post() arguments, but it diverges from it in a few ways.',
									},
									{
										type: 'array',
										items: {
											anyOf: [
												{
													$ref: '#/definitions/DataSources.FileDataReference',
												},
												{
													type: 'object',
													properties: {
														post_author: {
															type: 'number',
															description:
																'Username of the post author.\n\nIf missing, the default value will be resolved in the following order until one is available:\n\n* Default user defined in the runner configuration.\n* The first administrator in the database.\n* The first user in the database.\n* A newly created user.\n\nThe aggressive resolution is necessary because post_author is NOT NULL in the database schema.',
														},
														post_date: {
															type: 'string',
															description:
																"The date of the post in UTC. Accepts format 'YYYY-MM-DD HH:MM:SS'. Can be used to schedule future posts (when used with post_status: 'future').",
															default:
																'Current time',
														},
														post_content: {
															type: 'string',
															description:
																'The main post content. Can contain HTML, shortcodes, etc. While technically optional, posts are usually expected to have content.',
															default: '',
														},
														post_title: {
															type: 'string',
															description:
																'The post title.',
														},
														post_excerpt: {
															type: 'string',
															description:
																'The post excerpt. Default empty.',
														},
														post_status: {
															type: 'string',
															enum: [
																'publish',
																'pending',
																'draft',
																'auto-draft',
																'future',
																'private',
																'inherit',
																'trash',
															],
															description:
																'The post status',
														},
														post_type: {
															type: 'string',
															description:
																"The post type (e.g., 'post', 'page'). Default 'post'.",
														},
														comment_status: {
															type: 'string',
															enum: [
																'open',
																'closed',
															],
															description:
																"Whether comments are allowed ('open' or 'closed').",
															default: "'open'.",
														},
														post_password: {
															type: 'string',
															description:
																'A password to protect access. Default empty.',
														},
														post_name: {
															type: 'string',
															description:
																'The URL slug. Default sanitized post_title for new posts.',
														},
														post_parent_name: {
															type: 'string',
															description:
																'Post parent name for hierarchical post types (e.g., pages). Default empty.',
														},
														menu_order: {
															type: 'number',
															description:
																'Menu order within a post type. Default 0.',
														},
														post_mime_type: {
															type: 'string',
															description:
																'MIME type for attachments. Default empty.',
														},
														guid: {
															type: 'string',
															description:
																'Global Unique ID. Default empty.',
														},
														post_category: {
															type: 'array',
															items: {
																type: 'string',
															},
															description:
																"Array of category slugs. Defaults to the site's default category.",
														},
														post_tags: {
															type: 'array',
															items: {
																type: 'string',
															},
															description:
																'Array of tag names. Default empty.',
														},
														tax_input: {
															type: 'object',
															additionalProperties:
																{
																	type: 'array',
																	items: {
																		type: 'string',
																	},
																},
															description:
																'Taxonomy terms keyed by taxonomy name. For hierarchical taxonomies: array of term names. For non-hierarchical: array of term names or slugs.\n\nExamples: ```json tax_input: {   // For hierarchical taxonomies like categories   "category": ["Books", "Fiction", "Science Fiction"],\n\n  // For non-hierarchical taxonomies like tags   "post_tag": ["bestseller", "featured", "summer-reading"],\n\n  // For custom taxonomies   "genre": ["mystery", "thriller"],   "author": ["Jane Doe", "John Smith"] } ```',
														},
														meta_input: {
															type: 'object',
															additionalProperties:
																{
																	anyOf: [
																		{
																			type: 'string',
																		},
																		{
																			type: 'boolean',
																		},
																		{
																			type: 'number',
																		},
																		{
																			type: 'array',
																			items: {
																				$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																			},
																		},
																		{
																			type: 'object',
																			additionalProperties:
																				{
																					$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																				},
																		},
																	],
																},
															description:
																'Post meta keyed by meta key to value. Default empty.\n\nExamples: ```json meta_input: {   // Simple values   "price": "19.99",   "in_stock": true,   "stock": 42,\n\n  // Array values   "related_products": [123, 456, 789],   "product_colors": ["red", "blue", "green"],\n\n  // Object values   "_product_attributes": {     "color": {       "name": "Color",       "value": "Red",       "position": 0,       "visible": true     }   },   "seo_data": {     "title": "Custom SEO Title",     "description": "Custom meta description",     "keywords": ["product", "featured"]   } } ```',
														},
														page_template: {
															type: 'string',
															description:
																"Specifies the page template file to use. This parameter only applies if post_type is 'page'. For other post types, it's ignored. Provide the template filename (e.g., 'template-contact.php'). Include subdirectory if applicable (e.g., 'templates/full-width.php'). To set a template for non-page post types, use meta_input with key '_wp_page_template'.",
															default: '',
														},
													},
													required: ['post_title'],
													additionalProperties: false,
													description:
														'Post data type. It is inspired by the wp_insert_post() arguments, but it diverges from it in a few ways.',
												},
											],
										},
									},
								],
							},
						},
						required: ['source', 'type'],
					},
					{
						type: 'object',
						properties: { type: { type: 'string', const: 'wxr' } },
						required: ['type'],
						oneOf: [
							{
								type: 'object',
								additionalProperties: false,
								properties: {
									authorsMode: {
										type: 'string',
										const: 'map',
										description:
											'Map remote authors to existing local authors.',
									},
									authorsMap: {
										type: 'object',
										additionalProperties: {
											type: 'string',
										},
									},
									urlsMode: {
										type: 'string',
										enum: ['rewrite', 'preserve'],
										description:
											'Whether to rewrite the hrefs in the remote site\'s content URLs in the WXR file from the remote site domain to the current site domain\'s (and path etc).\n\nPossible values:\n\n* "rewrite" – Rewrite the hrefs to the current site domain\'s (and path etc).\n* "preserve" – Preserve the hrefs as they are.',
										default: '"rewrite".',
									},
									urlsMap: {
										type: 'object',
										additionalProperties: {
											$ref: '#/definitions/DataSources.URLReference',
										},
										description:
											'A mapping of base URLs to rewrite.',
										propertyNames: {
											$ref: '#/definitions/DataSources.URLReference',
										},
									},
									type: { type: 'string', const: 'wxr' },
									source: {
										anyOf: [
											{
												$ref: '#/definitions/DataSources.FileDataReference',
											},
											{
												type: 'array',
												items: {
													$ref: '#/definitions/DataSources.FileDataReference',
												},
											},
										],
									},
									staticAssets: {
										type: 'string',
										enum: ['fetch', 'hotlink'],
										description:
											'Static assets handling.\n\nPossible values:\n\n* "fetch" – Fetch the static assets and save them to the local filesystem.\n* "hotlink" – Hotlink the static assets from the remote site.',
										default: '"fetch".',
									},
									defaultAuthorUsername: {
										type: 'string',
										description:
											'The default author to use when `mode` is "default-author".',
										default: '"admin".',
									},
									importUsers: {
										type: 'boolean',
										description:
											'Whether to import users from the remote site.',
										default: 'false.',
									},
									importComments: {
										type: 'boolean',
										description:
											'Whether to import comments from the remote site.',
										default: 'false.',
									},
								},
								required: [
									'authorsMap',
									'authorsMode',
									'source',
									'type',
								],
							},
							{
								type: 'object',
								additionalProperties: false,
								properties: {
									authorsMode: {
										type: 'string',
										enum: ['create', 'default-author'],
										description:
											'How to handle authors that don\'t exist on the current site.\n\nPossible values:\n\n* "create" – Create a new author.\n* "default-author" – Use the default author.',
										default: '"create".',
									},
									authorsMap: {
										type: 'object',
										additionalProperties: {
											type: 'string',
										},
									},
									urlsMode: {
										type: 'string',
										enum: ['rewrite', 'preserve'],
										description:
											'Whether to rewrite the hrefs in the remote site\'s content URLs in the WXR file from the remote site domain to the current site domain\'s (and path etc).\n\nPossible values:\n\n* "rewrite" – Rewrite the hrefs to the current site domain\'s (and path etc).\n* "preserve" – Preserve the hrefs as they are.',
										default: '"rewrite".',
									},
									urlsMap: {
										type: 'object',
										additionalProperties: {
											$ref: '#/definitions/DataSources.URLReference',
										},
										description:
											'A mapping of base URLs to rewrite.',
										propertyNames: {
											$ref: '#/definitions/DataSources.URLReference',
										},
									},
									type: { type: 'string', const: 'wxr' },
									source: {
										anyOf: [
											{
												$ref: '#/definitions/DataSources.FileDataReference',
											},
											{
												type: 'array',
												items: {
													$ref: '#/definitions/DataSources.FileDataReference',
												},
											},
										],
									},
									staticAssets: {
										type: 'string',
										enum: ['fetch', 'hotlink'],
										description:
											'Static assets handling.\n\nPossible values:\n\n* "fetch" – Fetch the static assets and save them to the local filesystem.\n* "hotlink" – Hotlink the static assets from the remote site.',
										default: '"fetch".',
									},
									defaultAuthorUsername: {
										type: 'string',
										description:
											'The default author to use when `mode` is "default-author".',
										default: '"admin".',
									},
									importUsers: {
										type: 'boolean',
										description:
											'Whether to import users from the remote site.',
										default: 'false.',
									},
									importComments: {
										type: 'boolean',
										description:
											'Whether to import comments from the remote site.',
										default: 'false.',
									},
								},
								required: ['source', 'type'],
							},
						],
					},
				],
			},
		},
		users: {
			type: 'array',
			items: {
				type: 'object',
				properties: {
					username: { type: 'string' },
					email: { type: 'string' },
					role: { type: 'string' },
					meta: {
						type: 'object',
						additionalProperties: { type: 'string' },
					},
				},
				required: ['username', 'email', 'role', 'meta'],
				additionalProperties: false,
			},
		},
		roles: {
			type: 'array',
			items: {
				type: 'object',
				properties: {
					name: { type: 'string' },
					capabilities: {
						type: 'object',
						additionalProperties: { type: 'string' },
					},
				},
				required: ['name', 'capabilities'],
				additionalProperties: false,
			},
		},
		additionalStepsAfterExecution: {
			type: 'array',
			items: {
				type: 'object',
				discriminator: { propertyName: 'step' },
				required: ['step'],
				oneOf: [
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'activatePlugin' },
							pluginPath: {
								type: 'string',
								description:
									'Path to the plugin directory as absolute path (/wordpress/wp-content/plugins/plugin-name); or the plugin entry file relative to the plugins directory (plugin-name/plugin-name.php).',
							},
							humanReadableName: {
								type: 'string',
								description:
									'Human-readable name of the plugin for the progress bar.\n\nFor example, with the following Blueprint:\n\n```json {     "steps": [         {             "step": "activatePlugin",             "pluginPath": "wordpress-seo/wp-seo.php",             "humanReadableName": "Yoast SEO"         }     ] } ```\n\nThe progress bar will show "Activating Yoast SEO" instead of "Activating wordpress-seo/wp-seo.php".',
							},
						},
						required: ['step', 'pluginPath'],
						additionalProperties: false,
						description: '}}}',
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'activateTheme' },
							themeDirectoryName: {
								type: 'string',
								description:
									'The name of the theme directory inside wp-content/themes/',
							},
							humanReadableName: {
								type: 'string',
								description:
									'Human-readable name of the theme for the progress bar.\n\nFor example, with the following Blueprint:\n\n```json {     "steps": [         {             "step": "activateTheme",             "themeDirectoryName": "twentytwentythree",             "humanReadableName": "Twenty Twenty-Three"         }     ] } ```\n\nThe progress bar will show "Activating Twenty Twenty-Three" instead of "Activating twentytwentythree".',
							},
						},
						required: ['step', 'themeDirectoryName'],
						additionalProperties: false,
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'cp' },
							fromPath: { type: 'string' },
							toPath: { type: 'string' },
						},
						required: ['step', 'fromPath', 'toPath'],
						additionalProperties: false,
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'defineConstants' },
							constants: {
								type: 'object',
								additionalProperties: {
									type: ['boolean', 'string', 'number'],
								},
								properties: {
									WP_DEBUG: { type: 'boolean' },
									WP_DEBUG_LOG: { type: 'boolean' },
									WP_DEBUG_DISPLAY: { type: 'boolean' },
									SCRIPT_DEBUG: { type: 'boolean' },
								},
							},
						},
						required: ['step', 'constants'],
						additionalProperties: false,
					},
					{
						type: 'object',
						properties: {
							step: {
								type: 'string',
								const: 'enableMultisite',
								description:
									'Converts the target WordPress installation into a multisite network.',
							},
						},
						required: ['step'],
						additionalProperties: false,
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'importContent' },
							content: {
								type: 'array',
								items: {
									anyOf: [
										{
											type: 'object',
											properties: {
												type: {
													type: 'string',
													const: 'mysql-dump',
												},
												source: {
													anyOf: [
														{
															$ref: '#/definitions/DataSources.FileDataReference',
														},
														{
															type: 'array',
															items: {
																$ref: '#/definitions/DataSources.FileDataReference',
															},
														},
													],
												},
											},
											required: ['type', 'source'],
											additionalProperties: false,
										},
										{
											type: 'object',
											additionalProperties: false,
											properties: {
												urlsMode: {
													type: 'string',
													enum: [
														'rewrite',
														'preserve',
													],
													description:
														'Whether to rewrite the hrefs in the remote site\'s content URLs in the WXR file from the remote site domain to the current site domain\'s (and path etc).\n\nPossible values:\n\n* "rewrite" – Rewrite the hrefs to the current site domain\'s (and path etc).\n* "preserve" – Preserve the hrefs as they are.',
													default: '"rewrite".',
												},
												urlsMap: {
													type: 'object',
													additionalProperties: {
														$ref: '#/definitions/DataSources.URLReference',
													},
													description:
														'A mapping of base URLs to rewrite.',
													propertyNames: {
														$ref: '#/definitions/DataSources.URLReference',
													},
												},
												type: {
													type: 'string',
													const: 'posts',
												},
												source: {
													anyOf: [
														{
															$ref: '#/definitions/DataSources.FileDataReference',
														},
														{
															type: 'object',
															properties: {
																post_author: {
																	type: 'number',
																	description:
																		'Username of the post author.\n\nIf missing, the default value will be resolved in the following order until one is available:\n\n* Default user defined in the runner configuration.\n* The first administrator in the database.\n* The first user in the database.\n* A newly created user.\n\nThe aggressive resolution is necessary because post_author is NOT NULL in the database schema.',
																},
																post_date: {
																	type: 'string',
																	description:
																		"The date of the post in UTC. Accepts format 'YYYY-MM-DD HH:MM:SS'. Can be used to schedule future posts (when used with post_status: 'future').",
																	default:
																		'Current time',
																},
																post_content: {
																	type: 'string',
																	description:
																		'The main post content. Can contain HTML, shortcodes, etc. While technically optional, posts are usually expected to have content.',
																	default: '',
																},
																post_title: {
																	type: 'string',
																	description:
																		'The post title.',
																},
																post_excerpt: {
																	type: 'string',
																	description:
																		'The post excerpt. Default empty.',
																},
																post_status: {
																	type: 'string',
																	enum: [
																		'publish',
																		'pending',
																		'draft',
																		'auto-draft',
																		'future',
																		'private',
																		'inherit',
																		'trash',
																	],
																	description:
																		'The post status',
																},
																post_type: {
																	type: 'string',
																	description:
																		"The post type (e.g., 'post', 'page'). Default 'post'.",
																},
																comment_status:
																	{
																		type: 'string',
																		enum: [
																			'open',
																			'closed',
																		],
																		description:
																			"Whether comments are allowed ('open' or 'closed').",
																		default:
																			"'open'.",
																	},
																post_password: {
																	type: 'string',
																	description:
																		'A password to protect access. Default empty.',
																},
																post_name: {
																	type: 'string',
																	description:
																		'The URL slug. Default sanitized post_title for new posts.',
																},
																post_parent_name:
																	{
																		type: 'string',
																		description:
																			'Post parent name for hierarchical post types (e.g., pages). Default empty.',
																	},
																menu_order: {
																	type: 'number',
																	description:
																		'Menu order within a post type. Default 0.',
																},
																post_mime_type:
																	{
																		type: 'string',
																		description:
																			'MIME type for attachments. Default empty.',
																	},
																guid: {
																	type: 'string',
																	description:
																		'Global Unique ID. Default empty.',
																},
																post_category: {
																	type: 'array',
																	items: {
																		type: 'string',
																	},
																	description:
																		"Array of category slugs. Defaults to the site's default category.",
																},
																post_tags: {
																	type: 'array',
																	items: {
																		type: 'string',
																	},
																	description:
																		'Array of tag names. Default empty.',
																},
																tax_input: {
																	type: 'object',
																	additionalProperties:
																		{
																			type: 'array',
																			items: {
																				type: 'string',
																			},
																		},
																	description:
																		'Taxonomy terms keyed by taxonomy name. For hierarchical taxonomies: array of term names. For non-hierarchical: array of term names or slugs.\n\nExamples: ```json tax_input: {   // For hierarchical taxonomies like categories   "category": ["Books", "Fiction", "Science Fiction"],\n\n  // For non-hierarchical taxonomies like tags   "post_tag": ["bestseller", "featured", "summer-reading"],\n\n  // For custom taxonomies   "genre": ["mystery", "thriller"],   "author": ["Jane Doe", "John Smith"] } ```',
																},
																meta_input: {
																	type: 'object',
																	additionalProperties:
																		{
																			anyOf: [
																				{
																					type: 'string',
																				},
																				{
																					type: 'boolean',
																				},
																				{
																					type: 'number',
																				},
																				{
																					type: 'array',
																					items: {
																						$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																					},
																				},
																				{
																					type: 'object',
																					additionalProperties:
																						{
																							$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																						},
																				},
																			],
																		},
																	description:
																		'Post meta keyed by meta key to value. Default empty.\n\nExamples: ```json meta_input: {   // Simple values   "price": "19.99",   "in_stock": true,   "stock": 42,\n\n  // Array values   "related_products": [123, 456, 789],   "product_colors": ["red", "blue", "green"],\n\n  // Object values   "_product_attributes": {     "color": {       "name": "Color",       "value": "Red",       "position": 0,       "visible": true     }   },   "seo_data": {     "title": "Custom SEO Title",     "description": "Custom meta description",     "keywords": ["product", "featured"]   } } ```',
																},
																page_template: {
																	type: 'string',
																	description:
																		"Specifies the page template file to use. This parameter only applies if post_type is 'page'. For other post types, it's ignored. Provide the template filename (e.g., 'template-contact.php'). Include subdirectory if applicable (e.g., 'templates/full-width.php'). To set a template for non-page post types, use meta_input with key '_wp_page_template'.",
																	default: '',
																},
															},
															required: [
																'post_title',
															],
															additionalProperties: false,
															description:
																'Post data type. It is inspired by the wp_insert_post() arguments, but it diverges from it in a few ways.',
														},
														{
															type: 'array',
															items: {
																anyOf: [
																	{
																		$ref: '#/definitions/DataSources.FileDataReference',
																	},
																	{
																		type: 'object',
																		properties:
																			{
																				post_author:
																					{
																						type: 'number',
																						description:
																							'Username of the post author.\n\nIf missing, the default value will be resolved in the following order until one is available:\n\n* Default user defined in the runner configuration.\n* The first administrator in the database.\n* The first user in the database.\n* A newly created user.\n\nThe aggressive resolution is necessary because post_author is NOT NULL in the database schema.',
																					},
																				post_date:
																					{
																						type: 'string',
																						description:
																							"The date of the post in UTC. Accepts format 'YYYY-MM-DD HH:MM:SS'. Can be used to schedule future posts (when used with post_status: 'future').",
																						default:
																							'Current time',
																					},
																				post_content:
																					{
																						type: 'string',
																						description:
																							'The main post content. Can contain HTML, shortcodes, etc. While technically optional, posts are usually expected to have content.',
																						default:
																							'',
																					},
																				post_title:
																					{
																						type: 'string',
																						description:
																							'The post title.',
																					},
																				post_excerpt:
																					{
																						type: 'string',
																						description:
																							'The post excerpt. Default empty.',
																					},
																				post_status:
																					{
																						type: 'string',
																						enum: [
																							'publish',
																							'pending',
																							'draft',
																							'auto-draft',
																							'future',
																							'private',
																							'inherit',
																							'trash',
																						],
																						description:
																							'The post status',
																					},
																				post_type:
																					{
																						type: 'string',
																						description:
																							"The post type (e.g., 'post', 'page'). Default 'post'.",
																					},
																				comment_status:
																					{
																						type: 'string',
																						enum: [
																							'open',
																							'closed',
																						],
																						description:
																							"Whether comments are allowed ('open' or 'closed').",
																						default:
																							"'open'.",
																					},
																				post_password:
																					{
																						type: 'string',
																						description:
																							'A password to protect access. Default empty.',
																					},
																				post_name:
																					{
																						type: 'string',
																						description:
																							'The URL slug. Default sanitized post_title for new posts.',
																					},
																				post_parent_name:
																					{
																						type: 'string',
																						description:
																							'Post parent name for hierarchical post types (e.g., pages). Default empty.',
																					},
																				menu_order:
																					{
																						type: 'number',
																						description:
																							'Menu order within a post type. Default 0.',
																					},
																				post_mime_type:
																					{
																						type: 'string',
																						description:
																							'MIME type for attachments. Default empty.',
																					},
																				guid: {
																					type: 'string',
																					description:
																						'Global Unique ID. Default empty.',
																				},
																				post_category:
																					{
																						type: 'array',
																						items: {
																							type: 'string',
																						},
																						description:
																							"Array of category slugs. Defaults to the site's default category.",
																					},
																				post_tags:
																					{
																						type: 'array',
																						items: {
																							type: 'string',
																						},
																						description:
																							'Array of tag names. Default empty.',
																					},
																				tax_input:
																					{
																						type: 'object',
																						additionalProperties:
																							{
																								type: 'array',
																								items: {
																									type: 'string',
																								},
																							},
																						description:
																							'Taxonomy terms keyed by taxonomy name. For hierarchical taxonomies: array of term names. For non-hierarchical: array of term names or slugs.\n\nExamples: ```json tax_input: {   // For hierarchical taxonomies like categories   "category": ["Books", "Fiction", "Science Fiction"],\n\n  // For non-hierarchical taxonomies like tags   "post_tag": ["bestseller", "featured", "summer-reading"],\n\n  // For custom taxonomies   "genre": ["mystery", "thriller"],   "author": ["Jane Doe", "John Smith"] } ```',
																					},
																				meta_input:
																					{
																						type: 'object',
																						additionalProperties:
																							{
																								anyOf: [
																									{
																										type: 'string',
																									},
																									{
																										type: 'boolean',
																									},
																									{
																										type: 'number',
																									},
																									{
																										type: 'array',
																										items: {
																											$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																										},
																									},
																									{
																										type: 'object',
																										additionalProperties:
																											{
																												$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
																											},
																									},
																								],
																							},
																						description:
																							'Post meta keyed by meta key to value. Default empty.\n\nExamples: ```json meta_input: {   // Simple values   "price": "19.99",   "in_stock": true,   "stock": 42,\n\n  // Array values   "related_products": [123, 456, 789],   "product_colors": ["red", "blue", "green"],\n\n  // Object values   "_product_attributes": {     "color": {       "name": "Color",       "value": "Red",       "position": 0,       "visible": true     }   },   "seo_data": {     "title": "Custom SEO Title",     "description": "Custom meta description",     "keywords": ["product", "featured"]   } } ```',
																					},
																				page_template:
																					{
																						type: 'string',
																						description:
																							"Specifies the page template file to use. This parameter only applies if post_type is 'page'. For other post types, it's ignored. Provide the template filename (e.g., 'template-contact.php'). Include subdirectory if applicable (e.g., 'templates/full-width.php'). To set a template for non-page post types, use meta_input with key '_wp_page_template'.",
																						default:
																							'',
																					},
																			},
																		required:
																			[
																				'post_title',
																			],
																		additionalProperties: false,
																		description:
																			'Post data type. It is inspired by the wp_insert_post() arguments, but it diverges from it in a few ways.',
																	},
																],
															},
														},
													],
												},
											},
											required: ['source', 'type'],
										},
										{
											type: 'object',
											additionalProperties: false,
											properties: {
												authorsMode: {
													type: 'string',
													const: 'map',
													description:
														'Map remote authors to existing local authors.',
												},
												authorsMap: {
													type: 'object',
													additionalProperties: {
														type: 'string',
													},
												},
												urlsMode: {
													type: 'string',
													enum: [
														'rewrite',
														'preserve',
													],
													description:
														'Whether to rewrite the hrefs in the remote site\'s content URLs in the WXR file from the remote site domain to the current site domain\'s (and path etc).\n\nPossible values:\n\n* "rewrite" – Rewrite the hrefs to the current site domain\'s (and path etc).\n* "preserve" – Preserve the hrefs as they are.',
													default: '"rewrite".',
												},
												urlsMap: {
													type: 'object',
													additionalProperties: {
														$ref: '#/definitions/DataSources.URLReference',
													},
													description:
														'A mapping of base URLs to rewrite.',
													propertyNames: {
														$ref: '#/definitions/DataSources.URLReference',
													},
												},
												type: {
													type: 'string',
													const: 'wxr',
												},
												source: {
													anyOf: [
														{
															$ref: '#/definitions/DataSources.FileDataReference',
														},
														{
															type: 'array',
															items: {
																$ref: '#/definitions/DataSources.FileDataReference',
															},
														},
													],
												},
												staticAssets: {
													type: 'string',
													enum: ['fetch', 'hotlink'],
													description:
														'Static assets handling.\n\nPossible values:\n\n* "fetch" – Fetch the static assets and save them to the local filesystem.\n* "hotlink" – Hotlink the static assets from the remote site.',
													default: '"fetch".',
												},
												defaultAuthorUsername: {
													type: 'string',
													description:
														'The default author to use when `mode` is "default-author".',
													default: '"admin".',
												},
												importUsers: {
													type: 'boolean',
													description:
														'Whether to import users from the remote site.',
													default: 'false.',
												},
												importComments: {
													type: 'boolean',
													description:
														'Whether to import comments from the remote site.',
													default: 'false.',
												},
											},
											required: [
												'authorsMap',
												'authorsMode',
												'source',
												'type',
											],
										},
										{
											type: 'object',
											additionalProperties: false,
											properties: {
												authorsMode: {
													type: 'string',
													enum: [
														'create',
														'default-author',
													],
													description:
														'How to handle authors that don\'t exist on the current site.\n\nPossible values:\n\n* "create" – Create a new author.\n* "default-author" – Use the default author.',
													default: '"create".',
												},
												authorsMap: {
													type: 'object',
													additionalProperties: {
														type: 'string',
													},
												},
												urlsMode: {
													type: 'string',
													enum: [
														'rewrite',
														'preserve',
													],
													description:
														'Whether to rewrite the hrefs in the remote site\'s content URLs in the WXR file from the remote site domain to the current site domain\'s (and path etc).\n\nPossible values:\n\n* "rewrite" – Rewrite the hrefs to the current site domain\'s (and path etc).\n* "preserve" – Preserve the hrefs as they are.',
													default: '"rewrite".',
												},
												urlsMap: {
													type: 'object',
													additionalProperties: {
														$ref: '#/definitions/DataSources.URLReference',
													},
													description:
														'A mapping of base URLs to rewrite.',
													propertyNames: {
														$ref: '#/definitions/DataSources.URLReference',
													},
												},
												type: {
													type: 'string',
													const: 'wxr',
												},
												source: {
													anyOf: [
														{
															$ref: '#/definitions/DataSources.FileDataReference',
														},
														{
															type: 'array',
															items: {
																$ref: '#/definitions/DataSources.FileDataReference',
															},
														},
													],
												},
												staticAssets: {
													type: 'string',
													enum: ['fetch', 'hotlink'],
													description:
														'Static assets handling.\n\nPossible values:\n\n* "fetch" – Fetch the static assets and save them to the local filesystem.\n* "hotlink" – Hotlink the static assets from the remote site.',
													default: '"fetch".',
												},
												defaultAuthorUsername: {
													type: 'string',
													description:
														'The default author to use when `mode` is "default-author".',
													default: '"admin".',
												},
												importUsers: {
													type: 'boolean',
													description:
														'Whether to import users from the remote site.',
													default: 'false.',
												},
												importComments: {
													type: 'boolean',
													description:
														'Whether to import comments from the remote site.',
													default: 'false.',
												},
											},
											required: ['source', 'type'],
										},
									],
								},
							},
						},
						required: ['step', 'content'],
						additionalProperties: false,
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'importMedia' },
							media: {
								type: 'array',
								items: {
									anyOf: [
										{
											$ref: '#/definitions/DataSources.FileDataReference',
										},
										{
											type: 'object',
											properties: {
												source: {
													$ref: '#/definitions/DataSources.FileDataReference',
												},
												title: { type: 'string' },
												description: { type: 'string' },
												alt: { type: 'string' },
												caption: { type: 'string' },
											},
											required: ['source'],
											additionalProperties: false,
										},
									],
								},
							},
						},
						required: ['step', 'media'],
						additionalProperties: false,
					},
					{
						type: 'object',
						properties: {
							step: {
								type: 'string',
								const: 'importThemeStarterContent',
							},
							themeSlug: {
								type: 'string',
								description:
									'The name of the theme to import content from.',
							},
						},
						required: ['step'],
						additionalProperties: false,
					},
					{
						type: 'object',
						additionalProperties: false,
						properties: {
							source: {
								anyOf: [
									{
										$ref: '#/definitions/DataSources.DataReference',
									},
									{
										$ref: '#/definitions/DataSources.PluginDirectoryReference',
									},
								],
							},
							active: {
								type: 'boolean',
								description: 'Whether to activate the plugin.',
								default: 'true.',
							},
							activationOptions: {
								type: 'object',
								additionalProperties: {
									anyOf: [
										{ type: 'string' },
										{ type: 'boolean' },
										{ type: 'number' },
										{
											type: 'array',
											items: {
												$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
											},
										},
										{
											type: 'object',
											additionalProperties: {
												$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
											},
										},
									],
								},
								description:
									'Parameters to pass to the plugin during activation.\n\nThese options are stored in a site option that the plugin can access during its activation hook. The option name is:\n\n```php \'blueprint_activation_\' . plugin_basename( __FILE__ ) ```\n\nThis ensures uniqueness even when multiple versions of the same plugin exist. This is similar to how the `register_activation_hook` function requires the plugin file path as its first argument.\n\nThe Blueprint runner will remove the option after activating the plugin.\n\nExample:\n\nIn the Blueprint: ```json {     "source": "woocommerce",     "activationOptions": {         "storeCity": "Wrocław",         "storeCountry": "Poland",         "storePostalCode": "53-607"     } } ```\n\nIn the plugin\'s activation hook:\n\n```php register_activation_hook( __FILE__, function( $network_wide ) {     // Get the activation options from the transient     $option_name = \'blueprint_activation_\' . plugin_basename( __FILE__ );     $blueprint_activation_options = get_option( $option_name ) ?? [];\n\n    if ( $blueprint_activation_options ) {         $store_city = $blueprint_activation_options[\'storeCity\'] ?? \'\';         $store_country = $blueprint_activation_options[\'storeCountry\'] ?? \'\';         $store_postal_code = $blueprint_activation_options[\'storePostalCode\'] ?? \'\';\n\n        // ...do something with the options...     }\n\n    // Continue with normal activation... } ); ```',
							},
							targetDirectoryName: {
								type: 'string',
								description:
									'An explicit directory name within wp-content/plugins to install the plugin at. If not provided, it will be inferred from the plugin source.',
								pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
							},
							onError: {
								type: 'string',
								enum: ['skip-plugin', 'throw'],
								description:
									"Sometimes it's fine when a plugin fails to install.\n\nUse-case:   Compatibility testing. A Blueprint may install WordPress nightly with   a number of plugins to test. Some of those plugins may not yet be compatible   with the latest version of WordPress. This is something to take not of,   but not a strong reason to fail the entire Blueprint installation.",
								default: 'throw',
							},
							ifAlreadyInstalled: {
								type: 'string',
								enum: ['overwrite', 'skip', 'error'],
								description:
									'How to handle a plugin that is already installed.',
								default: 'overwrite',
							},
							humanReadableName: {
								type: 'string',
								description:
									'Human-readable name of the plugin for the progress bar.\n\nFor example, with the following Blueprint:\n\n```json {     "plugins": [         {             "source": "https://github.com/Automattic/jetpack/archive/refs/heads/beta.zip",             "humanReadableName": "Jetpack Beta"         }     ] } ```\n\nThe progress bar will show "Installing Jetpack Beta plugin" instead of "Installing https://github.com/Automattic/jetpack/archive/refs/heads/beta.zip".',
							},
							step: { type: 'string', const: 'installPlugin' },
						},
						required: ['source', 'step'],
					},
					{
						type: 'object',
						additionalProperties: false,
						properties: {
							source: {
								anyOf: [
									{
										$ref: '#/definitions/DataSources.ThemeDirectoryReference',
									},
									{
										$ref: '#/definitions/DataSources.DataReference',
									},
								],
							},
							importStarterContent: {
								type: 'boolean',
								description:
									"Whether to import the theme's starter content after installing it.",
							},
							targetDirectoryName: {
								type: 'string',
								description:
									'An explicit directory name within wp-content/themes to install the theme at. If not provided, it will be inferred from the theme source.',
								pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
							},
							onError: {
								type: 'string',
								enum: ['skip-theme', 'throw'],
								description:
									"Sometimes it's fine when a theme fails to install.",
								default: 'throw',
							},
							ifAlreadyInstalled: {
								type: 'string',
								enum: ['overwrite', 'skip', 'error'],
								description:
									'How to handle a theme that is already installed.',
								default: 'overwrite',
							},
							humanReadableName: {
								type: 'string',
								description:
									'Human-readable name of the theme for the progress bar.\n\nFor example, with the following Blueprint:\n\n```json {     "themes": [         {             "source": "https://github.com/Automattic/adventurer/archive/refs/heads/beta.zip",             "humanReadableName": "Adventurer"         }     ] } ```\n\nThe progress bar will show "Installing Adventurer theme" instead of "Installing https://github.com/Automattic/adventurer/archive/refs/heads/beta.zip".',
							},
							step: { type: 'string', const: 'installTheme' },
							active: {
								type: 'boolean',
								description:
									'Whether to activate the theme after installing it.\n\nThis is not a part of the theme definition. Only the step can explicitly provide this option. The default value is `true`.',
							},
						},
						required: ['source', 'step'],
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'mkdir' },
							path: { type: 'string' },
						},
						required: ['step', 'path'],
						additionalProperties: false,
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'mv' },
							fromPath: { type: 'string' },
							toPath: { type: 'string' },
						},
						required: ['step', 'fromPath', 'toPath'],
						additionalProperties: false,
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'rm' },
							path: { type: 'string' },
						},
						required: ['step', 'path'],
						additionalProperties: false,
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'rmdir' },
							path: { type: 'string' },
						},
						required: ['step', 'path'],
						additionalProperties: false,
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'resetData' },
							contentTypes: {
								type: 'array',
								items: {
									type: 'string',
									enum: ['posts', 'pages', 'comments'],
									description:
										'Content types created by a vanilla WordPress installation and controlled by `contentBaseline`.',
								},
								description:
									'Content types to remove. When omitted, all posts, pages, custom post types, and comments are removed.',
							},
						},
						required: ['step'],
						additionalProperties: false,
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'runPHP' },
							code: {
								$ref: '#/definitions/DataSources.FileDataReference',
								description: 'The PHP file to execute.',
							},
							env: {
								type: 'object',
								additionalProperties: { type: 'string' },
								description:
									'Environment variables to set for this run.',
							},
						},
						required: ['step', 'code'],
						additionalProperties: false,
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'runSQL' },
							source: {
								$ref: '#/definitions/DataSources.FileDataReference',
							},
						},
						required: ['step', 'source'],
						additionalProperties: false,
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'setSiteLanguage' },
							language: {
								type: 'string',
								description:
									"The language to set, e.g. 'en_US'",
							},
						},
						required: ['step', 'language'],
						additionalProperties: false,
						description:
							'Sets the site language and download translations for WordPress core and all the installed plugins and themes.',
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'setSiteOptions' },
							options: {
								type: 'object',
								additionalProperties: {
									anyOf: [
										{ type: 'string' },
										{ type: 'boolean' },
										{ type: 'number' },
										{
											type: 'array',
											items: {
												$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
											},
										},
										{
											type: 'object',
											additionalProperties: {
												$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
											},
										},
									],
								},
							},
						},
						required: ['step', 'options'],
						additionalProperties: false,
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'unzip' },
							zipFile: {
								$ref: '#/definitions/DataSources.FileDataReference',
								description:
									'The zip file resource to extract.',
							},
							extractToPath: {
								type: 'string',
								description:
									'The path to extract the zip file to inside the virtual filesystem.',
							},
						},
						required: ['step', 'zipFile', 'extractToPath'],
						additionalProperties: false,
						description:
							'Unzips a file. While this step is not strictly necessary, it is very convenient for:\n\n* Working with GitHub releases that output doubly zipped data.\n* Preprocessing zipped data before using them in the Blueprint.',
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'wp-cli' },
							command: { type: 'string' },
							wpCliPath: { type: 'string' },
						},
						required: ['step', 'command'],
						additionalProperties: false,
					},
					{
						type: 'object',
						properties: {
							step: { type: 'string', const: 'writeFiles' },
							files: {
								type: 'object',
								additionalProperties: {
									$ref: '#/definitions/DataSources.DataReference',
								},
							},
						},
						required: ['step', 'files'],
						additionalProperties: false,
					},
				],
			},
		},
	},
	required: ['version'],
	additionalProperties: false,
	allOf: [
		{
			if: {
				properties: { usersBaseline: { const: 'empty' } },
				required: ['usersBaseline'],
			},
			then: {
				properties: {
					contentBaseline: { const: 'empty' },
					users: {
						type: 'array',
						contains: {
							type: 'object',
							properties: { role: { const: 'administrator' } },
							required: ['role'],
						},
					},
				},
				required: ['contentBaseline', 'users'],
			},
		},
		{
			if: {
				properties: { wordpressVersion: { const: 'none' } },
				required: ['wordpressVersion'],
			},
			then: {
				properties: { contentBaseline: false, usersBaseline: false },
			},
		},
	],
};
const schema13 = {
	type: 'string',
	description:
		'A reference to a HTTP or HTTPS URL.\n\nThe URLs are parsed using the WHATWG URL standard, which means they can optionally contain usernames and passwords if needed.',
	format: 'whatwg-http-url',
};
const schema14 = {
	type: 'string',
	description:
		'A reference to a file in the Blueprint Execution Context – see the main proposal document for more context.\n\nThe path must start with either ./ or / to distinguish it from a plugin or theme slug. Regardless of the prefix (./ or /), the path is relative to the Blueprint Execution Context root:\n\n* Relative paths (./) are relative to the location of blueprint.json file.\n* Absolute paths (/) are chrooted at the Blueprint Execution Context root which is   still the directory where blueprint.json is located.\n\nIt is not possible to escape the Blueprint Execution Context via "../" sequences.',
	pattern: '^(?!.*(?:^|/)\\.\\.(?:/|$))(?:\\./|/).*$',
};
const schema17 = {
	description:
		'WordPress version, e.g. "latest", "beta", "trunk", "none", "6.4", "6.4.3", "6.8-RC1", or "6.7-beta2".\n\nThese refer to slugs of specific WordPress releases as listed in the first table column on https://wordpress.org/download/releases/. "none" is not a release. It means the Blueprint runs PHP without installing WordPress.\n\nThe WordPressVersion type is only meaningful in the top-level `wordpressVersion` property.',
	type: 'string',
	pattern:
		'^(?:latest|beta|trunk|nightly|none|\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?)$',
};
const schema26 = {
	type: 'string',
	pattern: '^\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?$',
};
const schema28 = {
	type: 'string',
	pattern:
		'^(?:latest|\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?)$',
};
const schema29 = {
	description:
		'PHP version, e.g. "8.1", "8.1.3", or "next".\n\nThese refer to PHP versions as listed in https://www.php.net/releases/. `next` previews the php-src development branch and is currently supported by the web runtime only.\n\nThe PHPVersion type is only meaningful in the top-level `phpVersion` property.',
	type: 'string',
	pattern: '^(?:latest|next|\\d+\\.\\d+(?:\\.\\d+)?)$',
};
const schema30 = {
	type: 'string',
	pattern: '^(?:latest|\\d+\\.\\d+(?:\\.\\d+)?)$',
};
const func2 = Object.prototype.hasOwnProperty;
const formats0 = {
	'whatwg-http-url': (value) => {
		try {
			const url = new URL(value);
			return (
				(url.protocol === 'http:' || url.protocol === 'https:') &&
				url.hostname !== ''
			);
		} catch {
			return false;
		}
	},
}['whatwg-http-url'];
const pattern0 = new RegExp('^(?!.*(?:^|/)\\.\\.(?:/|$))(?:\\./|/).*$', 'u');
const pattern1 = new RegExp(
	'^(?:latest|beta|trunk|nightly|none|\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?)$',
	'u'
);
const pattern8 = new RegExp(
	'^\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?$',
	'u'
);
const pattern10 = new RegExp(
	'^(?:latest|\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?)$',
	'u'
);
const pattern11 = new RegExp('^(?:latest|next|\\d+\\.\\d+(?:\\.\\d+)?)$', 'u');
const pattern12 = new RegExp('^(?:latest|\\d+\\.\\d+(?:\\.\\d+)?)$', 'u');
const pattern3 = new RegExp('^(?!(?:\\.|\\.\\.)$)[^/]+$', 'u');
const pattern18 = new RegExp('^[a-z0-9_-]{1,20}$', 'u');
const schema18 = {
	anyOf: [
		{ $ref: '#/definitions/DataSources.URLReference' },
		{ $ref: '#/definitions/DataSources.ExecutionContextPath' },
		{ $ref: '#/definitions/DataSources.InlineFile' },
		{ $ref: '#/definitions/DataSources.InlineDirectory' },
		{ $ref: '#/definitions/DataSources.GitPath' },
	],
	description: 'A union of all general data reference types.',
};
const schema21 = {
	type: 'object',
	properties: {
		filename: { type: 'string', pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$' },
		content: { type: 'string' },
	},
	required: ['filename', 'content'],
	additionalProperties: false,
	description:
		'A file that is inlined within the Blueprint JSON document.\n\nExample:\n\n```json {     "filename": "index.php",     "content": "<?php echo \'Hello, world!\'; ?>" } ```',
};
const schema22 = {
	type: 'object',
	properties: {
		directoryName: {
			type: 'string',
			pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
		},
		files: {
			type: 'object',
			additionalProperties: {
				anyOf: [
					{ type: 'string' },
					{ $ref: '#/definitions/DataSources.NestedInlineDirectory' },
				],
			},
			propertyNames: { pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$' },
		},
	},
	required: ['directoryName', 'files'],
	additionalProperties: false,
	description:
		'A directory that is inlined within the Blueprint JSON document.\n\nExample:\n\n```json {     "directoryName": "my-directory",     "files": {         "index.php": "<?php echo \'Hello, world!\'; ?>",         "my-sub-directory": {             "files": {                 "index.php": "<?php echo \'Hello, world!\'; ?>"             }         }     } } ```',
};
const schema23 = {
	type: 'object',
	properties: {
		files: {
			type: 'object',
			additionalProperties: {
				anyOf: [
					{ type: 'string' },
					{ $ref: '#/definitions/DataSources.NestedInlineDirectory' },
				],
			},
			propertyNames: { pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$' },
		},
	},
	required: ['files'],
	additionalProperties: false,
	description:
		'A child directory inside an inline directory.\n\nIts directory name comes from the parent `files` record key.',
};
const wrapper0 = { validate: validate14 };
function validate14(
	data,
	{ instancePath = '', parentData, parentDataProperty, rootData = data } = {}
) {
	let vErrors = null;
	let errors = 0;
	if (data && typeof data == 'object' && !Array.isArray(data)) {
		if (data.files === undefined) {
			const err0 = {
				instancePath,
				schemaPath: '#/required',
				keyword: 'required',
				params: { missingProperty: 'files' },
				message: "must have required property '" + 'files' + "'",
			};
			if (vErrors === null) {
				vErrors = [err0];
			} else {
				vErrors.push(err0);
			}
			errors++;
		}
		for (const key0 in data) {
			if (!(key0 === 'files')) {
				const err1 = {
					instancePath,
					schemaPath: '#/additionalProperties',
					keyword: 'additionalProperties',
					params: { additionalProperty: key0 },
					message: 'must NOT have additional properties',
				};
				if (vErrors === null) {
					vErrors = [err1];
				} else {
					vErrors.push(err1);
				}
				errors++;
			}
		}
		if (data.files !== undefined) {
			let data0 = data.files;
			if (data0 && typeof data0 == 'object' && !Array.isArray(data0)) {
				for (const key1 in data0) {
					const _errs4 = errors;
					if (typeof key1 === 'string') {
						if (!pattern3.test(key1)) {
							const err2 = {
								instancePath: instancePath + '/files',
								schemaPath:
									'#/properties/files/propertyNames/pattern',
								keyword: 'pattern',
								params: {
									pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
								},
								message:
									'must match pattern "' +
									'^(?!(?:\\.|\\.\\.)$)[^/]+$' +
									'"',
								propertyName: key1,
							};
							if (vErrors === null) {
								vErrors = [err2];
							} else {
								vErrors.push(err2);
							}
							errors++;
						}
					}
					var valid1 = _errs4 === errors;
					if (!valid1) {
						const err3 = {
							instancePath: instancePath + '/files',
							schemaPath: '#/properties/files/propertyNames',
							keyword: 'propertyNames',
							params: { propertyName: key1 },
							message: 'property name must be valid',
						};
						if (vErrors === null) {
							vErrors = [err3];
						} else {
							vErrors.push(err3);
						}
						errors++;
					}
				}
				for (const key2 in data0) {
					let data1 = data0[key2];
					const _errs7 = errors;
					let valid3 = false;
					const _errs8 = errors;
					if (typeof data1 !== 'string') {
						const err4 = {
							instancePath:
								instancePath +
								'/files/' +
								key2.replace(/~/g, '~0').replace(/\//g, '~1'),
							schemaPath:
								'#/properties/files/additionalProperties/anyOf/0/type',
							keyword: 'type',
							params: { type: 'string' },
							message: 'must be string',
						};
						if (vErrors === null) {
							vErrors = [err4];
						} else {
							vErrors.push(err4);
						}
						errors++;
					}
					var _valid0 = _errs8 === errors;
					valid3 = valid3 || _valid0;
					if (!valid3) {
						const _errs10 = errors;
						if (
							!wrapper0.validate(data1, {
								instancePath:
									instancePath +
									'/files/' +
									key2
										.replace(/~/g, '~0')
										.replace(/\//g, '~1'),
								parentData: data0,
								parentDataProperty: key2,
								rootData,
							})
						) {
							vErrors =
								vErrors === null
									? wrapper0.validate.errors
									: vErrors.concat(wrapper0.validate.errors);
							errors = vErrors.length;
						}
						var _valid0 = _errs10 === errors;
						valid3 = valid3 || _valid0;
					}
					if (!valid3) {
						const err5 = {
							instancePath:
								instancePath +
								'/files/' +
								key2.replace(/~/g, '~0').replace(/\//g, '~1'),
							schemaPath:
								'#/properties/files/additionalProperties/anyOf',
							keyword: 'anyOf',
							params: {},
							message: 'must match a schema in anyOf',
						};
						if (vErrors === null) {
							vErrors = [err5];
						} else {
							vErrors.push(err5);
						}
						errors++;
					} else {
						errors = _errs7;
						if (vErrors !== null) {
							if (_errs7) {
								vErrors.length = _errs7;
							} else {
								vErrors = null;
							}
						}
					}
				}
			} else {
				const err6 = {
					instancePath: instancePath + '/files',
					schemaPath: '#/properties/files/type',
					keyword: 'type',
					params: { type: 'object' },
					message: 'must be object',
				};
				if (vErrors === null) {
					vErrors = [err6];
				} else {
					vErrors.push(err6);
				}
				errors++;
			}
		}
	} else {
		const err7 = {
			instancePath,
			schemaPath: '#/type',
			keyword: 'type',
			params: { type: 'object' },
			message: 'must be object',
		};
		if (vErrors === null) {
			vErrors = [err7];
		} else {
			vErrors.push(err7);
		}
		errors++;
	}
	validate14.errors = vErrors;
	return errors === 0;
}
function validate13(
	data,
	{ instancePath = '', parentData, parentDataProperty, rootData = data } = {}
) {
	let vErrors = null;
	let errors = 0;
	if (data && typeof data == 'object' && !Array.isArray(data)) {
		if (data.directoryName === undefined) {
			const err0 = {
				instancePath,
				schemaPath: '#/required',
				keyword: 'required',
				params: { missingProperty: 'directoryName' },
				message:
					"must have required property '" + 'directoryName' + "'",
			};
			if (vErrors === null) {
				vErrors = [err0];
			} else {
				vErrors.push(err0);
			}
			errors++;
		}
		if (data.files === undefined) {
			const err1 = {
				instancePath,
				schemaPath: '#/required',
				keyword: 'required',
				params: { missingProperty: 'files' },
				message: "must have required property '" + 'files' + "'",
			};
			if (vErrors === null) {
				vErrors = [err1];
			} else {
				vErrors.push(err1);
			}
			errors++;
		}
		for (const key0 in data) {
			if (!(key0 === 'directoryName' || key0 === 'files')) {
				const err2 = {
					instancePath,
					schemaPath: '#/additionalProperties',
					keyword: 'additionalProperties',
					params: { additionalProperty: key0 },
					message: 'must NOT have additional properties',
				};
				if (vErrors === null) {
					vErrors = [err2];
				} else {
					vErrors.push(err2);
				}
				errors++;
			}
		}
		if (data.directoryName !== undefined) {
			let data0 = data.directoryName;
			if (typeof data0 === 'string') {
				if (!pattern3.test(data0)) {
					const err3 = {
						instancePath: instancePath + '/directoryName',
						schemaPath: '#/properties/directoryName/pattern',
						keyword: 'pattern',
						params: { pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$' },
						message:
							'must match pattern "' +
							'^(?!(?:\\.|\\.\\.)$)[^/]+$' +
							'"',
					};
					if (vErrors === null) {
						vErrors = [err3];
					} else {
						vErrors.push(err3);
					}
					errors++;
				}
			} else {
				const err4 = {
					instancePath: instancePath + '/directoryName',
					schemaPath: '#/properties/directoryName/type',
					keyword: 'type',
					params: { type: 'string' },
					message: 'must be string',
				};
				if (vErrors === null) {
					vErrors = [err4];
				} else {
					vErrors.push(err4);
				}
				errors++;
			}
		}
		if (data.files !== undefined) {
			let data1 = data.files;
			if (data1 && typeof data1 == 'object' && !Array.isArray(data1)) {
				for (const key1 in data1) {
					const _errs6 = errors;
					if (typeof key1 === 'string') {
						if (!pattern3.test(key1)) {
							const err5 = {
								instancePath: instancePath + '/files',
								schemaPath:
									'#/properties/files/propertyNames/pattern',
								keyword: 'pattern',
								params: {
									pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
								},
								message:
									'must match pattern "' +
									'^(?!(?:\\.|\\.\\.)$)[^/]+$' +
									'"',
								propertyName: key1,
							};
							if (vErrors === null) {
								vErrors = [err5];
							} else {
								vErrors.push(err5);
							}
							errors++;
						}
					}
					var valid1 = _errs6 === errors;
					if (!valid1) {
						const err6 = {
							instancePath: instancePath + '/files',
							schemaPath: '#/properties/files/propertyNames',
							keyword: 'propertyNames',
							params: { propertyName: key1 },
							message: 'property name must be valid',
						};
						if (vErrors === null) {
							vErrors = [err6];
						} else {
							vErrors.push(err6);
						}
						errors++;
					}
				}
				for (const key2 in data1) {
					let data2 = data1[key2];
					const _errs9 = errors;
					let valid3 = false;
					const _errs10 = errors;
					if (typeof data2 !== 'string') {
						const err7 = {
							instancePath:
								instancePath +
								'/files/' +
								key2.replace(/~/g, '~0').replace(/\//g, '~1'),
							schemaPath:
								'#/properties/files/additionalProperties/anyOf/0/type',
							keyword: 'type',
							params: { type: 'string' },
							message: 'must be string',
						};
						if (vErrors === null) {
							vErrors = [err7];
						} else {
							vErrors.push(err7);
						}
						errors++;
					}
					var _valid0 = _errs10 === errors;
					valid3 = valid3 || _valid0;
					if (!valid3) {
						const _errs12 = errors;
						if (
							!validate14(data2, {
								instancePath:
									instancePath +
									'/files/' +
									key2
										.replace(/~/g, '~0')
										.replace(/\//g, '~1'),
								parentData: data1,
								parentDataProperty: key2,
								rootData,
							})
						) {
							vErrors =
								vErrors === null
									? validate14.errors
									: vErrors.concat(validate14.errors);
							errors = vErrors.length;
						}
						var _valid0 = _errs12 === errors;
						valid3 = valid3 || _valid0;
					}
					if (!valid3) {
						const err8 = {
							instancePath:
								instancePath +
								'/files/' +
								key2.replace(/~/g, '~0').replace(/\//g, '~1'),
							schemaPath:
								'#/properties/files/additionalProperties/anyOf',
							keyword: 'anyOf',
							params: {},
							message: 'must match a schema in anyOf',
						};
						if (vErrors === null) {
							vErrors = [err8];
						} else {
							vErrors.push(err8);
						}
						errors++;
					} else {
						errors = _errs9;
						if (vErrors !== null) {
							if (_errs9) {
								vErrors.length = _errs9;
							} else {
								vErrors = null;
							}
						}
					}
				}
			} else {
				const err9 = {
					instancePath: instancePath + '/files',
					schemaPath: '#/properties/files/type',
					keyword: 'type',
					params: { type: 'object' },
					message: 'must be object',
				};
				if (vErrors === null) {
					vErrors = [err9];
				} else {
					vErrors.push(err9);
				}
				errors++;
			}
		}
	} else {
		const err10 = {
			instancePath,
			schemaPath: '#/type',
			keyword: 'type',
			params: { type: 'object' },
			message: 'must be object',
		};
		if (vErrors === null) {
			vErrors = [err10];
		} else {
			vErrors.push(err10);
		}
		errors++;
	}
	validate13.errors = vErrors;
	return errors === 0;
}
const schema24 = {
	type: 'object',
	properties: {
		gitRepository: {
			$ref: '#/definitions/DataSources.URLReference',
			description: 'A HTTP or HTTPS URL of the remote git repository.',
		},
		ref: {
			type: 'string',
			description:
				'A branch name, commit hash, or tag name.\n\nDefaults to HEAD.',
		},
		pathInRepository: {
			type: 'string',
			description:
				'A path inside the git repository this data reference points to.\n\nDefaults to the root of the repository.',
			pattern: '^(?!.*(?:^|/)\\.\\.(?:/|$)).*$',
		},
	},
	required: ['gitRepository'],
	additionalProperties: false,
	description: 'A reference to a remote git repository.',
};
const pattern7 = new RegExp('^(?!.*(?:^|/)\\.\\.(?:/|$)).*$', 'u');
function validate17(
	data,
	{ instancePath = '', parentData, parentDataProperty, rootData = data } = {}
) {
	let vErrors = null;
	let errors = 0;
	if (data && typeof data == 'object' && !Array.isArray(data)) {
		if (data.gitRepository === undefined) {
			const err0 = {
				instancePath,
				schemaPath: '#/required',
				keyword: 'required',
				params: { missingProperty: 'gitRepository' },
				message:
					"must have required property '" + 'gitRepository' + "'",
			};
			if (vErrors === null) {
				vErrors = [err0];
			} else {
				vErrors.push(err0);
			}
			errors++;
		}
		for (const key0 in data) {
			if (
				!(
					key0 === 'gitRepository' ||
					key0 === 'ref' ||
					key0 === 'pathInRepository'
				)
			) {
				const err1 = {
					instancePath,
					schemaPath: '#/additionalProperties',
					keyword: 'additionalProperties',
					params: { additionalProperty: key0 },
					message: 'must NOT have additional properties',
				};
				if (vErrors === null) {
					vErrors = [err1];
				} else {
					vErrors.push(err1);
				}
				errors++;
			}
		}
		if (data.gitRepository !== undefined) {
			let data0 = data.gitRepository;
			if (typeof data0 === 'string') {
				if (!formats0(data0)) {
					const err2 = {
						instancePath: instancePath + '/gitRepository',
						schemaPath:
							'#/definitions/DataSources.URLReference/format',
						keyword: 'format',
						params: { format: 'whatwg-http-url' },
						message:
							'must match format "' + 'whatwg-http-url' + '"',
					};
					if (vErrors === null) {
						vErrors = [err2];
					} else {
						vErrors.push(err2);
					}
					errors++;
				}
			} else {
				const err3 = {
					instancePath: instancePath + '/gitRepository',
					schemaPath: '#/definitions/DataSources.URLReference/type',
					keyword: 'type',
					params: { type: 'string' },
					message: 'must be string',
				};
				if (vErrors === null) {
					vErrors = [err3];
				} else {
					vErrors.push(err3);
				}
				errors++;
			}
		}
		if (data.ref !== undefined) {
			if (typeof data.ref !== 'string') {
				const err4 = {
					instancePath: instancePath + '/ref',
					schemaPath: '#/properties/ref/type',
					keyword: 'type',
					params: { type: 'string' },
					message: 'must be string',
				};
				if (vErrors === null) {
					vErrors = [err4];
				} else {
					vErrors.push(err4);
				}
				errors++;
			}
		}
		if (data.pathInRepository !== undefined) {
			let data2 = data.pathInRepository;
			if (typeof data2 === 'string') {
				if (!pattern7.test(data2)) {
					const err5 = {
						instancePath: instancePath + '/pathInRepository',
						schemaPath: '#/properties/pathInRepository/pattern',
						keyword: 'pattern',
						params: { pattern: '^(?!.*(?:^|/)\\.\\.(?:/|$)).*$' },
						message:
							'must match pattern "' +
							'^(?!.*(?:^|/)\\.\\.(?:/|$)).*$' +
							'"',
					};
					if (vErrors === null) {
						vErrors = [err5];
					} else {
						vErrors.push(err5);
					}
					errors++;
				}
			} else {
				const err6 = {
					instancePath: instancePath + '/pathInRepository',
					schemaPath: '#/properties/pathInRepository/type',
					keyword: 'type',
					params: { type: 'string' },
					message: 'must be string',
				};
				if (vErrors === null) {
					vErrors = [err6];
				} else {
					vErrors.push(err6);
				}
				errors++;
			}
		}
	} else {
		const err7 = {
			instancePath,
			schemaPath: '#/type',
			keyword: 'type',
			params: { type: 'object' },
			message: 'must be object',
		};
		if (vErrors === null) {
			vErrors = [err7];
		} else {
			vErrors.push(err7);
		}
		errors++;
	}
	validate17.errors = vErrors;
	return errors === 0;
}
function validate12(
	data,
	{ instancePath = '', parentData, parentDataProperty, rootData = data } = {}
) {
	let vErrors = null;
	let errors = 0;
	const _errs0 = errors;
	let valid0 = false;
	const _errs1 = errors;
	if (typeof data === 'string') {
		if (!formats0(data)) {
			const err0 = {
				instancePath,
				schemaPath: '#/definitions/DataSources.URLReference/format',
				keyword: 'format',
				params: { format: 'whatwg-http-url' },
				message: 'must match format "' + 'whatwg-http-url' + '"',
			};
			if (vErrors === null) {
				vErrors = [err0];
			} else {
				vErrors.push(err0);
			}
			errors++;
		}
	} else {
		const err1 = {
			instancePath,
			schemaPath: '#/definitions/DataSources.URLReference/type',
			keyword: 'type',
			params: { type: 'string' },
			message: 'must be string',
		};
		if (vErrors === null) {
			vErrors = [err1];
		} else {
			vErrors.push(err1);
		}
		errors++;
	}
	var _valid0 = _errs1 === errors;
	valid0 = valid0 || _valid0;
	if (!valid0) {
		const _errs4 = errors;
		if (typeof data === 'string') {
			if (!pattern0.test(data)) {
				const err2 = {
					instancePath,
					schemaPath:
						'#/definitions/DataSources.ExecutionContextPath/pattern',
					keyword: 'pattern',
					params: {
						pattern: '^(?!.*(?:^|/)\\.\\.(?:/|$))(?:\\./|/).*$',
					},
					message:
						'must match pattern "' +
						'^(?!.*(?:^|/)\\.\\.(?:/|$))(?:\\./|/).*$' +
						'"',
				};
				if (vErrors === null) {
					vErrors = [err2];
				} else {
					vErrors.push(err2);
				}
				errors++;
			}
		} else {
			const err3 = {
				instancePath,
				schemaPath:
					'#/definitions/DataSources.ExecutionContextPath/type',
				keyword: 'type',
				params: { type: 'string' },
				message: 'must be string',
			};
			if (vErrors === null) {
				vErrors = [err3];
			} else {
				vErrors.push(err3);
			}
			errors++;
		}
		var _valid0 = _errs4 === errors;
		valid0 = valid0 || _valid0;
		if (!valid0) {
			const _errs7 = errors;
			if (data && typeof data == 'object' && !Array.isArray(data)) {
				if (data.filename === undefined) {
					const err4 = {
						instancePath,
						schemaPath:
							'#/definitions/DataSources.InlineFile/required',
						keyword: 'required',
						params: { missingProperty: 'filename' },
						message:
							"must have required property '" + 'filename' + "'",
					};
					if (vErrors === null) {
						vErrors = [err4];
					} else {
						vErrors.push(err4);
					}
					errors++;
				}
				if (data.content === undefined) {
					const err5 = {
						instancePath,
						schemaPath:
							'#/definitions/DataSources.InlineFile/required',
						keyword: 'required',
						params: { missingProperty: 'content' },
						message:
							"must have required property '" + 'content' + "'",
					};
					if (vErrors === null) {
						vErrors = [err5];
					} else {
						vErrors.push(err5);
					}
					errors++;
				}
				for (const key0 in data) {
					if (!(key0 === 'filename' || key0 === 'content')) {
						const err6 = {
							instancePath,
							schemaPath:
								'#/definitions/DataSources.InlineFile/additionalProperties',
							keyword: 'additionalProperties',
							params: { additionalProperty: key0 },
							message: 'must NOT have additional properties',
						};
						if (vErrors === null) {
							vErrors = [err6];
						} else {
							vErrors.push(err6);
						}
						errors++;
					}
				}
				if (data.filename !== undefined) {
					let data0 = data.filename;
					if (typeof data0 === 'string') {
						if (!pattern3.test(data0)) {
							const err7 = {
								instancePath: instancePath + '/filename',
								schemaPath:
									'#/definitions/DataSources.InlineFile/properties/filename/pattern',
								keyword: 'pattern',
								params: {
									pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
								},
								message:
									'must match pattern "' +
									'^(?!(?:\\.|\\.\\.)$)[^/]+$' +
									'"',
							};
							if (vErrors === null) {
								vErrors = [err7];
							} else {
								vErrors.push(err7);
							}
							errors++;
						}
					} else {
						const err8 = {
							instancePath: instancePath + '/filename',
							schemaPath:
								'#/definitions/DataSources.InlineFile/properties/filename/type',
							keyword: 'type',
							params: { type: 'string' },
							message: 'must be string',
						};
						if (vErrors === null) {
							vErrors = [err8];
						} else {
							vErrors.push(err8);
						}
						errors++;
					}
				}
				if (data.content !== undefined) {
					if (typeof data.content !== 'string') {
						const err9 = {
							instancePath: instancePath + '/content',
							schemaPath:
								'#/definitions/DataSources.InlineFile/properties/content/type',
							keyword: 'type',
							params: { type: 'string' },
							message: 'must be string',
						};
						if (vErrors === null) {
							vErrors = [err9];
						} else {
							vErrors.push(err9);
						}
						errors++;
					}
				}
			} else {
				const err10 = {
					instancePath,
					schemaPath: '#/definitions/DataSources.InlineFile/type',
					keyword: 'type',
					params: { type: 'object' },
					message: 'must be object',
				};
				if (vErrors === null) {
					vErrors = [err10];
				} else {
					vErrors.push(err10);
				}
				errors++;
			}
			var _valid0 = _errs7 === errors;
			valid0 = valid0 || _valid0;
			if (!valid0) {
				const _errs15 = errors;
				if (
					!validate13(data, {
						instancePath,
						parentData,
						parentDataProperty,
						rootData,
					})
				) {
					vErrors =
						vErrors === null
							? validate13.errors
							: vErrors.concat(validate13.errors);
					errors = vErrors.length;
				}
				var _valid0 = _errs15 === errors;
				valid0 = valid0 || _valid0;
				if (!valid0) {
					const _errs16 = errors;
					if (
						!validate17(data, {
							instancePath,
							parentData,
							parentDataProperty,
							rootData,
						})
					) {
						vErrors =
							vErrors === null
								? validate17.errors
								: vErrors.concat(validate17.errors);
						errors = vErrors.length;
					}
					var _valid0 = _errs16 === errors;
					valid0 = valid0 || _valid0;
				}
			}
		}
	}
	if (!valid0) {
		const err11 = {
			instancePath,
			schemaPath: '#/anyOf',
			keyword: 'anyOf',
			params: {},
			message: 'must match a schema in anyOf',
		};
		if (vErrors === null) {
			vErrors = [err11];
		} else {
			vErrors.push(err11);
		}
		errors++;
	} else {
		errors = _errs0;
		if (vErrors !== null) {
			if (_errs0) {
				vErrors.length = _errs0;
			} else {
				vErrors = null;
			}
		}
	}
	validate12.errors = vErrors;
	return errors === 0;
}
const schema33 = {
	anyOf: [{ $ref: '#/definitions/DataSources.Slug' }, { type: 'string' }],
	description:
		'Theme directory reference, e.g. "twentytwentythree", "adventurer@4.6.0", or "twentytwentyfour@latest".\n\nThese refer to specific theme slugs in the WordPress.org theme repository.\n\nFor example, a reference to "adventurer" means the Adventurer theme as seen on https://wordpress.org/themes/adventurer/.\n\nThese references are only meaningful in:\n\n* The top-level `themes` array\n* The `installTheme` imperative step',
};
const schema34 = {
	type: 'string',
	description:
		'A WordPress.org directory slug.\n\nSlugs are intentionally treated as opaque strings. Playground should not reject future WordPress.org slug formats just because they do not match the directory conventions common today.',
};
function validate20(
	data,
	{ instancePath = '', parentData, parentDataProperty, rootData = data } = {}
) {
	let vErrors = null;
	let errors = 0;
	const _errs0 = errors;
	let valid0 = false;
	const _errs1 = errors;
	if (typeof data !== 'string') {
		const err0 = {
			instancePath,
			schemaPath: '#/definitions/DataSources.Slug/type',
			keyword: 'type',
			params: { type: 'string' },
			message: 'must be string',
		};
		if (vErrors === null) {
			vErrors = [err0];
		} else {
			vErrors.push(err0);
		}
		errors++;
	}
	var _valid0 = _errs1 === errors;
	valid0 = valid0 || _valid0;
	if (!valid0) {
		const _errs4 = errors;
		if (typeof data !== 'string') {
			const err1 = {
				instancePath,
				schemaPath: '#/anyOf/1/type',
				keyword: 'type',
				params: { type: 'string' },
				message: 'must be string',
			};
			if (vErrors === null) {
				vErrors = [err1];
			} else {
				vErrors.push(err1);
			}
			errors++;
		}
		var _valid0 = _errs4 === errors;
		valid0 = valid0 || _valid0;
	}
	if (!valid0) {
		const err2 = {
			instancePath,
			schemaPath: '#/anyOf',
			keyword: 'anyOf',
			params: {},
			message: 'must match a schema in anyOf',
		};
		if (vErrors === null) {
			vErrors = [err2];
		} else {
			vErrors.push(err2);
		}
		errors++;
	} else {
		errors = _errs0;
		if (vErrors !== null) {
			if (_errs0) {
				vErrors.length = _errs0;
			} else {
				vErrors = null;
			}
		}
	}
	validate20.errors = vErrors;
	return errors === 0;
}
const schema35 = {
	anyOf: [{ $ref: '#/definitions/DataSources.Slug' }, { type: 'string' }],
	description:
		'Plugin directory reference, e.g. "jetpack", "jetpack@6.4", or "akismet@6.4.3".\n\nThese refer to a specific plugin slugs in the WordPress.org plugin repository.\n\nFor example, a reference to "wordpress-seo" means the Yoast SEO plugin as seen on https://wordpress.org/plugins/wordpress-seo/.\n\nThe Plugin Directory Reference are only meaningful in:\n\n* The top-level `plugins` array\n* The `installPlugin` imperative step',
};
function validate30(
	data,
	{ instancePath = '', parentData, parentDataProperty, rootData = data } = {}
) {
	let vErrors = null;
	let errors = 0;
	const _errs0 = errors;
	let valid0 = false;
	const _errs1 = errors;
	if (typeof data !== 'string') {
		const err0 = {
			instancePath,
			schemaPath: '#/definitions/DataSources.Slug/type',
			keyword: 'type',
			params: { type: 'string' },
			message: 'must be string',
		};
		if (vErrors === null) {
			vErrors = [err0];
		} else {
			vErrors.push(err0);
		}
		errors++;
	}
	var _valid0 = _errs1 === errors;
	valid0 = valid0 || _valid0;
	if (!valid0) {
		const _errs4 = errors;
		if (typeof data !== 'string') {
			const err1 = {
				instancePath,
				schemaPath: '#/anyOf/1/type',
				keyword: 'type',
				params: { type: 'string' },
				message: 'must be string',
			};
			if (vErrors === null) {
				vErrors = [err1];
			} else {
				vErrors.push(err1);
			}
			errors++;
		}
		var _valid0 = _errs4 === errors;
		valid0 = valid0 || _valid0;
	}
	if (!valid0) {
		const err2 = {
			instancePath,
			schemaPath: '#/anyOf',
			keyword: 'anyOf',
			params: {},
			message: 'must match a schema in anyOf',
		};
		if (vErrors === null) {
			vErrors = [err2];
		} else {
			vErrors.push(err2);
		}
		errors++;
	} else {
		errors = _errs0;
		if (vErrors !== null) {
			if (_errs0) {
				vErrors.length = _errs0;
			} else {
				vErrors = null;
			}
		}
	}
	validate30.errors = vErrors;
	return errors === 0;
}
const schema37 = {
	anyOf: [
		{ type: 'string' },
		{ type: 'boolean' },
		{ type: 'number' },
		{
			type: 'array',
			items: {
				$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
			},
		},
		{
			type: 'object',
			additionalProperties: {
				$ref: '#/definitions/alias-1337903113-148581-148675-1337903113-100727-148689-1337903113-100693-148689-1337903113-0-165845',
			},
		},
	],
};
const wrapper1 = { validate: validate34 };
function validate34(
	data,
	{ instancePath = '', parentData, parentDataProperty, rootData = data } = {}
) {
	let vErrors = null;
	let errors = 0;
	const _errs0 = errors;
	let valid0 = false;
	const _errs1 = errors;
	if (typeof data !== 'string') {
		const err0 = {
			instancePath,
			schemaPath: '#/anyOf/0/type',
			keyword: 'type',
			params: { type: 'string' },
			message: 'must be string',
		};
		if (vErrors === null) {
			vErrors = [err0];
		} else {
			vErrors.push(err0);
		}
		errors++;
	}
	var _valid0 = _errs1 === errors;
	valid0 = valid0 || _valid0;
	if (!valid0) {
		const _errs3 = errors;
		if (typeof data !== 'boolean') {
			const err1 = {
				instancePath,
				schemaPath: '#/anyOf/1/type',
				keyword: 'type',
				params: { type: 'boolean' },
				message: 'must be boolean',
			};
			if (vErrors === null) {
				vErrors = [err1];
			} else {
				vErrors.push(err1);
			}
			errors++;
		}
		var _valid0 = _errs3 === errors;
		valid0 = valid0 || _valid0;
		if (!valid0) {
			const _errs5 = errors;
			if (!(typeof data == 'number' && isFinite(data))) {
				const err2 = {
					instancePath,
					schemaPath: '#/anyOf/2/type',
					keyword: 'type',
					params: { type: 'number' },
					message: 'must be number',
				};
				if (vErrors === null) {
					vErrors = [err2];
				} else {
					vErrors.push(err2);
				}
				errors++;
			}
			var _valid0 = _errs5 === errors;
			valid0 = valid0 || _valid0;
			if (!valid0) {
				const _errs7 = errors;
				if (Array.isArray(data)) {
					const len0 = data.length;
					for (let i0 = 0; i0 < len0; i0++) {
						if (
							!wrapper1.validate(data[i0], {
								instancePath: instancePath + '/' + i0,
								parentData: data,
								parentDataProperty: i0,
								rootData,
							})
						) {
							vErrors =
								vErrors === null
									? wrapper1.validate.errors
									: vErrors.concat(wrapper1.validate.errors);
							errors = vErrors.length;
						}
					}
				} else {
					const err3 = {
						instancePath,
						schemaPath: '#/anyOf/3/type',
						keyword: 'type',
						params: { type: 'array' },
						message: 'must be array',
					};
					if (vErrors === null) {
						vErrors = [err3];
					} else {
						vErrors.push(err3);
					}
					errors++;
				}
				var _valid0 = _errs7 === errors;
				valid0 = valid0 || _valid0;
				if (!valid0) {
					const _errs10 = errors;
					if (
						data &&
						typeof data == 'object' &&
						!Array.isArray(data)
					) {
						for (const key0 in data) {
							if (
								!wrapper1.validate(data[key0], {
									instancePath:
										instancePath +
										'/' +
										key0
											.replace(/~/g, '~0')
											.replace(/\//g, '~1'),
									parentData: data,
									parentDataProperty: key0,
									rootData,
								})
							) {
								vErrors =
									vErrors === null
										? wrapper1.validate.errors
										: vErrors.concat(
												wrapper1.validate.errors
											);
								errors = vErrors.length;
							}
						}
					} else {
						const err4 = {
							instancePath,
							schemaPath: '#/anyOf/4/type',
							keyword: 'type',
							params: { type: 'object' },
							message: 'must be object',
						};
						if (vErrors === null) {
							vErrors = [err4];
						} else {
							vErrors.push(err4);
						}
						errors++;
					}
					var _valid0 = _errs10 === errors;
					valid0 = valid0 || _valid0;
				}
			}
		}
	}
	if (!valid0) {
		const err5 = {
			instancePath,
			schemaPath: '#/anyOf',
			keyword: 'anyOf',
			params: {},
			message: 'must match a schema in anyOf',
		};
		if (vErrors === null) {
			vErrors = [err5];
		} else {
			vErrors.push(err5);
		}
		errors++;
	} else {
		errors = _errs0;
		if (vErrors !== null) {
			if (_errs0) {
				vErrors.length = _errs0;
			} else {
				vErrors = null;
			}
		}
	}
	validate34.errors = vErrors;
	return errors === 0;
}
const schema39 = {
	anyOf: [
		{ $ref: '#/definitions/DataSources.URLReference' },
		{ $ref: '#/definitions/DataSources.ExecutionContextPath' },
		{ $ref: '#/definitions/DataSources.TargetSitePath' },
		{ $ref: '#/definitions/DataSources.InlineFile' },
	],
	description: 'A data reference that must resolve to a single file.',
};
const schema42 = {
	type: 'string',
	description:
		'A path within the target WordPress site, relative to the WordPress root directory. For example, site:wp-content/uploads/2024/01/image.jpg.\n\nUnlike an execution-context path, this path is resolved from the mutable target filesystem when the consuming step runs. Earlier steps may therefore create the referenced file. The runner must keep the path inside the target WordPress root; it never names a file on the host filesystem.',
	pattern: '^site:(?!\\/*$)(?!\\.\\.(?:/|$))(?!.*\\/\\.\\.(?:/|$)).+$',
};
const pattern21 = new RegExp(
	'^site:(?!\\/*$)(?!\\.\\.(?:/|$))(?!.*\\/\\.\\.(?:/|$)).+$',
	'u'
);
function validate40(
	data,
	{ instancePath = '', parentData, parentDataProperty, rootData = data } = {}
) {
	let vErrors = null;
	let errors = 0;
	const _errs0 = errors;
	let valid0 = false;
	const _errs1 = errors;
	if (typeof data === 'string') {
		if (!formats0(data)) {
			const err0 = {
				instancePath,
				schemaPath: '#/definitions/DataSources.URLReference/format',
				keyword: 'format',
				params: { format: 'whatwg-http-url' },
				message: 'must match format "' + 'whatwg-http-url' + '"',
			};
			if (vErrors === null) {
				vErrors = [err0];
			} else {
				vErrors.push(err0);
			}
			errors++;
		}
	} else {
		const err1 = {
			instancePath,
			schemaPath: '#/definitions/DataSources.URLReference/type',
			keyword: 'type',
			params: { type: 'string' },
			message: 'must be string',
		};
		if (vErrors === null) {
			vErrors = [err1];
		} else {
			vErrors.push(err1);
		}
		errors++;
	}
	var _valid0 = _errs1 === errors;
	valid0 = valid0 || _valid0;
	if (!valid0) {
		const _errs4 = errors;
		if (typeof data === 'string') {
			if (!pattern0.test(data)) {
				const err2 = {
					instancePath,
					schemaPath:
						'#/definitions/DataSources.ExecutionContextPath/pattern',
					keyword: 'pattern',
					params: {
						pattern: '^(?!.*(?:^|/)\\.\\.(?:/|$))(?:\\./|/).*$',
					},
					message:
						'must match pattern "' +
						'^(?!.*(?:^|/)\\.\\.(?:/|$))(?:\\./|/).*$' +
						'"',
				};
				if (vErrors === null) {
					vErrors = [err2];
				} else {
					vErrors.push(err2);
				}
				errors++;
			}
		} else {
			const err3 = {
				instancePath,
				schemaPath:
					'#/definitions/DataSources.ExecutionContextPath/type',
				keyword: 'type',
				params: { type: 'string' },
				message: 'must be string',
			};
			if (vErrors === null) {
				vErrors = [err3];
			} else {
				vErrors.push(err3);
			}
			errors++;
		}
		var _valid0 = _errs4 === errors;
		valid0 = valid0 || _valid0;
		if (!valid0) {
			const _errs7 = errors;
			if (typeof data === 'string') {
				if (!pattern21.test(data)) {
					const err4 = {
						instancePath,
						schemaPath:
							'#/definitions/DataSources.TargetSitePath/pattern',
						keyword: 'pattern',
						params: {
							pattern:
								'^site:(?!\\/*$)(?!\\.\\.(?:/|$))(?!.*\\/\\.\\.(?:/|$)).+$',
						},
						message:
							'must match pattern "' +
							'^site:(?!\\/*$)(?!\\.\\.(?:/|$))(?!.*\\/\\.\\.(?:/|$)).+$' +
							'"',
					};
					if (vErrors === null) {
						vErrors = [err4];
					} else {
						vErrors.push(err4);
					}
					errors++;
				}
			} else {
				const err5 = {
					instancePath,
					schemaPath: '#/definitions/DataSources.TargetSitePath/type',
					keyword: 'type',
					params: { type: 'string' },
					message: 'must be string',
				};
				if (vErrors === null) {
					vErrors = [err5];
				} else {
					vErrors.push(err5);
				}
				errors++;
			}
			var _valid0 = _errs7 === errors;
			valid0 = valid0 || _valid0;
			if (!valid0) {
				const _errs10 = errors;
				if (data && typeof data == 'object' && !Array.isArray(data)) {
					if (data.filename === undefined) {
						const err6 = {
							instancePath,
							schemaPath:
								'#/definitions/DataSources.InlineFile/required',
							keyword: 'required',
							params: { missingProperty: 'filename' },
							message:
								"must have required property '" +
								'filename' +
								"'",
						};
						if (vErrors === null) {
							vErrors = [err6];
						} else {
							vErrors.push(err6);
						}
						errors++;
					}
					if (data.content === undefined) {
						const err7 = {
							instancePath,
							schemaPath:
								'#/definitions/DataSources.InlineFile/required',
							keyword: 'required',
							params: { missingProperty: 'content' },
							message:
								"must have required property '" +
								'content' +
								"'",
						};
						if (vErrors === null) {
							vErrors = [err7];
						} else {
							vErrors.push(err7);
						}
						errors++;
					}
					for (const key0 in data) {
						if (!(key0 === 'filename' || key0 === 'content')) {
							const err8 = {
								instancePath,
								schemaPath:
									'#/definitions/DataSources.InlineFile/additionalProperties',
								keyword: 'additionalProperties',
								params: { additionalProperty: key0 },
								message: 'must NOT have additional properties',
							};
							if (vErrors === null) {
								vErrors = [err8];
							} else {
								vErrors.push(err8);
							}
							errors++;
						}
					}
					if (data.filename !== undefined) {
						let data0 = data.filename;
						if (typeof data0 === 'string') {
							if (!pattern3.test(data0)) {
								const err9 = {
									instancePath: instancePath + '/filename',
									schemaPath:
										'#/definitions/DataSources.InlineFile/properties/filename/pattern',
									keyword: 'pattern',
									params: {
										pattern: '^(?!(?:\\.|\\.\\.)$)[^/]+$',
									},
									message:
										'must match pattern "' +
										'^(?!(?:\\.|\\.\\.)$)[^/]+$' +
										'"',
								};
								if (vErrors === null) {
									vErrors = [err9];
								} else {
									vErrors.push(err9);
								}
								errors++;
							}
						} else {
							const err10 = {
								instancePath: instancePath + '/filename',
								schemaPath:
									'#/definitions/DataSources.InlineFile/properties/filename/type',
								keyword: 'type',
								params: { type: 'string' },
								message: 'must be string',
							};
							if (vErrors === null) {
								vErrors = [err10];
							} else {
								vErrors.push(err10);
							}
							errors++;
						}
					}
					if (data.content !== undefined) {
						if (typeof data.content !== 'string') {
							const err11 = {
								instancePath: instancePath + '/content',
								schemaPath:
									'#/definitions/DataSources.InlineFile/properties/content/type',
								keyword: 'type',
								params: { type: 'string' },
								message: 'must be string',
							};
							if (vErrors === null) {
								vErrors = [err11];
							} else {
								vErrors.push(err11);
							}
							errors++;
						}
					}
				} else {
					const err12 = {
						instancePath,
						schemaPath: '#/definitions/DataSources.InlineFile/type',
						keyword: 'type',
						params: { type: 'object' },
						message: 'must be object',
					};
					if (vErrors === null) {
						vErrors = [err12];
					} else {
						vErrors.push(err12);
					}
					errors++;
				}
				var _valid0 = _errs10 === errors;
				valid0 = valid0 || _valid0;
			}
		}
	}
	if (!valid0) {
		const err13 = {
			instancePath,
			schemaPath: '#/anyOf',
			keyword: 'anyOf',
			params: {},
			message: 'must match a schema in anyOf',
		};
		if (vErrors === null) {
			vErrors = [err13];
		} else {
			vErrors.push(err13);
		}
		errors++;
	} else {
		errors = _errs0;
		if (vErrors !== null) {
			if (_errs0) {
				vErrors.length = _errs0;
			} else {
				vErrors = null;
			}
		}
	}
	validate40.errors = vErrors;
	return errors === 0;
}
function validate11(
	data,
	{ instancePath = '', parentData, parentDataProperty, rootData = data } = {}
) {
	let vErrors = null;
	let errors = 0;
	const _errs2 = errors;
	let valid1 = true;
	const _errs3 = errors;
	if (data && typeof data == 'object' && !Array.isArray(data)) {
		let missing0;
		if (data.usersBaseline === undefined && (missing0 = 'usersBaseline')) {
			const err0 = {};
			if (vErrors === null) {
				vErrors = [err0];
			} else {
				vErrors.push(err0);
			}
			errors++;
		} else {
			if (data.usersBaseline !== undefined) {
				if ('empty' !== data.usersBaseline) {
					const err1 = {};
					if (vErrors === null) {
						vErrors = [err1];
					} else {
						vErrors.push(err1);
					}
					errors++;
				}
			}
		}
	}
	var _valid0 = _errs3 === errors;
	errors = _errs2;
	if (vErrors !== null) {
		if (_errs2) {
			vErrors.length = _errs2;
		} else {
			vErrors = null;
		}
	}
	if (_valid0) {
		const _errs5 = errors;
		if (data && typeof data == 'object' && !Array.isArray(data)) {
			if (data.contentBaseline === undefined) {
				const err2 = {
					instancePath,
					schemaPath: '#/allOf/0/then/required',
					keyword: 'required',
					params: { missingProperty: 'contentBaseline' },
					message:
						"must have required property '" +
						'contentBaseline' +
						"'",
				};
				if (vErrors === null) {
					vErrors = [err2];
				} else {
					vErrors.push(err2);
				}
				errors++;
			}
			if (data.users === undefined) {
				const err3 = {
					instancePath,
					schemaPath: '#/allOf/0/then/required',
					keyword: 'required',
					params: { missingProperty: 'users' },
					message: "must have required property '" + 'users' + "'",
				};
				if (vErrors === null) {
					vErrors = [err3];
				} else {
					vErrors.push(err3);
				}
				errors++;
			}
			if (data.contentBaseline !== undefined) {
				if ('empty' !== data.contentBaseline) {
					const err4 = {
						instancePath: instancePath + '/contentBaseline',
						schemaPath:
							'#/allOf/0/then/properties/contentBaseline/const',
						keyword: 'const',
						params: { allowedValue: 'empty' },
						message: 'must be equal to constant',
					};
					if (vErrors === null) {
						vErrors = [err4];
					} else {
						vErrors.push(err4);
					}
					errors++;
				}
			}
			if (data.users !== undefined) {
				let data2 = data.users;
				if (Array.isArray(data2)) {
					const _errs9 = errors;
					const len0 = data2.length;
					for (let i0 = 0; i0 < len0; i0++) {
						let data3 = data2[i0];
						const _errs10 = errors;
						if (
							data3 &&
							typeof data3 == 'object' &&
							!Array.isArray(data3)
						) {
							if (data3.role === undefined) {
								const err5 = {
									instancePath: instancePath + '/users/' + i0,
									schemaPath:
										'#/allOf/0/then/properties/users/contains/required',
									keyword: 'required',
									params: { missingProperty: 'role' },
									message:
										"must have required property '" +
										'role' +
										"'",
								};
								if (vErrors === null) {
									vErrors = [err5];
								} else {
									vErrors.push(err5);
								}
								errors++;
							}
							if (data3.role !== undefined) {
								if ('administrator' !== data3.role) {
									const err6 = {
										instancePath:
											instancePath +
											'/users/' +
											i0 +
											'/role',
										schemaPath:
											'#/allOf/0/then/properties/users/contains/properties/role/const',
										keyword: 'const',
										params: {
											allowedValue: 'administrator',
										},
										message: 'must be equal to constant',
									};
									if (vErrors === null) {
										vErrors = [err6];
									} else {
										vErrors.push(err6);
									}
									errors++;
								}
							}
						} else {
							const err7 = {
								instancePath: instancePath + '/users/' + i0,
								schemaPath:
									'#/allOf/0/then/properties/users/contains/type',
								keyword: 'type',
								params: { type: 'object' },
								message: 'must be object',
							};
							if (vErrors === null) {
								vErrors = [err7];
							} else {
								vErrors.push(err7);
							}
							errors++;
						}
						var valid4 = _errs10 === errors;
						if (valid4) {
							break;
						}
					}
					if (!valid4) {
						const err8 = {
							instancePath: instancePath + '/users',
							schemaPath:
								'#/allOf/0/then/properties/users/contains',
							keyword: 'contains',
							params: { minContains: 1 },
							message: 'must contain at least 1 valid item(s)',
						};
						if (vErrors === null) {
							vErrors = [err8];
						} else {
							vErrors.push(err8);
						}
						errors++;
					} else {
						errors = _errs9;
						if (vErrors !== null) {
							if (_errs9) {
								vErrors.length = _errs9;
							} else {
								vErrors = null;
							}
						}
					}
				} else {
					const err9 = {
						instancePath: instancePath + '/users',
						schemaPath: '#/allOf/0/then/properties/users/type',
						keyword: 'type',
						params: { type: 'array' },
						message: 'must be array',
					};
					if (vErrors === null) {
						vErrors = [err9];
					} else {
						vErrors.push(err9);
					}
					errors++;
				}
			}
		}
		var _valid0 = _errs5 === errors;
		valid1 = _valid0;
	}
	if (!valid1) {
		const err10 = {
			instancePath,
			schemaPath: '#/allOf/0/if',
			keyword: 'if',
			params: { failingKeyword: 'then' },
			message: 'must match "then" schema',
		};
		if (vErrors === null) {
			vErrors = [err10];
		} else {
			vErrors.push(err10);
		}
		errors++;
	}
	const _errs14 = errors;
	let valid6 = true;
	const _errs15 = errors;
	if (data && typeof data == 'object' && !Array.isArray(data)) {
		let missing1;
		if (
			data.wordpressVersion === undefined &&
			(missing1 = 'wordpressVersion')
		) {
			const err11 = {};
			if (vErrors === null) {
				vErrors = [err11];
			} else {
				vErrors.push(err11);
			}
			errors++;
		} else {
			if (data.wordpressVersion !== undefined) {
				if ('none' !== data.wordpressVersion) {
					const err12 = {};
					if (vErrors === null) {
						vErrors = [err12];
					} else {
						vErrors.push(err12);
					}
					errors++;
				}
			}
		}
	}
	var _valid1 = _errs15 === errors;
	errors = _errs14;
	if (vErrors !== null) {
		if (_errs14) {
			vErrors.length = _errs14;
		} else {
			vErrors = null;
		}
	}
	if (_valid1) {
		const _errs17 = errors;
		if (data && typeof data == 'object' && !Array.isArray(data)) {
			if (data.contentBaseline !== undefined) {
				const err13 = {
					instancePath: instancePath + '/contentBaseline',
					schemaPath:
						'#/allOf/1/then/properties/contentBaseline/false schema',
					keyword: 'false schema',
					params: {},
					message: 'boolean schema is false',
				};
				if (vErrors === null) {
					vErrors = [err13];
				} else {
					vErrors.push(err13);
				}
				errors++;
			}
			if (data.usersBaseline !== undefined) {
				const err14 = {
					instancePath: instancePath + '/usersBaseline',
					schemaPath:
						'#/allOf/1/then/properties/usersBaseline/false schema',
					keyword: 'false schema',
					params: {},
					message: 'boolean schema is false',
				};
				if (vErrors === null) {
					vErrors = [err14];
				} else {
					vErrors.push(err14);
				}
				errors++;
			}
		}
		var _valid1 = _errs17 === errors;
		valid6 = _valid1;
	}
	if (!valid6) {
		const err15 = {
			instancePath,
			schemaPath: '#/allOf/1/if',
			keyword: 'if',
			params: { failingKeyword: 'then' },
			message: 'must match "then" schema',
		};
		if (vErrors === null) {
			vErrors = [err15];
		} else {
			vErrors.push(err15);
		}
		errors++;
	}
	if (data && typeof data == 'object' && !Array.isArray(data)) {
		if (data.version === undefined) {
			const err16 = {
				instancePath,
				schemaPath: '#/required',
				keyword: 'required',
				params: { missingProperty: 'version' },
				message: "must have required property '" + 'version' + "'",
			};
			if (vErrors === null) {
				vErrors = [err16];
			} else {
				vErrors.push(err16);
			}
			errors++;
		}
		for (const key0 in data) {
			if (!func2.call(schema12.properties, key0)) {
				const err17 = {
					instancePath,
					schemaPath: '#/additionalProperties',
					keyword: 'additionalProperties',
					params: { additionalProperty: key0 },
					message: 'must NOT have additional properties',
				};
				if (vErrors === null) {
					vErrors = [err17];
				} else {
					vErrors.push(err17);
				}
				errors++;
			}
		}
		if (data.version !== undefined) {
			let data8 = data.version;
			if (!(typeof data8 == 'number' && isFinite(data8))) {
				const err18 = {
					instancePath: instancePath + '/version',
					schemaPath: '#/properties/version/type',
					keyword: 'type',
					params: { type: 'number' },
					message: 'must be number',
				};
				if (vErrors === null) {
					vErrors = [err18];
				} else {
					vErrors.push(err18);
				}
				errors++;
			}
			if (2 !== data8) {
				const err19 = {
					instancePath: instancePath + '/version',
					schemaPath: '#/properties/version/const',
					keyword: 'const',
					params: { allowedValue: 2 },
					message: 'must be equal to constant',
				};
				if (vErrors === null) {
					vErrors = [err19];
				} else {
					vErrors.push(err19);
				}
				errors++;
			}
		}
		if (data.$schema !== undefined) {
			let data9 = data.$schema;
			const _errs22 = errors;
			let valid10 = false;
			const _errs23 = errors;
			if (typeof data9 === 'string') {
				if (!formats0(data9)) {
					const err20 = {
						instancePath: instancePath + '/$schema',
						schemaPath:
							'#/definitions/DataSources.URLReference/format',
						keyword: 'format',
						params: { format: 'whatwg-http-url' },
						message:
							'must match format "' + 'whatwg-http-url' + '"',
					};
					if (vErrors === null) {
						vErrors = [err20];
					} else {
						vErrors.push(err20);
					}
					errors++;
				}
			} else {
				const err21 = {
					instancePath: instancePath + '/$schema',
					schemaPath: '#/definitions/DataSources.URLReference/type',
					keyword: 'type',
					params: { type: 'string' },
					message: 'must be string',
				};
				if (vErrors === null) {
					vErrors = [err21];
				} else {
					vErrors.push(err21);
				}
				errors++;
			}
			var _valid2 = _errs23 === errors;
			valid10 = valid10 || _valid2;
			if (!valid10) {
				const _errs26 = errors;
				if (typeof data9 === 'string') {
					if (!pattern0.test(data9)) {
						const err22 = {
							instancePath: instancePath + '/$schema',
							schemaPath:
								'#/definitions/DataSources.ExecutionContextPath/pattern',
							keyword: 'pattern',
							params: {
								pattern:
									'^(?!.*(?:^|/)\\.\\.(?:/|$))(?:\\./|/).*$',
							},
							message:
								'must match pattern "' +
								'^(?!.*(?:^|/)\\.\\.(?:/|$))(?:\\./|/).*$' +
								'"',
						};
						if (vErrors === null) {
							vErrors = [err22];
						} else {
							vErrors.push(err22);
						}
						errors++;
					}
				} else {
					const err23 = {
						instancePath: instancePath + '/$schema',
						schemaPath:
							'#/definitions/DataSources.ExecutionContextPath/type',
						keyword: 'type',
						params: { type: 'string' },
						message: 'must be string',
					};
					if (vErrors === null) {
						vErrors = [err23];
					} else {
						vErrors.push(err23);
					}
					errors++;
				}
				var _valid2 = _errs26 === errors;
				valid10 = valid10 || _valid2;
			}
			if (!valid10) {
				const err24 = {
					instancePath: instancePath + '/$schema',
					schemaPath: '#/properties/%24schema/anyOf',
					keyword: 'anyOf',
					params: {},
					message: 'must match a schema in anyOf',
				};
				if (vErrors === null) {
					vErrors = [err24];
				} else {
					vErrors.push(err24);
				}
				errors++;
			} else {
				errors = _errs22;
				if (vErrors !== null) {
					if (_errs22) {
						vErrors.length = _errs22;
					} else {
						vErrors = null;
					}
				}
			}
		}
		if (data.blueprintMeta !== undefined) {
			let data10 = data.blueprintMeta;
			if (data10 && typeof data10 == 'object' && !Array.isArray(data10)) {
				for (const key1 in data10) {
					if (
						!func2.call(
							schema12.properties.blueprintMeta.properties,
							key1
						)
					) {
						const err25 = {
							instancePath: instancePath + '/blueprintMeta',
							schemaPath:
								'#/properties/blueprintMeta/additionalProperties',
							keyword: 'additionalProperties',
							params: { additionalProperty: key1 },
							message: 'must NOT have additional properties',
						};
						if (vErrors === null) {
							vErrors = [err25];
						} else {
							vErrors.push(err25);
						}
						errors++;
					}
				}
				if (data10.name !== undefined) {
					if (typeof data10.name !== 'string') {
						const err26 = {
							instancePath: instancePath + '/blueprintMeta/name',
							schemaPath:
								'#/properties/blueprintMeta/properties/name/type',
							keyword: 'type',
							params: { type: 'string' },
							message: 'must be string',
						};
						if (vErrors === null) {
							vErrors = [err26];
						} else {
							vErrors.push(err26);
						}
						errors++;
					}
				}
				if (data10.description !== undefined) {
					if (typeof data10.description !== 'string') {
						const err27 = {
							instancePath:
								instancePath + '/blueprintMeta/description',
							schemaPath:
								'#/properties/blueprintMeta/properties/description/type',
							keyword: 'type',
							params: { type: 'string' },
							message: 'must be string',
						};
						if (vErrors === null) {
							vErrors = [err27];
						} else {
							vErrors.push(err27);
						}
						errors++;
					}
				}
				if (data10.moreInfo !== undefined) {
					if (typeof data10.moreInfo !== 'string') {
						const err28 = {
							instancePath:
								instancePath + '/blueprintMeta/moreInfo',
							schemaPath:
								'#/properties/blueprintMeta/properties/moreInfo/type',
							keyword: 'type',
							params: { type: 'string' },
							message: 'must be string',
						};
						if (vErrors === null) {
							vErrors = [err28];
						} else {
							vErrors.push(err28);
						}
						errors++;
					}
				}
				if (data10.version !== undefined) {
					if (typeof data10.version !== 'string') {
						const err29 = {
							instancePath:
								instancePath + '/blueprintMeta/version',
							schemaPath:
								'#/properties/blueprintMeta/properties/version/type',
							keyword: 'type',
							params: { type: 'string' },
							message: 'must be string',
						};
						if (vErrors === null) {
							vErrors = [err29];
						} else {
							vErrors.push(err29);
						}
						errors++;
					}
				}
				if (data10.authors !== undefined) {
					let data15 = data10.authors;
					if (Array.isArray(data15)) {
						const len1 = data15.length;
						for (let i1 = 0; i1 < len1; i1++) {
							if (typeof data15[i1] !== 'string') {
								const err30 = {
									instancePath:
										instancePath +
										'/blueprintMeta/authors/' +
										i1,
									schemaPath:
										'#/properties/blueprintMeta/properties/authors/items/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err30];
								} else {
									vErrors.push(err30);
								}
								errors++;
							}
						}
					} else {
						const err31 = {
							instancePath:
								instancePath + '/blueprintMeta/authors',
							schemaPath:
								'#/properties/blueprintMeta/properties/authors/type',
							keyword: 'type',
							params: { type: 'array' },
							message: 'must be array',
						};
						if (vErrors === null) {
							vErrors = [err31];
						} else {
							vErrors.push(err31);
						}
						errors++;
					}
				}
				if (data10.homepage !== undefined) {
					let data17 = data10.homepage;
					if (typeof data17 === 'string') {
						if (!formats0(data17)) {
							const err32 = {
								instancePath:
									instancePath + '/blueprintMeta/homepage',
								schemaPath:
									'#/definitions/DataSources.URLReference/format',
								keyword: 'format',
								params: { format: 'whatwg-http-url' },
								message:
									'must match format "' +
									'whatwg-http-url' +
									'"',
							};
							if (vErrors === null) {
								vErrors = [err32];
							} else {
								vErrors.push(err32);
							}
							errors++;
						}
					} else {
						const err33 = {
							instancePath:
								instancePath + '/blueprintMeta/homepage',
							schemaPath:
								'#/definitions/DataSources.URLReference/type',
							keyword: 'type',
							params: { type: 'string' },
							message: 'must be string',
						};
						if (vErrors === null) {
							vErrors = [err33];
						} else {
							vErrors.push(err33);
						}
						errors++;
					}
				}
				if (data10.donateLink !== undefined) {
					let data18 = data10.donateLink;
					if (typeof data18 === 'string') {
						if (!formats0(data18)) {
							const err34 = {
								instancePath:
									instancePath + '/blueprintMeta/donateLink',
								schemaPath:
									'#/definitions/DataSources.URLReference/format',
								keyword: 'format',
								params: { format: 'whatwg-http-url' },
								message:
									'must match format "' +
									'whatwg-http-url' +
									'"',
							};
							if (vErrors === null) {
								vErrors = [err34];
							} else {
								vErrors.push(err34);
							}
							errors++;
						}
					} else {
						const err35 = {
							instancePath:
								instancePath + '/blueprintMeta/donateLink',
							schemaPath:
								'#/definitions/DataSources.URLReference/type',
							keyword: 'type',
							params: { type: 'string' },
							message: 'must be string',
						};
						if (vErrors === null) {
							vErrors = [err35];
						} else {
							vErrors.push(err35);
						}
						errors++;
					}
				}
				if (data10.tags !== undefined) {
					let data19 = data10.tags;
					if (Array.isArray(data19)) {
						const len2 = data19.length;
						for (let i2 = 0; i2 < len2; i2++) {
							if (typeof data19[i2] !== 'string') {
								const err36 = {
									instancePath:
										instancePath +
										'/blueprintMeta/tags/' +
										i2,
									schemaPath:
										'#/properties/blueprintMeta/properties/tags/items/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err36];
								} else {
									vErrors.push(err36);
								}
								errors++;
							}
						}
					} else {
						const err37 = {
							instancePath: instancePath + '/blueprintMeta/tags',
							schemaPath:
								'#/properties/blueprintMeta/properties/tags/type',
							keyword: 'type',
							params: { type: 'array' },
							message: 'must be array',
						};
						if (vErrors === null) {
							vErrors = [err37];
						} else {
							vErrors.push(err37);
						}
						errors++;
					}
				}
				if (data10.license !== undefined) {
					if (typeof data10.license !== 'string') {
						const err38 = {
							instancePath:
								instancePath + '/blueprintMeta/license',
							schemaPath:
								'#/properties/blueprintMeta/properties/license/type',
							keyword: 'type',
							params: { type: 'string' },
							message: 'must be string',
						};
						if (vErrors === null) {
							vErrors = [err38];
						} else {
							vErrors.push(err38);
						}
						errors++;
					}
				}
			} else {
				const err39 = {
					instancePath: instancePath + '/blueprintMeta',
					schemaPath: '#/properties/blueprintMeta/type',
					keyword: 'type',
					params: { type: 'object' },
					message: 'must be object',
				};
				if (vErrors === null) {
					vErrors = [err39];
				} else {
					vErrors.push(err39);
				}
				errors++;
			}
		}
		if (data.applicationOptions !== undefined) {
			let data22 = data.applicationOptions;
			if (data22 && typeof data22 == 'object' && !Array.isArray(data22)) {
				if (data22['wordpress-playground'] === undefined) {
					const err40 = {
						instancePath: instancePath + '/applicationOptions',
						schemaPath: '#/properties/applicationOptions/required',
						keyword: 'required',
						params: { missingProperty: 'wordpress-playground' },
						message:
							"must have required property '" +
							'wordpress-playground' +
							"'",
					};
					if (vErrors === null) {
						vErrors = [err40];
					} else {
						vErrors.push(err40);
					}
					errors++;
				}
				for (const key2 in data22) {
					if (!(key2 === 'wordpress-playground')) {
						const err41 = {
							instancePath: instancePath + '/applicationOptions',
							schemaPath:
								'#/properties/applicationOptions/additionalProperties',
							keyword: 'additionalProperties',
							params: { additionalProperty: key2 },
							message: 'must NOT have additional properties',
						};
						if (vErrors === null) {
							vErrors = [err41];
						} else {
							vErrors.push(err41);
						}
						errors++;
					}
				}
				if (data22['wordpress-playground'] !== undefined) {
					let data23 = data22['wordpress-playground'];
					if (
						data23 &&
						typeof data23 == 'object' &&
						!Array.isArray(data23)
					) {
						for (const key3 in data23) {
							if (
								!(
									key3 === 'landingPage' ||
									key3 === 'login' ||
									key3 === 'networkAccess' ||
									key3 === 'loadPhpExtensions'
								)
							) {
								const err42 = {
									instancePath:
										instancePath +
										'/applicationOptions/wordpress-playground',
									schemaPath:
										'#/properties/applicationOptions/properties/wordpress-playground/additionalProperties',
									keyword: 'additionalProperties',
									params: { additionalProperty: key3 },
									message:
										'must NOT have additional properties',
								};
								if (vErrors === null) {
									vErrors = [err42];
								} else {
									vErrors.push(err42);
								}
								errors++;
							}
						}
						if (data23.landingPage !== undefined) {
							if (typeof data23.landingPage !== 'string') {
								const err43 = {
									instancePath:
										instancePath +
										'/applicationOptions/wordpress-playground/landingPage',
									schemaPath:
										'#/properties/applicationOptions/properties/wordpress-playground/properties/landingPage/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err43];
								} else {
									vErrors.push(err43);
								}
								errors++;
							}
						}
						if (data23.login !== undefined) {
							let data25 = data23.login;
							const _errs65 = errors;
							let valid22 = false;
							const _errs66 = errors;
							if (typeof data25 !== 'boolean') {
								const err44 = {
									instancePath:
										instancePath +
										'/applicationOptions/wordpress-playground/login',
									schemaPath:
										'#/properties/applicationOptions/properties/wordpress-playground/properties/login/anyOf/0/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err44];
								} else {
									vErrors.push(err44);
								}
								errors++;
							}
							var _valid3 = _errs66 === errors;
							valid22 = valid22 || _valid3;
							if (!valid22) {
								const _errs68 = errors;
								if (
									data25 &&
									typeof data25 == 'object' &&
									!Array.isArray(data25)
								) {
									if (data25.username === undefined) {
										const err45 = {
											instancePath:
												instancePath +
												'/applicationOptions/wordpress-playground/login',
											schemaPath:
												'#/properties/applicationOptions/properties/wordpress-playground/properties/login/anyOf/1/required',
											keyword: 'required',
											params: {
												missingProperty: 'username',
											},
											message:
												"must have required property '" +
												'username' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err45];
										} else {
											vErrors.push(err45);
										}
										errors++;
									}
									if (data25.password === undefined) {
										const err46 = {
											instancePath:
												instancePath +
												'/applicationOptions/wordpress-playground/login',
											schemaPath:
												'#/properties/applicationOptions/properties/wordpress-playground/properties/login/anyOf/1/required',
											keyword: 'required',
											params: {
												missingProperty: 'password',
											},
											message:
												"must have required property '" +
												'password' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err46];
										} else {
											vErrors.push(err46);
										}
										errors++;
									}
									for (const key4 in data25) {
										if (
											!(
												key4 === 'username' ||
												key4 === 'password'
											)
										) {
											const err47 = {
												instancePath:
													instancePath +
													'/applicationOptions/wordpress-playground/login',
												schemaPath:
													'#/properties/applicationOptions/properties/wordpress-playground/properties/login/anyOf/1/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key4,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err47];
											} else {
												vErrors.push(err47);
											}
											errors++;
										}
									}
									if (data25.username !== undefined) {
										if (
											typeof data25.username !== 'string'
										) {
											const err48 = {
												instancePath:
													instancePath +
													'/applicationOptions/wordpress-playground/login/username',
												schemaPath:
													'#/properties/applicationOptions/properties/wordpress-playground/properties/login/anyOf/1/properties/username/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err48];
											} else {
												vErrors.push(err48);
											}
											errors++;
										}
									}
									if (data25.password !== undefined) {
										if (
											typeof data25.password !== 'string'
										) {
											const err49 = {
												instancePath:
													instancePath +
													'/applicationOptions/wordpress-playground/login/password',
												schemaPath:
													'#/properties/applicationOptions/properties/wordpress-playground/properties/login/anyOf/1/properties/password/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err49];
											} else {
												vErrors.push(err49);
											}
											errors++;
										}
									}
								} else {
									const err50 = {
										instancePath:
											instancePath +
											'/applicationOptions/wordpress-playground/login',
										schemaPath:
											'#/properties/applicationOptions/properties/wordpress-playground/properties/login/anyOf/1/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err50];
									} else {
										vErrors.push(err50);
									}
									errors++;
								}
								var _valid3 = _errs68 === errors;
								valid22 = valid22 || _valid3;
							}
							if (!valid22) {
								const err51 = {
									instancePath:
										instancePath +
										'/applicationOptions/wordpress-playground/login',
									schemaPath:
										'#/properties/applicationOptions/properties/wordpress-playground/properties/login/anyOf',
									keyword: 'anyOf',
									params: {},
									message: 'must match a schema in anyOf',
								};
								if (vErrors === null) {
									vErrors = [err51];
								} else {
									vErrors.push(err51);
								}
								errors++;
							} else {
								errors = _errs65;
								if (vErrors !== null) {
									if (_errs65) {
										vErrors.length = _errs65;
									} else {
										vErrors = null;
									}
								}
							}
						}
						if (data23.networkAccess !== undefined) {
							if (typeof data23.networkAccess !== 'boolean') {
								const err52 = {
									instancePath:
										instancePath +
										'/applicationOptions/wordpress-playground/networkAccess',
									schemaPath:
										'#/properties/applicationOptions/properties/wordpress-playground/properties/networkAccess/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err52];
								} else {
									vErrors.push(err52);
								}
								errors++;
							}
						}
						if (data23.loadPhpExtensions !== undefined) {
							let data29 = data23.loadPhpExtensions;
							if (Array.isArray(data29)) {
								const len3 = data29.length;
								for (let i3 = 0; i3 < len3; i3++) {
									let data30 = data29[i3];
									if (typeof data30 !== 'string') {
										const err53 = {
											instancePath:
												instancePath +
												'/applicationOptions/wordpress-playground/loadPhpExtensions/' +
												i3,
											schemaPath:
												'#/properties/applicationOptions/properties/wordpress-playground/properties/loadPhpExtensions/items/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err53];
										} else {
											vErrors.push(err53);
										}
										errors++;
									}
									if ('intl' !== data30) {
										const err54 = {
											instancePath:
												instancePath +
												'/applicationOptions/wordpress-playground/loadPhpExtensions/' +
												i3,
											schemaPath:
												'#/properties/applicationOptions/properties/wordpress-playground/properties/loadPhpExtensions/items/const',
											keyword: 'const',
											params: { allowedValue: 'intl' },
											message:
												'must be equal to constant',
										};
										if (vErrors === null) {
											vErrors = [err54];
										} else {
											vErrors.push(err54);
										}
										errors++;
									}
								}
							} else {
								const err55 = {
									instancePath:
										instancePath +
										'/applicationOptions/wordpress-playground/loadPhpExtensions',
									schemaPath:
										'#/properties/applicationOptions/properties/wordpress-playground/properties/loadPhpExtensions/type',
									keyword: 'type',
									params: { type: 'array' },
									message: 'must be array',
								};
								if (vErrors === null) {
									vErrors = [err55];
								} else {
									vErrors.push(err55);
								}
								errors++;
							}
						}
					} else {
						const err56 = {
							instancePath:
								instancePath +
								'/applicationOptions/wordpress-playground',
							schemaPath:
								'#/properties/applicationOptions/properties/wordpress-playground/type',
							keyword: 'type',
							params: { type: 'object' },
							message: 'must be object',
						};
						if (vErrors === null) {
							vErrors = [err56];
						} else {
							vErrors.push(err56);
						}
						errors++;
					}
				}
			} else {
				const err57 = {
					instancePath: instancePath + '/applicationOptions',
					schemaPath: '#/properties/applicationOptions/type',
					keyword: 'type',
					params: { type: 'object' },
					message: 'must be object',
				};
				if (vErrors === null) {
					vErrors = [err57];
				} else {
					vErrors.push(err57);
				}
				errors++;
			}
		}
		if (data.contentBaseline !== undefined) {
			let data31 = data.contentBaseline;
			const _errs82 = errors;
			let valid26 = false;
			const _errs83 = errors;
			if (typeof data31 !== 'string') {
				const err58 = {
					instancePath: instancePath + '/contentBaseline',
					schemaPath: '#/properties/contentBaseline/anyOf/0/type',
					keyword: 'type',
					params: { type: 'string' },
					message: 'must be string',
				};
				if (vErrors === null) {
					vErrors = [err58];
				} else {
					vErrors.push(err58);
				}
				errors++;
			}
			if ('keep-all' !== data31) {
				const err59 = {
					instancePath: instancePath + '/contentBaseline',
					schemaPath: '#/properties/contentBaseline/anyOf/0/const',
					keyword: 'const',
					params: { allowedValue: 'keep-all' },
					message: 'must be equal to constant',
				};
				if (vErrors === null) {
					vErrors = [err59];
				} else {
					vErrors.push(err59);
				}
				errors++;
			}
			var _valid4 = _errs83 === errors;
			valid26 = valid26 || _valid4;
			if (!valid26) {
				const _errs85 = errors;
				if (typeof data31 !== 'string') {
					const err60 = {
						instancePath: instancePath + '/contentBaseline',
						schemaPath: '#/properties/contentBaseline/anyOf/1/type',
						keyword: 'type',
						params: { type: 'string' },
						message: 'must be string',
					};
					if (vErrors === null) {
						vErrors = [err60];
					} else {
						vErrors.push(err60);
					}
					errors++;
				}
				if ('empty' !== data31) {
					const err61 = {
						instancePath: instancePath + '/contentBaseline',
						schemaPath:
							'#/properties/contentBaseline/anyOf/1/const',
						keyword: 'const',
						params: { allowedValue: 'empty' },
						message: 'must be equal to constant',
					};
					if (vErrors === null) {
						vErrors = [err61];
					} else {
						vErrors.push(err61);
					}
					errors++;
				}
				var _valid4 = _errs85 === errors;
				valid26 = valid26 || _valid4;
				if (!valid26) {
					const _errs87 = errors;
					if (typeof data31 !== 'string') {
						const err62 = {
							instancePath: instancePath + '/contentBaseline',
							schemaPath:
								'#/properties/contentBaseline/anyOf/2/type',
							keyword: 'type',
							params: { type: 'string' },
							message: 'must be string',
						};
						if (vErrors === null) {
							vErrors = [err62];
						} else {
							vErrors.push(err62);
						}
						errors++;
					}
					if (!(data31 === 'posts' || data31 === 'pages')) {
						const err63 = {
							instancePath: instancePath + '/contentBaseline',
							schemaPath:
								'#/properties/contentBaseline/anyOf/2/enum',
							keyword: 'enum',
							params: {
								allowedValues:
									schema12.properties.contentBaseline.anyOf[2]
										.enum,
							},
							message:
								'must be equal to one of the allowed values',
						};
						if (vErrors === null) {
							vErrors = [err63];
						} else {
							vErrors.push(err63);
						}
						errors++;
					}
					var _valid4 = _errs87 === errors;
					valid26 = valid26 || _valid4;
					if (!valid26) {
						const _errs89 = errors;
						const _errs92 = errors;
						let valid28 = true;
						const _errs93 = errors;
						if (Array.isArray(data31)) {
							const _errs94 = errors;
							const len4 = data31.length;
							for (let i4 = 0; i4 < len4; i4++) {
								const _errs95 = errors;
								if ('comments' !== data31[i4]) {
									const err64 = {};
									if (vErrors === null) {
										vErrors = [err64];
									} else {
										vErrors.push(err64);
									}
									errors++;
								}
								var valid29 = _errs95 === errors;
								if (valid29) {
									break;
								}
							}
							if (!valid29) {
								const err65 = {};
								if (vErrors === null) {
									vErrors = [err65];
								} else {
									vErrors.push(err65);
								}
								errors++;
							} else {
								errors = _errs94;
								if (vErrors !== null) {
									if (_errs94) {
										vErrors.length = _errs94;
									} else {
										vErrors = null;
									}
								}
							}
						}
						var _valid5 = _errs93 === errors;
						errors = _errs92;
						if (vErrors !== null) {
							if (_errs92) {
								vErrors.length = _errs92;
							} else {
								vErrors = null;
							}
						}
						if (_valid5) {
							const _errs96 = errors;
							if (Array.isArray(data31)) {
								const _errs98 = errors;
								const len5 = data31.length;
								for (let i5 = 0; i5 < len5; i5++) {
									const _errs99 = errors;
									if ('posts' !== data31[i5]) {
										const err66 = {
											instancePath:
												instancePath +
												'/contentBaseline/' +
												i5,
											schemaPath:
												'#/properties/contentBaseline/anyOf/3/allOf/0/then/allOf/0/contains/const',
											keyword: 'const',
											params: { allowedValue: 'posts' },
											message:
												'must be equal to constant',
										};
										if (vErrors === null) {
											vErrors = [err66];
										} else {
											vErrors.push(err66);
										}
										errors++;
									}
									var valid31 = _errs99 === errors;
									if (valid31) {
										break;
									}
								}
								if (!valid31) {
									const err67 = {
										instancePath:
											instancePath + '/contentBaseline',
										schemaPath:
											'#/properties/contentBaseline/anyOf/3/allOf/0/then/allOf/0/contains',
										keyword: 'contains',
										params: { minContains: 1 },
										message:
											'must contain at least 1 valid item(s)',
									};
									if (vErrors === null) {
										vErrors = [err67];
									} else {
										vErrors.push(err67);
									}
									errors++;
								} else {
									errors = _errs98;
									if (vErrors !== null) {
										if (_errs98) {
											vErrors.length = _errs98;
										} else {
											vErrors = null;
										}
									}
								}
							}
							if (Array.isArray(data31)) {
								const _errs101 = errors;
								const len6 = data31.length;
								for (let i6 = 0; i6 < len6; i6++) {
									const _errs102 = errors;
									if ('pages' !== data31[i6]) {
										const err68 = {
											instancePath:
												instancePath +
												'/contentBaseline/' +
												i6,
											schemaPath:
												'#/properties/contentBaseline/anyOf/3/allOf/0/then/allOf/1/contains/const',
											keyword: 'const',
											params: { allowedValue: 'pages' },
											message:
												'must be equal to constant',
										};
										if (vErrors === null) {
											vErrors = [err68];
										} else {
											vErrors.push(err68);
										}
										errors++;
									}
									var valid32 = _errs102 === errors;
									if (valid32) {
										break;
									}
								}
								if (!valid32) {
									const err69 = {
										instancePath:
											instancePath + '/contentBaseline',
										schemaPath:
											'#/properties/contentBaseline/anyOf/3/allOf/0/then/allOf/1/contains',
										keyword: 'contains',
										params: { minContains: 1 },
										message:
											'must contain at least 1 valid item(s)',
									};
									if (vErrors === null) {
										vErrors = [err69];
									} else {
										vErrors.push(err69);
									}
									errors++;
								} else {
									errors = _errs101;
									if (vErrors !== null) {
										if (_errs101) {
											vErrors.length = _errs101;
										} else {
											vErrors = null;
										}
									}
								}
							}
							var _valid5 = _errs96 === errors;
							valid28 = _valid5;
						}
						if (!valid28) {
							const err70 = {
								instancePath: instancePath + '/contentBaseline',
								schemaPath:
									'#/properties/contentBaseline/anyOf/3/allOf/0/if',
								keyword: 'if',
								params: { failingKeyword: 'then' },
								message: 'must match "then" schema',
							};
							if (vErrors === null) {
								vErrors = [err70];
							} else {
								vErrors.push(err70);
							}
							errors++;
						}
						if (Array.isArray(data31)) {
							if (data31.length < 1) {
								const err71 = {
									instancePath:
										instancePath + '/contentBaseline',
									schemaPath:
										'#/properties/contentBaseline/anyOf/3/minItems',
									keyword: 'minItems',
									params: { limit: 1 },
									message: 'must NOT have fewer than 1 items',
								};
								if (vErrors === null) {
									vErrors = [err71];
								} else {
									vErrors.push(err71);
								}
								errors++;
							}
							const len7 = data31.length;
							for (let i7 = 0; i7 < len7; i7++) {
								let data35 = data31[i7];
								if (typeof data35 !== 'string') {
									const err72 = {
										instancePath:
											instancePath +
											'/contentBaseline/' +
											i7,
										schemaPath:
											'#/properties/contentBaseline/anyOf/3/items/type',
										keyword: 'type',
										params: { type: 'string' },
										message: 'must be string',
									};
									if (vErrors === null) {
										vErrors = [err72];
									} else {
										vErrors.push(err72);
									}
									errors++;
								}
								if (
									!(
										data35 === 'posts' ||
										data35 === 'pages' ||
										data35 === 'comments'
									)
								) {
									const err73 = {
										instancePath:
											instancePath +
											'/contentBaseline/' +
											i7,
										schemaPath:
											'#/properties/contentBaseline/anyOf/3/items/enum',
										keyword: 'enum',
										params: {
											allowedValues:
												schema12.properties
													.contentBaseline.anyOf[3]
													.items.enum,
										},
										message:
											'must be equal to one of the allowed values',
									};
									if (vErrors === null) {
										vErrors = [err73];
									} else {
										vErrors.push(err73);
									}
									errors++;
								}
							}
							let i8 = data31.length;
							let j0;
							if (i8 > 1) {
								const indices0 = {};
								for (; i8--; ) {
									let item0 = data31[i8];
									if (typeof item0 !== 'string') {
										continue;
									}
									if (typeof indices0[item0] == 'number') {
										j0 = indices0[item0];
										const err74 = {
											instancePath:
												instancePath +
												'/contentBaseline',
											schemaPath:
												'#/properties/contentBaseline/anyOf/3/uniqueItems',
											keyword: 'uniqueItems',
											params: { i: i8, j: j0 },
											message:
												'must NOT have duplicate items (items ## ' +
												j0 +
												' and ' +
												i8 +
												' are identical)',
										};
										if (vErrors === null) {
											vErrors = [err74];
										} else {
											vErrors.push(err74);
										}
										errors++;
										break;
									}
									indices0[item0] = i8;
								}
							}
						} else {
							const err75 = {
								instancePath: instancePath + '/contentBaseline',
								schemaPath:
									'#/properties/contentBaseline/anyOf/3/type',
								keyword: 'type',
								params: { type: 'array' },
								message: 'must be array',
							};
							if (vErrors === null) {
								vErrors = [err75];
							} else {
								vErrors.push(err75);
							}
							errors++;
						}
						var _valid4 = _errs89 === errors;
						valid26 = valid26 || _valid4;
					}
				}
			}
			if (!valid26) {
				const err76 = {
					instancePath: instancePath + '/contentBaseline',
					schemaPath: '#/properties/contentBaseline/anyOf',
					keyword: 'anyOf',
					params: {},
					message: 'must match a schema in anyOf',
				};
				if (vErrors === null) {
					vErrors = [err76];
				} else {
					vErrors.push(err76);
				}
				errors++;
			} else {
				errors = _errs82;
				if (vErrors !== null) {
					if (_errs82) {
						vErrors.length = _errs82;
					} else {
						vErrors = null;
					}
				}
			}
		}
		if (data.usersBaseline !== undefined) {
			let data36 = data.usersBaseline;
			if (typeof data36 !== 'string') {
				const err77 = {
					instancePath: instancePath + '/usersBaseline',
					schemaPath: '#/properties/usersBaseline/type',
					keyword: 'type',
					params: { type: 'string' },
					message: 'must be string',
				};
				if (vErrors === null) {
					vErrors = [err77];
				} else {
					vErrors.push(err77);
				}
				errors++;
			}
			if (!(data36 === 'keep-all' || data36 === 'empty')) {
				const err78 = {
					instancePath: instancePath + '/usersBaseline',
					schemaPath: '#/properties/usersBaseline/enum',
					keyword: 'enum',
					params: {
						allowedValues: schema12.properties.usersBaseline.enum,
					},
					message: 'must be equal to one of the allowed values',
				};
				if (vErrors === null) {
					vErrors = [err78];
				} else {
					vErrors.push(err78);
				}
				errors++;
			}
		}
		if (data.siteLanguage !== undefined) {
			if (typeof data.siteLanguage !== 'string') {
				const err79 = {
					instancePath: instancePath + '/siteLanguage',
					schemaPath: '#/properties/siteLanguage/type',
					keyword: 'type',
					params: { type: 'string' },
					message: 'must be string',
				};
				if (vErrors === null) {
					vErrors = [err79];
				} else {
					vErrors.push(err79);
				}
				errors++;
			}
		}
		if (data.siteOptions !== undefined) {
			let data38 = data.siteOptions;
			if (data38 && typeof data38 == 'object' && !Array.isArray(data38)) {
				for (const key5 in data38) {
					if (
						!(
							key5 === 'blogname' ||
							key5 === 'timezone_string' ||
							key5 === 'permalink_structure' ||
							key5 === 'siteUrl'
						)
					) {
						let data39 = data38[key5];
						if (
							(!data39 || typeof data39 != 'object') &&
							typeof data39 !== 'string' &&
							typeof data39 !== 'boolean' &&
							!(typeof data39 == 'number' && isFinite(data39))
						) {
							const err80 = {
								instancePath:
									instancePath +
									'/siteOptions/' +
									key5
										.replace(/~/g, '~0')
										.replace(/\//g, '~1'),
								schemaPath:
									'#/properties/siteOptions/additionalProperties/type',
								keyword: 'type',
								params: {
									type: schema12.properties.siteOptions
										.additionalProperties.type,
								},
								message:
									'must be string,boolean,number,array,object',
							};
							if (vErrors === null) {
								vErrors = [err80];
							} else {
								vErrors.push(err80);
							}
							errors++;
						}
					}
				}
				if (data38.blogname !== undefined) {
					if (typeof data38.blogname !== 'string') {
						const err81 = {
							instancePath:
								instancePath + '/siteOptions/blogname',
							schemaPath:
								'#/properties/siteOptions/properties/blogname/type',
							keyword: 'type',
							params: { type: 'string' },
							message: 'must be string',
						};
						if (vErrors === null) {
							vErrors = [err81];
						} else {
							vErrors.push(err81);
						}
						errors++;
					}
				}
				if (data38.timezone_string !== undefined) {
					if (typeof data38.timezone_string !== 'string') {
						const err82 = {
							instancePath:
								instancePath + '/siteOptions/timezone_string',
							schemaPath:
								'#/properties/siteOptions/properties/timezone_string/type',
							keyword: 'type',
							params: { type: 'string' },
							message: 'must be string',
						};
						if (vErrors === null) {
							vErrors = [err82];
						} else {
							vErrors.push(err82);
						}
						errors++;
					}
				}
				if (data38.permalink_structure !== undefined) {
					let data42 = data38.permalink_structure;
					const _errs119 = errors;
					let valid38 = false;
					const _errs120 = errors;
					if (typeof data42 !== 'string') {
						const err83 = {
							instancePath:
								instancePath +
								'/siteOptions/permalink_structure',
							schemaPath:
								'#/properties/siteOptions/properties/permalink_structure/anyOf/0/type',
							keyword: 'type',
							params: { type: 'string' },
							message: 'must be string',
						};
						if (vErrors === null) {
							vErrors = [err83];
						} else {
							vErrors.push(err83);
						}
						errors++;
					}
					var _valid6 = _errs120 === errors;
					valid38 = valid38 || _valid6;
					if (!valid38) {
						const _errs122 = errors;
						if (typeof data42 !== 'boolean') {
							const err84 = {
								instancePath:
									instancePath +
									'/siteOptions/permalink_structure',
								schemaPath:
									'#/properties/siteOptions/properties/permalink_structure/anyOf/1/type',
								keyword: 'type',
								params: { type: 'boolean' },
								message: 'must be boolean',
							};
							if (vErrors === null) {
								vErrors = [err84];
							} else {
								vErrors.push(err84);
							}
							errors++;
						}
						if (false !== data42) {
							const err85 = {
								instancePath:
									instancePath +
									'/siteOptions/permalink_structure',
								schemaPath:
									'#/properties/siteOptions/properties/permalink_structure/anyOf/1/const',
								keyword: 'const',
								params: { allowedValue: false },
								message: 'must be equal to constant',
							};
							if (vErrors === null) {
								vErrors = [err85];
							} else {
								vErrors.push(err85);
							}
							errors++;
						}
						var _valid6 = _errs122 === errors;
						valid38 = valid38 || _valid6;
					}
					if (!valid38) {
						const err86 = {
							instancePath:
								instancePath +
								'/siteOptions/permalink_structure',
							schemaPath:
								'#/properties/siteOptions/properties/permalink_structure/anyOf',
							keyword: 'anyOf',
							params: {},
							message: 'must match a schema in anyOf',
						};
						if (vErrors === null) {
							vErrors = [err86];
						} else {
							vErrors.push(err86);
						}
						errors++;
					} else {
						errors = _errs119;
						if (vErrors !== null) {
							if (_errs119) {
								vErrors.length = _errs119;
							} else {
								vErrors = null;
							}
						}
					}
				}
				if (data38.siteUrl !== undefined) {
					const err87 = {
						instancePath: instancePath + '/siteOptions/siteUrl',
						schemaPath:
							'#/properties/siteOptions/properties/siteUrl/false schema',
						keyword: 'false schema',
						params: {},
						message: 'boolean schema is false',
					};
					if (vErrors === null) {
						vErrors = [err87];
					} else {
						vErrors.push(err87);
					}
					errors++;
				}
			} else {
				const err88 = {
					instancePath: instancePath + '/siteOptions',
					schemaPath: '#/properties/siteOptions/type',
					keyword: 'type',
					params: { type: 'object' },
					message: 'must be object',
				};
				if (vErrors === null) {
					vErrors = [err88];
				} else {
					vErrors.push(err88);
				}
				errors++;
			}
		}
		if (data.constants !== undefined) {
			let data44 = data.constants;
			if (data44 && typeof data44 == 'object' && !Array.isArray(data44)) {
				for (const key6 in data44) {
					if (
						!(
							key6 === 'WP_DEBUG' ||
							key6 === 'WP_DEBUG_LOG' ||
							key6 === 'WP_DEBUG_DISPLAY' ||
							key6 === 'SCRIPT_DEBUG'
						)
					) {
						let data45 = data44[key6];
						if (
							typeof data45 !== 'boolean' &&
							typeof data45 !== 'string' &&
							!(typeof data45 == 'number' && isFinite(data45))
						) {
							const err89 = {
								instancePath:
									instancePath +
									'/constants/' +
									key6
										.replace(/~/g, '~0')
										.replace(/\//g, '~1'),
								schemaPath:
									'#/properties/constants/additionalProperties/type',
								keyword: 'type',
								params: {
									type: schema12.properties.constants
										.additionalProperties.type,
								},
								message: 'must be boolean,string,number',
							};
							if (vErrors === null) {
								vErrors = [err89];
							} else {
								vErrors.push(err89);
							}
							errors++;
						}
					}
				}
				if (data44.WP_DEBUG !== undefined) {
					if (typeof data44.WP_DEBUG !== 'boolean') {
						const err90 = {
							instancePath: instancePath + '/constants/WP_DEBUG',
							schemaPath:
								'#/properties/constants/properties/WP_DEBUG/type',
							keyword: 'type',
							params: { type: 'boolean' },
							message: 'must be boolean',
						};
						if (vErrors === null) {
							vErrors = [err90];
						} else {
							vErrors.push(err90);
						}
						errors++;
					}
				}
				if (data44.WP_DEBUG_LOG !== undefined) {
					if (typeof data44.WP_DEBUG_LOG !== 'boolean') {
						const err91 = {
							instancePath:
								instancePath + '/constants/WP_DEBUG_LOG',
							schemaPath:
								'#/properties/constants/properties/WP_DEBUG_LOG/type',
							keyword: 'type',
							params: { type: 'boolean' },
							message: 'must be boolean',
						};
						if (vErrors === null) {
							vErrors = [err91];
						} else {
							vErrors.push(err91);
						}
						errors++;
					}
				}
				if (data44.WP_DEBUG_DISPLAY !== undefined) {
					if (typeof data44.WP_DEBUG_DISPLAY !== 'boolean') {
						const err92 = {
							instancePath:
								instancePath + '/constants/WP_DEBUG_DISPLAY',
							schemaPath:
								'#/properties/constants/properties/WP_DEBUG_DISPLAY/type',
							keyword: 'type',
							params: { type: 'boolean' },
							message: 'must be boolean',
						};
						if (vErrors === null) {
							vErrors = [err92];
						} else {
							vErrors.push(err92);
						}
						errors++;
					}
				}
				if (data44.SCRIPT_DEBUG !== undefined) {
					if (typeof data44.SCRIPT_DEBUG !== 'boolean') {
						const err93 = {
							instancePath:
								instancePath + '/constants/SCRIPT_DEBUG',
							schemaPath:
								'#/properties/constants/properties/SCRIPT_DEBUG/type',
							keyword: 'type',
							params: { type: 'boolean' },
							message: 'must be boolean',
						};
						if (vErrors === null) {
							vErrors = [err93];
						} else {
							vErrors.push(err93);
						}
						errors++;
					}
				}
			} else {
				const err94 = {
					instancePath: instancePath + '/constants',
					schemaPath: '#/properties/constants/type',
					keyword: 'type',
					params: { type: 'object' },
					message: 'must be object',
				};
				if (vErrors === null) {
					vErrors = [err94];
				} else {
					vErrors.push(err94);
				}
				errors++;
			}
		}
		if (data.wordpressVersion !== undefined) {
			let data50 = data.wordpressVersion;
			const _errs138 = errors;
			let valid41 = false;
			const _errs139 = errors;
			if (typeof data50 === 'string') {
				if (!pattern1.test(data50)) {
					const err95 = {
						instancePath: instancePath + '/wordpressVersion',
						schemaPath:
							'#/definitions/DataSources.WordPressVersion/pattern',
						keyword: 'pattern',
						params: {
							pattern:
								'^(?:latest|beta|trunk|nightly|none|\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?)$',
						},
						message:
							'must match pattern "' +
							'^(?:latest|beta|trunk|nightly|none|\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?)$' +
							'"',
					};
					if (vErrors === null) {
						vErrors = [err95];
					} else {
						vErrors.push(err95);
					}
					errors++;
				}
			} else {
				const err96 = {
					instancePath: instancePath + '/wordpressVersion',
					schemaPath:
						'#/definitions/DataSources.WordPressVersion/type',
					keyword: 'type',
					params: { type: 'string' },
					message: 'must be string',
				};
				if (vErrors === null) {
					vErrors = [err96];
				} else {
					vErrors.push(err96);
				}
				errors++;
			}
			var _valid7 = _errs139 === errors;
			valid41 = valid41 || _valid7;
			if (!valid41) {
				const _errs142 = errors;
				if (
					!validate12(data50, {
						instancePath: instancePath + '/wordpressVersion',
						parentData: data,
						parentDataProperty: 'wordpressVersion',
						rootData,
					})
				) {
					vErrors =
						vErrors === null
							? validate12.errors
							: vErrors.concat(validate12.errors);
					errors = vErrors.length;
				}
				var _valid7 = _errs142 === errors;
				valid41 = valid41 || _valid7;
				if (!valid41) {
					const _errs143 = errors;
					if (
						data50 &&
						typeof data50 == 'object' &&
						!Array.isArray(data50)
					) {
						if (data50.min === undefined) {
							const err97 = {
								instancePath:
									instancePath + '/wordpressVersion',
								schemaPath:
									'#/properties/wordpressVersion/anyOf/2/required',
								keyword: 'required',
								params: { missingProperty: 'min' },
								message:
									"must have required property '" +
									'min' +
									"'",
							};
							if (vErrors === null) {
								vErrors = [err97];
							} else {
								vErrors.push(err97);
							}
							errors++;
						}
						for (const key7 in data50) {
							if (
								!(
									key7 === 'min' ||
									key7 === 'max' ||
									key7 === 'preferred'
								)
							) {
								const err98 = {
									instancePath:
										instancePath + '/wordpressVersion',
									schemaPath:
										'#/properties/wordpressVersion/anyOf/2/additionalProperties',
									keyword: 'additionalProperties',
									params: { additionalProperty: key7 },
									message:
										'must NOT have additional properties',
								};
								if (vErrors === null) {
									vErrors = [err98];
								} else {
									vErrors.push(err98);
								}
								errors++;
							}
						}
						if (data50.min !== undefined) {
							let data51 = data50.min;
							if (typeof data51 === 'string') {
								if (!pattern8.test(data51)) {
									const err99 = {
										instancePath:
											instancePath +
											'/wordpressVersion/min',
										schemaPath:
											'#/definitions/DataSources.WordPressVersionConstraintVersion/pattern',
										keyword: 'pattern',
										params: {
											pattern:
												'^\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?$',
										},
										message:
											'must match pattern "' +
											'^\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?$' +
											'"',
									};
									if (vErrors === null) {
										vErrors = [err99];
									} else {
										vErrors.push(err99);
									}
									errors++;
								}
							} else {
								const err100 = {
									instancePath:
										instancePath + '/wordpressVersion/min',
									schemaPath:
										'#/definitions/DataSources.WordPressVersionConstraintVersion/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err100];
								} else {
									vErrors.push(err100);
								}
								errors++;
							}
						}
						if (data50.max !== undefined) {
							let data52 = data50.max;
							if (typeof data52 === 'string') {
								if (!pattern8.test(data52)) {
									const err101 = {
										instancePath:
											instancePath +
											'/wordpressVersion/max',
										schemaPath:
											'#/definitions/DataSources.WordPressVersionConstraintVersion/pattern',
										keyword: 'pattern',
										params: {
											pattern:
												'^\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?$',
										},
										message:
											'must match pattern "' +
											'^\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?$' +
											'"',
									};
									if (vErrors === null) {
										vErrors = [err101];
									} else {
										vErrors.push(err101);
									}
									errors++;
								}
							} else {
								const err102 = {
									instancePath:
										instancePath + '/wordpressVersion/max',
									schemaPath:
										'#/definitions/DataSources.WordPressVersionConstraintVersion/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err102];
								} else {
									vErrors.push(err102);
								}
								errors++;
							}
						}
						if (data50.preferred !== undefined) {
							let data53 = data50.preferred;
							if (typeof data53 === 'string') {
								if (!pattern10.test(data53)) {
									const err103 = {
										instancePath:
											instancePath +
											'/wordpressVersion/preferred',
										schemaPath:
											'#/definitions/DataSources.WordPressVersionPreferredVersion/pattern',
										keyword: 'pattern',
										params: {
											pattern:
												'^(?:latest|\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?)$',
										},
										message:
											'must match pattern "' +
											'^(?:latest|\\d+\\.\\d+(?:\\.\\d+)?(?:-(?:beta\\d+|[Rr][Cc]\\d+))?)$' +
											'"',
									};
									if (vErrors === null) {
										vErrors = [err103];
									} else {
										vErrors.push(err103);
									}
									errors++;
								}
							} else {
								const err104 = {
									instancePath:
										instancePath +
										'/wordpressVersion/preferred',
									schemaPath:
										'#/definitions/DataSources.WordPressVersionPreferredVersion/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err104];
								} else {
									vErrors.push(err104);
								}
								errors++;
							}
						}
					} else {
						const err105 = {
							instancePath: instancePath + '/wordpressVersion',
							schemaPath:
								'#/properties/wordpressVersion/anyOf/2/type',
							keyword: 'type',
							params: { type: 'object' },
							message: 'must be object',
						};
						if (vErrors === null) {
							vErrors = [err105];
						} else {
							vErrors.push(err105);
						}
						errors++;
					}
					var _valid7 = _errs143 === errors;
					valid41 = valid41 || _valid7;
				}
			}
			if (!valid41) {
				const err106 = {
					instancePath: instancePath + '/wordpressVersion',
					schemaPath: '#/properties/wordpressVersion/anyOf',
					keyword: 'anyOf',
					params: {},
					message: 'must match a schema in anyOf',
				};
				if (vErrors === null) {
					vErrors = [err106];
				} else {
					vErrors.push(err106);
				}
				errors++;
			} else {
				errors = _errs138;
				if (vErrors !== null) {
					if (_errs138) {
						vErrors.length = _errs138;
					} else {
						vErrors = null;
					}
				}
			}
		}
		if (data.phpVersion !== undefined) {
			let data54 = data.phpVersion;
			const _errs156 = errors;
			let valid47 = false;
			const _errs157 = errors;
			if (typeof data54 === 'string') {
				if (!pattern11.test(data54)) {
					const err107 = {
						instancePath: instancePath + '/phpVersion',
						schemaPath:
							'#/definitions/DataSources.PHPVersion/pattern',
						keyword: 'pattern',
						params: {
							pattern:
								'^(?:latest|next|\\d+\\.\\d+(?:\\.\\d+)?)$',
						},
						message:
							'must match pattern "' +
							'^(?:latest|next|\\d+\\.\\d+(?:\\.\\d+)?)$' +
							'"',
					};
					if (vErrors === null) {
						vErrors = [err107];
					} else {
						vErrors.push(err107);
					}
					errors++;
				}
			} else {
				const err108 = {
					instancePath: instancePath + '/phpVersion',
					schemaPath: '#/definitions/DataSources.PHPVersion/type',
					keyword: 'type',
					params: { type: 'string' },
					message: 'must be string',
				};
				if (vErrors === null) {
					vErrors = [err108];
				} else {
					vErrors.push(err108);
				}
				errors++;
			}
			var _valid8 = _errs157 === errors;
			valid47 = valid47 || _valid8;
			if (!valid47) {
				const _errs160 = errors;
				if (
					data54 &&
					typeof data54 == 'object' &&
					!Array.isArray(data54)
				) {
					for (const key8 in data54) {
						if (
							!(
								key8 === 'min' ||
								key8 === 'recommended' ||
								key8 === 'max'
							)
						) {
							const err109 = {
								instancePath: instancePath + '/phpVersion',
								schemaPath:
									'#/properties/phpVersion/anyOf/1/additionalProperties',
								keyword: 'additionalProperties',
								params: { additionalProperty: key8 },
								message: 'must NOT have additional properties',
							};
							if (vErrors === null) {
								vErrors = [err109];
							} else {
								vErrors.push(err109);
							}
							errors++;
						}
					}
					if (data54.min !== undefined) {
						let data55 = data54.min;
						if (typeof data55 === 'string') {
							if (!pattern12.test(data55)) {
								const err110 = {
									instancePath:
										instancePath + '/phpVersion/min',
									schemaPath:
										'#/definitions/DataSources.PHPVersionConstraintVersion/pattern',
									keyword: 'pattern',
									params: {
										pattern:
											'^(?:latest|\\d+\\.\\d+(?:\\.\\d+)?)$',
									},
									message:
										'must match pattern "' +
										'^(?:latest|\\d+\\.\\d+(?:\\.\\d+)?)$' +
										'"',
								};
								if (vErrors === null) {
									vErrors = [err110];
								} else {
									vErrors.push(err110);
								}
								errors++;
							}
						} else {
							const err111 = {
								instancePath: instancePath + '/phpVersion/min',
								schemaPath:
									'#/definitions/DataSources.PHPVersionConstraintVersion/type',
								keyword: 'type',
								params: { type: 'string' },
								message: 'must be string',
							};
							if (vErrors === null) {
								vErrors = [err111];
							} else {
								vErrors.push(err111);
							}
							errors++;
						}
					}
					if (data54.recommended !== undefined) {
						let data56 = data54.recommended;
						if (typeof data56 === 'string') {
							if (!pattern12.test(data56)) {
								const err112 = {
									instancePath:
										instancePath +
										'/phpVersion/recommended',
									schemaPath:
										'#/definitions/DataSources.PHPVersionConstraintVersion/pattern',
									keyword: 'pattern',
									params: {
										pattern:
											'^(?:latest|\\d+\\.\\d+(?:\\.\\d+)?)$',
									},
									message:
										'must match pattern "' +
										'^(?:latest|\\d+\\.\\d+(?:\\.\\d+)?)$' +
										'"',
								};
								if (vErrors === null) {
									vErrors = [err112];
								} else {
									vErrors.push(err112);
								}
								errors++;
							}
						} else {
							const err113 = {
								instancePath:
									instancePath + '/phpVersion/recommended',
								schemaPath:
									'#/definitions/DataSources.PHPVersionConstraintVersion/type',
								keyword: 'type',
								params: { type: 'string' },
								message: 'must be string',
							};
							if (vErrors === null) {
								vErrors = [err113];
							} else {
								vErrors.push(err113);
							}
							errors++;
						}
					}
					if (data54.max !== undefined) {
						let data57 = data54.max;
						if (typeof data57 === 'string') {
							if (!pattern12.test(data57)) {
								const err114 = {
									instancePath:
										instancePath + '/phpVersion/max',
									schemaPath:
										'#/definitions/DataSources.PHPVersionConstraintVersion/pattern',
									keyword: 'pattern',
									params: {
										pattern:
											'^(?:latest|\\d+\\.\\d+(?:\\.\\d+)?)$',
									},
									message:
										'must match pattern "' +
										'^(?:latest|\\d+\\.\\d+(?:\\.\\d+)?)$' +
										'"',
								};
								if (vErrors === null) {
									vErrors = [err114];
								} else {
									vErrors.push(err114);
								}
								errors++;
							}
						} else {
							const err115 = {
								instancePath: instancePath + '/phpVersion/max',
								schemaPath:
									'#/definitions/DataSources.PHPVersionConstraintVersion/type',
								keyword: 'type',
								params: { type: 'string' },
								message: 'must be string',
							};
							if (vErrors === null) {
								vErrors = [err115];
							} else {
								vErrors.push(err115);
							}
							errors++;
						}
					}
				} else {
					const err116 = {
						instancePath: instancePath + '/phpVersion',
						schemaPath: '#/properties/phpVersion/anyOf/1/type',
						keyword: 'type',
						params: { type: 'object' },
						message: 'must be object',
					};
					if (vErrors === null) {
						vErrors = [err116];
					} else {
						vErrors.push(err116);
					}
					errors++;
				}
				var _valid8 = _errs160 === errors;
				valid47 = valid47 || _valid8;
			}
			if (!valid47) {
				const err117 = {
					instancePath: instancePath + '/phpVersion',
					schemaPath: '#/properties/phpVersion/anyOf',
					keyword: 'anyOf',
					params: {},
					message: 'must match a schema in anyOf',
				};
				if (vErrors === null) {
					vErrors = [err117];
				} else {
					vErrors.push(err117);
				}
				errors++;
			} else {
				errors = _errs156;
				if (vErrors !== null) {
					if (_errs156) {
						vErrors.length = _errs156;
					} else {
						vErrors = null;
					}
				}
			}
		}
		if (data.activeTheme !== undefined) {
			let data58 = data.activeTheme;
			const _errs173 = errors;
			let valid53 = false;
			const _errs174 = errors;
			if (
				!validate20(data58, {
					instancePath: instancePath + '/activeTheme',
					parentData: data,
					parentDataProperty: 'activeTheme',
					rootData,
				})
			) {
				vErrors =
					vErrors === null
						? validate20.errors
						: vErrors.concat(validate20.errors);
				errors = vErrors.length;
			}
			var _valid9 = _errs174 === errors;
			valid53 = valid53 || _valid9;
			if (!valid53) {
				const _errs175 = errors;
				if (
					!validate12(data58, {
						instancePath: instancePath + '/activeTheme',
						parentData: data,
						parentDataProperty: 'activeTheme',
						rootData,
					})
				) {
					vErrors =
						vErrors === null
							? validate12.errors
							: vErrors.concat(validate12.errors);
					errors = vErrors.length;
				}
				var _valid9 = _errs175 === errors;
				valid53 = valid53 || _valid9;
				if (!valid53) {
					const _errs176 = errors;
					if (
						data58 &&
						typeof data58 == 'object' &&
						!Array.isArray(data58)
					) {
						if (data58.source === undefined) {
							const err118 = {
								instancePath: instancePath + '/activeTheme',
								schemaPath:
									'#/properties/activeTheme/anyOf/2/required',
								keyword: 'required',
								params: { missingProperty: 'source' },
								message:
									"must have required property '" +
									'source' +
									"'",
							};
							if (vErrors === null) {
								vErrors = [err118];
							} else {
								vErrors.push(err118);
							}
							errors++;
						}
						for (const key9 in data58) {
							if (
								!(
									key9 === 'source' ||
									key9 === 'importStarterContent' ||
									key9 === 'targetDirectoryName' ||
									key9 === 'onError' ||
									key9 === 'ifAlreadyInstalled' ||
									key9 === 'humanReadableName'
								)
							) {
								const err119 = {
									instancePath: instancePath + '/activeTheme',
									schemaPath:
										'#/properties/activeTheme/anyOf/2/additionalProperties',
									keyword: 'additionalProperties',
									params: { additionalProperty: key9 },
									message:
										'must NOT have additional properties',
								};
								if (vErrors === null) {
									vErrors = [err119];
								} else {
									vErrors.push(err119);
								}
								errors++;
							}
						}
						if (data58.source !== undefined) {
							let data59 = data58.source;
							const _errs180 = errors;
							let valid55 = false;
							const _errs181 = errors;
							if (
								!validate20(data59, {
									instancePath:
										instancePath + '/activeTheme/source',
									parentData: data58,
									parentDataProperty: 'source',
									rootData,
								})
							) {
								vErrors =
									vErrors === null
										? validate20.errors
										: vErrors.concat(validate20.errors);
								errors = vErrors.length;
							}
							var _valid10 = _errs181 === errors;
							valid55 = valid55 || _valid10;
							if (!valid55) {
								const _errs182 = errors;
								if (
									!validate12(data59, {
										instancePath:
											instancePath +
											'/activeTheme/source',
										parentData: data58,
										parentDataProperty: 'source',
										rootData,
									})
								) {
									vErrors =
										vErrors === null
											? validate12.errors
											: vErrors.concat(validate12.errors);
									errors = vErrors.length;
								}
								var _valid10 = _errs182 === errors;
								valid55 = valid55 || _valid10;
							}
							if (!valid55) {
								const err120 = {
									instancePath:
										instancePath + '/activeTheme/source',
									schemaPath:
										'#/properties/activeTheme/anyOf/2/properties/source/anyOf',
									keyword: 'anyOf',
									params: {},
									message: 'must match a schema in anyOf',
								};
								if (vErrors === null) {
									vErrors = [err120];
								} else {
									vErrors.push(err120);
								}
								errors++;
							} else {
								errors = _errs180;
								if (vErrors !== null) {
									if (_errs180) {
										vErrors.length = _errs180;
									} else {
										vErrors = null;
									}
								}
							}
						}
						if (data58.importStarterContent !== undefined) {
							if (
								typeof data58.importStarterContent !== 'boolean'
							) {
								const err121 = {
									instancePath:
										instancePath +
										'/activeTheme/importStarterContent',
									schemaPath:
										'#/properties/activeTheme/anyOf/2/properties/importStarterContent/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err121];
								} else {
									vErrors.push(err121);
								}
								errors++;
							}
						}
						if (data58.targetDirectoryName !== undefined) {
							let data61 = data58.targetDirectoryName;
							if (typeof data61 === 'string') {
								if (!pattern3.test(data61)) {
									const err122 = {
										instancePath:
											instancePath +
											'/activeTheme/targetDirectoryName',
										schemaPath:
											'#/properties/activeTheme/anyOf/2/properties/targetDirectoryName/pattern',
										keyword: 'pattern',
										params: {
											pattern:
												'^(?!(?:\\.|\\.\\.)$)[^/]+$',
										},
										message:
											'must match pattern "' +
											'^(?!(?:\\.|\\.\\.)$)[^/]+$' +
											'"',
									};
									if (vErrors === null) {
										vErrors = [err122];
									} else {
										vErrors.push(err122);
									}
									errors++;
								}
							} else {
								const err123 = {
									instancePath:
										instancePath +
										'/activeTheme/targetDirectoryName',
									schemaPath:
										'#/properties/activeTheme/anyOf/2/properties/targetDirectoryName/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err123];
								} else {
									vErrors.push(err123);
								}
								errors++;
							}
						}
						if (data58.onError !== undefined) {
							let data62 = data58.onError;
							if (typeof data62 !== 'string') {
								const err124 = {
									instancePath:
										instancePath + '/activeTheme/onError',
									schemaPath:
										'#/properties/activeTheme/anyOf/2/properties/onError/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err124];
								} else {
									vErrors.push(err124);
								}
								errors++;
							}
							if (
								!(data62 === 'skip-theme' || data62 === 'throw')
							) {
								const err125 = {
									instancePath:
										instancePath + '/activeTheme/onError',
									schemaPath:
										'#/properties/activeTheme/anyOf/2/properties/onError/enum',
									keyword: 'enum',
									params: {
										allowedValues:
											schema12.properties.activeTheme
												.anyOf[2].properties.onError
												.enum,
									},
									message:
										'must be equal to one of the allowed values',
								};
								if (vErrors === null) {
									vErrors = [err125];
								} else {
									vErrors.push(err125);
								}
								errors++;
							}
						}
						if (data58.ifAlreadyInstalled !== undefined) {
							let data63 = data58.ifAlreadyInstalled;
							if (typeof data63 !== 'string') {
								const err126 = {
									instancePath:
										instancePath +
										'/activeTheme/ifAlreadyInstalled',
									schemaPath:
										'#/properties/activeTheme/anyOf/2/properties/ifAlreadyInstalled/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err126];
								} else {
									vErrors.push(err126);
								}
								errors++;
							}
							if (
								!(
									data63 === 'overwrite' ||
									data63 === 'skip' ||
									data63 === 'error'
								)
							) {
								const err127 = {
									instancePath:
										instancePath +
										'/activeTheme/ifAlreadyInstalled',
									schemaPath:
										'#/properties/activeTheme/anyOf/2/properties/ifAlreadyInstalled/enum',
									keyword: 'enum',
									params: {
										allowedValues:
											schema12.properties.activeTheme
												.anyOf[2].properties
												.ifAlreadyInstalled.enum,
									},
									message:
										'must be equal to one of the allowed values',
								};
								if (vErrors === null) {
									vErrors = [err127];
								} else {
									vErrors.push(err127);
								}
								errors++;
							}
						}
						if (data58.humanReadableName !== undefined) {
							if (typeof data58.humanReadableName !== 'string') {
								const err128 = {
									instancePath:
										instancePath +
										'/activeTheme/humanReadableName',
									schemaPath:
										'#/properties/activeTheme/anyOf/2/properties/humanReadableName/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err128];
								} else {
									vErrors.push(err128);
								}
								errors++;
							}
						}
					} else {
						const err129 = {
							instancePath: instancePath + '/activeTheme',
							schemaPath: '#/properties/activeTheme/anyOf/2/type',
							keyword: 'type',
							params: { type: 'object' },
							message: 'must be object',
						};
						if (vErrors === null) {
							vErrors = [err129];
						} else {
							vErrors.push(err129);
						}
						errors++;
					}
					var _valid9 = _errs176 === errors;
					valid53 = valid53 || _valid9;
				}
			}
			if (!valid53) {
				const err130 = {
					instancePath: instancePath + '/activeTheme',
					schemaPath: '#/properties/activeTheme/anyOf',
					keyword: 'anyOf',
					params: {},
					message: 'must match a schema in anyOf',
				};
				if (vErrors === null) {
					vErrors = [err130];
				} else {
					vErrors.push(err130);
				}
				errors++;
			} else {
				errors = _errs173;
				if (vErrors !== null) {
					if (_errs173) {
						vErrors.length = _errs173;
					} else {
						vErrors = null;
					}
				}
			}
		}
		if (data.themes !== undefined) {
			let data65 = data.themes;
			if (Array.isArray(data65)) {
				const len8 = data65.length;
				for (let i9 = 0; i9 < len8; i9++) {
					let data66 = data65[i9];
					const _errs196 = errors;
					let valid58 = false;
					const _errs197 = errors;
					if (
						!validate20(data66, {
							instancePath: instancePath + '/themes/' + i9,
							parentData: data65,
							parentDataProperty: i9,
							rootData,
						})
					) {
						vErrors =
							vErrors === null
								? validate20.errors
								: vErrors.concat(validate20.errors);
						errors = vErrors.length;
					}
					var _valid11 = _errs197 === errors;
					valid58 = valid58 || _valid11;
					if (!valid58) {
						const _errs198 = errors;
						if (
							!validate12(data66, {
								instancePath: instancePath + '/themes/' + i9,
								parentData: data65,
								parentDataProperty: i9,
								rootData,
							})
						) {
							vErrors =
								vErrors === null
									? validate12.errors
									: vErrors.concat(validate12.errors);
							errors = vErrors.length;
						}
						var _valid11 = _errs198 === errors;
						valid58 = valid58 || _valid11;
						if (!valid58) {
							const _errs199 = errors;
							if (
								data66 &&
								typeof data66 == 'object' &&
								!Array.isArray(data66)
							) {
								if (data66.source === undefined) {
									const err131 = {
										instancePath:
											instancePath + '/themes/' + i9,
										schemaPath:
											'#/properties/themes/items/anyOf/2/required',
										keyword: 'required',
										params: { missingProperty: 'source' },
										message:
											"must have required property '" +
											'source' +
											"'",
									};
									if (vErrors === null) {
										vErrors = [err131];
									} else {
										vErrors.push(err131);
									}
									errors++;
								}
								for (const key10 in data66) {
									if (
										!(
											key10 === 'source' ||
											key10 === 'importStarterContent' ||
											key10 === 'targetDirectoryName' ||
											key10 === 'onError' ||
											key10 === 'ifAlreadyInstalled' ||
											key10 === 'humanReadableName'
										)
									) {
										const err132 = {
											instancePath:
												instancePath + '/themes/' + i9,
											schemaPath:
												'#/properties/themes/items/anyOf/2/additionalProperties',
											keyword: 'additionalProperties',
											params: {
												additionalProperty: key10,
											},
											message:
												'must NOT have additional properties',
										};
										if (vErrors === null) {
											vErrors = [err132];
										} else {
											vErrors.push(err132);
										}
										errors++;
									}
								}
								if (data66.source !== undefined) {
									let data67 = data66.source;
									const _errs203 = errors;
									let valid60 = false;
									const _errs204 = errors;
									if (
										!validate20(data67, {
											instancePath:
												instancePath +
												'/themes/' +
												i9 +
												'/source',
											parentData: data66,
											parentDataProperty: 'source',
											rootData,
										})
									) {
										vErrors =
											vErrors === null
												? validate20.errors
												: vErrors.concat(
														validate20.errors
													);
										errors = vErrors.length;
									}
									var _valid12 = _errs204 === errors;
									valid60 = valid60 || _valid12;
									if (!valid60) {
										const _errs205 = errors;
										if (
											!validate12(data67, {
												instancePath:
													instancePath +
													'/themes/' +
													i9 +
													'/source',
												parentData: data66,
												parentDataProperty: 'source',
												rootData,
											})
										) {
											vErrors =
												vErrors === null
													? validate12.errors
													: vErrors.concat(
															validate12.errors
														);
											errors = vErrors.length;
										}
										var _valid12 = _errs205 === errors;
										valid60 = valid60 || _valid12;
									}
									if (!valid60) {
										const err133 = {
											instancePath:
												instancePath +
												'/themes/' +
												i9 +
												'/source',
											schemaPath:
												'#/properties/themes/items/anyOf/2/properties/source/anyOf',
											keyword: 'anyOf',
											params: {},
											message:
												'must match a schema in anyOf',
										};
										if (vErrors === null) {
											vErrors = [err133];
										} else {
											vErrors.push(err133);
										}
										errors++;
									} else {
										errors = _errs203;
										if (vErrors !== null) {
											if (_errs203) {
												vErrors.length = _errs203;
											} else {
												vErrors = null;
											}
										}
									}
								}
								if (data66.importStarterContent !== undefined) {
									if (
										typeof data66.importStarterContent !==
										'boolean'
									) {
										const err134 = {
											instancePath:
												instancePath +
												'/themes/' +
												i9 +
												'/importStarterContent',
											schemaPath:
												'#/properties/themes/items/anyOf/2/properties/importStarterContent/type',
											keyword: 'type',
											params: { type: 'boolean' },
											message: 'must be boolean',
										};
										if (vErrors === null) {
											vErrors = [err134];
										} else {
											vErrors.push(err134);
										}
										errors++;
									}
								}
								if (data66.targetDirectoryName !== undefined) {
									let data69 = data66.targetDirectoryName;
									if (typeof data69 === 'string') {
										if (!pattern3.test(data69)) {
											const err135 = {
												instancePath:
													instancePath +
													'/themes/' +
													i9 +
													'/targetDirectoryName',
												schemaPath:
													'#/properties/themes/items/anyOf/2/properties/targetDirectoryName/pattern',
												keyword: 'pattern',
												params: {
													pattern:
														'^(?!(?:\\.|\\.\\.)$)[^/]+$',
												},
												message:
													'must match pattern "' +
													'^(?!(?:\\.|\\.\\.)$)[^/]+$' +
													'"',
											};
											if (vErrors === null) {
												vErrors = [err135];
											} else {
												vErrors.push(err135);
											}
											errors++;
										}
									} else {
										const err136 = {
											instancePath:
												instancePath +
												'/themes/' +
												i9 +
												'/targetDirectoryName',
											schemaPath:
												'#/properties/themes/items/anyOf/2/properties/targetDirectoryName/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err136];
										} else {
											vErrors.push(err136);
										}
										errors++;
									}
								}
								if (data66.onError !== undefined) {
									let data70 = data66.onError;
									if (typeof data70 !== 'string') {
										const err137 = {
											instancePath:
												instancePath +
												'/themes/' +
												i9 +
												'/onError',
											schemaPath:
												'#/properties/themes/items/anyOf/2/properties/onError/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err137];
										} else {
											vErrors.push(err137);
										}
										errors++;
									}
									if (
										!(
											data70 === 'skip-theme' ||
											data70 === 'throw'
										)
									) {
										const err138 = {
											instancePath:
												instancePath +
												'/themes/' +
												i9 +
												'/onError',
											schemaPath:
												'#/properties/themes/items/anyOf/2/properties/onError/enum',
											keyword: 'enum',
											params: {
												allowedValues:
													schema12.properties.themes
														.items.anyOf[2]
														.properties.onError
														.enum,
											},
											message:
												'must be equal to one of the allowed values',
										};
										if (vErrors === null) {
											vErrors = [err138];
										} else {
											vErrors.push(err138);
										}
										errors++;
									}
								}
								if (data66.ifAlreadyInstalled !== undefined) {
									let data71 = data66.ifAlreadyInstalled;
									if (typeof data71 !== 'string') {
										const err139 = {
											instancePath:
												instancePath +
												'/themes/' +
												i9 +
												'/ifAlreadyInstalled',
											schemaPath:
												'#/properties/themes/items/anyOf/2/properties/ifAlreadyInstalled/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err139];
										} else {
											vErrors.push(err139);
										}
										errors++;
									}
									if (
										!(
											data71 === 'overwrite' ||
											data71 === 'skip' ||
											data71 === 'error'
										)
									) {
										const err140 = {
											instancePath:
												instancePath +
												'/themes/' +
												i9 +
												'/ifAlreadyInstalled',
											schemaPath:
												'#/properties/themes/items/anyOf/2/properties/ifAlreadyInstalled/enum',
											keyword: 'enum',
											params: {
												allowedValues:
													schema12.properties.themes
														.items.anyOf[2]
														.properties
														.ifAlreadyInstalled
														.enum,
											},
											message:
												'must be equal to one of the allowed values',
										};
										if (vErrors === null) {
											vErrors = [err140];
										} else {
											vErrors.push(err140);
										}
										errors++;
									}
								}
								if (data66.humanReadableName !== undefined) {
									if (
										typeof data66.humanReadableName !==
										'string'
									) {
										const err141 = {
											instancePath:
												instancePath +
												'/themes/' +
												i9 +
												'/humanReadableName',
											schemaPath:
												'#/properties/themes/items/anyOf/2/properties/humanReadableName/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err141];
										} else {
											vErrors.push(err141);
										}
										errors++;
									}
								}
							} else {
								const err142 = {
									instancePath:
										instancePath + '/themes/' + i9,
									schemaPath:
										'#/properties/themes/items/anyOf/2/type',
									keyword: 'type',
									params: { type: 'object' },
									message: 'must be object',
								};
								if (vErrors === null) {
									vErrors = [err142];
								} else {
									vErrors.push(err142);
								}
								errors++;
							}
							var _valid11 = _errs199 === errors;
							valid58 = valid58 || _valid11;
						}
					}
					if (!valid58) {
						const err143 = {
							instancePath: instancePath + '/themes/' + i9,
							schemaPath: '#/properties/themes/items/anyOf',
							keyword: 'anyOf',
							params: {},
							message: 'must match a schema in anyOf',
						};
						if (vErrors === null) {
							vErrors = [err143];
						} else {
							vErrors.push(err143);
						}
						errors++;
					} else {
						errors = _errs196;
						if (vErrors !== null) {
							if (_errs196) {
								vErrors.length = _errs196;
							} else {
								vErrors = null;
							}
						}
					}
				}
			} else {
				const err144 = {
					instancePath: instancePath + '/themes',
					schemaPath: '#/properties/themes/type',
					keyword: 'type',
					params: { type: 'array' },
					message: 'must be array',
				};
				if (vErrors === null) {
					vErrors = [err144];
				} else {
					vErrors.push(err144);
				}
				errors++;
			}
		}
		if (data.plugins !== undefined) {
			let data73 = data.plugins;
			if (Array.isArray(data73)) {
				const len9 = data73.length;
				for (let i10 = 0; i10 < len9; i10++) {
					let data74 = data73[i10];
					const _errs219 = errors;
					let valid63 = false;
					const _errs220 = errors;
					if (
						!validate12(data74, {
							instancePath: instancePath + '/plugins/' + i10,
							parentData: data73,
							parentDataProperty: i10,
							rootData,
						})
					) {
						vErrors =
							vErrors === null
								? validate12.errors
								: vErrors.concat(validate12.errors);
						errors = vErrors.length;
					}
					var _valid13 = _errs220 === errors;
					valid63 = valid63 || _valid13;
					if (!valid63) {
						const _errs221 = errors;
						if (
							!validate30(data74, {
								instancePath: instancePath + '/plugins/' + i10,
								parentData: data73,
								parentDataProperty: i10,
								rootData,
							})
						) {
							vErrors =
								vErrors === null
									? validate30.errors
									: vErrors.concat(validate30.errors);
							errors = vErrors.length;
						}
						var _valid13 = _errs221 === errors;
						valid63 = valid63 || _valid13;
						if (!valid63) {
							const _errs222 = errors;
							if (
								data74 &&
								typeof data74 == 'object' &&
								!Array.isArray(data74)
							) {
								if (data74.source === undefined) {
									const err145 = {
										instancePath:
											instancePath + '/plugins/' + i10,
										schemaPath:
											'#/properties/plugins/items/anyOf/2/required',
										keyword: 'required',
										params: { missingProperty: 'source' },
										message:
											"must have required property '" +
											'source' +
											"'",
									};
									if (vErrors === null) {
										vErrors = [err145];
									} else {
										vErrors.push(err145);
									}
									errors++;
								}
								for (const key11 in data74) {
									if (
										!(
											key11 === 'source' ||
											key11 === 'active' ||
											key11 === 'activationOptions' ||
											key11 === 'targetDirectoryName' ||
											key11 === 'onError' ||
											key11 === 'ifAlreadyInstalled' ||
											key11 === 'humanReadableName'
										)
									) {
										const err146 = {
											instancePath:
												instancePath +
												'/plugins/' +
												i10,
											schemaPath:
												'#/properties/plugins/items/anyOf/2/additionalProperties',
											keyword: 'additionalProperties',
											params: {
												additionalProperty: key11,
											},
											message:
												'must NOT have additional properties',
										};
										if (vErrors === null) {
											vErrors = [err146];
										} else {
											vErrors.push(err146);
										}
										errors++;
									}
								}
								if (data74.source !== undefined) {
									let data75 = data74.source;
									const _errs226 = errors;
									let valid65 = false;
									const _errs227 = errors;
									if (
										!validate12(data75, {
											instancePath:
												instancePath +
												'/plugins/' +
												i10 +
												'/source',
											parentData: data74,
											parentDataProperty: 'source',
											rootData,
										})
									) {
										vErrors =
											vErrors === null
												? validate12.errors
												: vErrors.concat(
														validate12.errors
													);
										errors = vErrors.length;
									}
									var _valid14 = _errs227 === errors;
									valid65 = valid65 || _valid14;
									if (!valid65) {
										const _errs228 = errors;
										if (
											!validate30(data75, {
												instancePath:
													instancePath +
													'/plugins/' +
													i10 +
													'/source',
												parentData: data74,
												parentDataProperty: 'source',
												rootData,
											})
										) {
											vErrors =
												vErrors === null
													? validate30.errors
													: vErrors.concat(
															validate30.errors
														);
											errors = vErrors.length;
										}
										var _valid14 = _errs228 === errors;
										valid65 = valid65 || _valid14;
									}
									if (!valid65) {
										const err147 = {
											instancePath:
												instancePath +
												'/plugins/' +
												i10 +
												'/source',
											schemaPath:
												'#/properties/plugins/items/anyOf/2/properties/source/anyOf',
											keyword: 'anyOf',
											params: {},
											message:
												'must match a schema in anyOf',
										};
										if (vErrors === null) {
											vErrors = [err147];
										} else {
											vErrors.push(err147);
										}
										errors++;
									} else {
										errors = _errs226;
										if (vErrors !== null) {
											if (_errs226) {
												vErrors.length = _errs226;
											} else {
												vErrors = null;
											}
										}
									}
								}
								if (data74.active !== undefined) {
									if (typeof data74.active !== 'boolean') {
										const err148 = {
											instancePath:
												instancePath +
												'/plugins/' +
												i10 +
												'/active',
											schemaPath:
												'#/properties/plugins/items/anyOf/2/properties/active/type',
											keyword: 'type',
											params: { type: 'boolean' },
											message: 'must be boolean',
										};
										if (vErrors === null) {
											vErrors = [err148];
										} else {
											vErrors.push(err148);
										}
										errors++;
									}
								}
								if (data74.activationOptions !== undefined) {
									let data77 = data74.activationOptions;
									if (
										data77 &&
										typeof data77 == 'object' &&
										!Array.isArray(data77)
									) {
										for (const key12 in data77) {
											let data78 = data77[key12];
											const _errs235 = errors;
											let valid67 = false;
											const _errs236 = errors;
											if (typeof data78 !== 'string') {
												const err149 = {
													instancePath:
														instancePath +
														'/plugins/' +
														i10 +
														'/activationOptions/' +
														key12
															.replace(/~/g, '~0')
															.replace(
																/\//g,
																'~1'
															),
													schemaPath:
														'#/properties/plugins/items/anyOf/2/properties/activationOptions/additionalProperties/anyOf/0/type',
													keyword: 'type',
													params: { type: 'string' },
													message: 'must be string',
												};
												if (vErrors === null) {
													vErrors = [err149];
												} else {
													vErrors.push(err149);
												}
												errors++;
											}
											var _valid15 = _errs236 === errors;
											valid67 = valid67 || _valid15;
											if (!valid67) {
												const _errs238 = errors;
												if (
													typeof data78 !== 'boolean'
												) {
													const err150 = {
														instancePath:
															instancePath +
															'/plugins/' +
															i10 +
															'/activationOptions/' +
															key12
																.replace(
																	/~/g,
																	'~0'
																)
																.replace(
																	/\//g,
																	'~1'
																),
														schemaPath:
															'#/properties/plugins/items/anyOf/2/properties/activationOptions/additionalProperties/anyOf/1/type',
														keyword: 'type',
														params: {
															type: 'boolean',
														},
														message:
															'must be boolean',
													};
													if (vErrors === null) {
														vErrors = [err150];
													} else {
														vErrors.push(err150);
													}
													errors++;
												}
												var _valid15 =
													_errs238 === errors;
												valid67 = valid67 || _valid15;
												if (!valid67) {
													const _errs240 = errors;
													if (
														!(
															typeof data78 ==
																'number' &&
															isFinite(data78)
														)
													) {
														const err151 = {
															instancePath:
																instancePath +
																'/plugins/' +
																i10 +
																'/activationOptions/' +
																key12
																	.replace(
																		/~/g,
																		'~0'
																	)
																	.replace(
																		/\//g,
																		'~1'
																	),
															schemaPath:
																'#/properties/plugins/items/anyOf/2/properties/activationOptions/additionalProperties/anyOf/2/type',
															keyword: 'type',
															params: {
																type: 'number',
															},
															message:
																'must be number',
														};
														if (vErrors === null) {
															vErrors = [err151];
														} else {
															vErrors.push(
																err151
															);
														}
														errors++;
													}
													var _valid15 =
														_errs240 === errors;
													valid67 =
														valid67 || _valid15;
													if (!valid67) {
														const _errs242 = errors;
														if (
															Array.isArray(
																data78
															)
														) {
															const len10 =
																data78.length;
															for (
																let i11 = 0;
																i11 < len10;
																i11++
															) {
																if (
																	!validate34(
																		data78[
																			i11
																		],
																		{
																			instancePath:
																				instancePath +
																				'/plugins/' +
																				i10 +
																				'/activationOptions/' +
																				key12
																					.replace(
																						/~/g,
																						'~0'
																					)
																					.replace(
																						/\//g,
																						'~1'
																					) +
																				'/' +
																				i11,
																			parentData:
																				data78,
																			parentDataProperty:
																				i11,
																			rootData,
																		}
																	)
																) {
																	vErrors =
																		vErrors ===
																		null
																			? validate34.errors
																			: vErrors.concat(
																					validate34.errors
																				);
																	errors =
																		vErrors.length;
																}
															}
														} else {
															const err152 = {
																instancePath:
																	instancePath +
																	'/plugins/' +
																	i10 +
																	'/activationOptions/' +
																	key12
																		.replace(
																			/~/g,
																			'~0'
																		)
																		.replace(
																			/\//g,
																			'~1'
																		),
																schemaPath:
																	'#/properties/plugins/items/anyOf/2/properties/activationOptions/additionalProperties/anyOf/3/type',
																keyword: 'type',
																params: {
																	type: 'array',
																},
																message:
																	'must be array',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err152,
																];
															} else {
																vErrors.push(
																	err152
																);
															}
															errors++;
														}
														var _valid15 =
															_errs242 === errors;
														valid67 =
															valid67 || _valid15;
														if (!valid67) {
															const _errs245 =
																errors;
															if (
																data78 &&
																typeof data78 ==
																	'object' &&
																!Array.isArray(
																	data78
																)
															) {
																for (const key13 in data78) {
																	if (
																		!validate34(
																			data78[
																				key13
																			],
																			{
																				instancePath:
																					instancePath +
																					'/plugins/' +
																					i10 +
																					'/activationOptions/' +
																					key12
																						.replace(
																							/~/g,
																							'~0'
																						)
																						.replace(
																							/\//g,
																							'~1'
																						) +
																					'/' +
																					key13
																						.replace(
																							/~/g,
																							'~0'
																						)
																						.replace(
																							/\//g,
																							'~1'
																						),
																				parentData:
																					data78,
																				parentDataProperty:
																					key13,
																				rootData,
																			}
																		)
																	) {
																		vErrors =
																			vErrors ===
																			null
																				? validate34.errors
																				: vErrors.concat(
																						validate34.errors
																					);
																		errors =
																			vErrors.length;
																	}
																}
															} else {
																const err153 = {
																	instancePath:
																		instancePath +
																		'/plugins/' +
																		i10 +
																		'/activationOptions/' +
																		key12
																			.replace(
																				/~/g,
																				'~0'
																			)
																			.replace(
																				/\//g,
																				'~1'
																			),
																	schemaPath:
																		'#/properties/plugins/items/anyOf/2/properties/activationOptions/additionalProperties/anyOf/4/type',
																	keyword:
																		'type',
																	params: {
																		type: 'object',
																	},
																	message:
																		'must be object',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err153,
																	];
																} else {
																	vErrors.push(
																		err153
																	);
																}
																errors++;
															}
															var _valid15 =
																_errs245 ===
																errors;
															valid67 =
																valid67 ||
																_valid15;
														}
													}
												}
											}
											if (!valid67) {
												const err154 = {
													instancePath:
														instancePath +
														'/plugins/' +
														i10 +
														'/activationOptions/' +
														key12
															.replace(/~/g, '~0')
															.replace(
																/\//g,
																'~1'
															),
													schemaPath:
														'#/properties/plugins/items/anyOf/2/properties/activationOptions/additionalProperties/anyOf',
													keyword: 'anyOf',
													params: {},
													message:
														'must match a schema in anyOf',
												};
												if (vErrors === null) {
													vErrors = [err154];
												} else {
													vErrors.push(err154);
												}
												errors++;
											} else {
												errors = _errs235;
												if (vErrors !== null) {
													if (_errs235) {
														vErrors.length =
															_errs235;
													} else {
														vErrors = null;
													}
												}
											}
										}
									} else {
										const err155 = {
											instancePath:
												instancePath +
												'/plugins/' +
												i10 +
												'/activationOptions',
											schemaPath:
												'#/properties/plugins/items/anyOf/2/properties/activationOptions/type',
											keyword: 'type',
											params: { type: 'object' },
											message: 'must be object',
										};
										if (vErrors === null) {
											vErrors = [err155];
										} else {
											vErrors.push(err155);
										}
										errors++;
									}
								}
								if (data74.targetDirectoryName !== undefined) {
									let data81 = data74.targetDirectoryName;
									if (typeof data81 === 'string') {
										if (!pattern3.test(data81)) {
											const err156 = {
												instancePath:
													instancePath +
													'/plugins/' +
													i10 +
													'/targetDirectoryName',
												schemaPath:
													'#/properties/plugins/items/anyOf/2/properties/targetDirectoryName/pattern',
												keyword: 'pattern',
												params: {
													pattern:
														'^(?!(?:\\.|\\.\\.)$)[^/]+$',
												},
												message:
													'must match pattern "' +
													'^(?!(?:\\.|\\.\\.)$)[^/]+$' +
													'"',
											};
											if (vErrors === null) {
												vErrors = [err156];
											} else {
												vErrors.push(err156);
											}
											errors++;
										}
									} else {
										const err157 = {
											instancePath:
												instancePath +
												'/plugins/' +
												i10 +
												'/targetDirectoryName',
											schemaPath:
												'#/properties/plugins/items/anyOf/2/properties/targetDirectoryName/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err157];
										} else {
											vErrors.push(err157);
										}
										errors++;
									}
								}
								if (data74.onError !== undefined) {
									let data82 = data74.onError;
									if (typeof data82 !== 'string') {
										const err158 = {
											instancePath:
												instancePath +
												'/plugins/' +
												i10 +
												'/onError',
											schemaPath:
												'#/properties/plugins/items/anyOf/2/properties/onError/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err158];
										} else {
											vErrors.push(err158);
										}
										errors++;
									}
									if (
										!(
											data82 === 'skip-plugin' ||
											data82 === 'throw'
										)
									) {
										const err159 = {
											instancePath:
												instancePath +
												'/plugins/' +
												i10 +
												'/onError',
											schemaPath:
												'#/properties/plugins/items/anyOf/2/properties/onError/enum',
											keyword: 'enum',
											params: {
												allowedValues:
													schema12.properties.plugins
														.items.anyOf[2]
														.properties.onError
														.enum,
											},
											message:
												'must be equal to one of the allowed values',
										};
										if (vErrors === null) {
											vErrors = [err159];
										} else {
											vErrors.push(err159);
										}
										errors++;
									}
								}
								if (data74.ifAlreadyInstalled !== undefined) {
									let data83 = data74.ifAlreadyInstalled;
									if (typeof data83 !== 'string') {
										const err160 = {
											instancePath:
												instancePath +
												'/plugins/' +
												i10 +
												'/ifAlreadyInstalled',
											schemaPath:
												'#/properties/plugins/items/anyOf/2/properties/ifAlreadyInstalled/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err160];
										} else {
											vErrors.push(err160);
										}
										errors++;
									}
									if (
										!(
											data83 === 'overwrite' ||
											data83 === 'skip' ||
											data83 === 'error'
										)
									) {
										const err161 = {
											instancePath:
												instancePath +
												'/plugins/' +
												i10 +
												'/ifAlreadyInstalled',
											schemaPath:
												'#/properties/plugins/items/anyOf/2/properties/ifAlreadyInstalled/enum',
											keyword: 'enum',
											params: {
												allowedValues:
													schema12.properties.plugins
														.items.anyOf[2]
														.properties
														.ifAlreadyInstalled
														.enum,
											},
											message:
												'must be equal to one of the allowed values',
										};
										if (vErrors === null) {
											vErrors = [err161];
										} else {
											vErrors.push(err161);
										}
										errors++;
									}
								}
								if (data74.humanReadableName !== undefined) {
									if (
										typeof data74.humanReadableName !==
										'string'
									) {
										const err162 = {
											instancePath:
												instancePath +
												'/plugins/' +
												i10 +
												'/humanReadableName',
											schemaPath:
												'#/properties/plugins/items/anyOf/2/properties/humanReadableName/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err162];
										} else {
											vErrors.push(err162);
										}
										errors++;
									}
								}
							} else {
								const err163 = {
									instancePath:
										instancePath + '/plugins/' + i10,
									schemaPath:
										'#/properties/plugins/items/anyOf/2/type',
									keyword: 'type',
									params: { type: 'object' },
									message: 'must be object',
								};
								if (vErrors === null) {
									vErrors = [err163];
								} else {
									vErrors.push(err163);
								}
								errors++;
							}
							var _valid13 = _errs222 === errors;
							valid63 = valid63 || _valid13;
						}
					}
					if (!valid63) {
						const err164 = {
							instancePath: instancePath + '/plugins/' + i10,
							schemaPath: '#/properties/plugins/items/anyOf',
							keyword: 'anyOf',
							params: {},
							message: 'must match a schema in anyOf',
						};
						if (vErrors === null) {
							vErrors = [err164];
						} else {
							vErrors.push(err164);
						}
						errors++;
					} else {
						errors = _errs219;
						if (vErrors !== null) {
							if (_errs219) {
								vErrors.length = _errs219;
							} else {
								vErrors = null;
							}
						}
					}
				}
			} else {
				const err165 = {
					instancePath: instancePath + '/plugins',
					schemaPath: '#/properties/plugins/type',
					keyword: 'type',
					params: { type: 'array' },
					message: 'must be array',
				};
				if (vErrors === null) {
					vErrors = [err165];
				} else {
					vErrors.push(err165);
				}
				errors++;
			}
		}
		if (data.muPlugins !== undefined) {
			let data85 = data.muPlugins;
			if (Array.isArray(data85)) {
				const len11 = data85.length;
				for (let i12 = 0; i12 < len11; i12++) {
					if (
						!validate12(data85[i12], {
							instancePath: instancePath + '/muPlugins/' + i12,
							parentData: data85,
							parentDataProperty: i12,
							rootData,
						})
					) {
						vErrors =
							vErrors === null
								? validate12.errors
								: vErrors.concat(validate12.errors);
						errors = vErrors.length;
					}
				}
			} else {
				const err166 = {
					instancePath: instancePath + '/muPlugins',
					schemaPath: '#/properties/muPlugins/type',
					keyword: 'type',
					params: { type: 'array' },
					message: 'must be array',
				};
				if (vErrors === null) {
					vErrors = [err166];
				} else {
					vErrors.push(err166);
				}
				errors++;
			}
		}
		if (data.postTypes !== undefined) {
			let data87 = data.postTypes;
			if (data87 && typeof data87 == 'object' && !Array.isArray(data87)) {
				for (const key14 in data87) {
					const _errs262 = errors;
					if (typeof key14 === 'string') {
						if (!pattern18.test(key14)) {
							const err167 = {
								instancePath: instancePath + '/postTypes',
								schemaPath:
									'#/properties/postTypes/propertyNames/pattern',
								keyword: 'pattern',
								params: { pattern: '^[a-z0-9_-]{1,20}$' },
								message:
									'must match pattern "' +
									'^[a-z0-9_-]{1,20}$' +
									'"',
								propertyName: key14,
							};
							if (vErrors === null) {
								vErrors = [err167];
							} else {
								vErrors.push(err167);
							}
							errors++;
						}
					}
					var valid73 = _errs262 === errors;
					if (!valid73) {
						const err168 = {
							instancePath: instancePath + '/postTypes',
							schemaPath: '#/properties/postTypes/propertyNames',
							keyword: 'propertyNames',
							params: { propertyName: key14 },
							message: 'property name must be valid',
						};
						if (vErrors === null) {
							vErrors = [err168];
						} else {
							vErrors.push(err168);
						}
						errors++;
					}
				}
				for (const key15 in data87) {
					let data88 = data87[key15];
					const _errs265 = errors;
					let valid75 = false;
					const _errs266 = errors;
					if (
						data88 &&
						typeof data88 == 'object' &&
						!Array.isArray(data88)
					) {
						for (const key16 in data88) {
							if (
								!func2.call(
									schema12.properties.postTypes
										.additionalProperties.anyOf[0]
										.properties,
									key16
								)
							) {
								const err169 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1'),
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/additionalProperties',
									keyword: 'additionalProperties',
									params: { additionalProperty: key16 },
									message:
										'must NOT have additional properties',
								};
								if (vErrors === null) {
									vErrors = [err169];
								} else {
									vErrors.push(err169);
								}
								errors++;
							}
						}
						if (data88.label !== undefined) {
							if (typeof data88.label !== 'string') {
								const err170 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/label',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/label/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err170];
								} else {
									vErrors.push(err170);
								}
								errors++;
							}
						}
						if (data88.labels !== undefined) {
							let data90 = data88.labels;
							if (
								data90 &&
								typeof data90 == 'object' &&
								!Array.isArray(data90)
							) {
								for (const key17 in data90) {
									if (
										!func2.call(
											schema12.properties.postTypes
												.additionalProperties.anyOf[0]
												.properties.labels.properties,
											key17
										)
									) {
										if (typeof data90[key17] !== 'string') {
											const err171 = {
												instancePath:
													instancePath +
													'/postTypes/' +
													key15
														.replace(/~/g, '~0')
														.replace(/\//g, '~1') +
													'/labels/' +
													key17
														.replace(/~/g, '~0')
														.replace(/\//g, '~1'),
												schemaPath:
													'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/additionalProperties/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err171];
											} else {
												vErrors.push(err171);
											}
											errors++;
										}
									}
								}
								if (data90.name !== undefined) {
									if (typeof data90.name !== 'string') {
										const err172 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/name',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/name/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err172];
										} else {
											vErrors.push(err172);
										}
										errors++;
									}
								}
								if (data90.singular_name !== undefined) {
									if (
										typeof data90.singular_name !== 'string'
									) {
										const err173 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/singular_name',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/singular_name/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err173];
										} else {
											vErrors.push(err173);
										}
										errors++;
									}
								}
								if (data90.add_new !== undefined) {
									if (typeof data90.add_new !== 'string') {
										const err174 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/add_new',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/add_new/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err174];
										} else {
											vErrors.push(err174);
										}
										errors++;
									}
								}
								if (data90.add_new_item !== undefined) {
									if (
										typeof data90.add_new_item !== 'string'
									) {
										const err175 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/add_new_item',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/add_new_item/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err175];
										} else {
											vErrors.push(err175);
										}
										errors++;
									}
								}
								if (data90.edit_item !== undefined) {
									if (typeof data90.edit_item !== 'string') {
										const err176 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/edit_item',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/edit_item/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err176];
										} else {
											vErrors.push(err176);
										}
										errors++;
									}
								}
								if (data90.new_item !== undefined) {
									if (typeof data90.new_item !== 'string') {
										const err177 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/new_item',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/new_item/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err177];
										} else {
											vErrors.push(err177);
										}
										errors++;
									}
								}
								if (data90.view_item !== undefined) {
									if (typeof data90.view_item !== 'string') {
										const err178 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/view_item',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/view_item/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err178];
										} else {
											vErrors.push(err178);
										}
										errors++;
									}
								}
								if (data90.view_items !== undefined) {
									if (typeof data90.view_items !== 'string') {
										const err179 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/view_items',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/view_items/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err179];
										} else {
											vErrors.push(err179);
										}
										errors++;
									}
								}
								if (data90.search_items !== undefined) {
									if (
										typeof data90.search_items !== 'string'
									) {
										const err180 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/search_items',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/search_items/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err180];
										} else {
											vErrors.push(err180);
										}
										errors++;
									}
								}
								if (data90.not_found !== undefined) {
									if (typeof data90.not_found !== 'string') {
										const err181 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/not_found',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/not_found/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err181];
										} else {
											vErrors.push(err181);
										}
										errors++;
									}
								}
								if (data90.not_found_in_trash !== undefined) {
									if (
										typeof data90.not_found_in_trash !==
										'string'
									) {
										const err182 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/not_found_in_trash',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/not_found_in_trash/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err182];
										} else {
											vErrors.push(err182);
										}
										errors++;
									}
								}
								if (data90.parent_item_colon !== undefined) {
									if (
										typeof data90.parent_item_colon !==
										'string'
									) {
										const err183 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/parent_item_colon',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/parent_item_colon/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err183];
										} else {
											vErrors.push(err183);
										}
										errors++;
									}
								}
								if (data90.all_items !== undefined) {
									if (typeof data90.all_items !== 'string') {
										const err184 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/all_items',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/all_items/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err184];
										} else {
											vErrors.push(err184);
										}
										errors++;
									}
								}
								if (data90.archives !== undefined) {
									if (typeof data90.archives !== 'string') {
										const err185 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/archives',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/archives/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err185];
										} else {
											vErrors.push(err185);
										}
										errors++;
									}
								}
								if (data90.attributes !== undefined) {
									if (typeof data90.attributes !== 'string') {
										const err186 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/attributes',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/attributes/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err186];
										} else {
											vErrors.push(err186);
										}
										errors++;
									}
								}
								if (data90.insert_into_item !== undefined) {
									if (
										typeof data90.insert_into_item !==
										'string'
									) {
										const err187 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/insert_into_item',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/insert_into_item/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err187];
										} else {
											vErrors.push(err187);
										}
										errors++;
									}
								}
								if (
									data90.uploaded_to_this_item !== undefined
								) {
									if (
										typeof data90.uploaded_to_this_item !==
										'string'
									) {
										const err188 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/uploaded_to_this_item',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/uploaded_to_this_item/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err188];
										} else {
											vErrors.push(err188);
										}
										errors++;
									}
								}
								if (data90.featured_image !== undefined) {
									if (
										typeof data90.featured_image !==
										'string'
									) {
										const err189 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/featured_image',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/featured_image/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err189];
										} else {
											vErrors.push(err189);
										}
										errors++;
									}
								}
								if (data90.set_featured_image !== undefined) {
									if (
										typeof data90.set_featured_image !==
										'string'
									) {
										const err190 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/set_featured_image',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/set_featured_image/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err190];
										} else {
											vErrors.push(err190);
										}
										errors++;
									}
								}
								if (
									data90.remove_featured_image !== undefined
								) {
									if (
										typeof data90.remove_featured_image !==
										'string'
									) {
										const err191 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/remove_featured_image',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/remove_featured_image/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err191];
										} else {
											vErrors.push(err191);
										}
										errors++;
									}
								}
								if (data90.use_featured_image !== undefined) {
									if (
										typeof data90.use_featured_image !==
										'string'
									) {
										const err192 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/use_featured_image',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/use_featured_image/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err192];
										} else {
											vErrors.push(err192);
										}
										errors++;
									}
								}
								if (data90.menu_name !== undefined) {
									if (typeof data90.menu_name !== 'string') {
										const err193 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/menu_name',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/menu_name/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err193];
										} else {
											vErrors.push(err193);
										}
										errors++;
									}
								}
								if (data90.filter_items_list !== undefined) {
									if (
										typeof data90.filter_items_list !==
										'string'
									) {
										const err194 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/filter_items_list',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/filter_items_list/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err194];
										} else {
											vErrors.push(err194);
										}
										errors++;
									}
								}
								if (data90.filter_by_date !== undefined) {
									if (
										typeof data90.filter_by_date !==
										'string'
									) {
										const err195 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/filter_by_date',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/filter_by_date/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err195];
										} else {
											vErrors.push(err195);
										}
										errors++;
									}
								}
								if (
									data90.items_list_navigation !== undefined
								) {
									if (
										typeof data90.items_list_navigation !==
										'string'
									) {
										const err196 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/items_list_navigation',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/items_list_navigation/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err196];
										} else {
											vErrors.push(err196);
										}
										errors++;
									}
								}
								if (data90.items_list !== undefined) {
									if (typeof data90.items_list !== 'string') {
										const err197 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/items_list',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/items_list/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err197];
										} else {
											vErrors.push(err197);
										}
										errors++;
									}
								}
								if (data90.item_published !== undefined) {
									if (
										typeof data90.item_published !==
										'string'
									) {
										const err198 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/item_published',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/item_published/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err198];
										} else {
											vErrors.push(err198);
										}
										errors++;
									}
								}
								if (
									data90.item_published_privately !==
									undefined
								) {
									if (
										typeof data90.item_published_privately !==
										'string'
									) {
										const err199 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/item_published_privately',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/item_published_privately/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err199];
										} else {
											vErrors.push(err199);
										}
										errors++;
									}
								}
								if (
									data90.item_reverted_to_draft !== undefined
								) {
									if (
										typeof data90.item_reverted_to_draft !==
										'string'
									) {
										const err200 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/item_reverted_to_draft',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/item_reverted_to_draft/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err200];
										} else {
											vErrors.push(err200);
										}
										errors++;
									}
								}
								if (data90.item_trashed !== undefined) {
									if (
										typeof data90.item_trashed !== 'string'
									) {
										const err201 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/item_trashed',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/item_trashed/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err201];
										} else {
											vErrors.push(err201);
										}
										errors++;
									}
								}
								if (data90.item_scheduled !== undefined) {
									if (
										typeof data90.item_scheduled !==
										'string'
									) {
										const err202 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/item_scheduled',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/item_scheduled/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err202];
										} else {
											vErrors.push(err202);
										}
										errors++;
									}
								}
								if (data90.item_updated !== undefined) {
									if (
										typeof data90.item_updated !== 'string'
									) {
										const err203 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/item_updated',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/item_updated/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err203];
										} else {
											vErrors.push(err203);
										}
										errors++;
									}
								}
								if (data90.item_link !== undefined) {
									if (typeof data90.item_link !== 'string') {
										const err204 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/item_link',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/item_link/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err204];
										} else {
											vErrors.push(err204);
										}
										errors++;
									}
								}
								if (
									data90.item_link_description !== undefined
								) {
									if (
										typeof data90.item_link_description !==
										'string'
									) {
										const err205 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/labels/item_link_description',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/properties/item_link_description/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err205];
										} else {
											vErrors.push(err205);
										}
										errors++;
									}
								}
							} else {
								const err206 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/labels',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/labels/type',
									keyword: 'type',
									params: { type: 'object' },
									message: 'must be object',
								};
								if (vErrors === null) {
									vErrors = [err206];
								} else {
									vErrors.push(err206);
								}
								errors++;
							}
						}
						if (data88.description !== undefined) {
							if (typeof data88.description !== 'string') {
								const err207 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/description',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/description/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err207];
								} else {
									vErrors.push(err207);
								}
								errors++;
							}
						}
						if (data88.public !== undefined) {
							if (typeof data88.public !== 'boolean') {
								const err208 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/public',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/public/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err208];
								} else {
									vErrors.push(err208);
								}
								errors++;
							}
						}
						if (data88.hierarchical !== undefined) {
							if (typeof data88.hierarchical !== 'boolean') {
								const err209 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/hierarchical',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/hierarchical/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err209];
								} else {
									vErrors.push(err209);
								}
								errors++;
							}
						}
						if (data88.exclude_from_search !== undefined) {
							if (
								typeof data88.exclude_from_search !== 'boolean'
							) {
								const err210 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/exclude_from_search',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/exclude_from_search/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err210];
								} else {
									vErrors.push(err210);
								}
								errors++;
							}
						}
						if (data88.publicly_queryable !== undefined) {
							if (
								typeof data88.publicly_queryable !== 'boolean'
							) {
								const err211 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/publicly_queryable',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/publicly_queryable/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err211];
								} else {
									vErrors.push(err211);
								}
								errors++;
							}
						}
						if (data88.show_ui !== undefined) {
							if (typeof data88.show_ui !== 'boolean') {
								const err212 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/show_ui',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/show_ui/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err212];
								} else {
									vErrors.push(err212);
								}
								errors++;
							}
						}
						if (data88.show_in_menu !== undefined) {
							let data132 = data88.show_in_menu;
							if (
								typeof data132 !== 'boolean' &&
								typeof data132 !== 'string'
							) {
								const err213 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/show_in_menu',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/show_in_menu/type',
									keyword: 'type',
									params: {
										type: schema12.properties.postTypes
											.additionalProperties.anyOf[0]
											.properties.show_in_menu.type,
									},
									message: 'must be boolean,string',
								};
								if (vErrors === null) {
									vErrors = [err213];
								} else {
									vErrors.push(err213);
								}
								errors++;
							}
						}
						if (data88.show_in_admin_bar !== undefined) {
							if (typeof data88.show_in_admin_bar !== 'boolean') {
								const err214 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/show_in_admin_bar',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/show_in_admin_bar/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err214];
								} else {
									vErrors.push(err214);
								}
								errors++;
							}
						}
						if (data88.show_in_nav_menus !== undefined) {
							if (typeof data88.show_in_nav_menus !== 'boolean') {
								const err215 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/show_in_nav_menus',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/show_in_nav_menus/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err215];
								} else {
									vErrors.push(err215);
								}
								errors++;
							}
						}
						if (data88.show_in_rest !== undefined) {
							if (typeof data88.show_in_rest !== 'boolean') {
								const err216 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/show_in_rest',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/show_in_rest/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err216];
								} else {
									vErrors.push(err216);
								}
								errors++;
							}
						}
						if (data88.rest_base !== undefined) {
							if (typeof data88.rest_base !== 'string') {
								const err217 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/rest_base',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/rest_base/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err217];
								} else {
									vErrors.push(err217);
								}
								errors++;
							}
						}
						if (data88.rest_namespace !== undefined) {
							if (typeof data88.rest_namespace !== 'string') {
								const err218 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/rest_namespace',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/rest_namespace/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err218];
								} else {
									vErrors.push(err218);
								}
								errors++;
							}
						}
						if (data88.rest_controller_class !== undefined) {
							if (
								typeof data88.rest_controller_class !== 'string'
							) {
								const err219 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/rest_controller_class',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/rest_controller_class/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err219];
								} else {
									vErrors.push(err219);
								}
								errors++;
							}
						}
						if (data88.menu_icon !== undefined) {
							if (typeof data88.menu_icon !== 'string') {
								const err220 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/menu_icon',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/menu_icon/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err220];
								} else {
									vErrors.push(err220);
								}
								errors++;
							}
						}
						if (data88.menu_position !== undefined) {
							let data140 = data88.menu_position;
							if (
								typeof data140 !== 'string' &&
								!(
									typeof data140 == 'number' &&
									isFinite(data140)
								)
							) {
								const err221 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/menu_position',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/menu_position/type',
									keyword: 'type',
									params: {
										type: schema12.properties.postTypes
											.additionalProperties.anyOf[0]
											.properties.menu_position.type,
									},
									message: 'must be string,number',
								};
								if (vErrors === null) {
									vErrors = [err221];
								} else {
									vErrors.push(err221);
								}
								errors++;
							}
						}
						if (data88.rename_capabilities !== undefined) {
							if (
								typeof data88.rename_capabilities !== 'boolean'
							) {
								const err222 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/rename_capabilities',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/rename_capabilities/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err222];
								} else {
									vErrors.push(err222);
								}
								errors++;
							}
						}
						if (data88.singular_capability_name !== undefined) {
							if (
								typeof data88.singular_capability_name !==
								'string'
							) {
								const err223 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/singular_capability_name',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/singular_capability_name/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err223];
								} else {
									vErrors.push(err223);
								}
								errors++;
							}
						}
						if (data88.plural_capability_name !== undefined) {
							if (
								typeof data88.plural_capability_name !==
								'string'
							) {
								const err224 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/plural_capability_name',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/plural_capability_name/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err224];
								} else {
									vErrors.push(err224);
								}
								errors++;
							}
						}
						if (data88.taxonomies !== undefined) {
							let data144 = data88.taxonomies;
							if (Array.isArray(data144)) {
								const len12 = data144.length;
								for (let i13 = 0; i13 < len12; i13++) {
									if (typeof data144[i13] !== 'string') {
										const err225 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/taxonomies/' +
												i13,
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/taxonomies/items/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err225];
										} else {
											vErrors.push(err225);
										}
										errors++;
									}
								}
							} else {
								const err226 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/taxonomies',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/taxonomies/type',
									keyword: 'type',
									params: { type: 'array' },
									message: 'must be array',
								};
								if (vErrors === null) {
									vErrors = [err226];
								} else {
									vErrors.push(err226);
								}
								errors++;
							}
						}
						if (data88.query_var_name !== undefined) {
							if (typeof data88.query_var_name !== 'string') {
								const err227 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/query_var_name',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/query_var_name/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err227];
								} else {
									vErrors.push(err227);
								}
								errors++;
							}
						}
						if (data88.register_meta_box_cb !== undefined) {
							if (
								typeof data88.register_meta_box_cb !== 'string'
							) {
								const err228 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/register_meta_box_cb',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/register_meta_box_cb/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err228];
								} else {
									vErrors.push(err228);
								}
								errors++;
							}
						}
						if (data88.enter_title_here !== undefined) {
							if (typeof data88.enter_title_here !== 'string') {
								const err229 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/enter_title_here',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/enter_title_here/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err229];
								} else {
									vErrors.push(err229);
								}
								errors++;
							}
						}
						if (data88.capability_type !== undefined) {
							let data149 = data88.capability_type;
							const _errs391 = errors;
							let valid81 = false;
							const _errs392 = errors;
							if (typeof data149 !== 'string') {
								const err230 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/capability_type',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/capability_type/anyOf/0/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err230];
								} else {
									vErrors.push(err230);
								}
								errors++;
							}
							var _valid17 = _errs392 === errors;
							valid81 = valid81 || _valid17;
							if (!valid81) {
								const _errs394 = errors;
								if (Array.isArray(data149)) {
									if (data149.length > 2) {
										const err231 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/capability_type',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/capability_type/anyOf/1/maxItems',
											keyword: 'maxItems',
											params: { limit: 2 },
											message:
												'must NOT have more than 2 items',
										};
										if (vErrors === null) {
											vErrors = [err231];
										} else {
											vErrors.push(err231);
										}
										errors++;
									}
									if (data149.length < 2) {
										const err232 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/capability_type',
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/capability_type/anyOf/1/minItems',
											keyword: 'minItems',
											params: { limit: 2 },
											message:
												'must NOT have fewer than 2 items',
										};
										if (vErrors === null) {
											vErrors = [err232];
										} else {
											vErrors.push(err232);
										}
										errors++;
									}
									const len13 = data149.length;
									for (let i14 = 0; i14 < len13; i14++) {
										if (typeof data149[i14] !== 'string') {
											const err233 = {
												instancePath:
													instancePath +
													'/postTypes/' +
													key15
														.replace(/~/g, '~0')
														.replace(/\//g, '~1') +
													'/capability_type/' +
													i14,
												schemaPath:
													'#/properties/postTypes/additionalProperties/anyOf/0/properties/capability_type/anyOf/1/items/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err233];
											} else {
												vErrors.push(err233);
											}
											errors++;
										}
									}
								} else {
									const err234 = {
										instancePath:
											instancePath +
											'/postTypes/' +
											key15
												.replace(/~/g, '~0')
												.replace(/\//g, '~1') +
											'/capability_type',
										schemaPath:
											'#/properties/postTypes/additionalProperties/anyOf/0/properties/capability_type/anyOf/1/type',
										keyword: 'type',
										params: { type: 'array' },
										message: 'must be array',
									};
									if (vErrors === null) {
										vErrors = [err234];
									} else {
										vErrors.push(err234);
									}
									errors++;
								}
								var _valid17 = _errs394 === errors;
								valid81 = valid81 || _valid17;
							}
							if (!valid81) {
								const err235 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/capability_type',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/capability_type/anyOf',
									keyword: 'anyOf',
									params: {},
									message: 'must match a schema in anyOf',
								};
								if (vErrors === null) {
									vErrors = [err235];
								} else {
									vErrors.push(err235);
								}
								errors++;
							} else {
								errors = _errs391;
								if (vErrors !== null) {
									if (_errs391) {
										vErrors.length = _errs391;
									} else {
										vErrors = null;
									}
								}
							}
						}
						if (data88.capabilities !== undefined) {
							let data151 = data88.capabilities;
							if (
								data151 &&
								typeof data151 == 'object' &&
								!Array.isArray(data151)
							) {
								for (const key18 in data151) {
									if (typeof data151[key18] !== 'string') {
										const err236 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/capabilities/' +
												key18
													.replace(/~/g, '~0')
													.replace(/\//g, '~1'),
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/capabilities/additionalProperties/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err236];
										} else {
											vErrors.push(err236);
										}
										errors++;
									}
								}
							} else {
								const err237 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/capabilities',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/capabilities/type',
									keyword: 'type',
									params: { type: 'object' },
									message: 'must be object',
								};
								if (vErrors === null) {
									vErrors = [err237];
								} else {
									vErrors.push(err237);
								}
								errors++;
							}
						}
						if (data88.map_meta_cap !== undefined) {
							if (typeof data88.map_meta_cap !== 'boolean') {
								const err238 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/map_meta_cap',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/map_meta_cap/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err238];
								} else {
									vErrors.push(err238);
								}
								errors++;
							}
						}
						if (data88.supports !== undefined) {
							let data154 = data88.supports;
							if (Array.isArray(data154)) {
								const len14 = data154.length;
								for (let i15 = 0; i15 < len14; i15++) {
									let data155 = data154[i15];
									if (typeof data155 !== 'string') {
										const err239 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/supports/' +
												i15,
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/supports/allOf/0/items/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err239];
										} else {
											vErrors.push(err239);
										}
										errors++;
									}
									if (
										!(
											data155 === 'title' ||
											data155 === 'editor' ||
											data155 === 'author' ||
											data155 === 'thumbnail' ||
											data155 === 'excerpt' ||
											data155 === 'trackbacks' ||
											data155 === 'custom-fields' ||
											data155 === 'comments' ||
											data155 === 'revisions' ||
											data155 === 'page-attributes' ||
											data155 === 'post-formats'
										)
									) {
										const err240 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/supports/' +
												i15,
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/supports/allOf/0/items/enum',
											keyword: 'enum',
											params: {
												allowedValues:
													schema12.properties
														.postTypes
														.additionalProperties
														.anyOf[0].properties
														.supports.allOf[0].items
														.enum,
											},
											message:
												'must be equal to one of the allowed values',
										};
										if (vErrors === null) {
											vErrors = [err240];
										} else {
											vErrors.push(err240);
										}
										errors++;
									}
								}
							} else {
								const err241 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/supports',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/supports/allOf/0/type',
									keyword: 'type',
									params: { type: 'array' },
									message: 'must be array',
								};
								if (vErrors === null) {
									vErrors = [err241];
								} else {
									vErrors.push(err241);
								}
								errors++;
							}
							if (Array.isArray(data154)) {
								const len15 = data154.length;
								for (let i16 = 0; i16 < len15; i16++) {
									if (typeof data154[i16] !== 'string') {
										const err242 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/supports/' +
												i16,
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/supports/allOf/1/items/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err242];
										} else {
											vErrors.push(err242);
										}
										errors++;
									}
								}
							} else {
								const err243 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/supports',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/supports/allOf/1/type',
									keyword: 'type',
									params: { type: 'array' },
									message: 'must be array',
								};
								if (vErrors === null) {
									vErrors = [err243];
								} else {
									vErrors.push(err243);
								}
								errors++;
							}
						}
						if (data88.has_archive !== undefined) {
							let data157 = data88.has_archive;
							if (
								typeof data157 !== 'boolean' &&
								typeof data157 !== 'string'
							) {
								const err244 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/has_archive',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/has_archive/type',
									keyword: 'type',
									params: {
										type: schema12.properties.postTypes
											.additionalProperties.anyOf[0]
											.properties.has_archive.type,
									},
									message: 'must be boolean,string',
								};
								if (vErrors === null) {
									vErrors = [err244];
								} else {
									vErrors.push(err244);
								}
								errors++;
							}
						}
						if (data88.rewrite !== undefined) {
							let data158 = data88.rewrite;
							const _errs417 = errors;
							let valid90 = false;
							const _errs418 = errors;
							if (typeof data158 !== 'boolean') {
								const err245 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/rewrite',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/rewrite/anyOf/0/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err245];
								} else {
									vErrors.push(err245);
								}
								errors++;
							}
							var _valid18 = _errs418 === errors;
							valid90 = valid90 || _valid18;
							if (!valid90) {
								const _errs420 = errors;
								if (
									data158 &&
									typeof data158 == 'object' &&
									!Array.isArray(data158)
								) {
									for (const key19 in data158) {
										if (
											!(
												key19 === 'slug' ||
												key19 === 'with_front' ||
												key19 === 'pages' ||
												key19 === 'feeds' ||
												key19 === 'ep_mask'
											)
										) {
											const err246 = {
												instancePath:
													instancePath +
													'/postTypes/' +
													key15
														.replace(/~/g, '~0')
														.replace(/\//g, '~1') +
													'/rewrite',
												schemaPath:
													'#/properties/postTypes/additionalProperties/anyOf/0/properties/rewrite/anyOf/1/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key19,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err246];
											} else {
												vErrors.push(err246);
											}
											errors++;
										}
									}
									if (data158.slug !== undefined) {
										if (typeof data158.slug !== 'string') {
											const err247 = {
												instancePath:
													instancePath +
													'/postTypes/' +
													key15
														.replace(/~/g, '~0')
														.replace(/\//g, '~1') +
													'/rewrite/slug',
												schemaPath:
													'#/properties/postTypes/additionalProperties/anyOf/0/properties/rewrite/anyOf/1/properties/slug/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err247];
											} else {
												vErrors.push(err247);
											}
											errors++;
										}
									}
									if (data158.with_front !== undefined) {
										if (
											typeof data158.with_front !==
											'boolean'
										) {
											const err248 = {
												instancePath:
													instancePath +
													'/postTypes/' +
													key15
														.replace(/~/g, '~0')
														.replace(/\//g, '~1') +
													'/rewrite/with_front',
												schemaPath:
													'#/properties/postTypes/additionalProperties/anyOf/0/properties/rewrite/anyOf/1/properties/with_front/type',
												keyword: 'type',
												params: { type: 'boolean' },
												message: 'must be boolean',
											};
											if (vErrors === null) {
												vErrors = [err248];
											} else {
												vErrors.push(err248);
											}
											errors++;
										}
									}
									if (data158.pages !== undefined) {
										if (
											typeof data158.pages !== 'boolean'
										) {
											const err249 = {
												instancePath:
													instancePath +
													'/postTypes/' +
													key15
														.replace(/~/g, '~0')
														.replace(/\//g, '~1') +
													'/rewrite/pages',
												schemaPath:
													'#/properties/postTypes/additionalProperties/anyOf/0/properties/rewrite/anyOf/1/properties/pages/type',
												keyword: 'type',
												params: { type: 'boolean' },
												message: 'must be boolean',
											};
											if (vErrors === null) {
												vErrors = [err249];
											} else {
												vErrors.push(err249);
											}
											errors++;
										}
									}
									if (data158.feeds !== undefined) {
										if (
											typeof data158.feeds !== 'boolean'
										) {
											const err250 = {
												instancePath:
													instancePath +
													'/postTypes/' +
													key15
														.replace(/~/g, '~0')
														.replace(/\//g, '~1') +
													'/rewrite/feeds',
												schemaPath:
													'#/properties/postTypes/additionalProperties/anyOf/0/properties/rewrite/anyOf/1/properties/feeds/type',
												keyword: 'type',
												params: { type: 'boolean' },
												message: 'must be boolean',
											};
											if (vErrors === null) {
												vErrors = [err250];
											} else {
												vErrors.push(err250);
											}
											errors++;
										}
									}
									if (data158.ep_mask !== undefined) {
										let data163 = data158.ep_mask;
										if (
											!(
												typeof data163 == 'number' &&
												isFinite(data163)
											)
										) {
											const err251 = {
												instancePath:
													instancePath +
													'/postTypes/' +
													key15
														.replace(/~/g, '~0')
														.replace(/\//g, '~1') +
													'/rewrite/ep_mask',
												schemaPath:
													'#/properties/postTypes/additionalProperties/anyOf/0/properties/rewrite/anyOf/1/properties/ep_mask/type',
												keyword: 'type',
												params: { type: 'number' },
												message: 'must be number',
											};
											if (vErrors === null) {
												vErrors = [err251];
											} else {
												vErrors.push(err251);
											}
											errors++;
										}
									}
								} else {
									const err252 = {
										instancePath:
											instancePath +
											'/postTypes/' +
											key15
												.replace(/~/g, '~0')
												.replace(/\//g, '~1') +
											'/rewrite',
										schemaPath:
											'#/properties/postTypes/additionalProperties/anyOf/0/properties/rewrite/anyOf/1/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err252];
									} else {
										vErrors.push(err252);
									}
									errors++;
								}
								var _valid18 = _errs420 === errors;
								valid90 = valid90 || _valid18;
							}
							if (!valid90) {
								const err253 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/rewrite',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/rewrite/anyOf',
									keyword: 'anyOf',
									params: {},
									message: 'must match a schema in anyOf',
								};
								if (vErrors === null) {
									vErrors = [err253];
								} else {
									vErrors.push(err253);
								}
								errors++;
							} else {
								errors = _errs417;
								if (vErrors !== null) {
									if (_errs417) {
										vErrors.length = _errs417;
									} else {
										vErrors = null;
									}
								}
							}
						}
						if (data88.query_var !== undefined) {
							let data164 = data88.query_var;
							if (
								typeof data164 !== 'boolean' &&
								typeof data164 !== 'string'
							) {
								const err254 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/query_var',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/query_var/type',
									keyword: 'type',
									params: {
										type: schema12.properties.postTypes
											.additionalProperties.anyOf[0]
											.properties.query_var.type,
									},
									message: 'must be boolean,string',
								};
								if (vErrors === null) {
									vErrors = [err254];
								} else {
									vErrors.push(err254);
								}
								errors++;
							}
						}
						if (data88.can_export !== undefined) {
							if (typeof data88.can_export !== 'boolean') {
								const err255 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/can_export',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/can_export/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err255];
								} else {
									vErrors.push(err255);
								}
								errors++;
							}
						}
						if (data88.delete_with_user !== undefined) {
							if (typeof data88.delete_with_user !== 'boolean') {
								const err256 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/delete_with_user',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/delete_with_user/type',
									keyword: 'type',
									params: { type: 'boolean' },
									message: 'must be boolean',
								};
								if (vErrors === null) {
									vErrors = [err256];
								} else {
									vErrors.push(err256);
								}
								errors++;
							}
						}
						if (data88.template !== undefined) {
							let data167 = data88.template;
							if (Array.isArray(data167)) {
								const len16 = data167.length;
								for (let i17 = 0; i17 < len16; i17++) {
									let data168 = data167[i17];
									if (Array.isArray(data168)) {
										if (data168.length > 2) {
											const err257 = {
												instancePath:
													instancePath +
													'/postTypes/' +
													key15
														.replace(/~/g, '~0')
														.replace(/\//g, '~1') +
													'/template/' +
													i17,
												schemaPath:
													'#/properties/postTypes/additionalProperties/anyOf/0/properties/template/items/maxItems',
												keyword: 'maxItems',
												params: { limit: 2 },
												message:
													'must NOT have more than 2 items',
											};
											if (vErrors === null) {
												vErrors = [err257];
											} else {
												vErrors.push(err257);
											}
											errors++;
										}
										if (data168.length < 2) {
											const err258 = {
												instancePath:
													instancePath +
													'/postTypes/' +
													key15
														.replace(/~/g, '~0')
														.replace(/\//g, '~1') +
													'/template/' +
													i17,
												schemaPath:
													'#/properties/postTypes/additionalProperties/anyOf/0/properties/template/items/minItems',
												keyword: 'minItems',
												params: { limit: 2 },
												message:
													'must NOT have fewer than 2 items',
											};
											if (vErrors === null) {
												vErrors = [err258];
											} else {
												vErrors.push(err258);
											}
											errors++;
										}
										const len17 = data168.length;
										if (len17 > 0) {
											if (
												typeof data168[0] !== 'string'
											) {
												const err259 = {
													instancePath:
														instancePath +
														'/postTypes/' +
														key15
															.replace(/~/g, '~0')
															.replace(
																/\//g,
																'~1'
															) +
														'/template/' +
														i17 +
														'/0',
													schemaPath:
														'#/properties/postTypes/additionalProperties/anyOf/0/properties/template/items/items/0/type',
													keyword: 'type',
													params: { type: 'string' },
													message: 'must be string',
												};
												if (vErrors === null) {
													vErrors = [err259];
												} else {
													vErrors.push(err259);
												}
												errors++;
											}
										}
										if (len17 > 1) {
											let data170 = data168[1];
											if (
												data170 &&
												typeof data170 == 'object' &&
												!Array.isArray(data170)
											) {
												for (const key20 in data170) {
													let data171 =
														data170[key20];
													const _errs449 = errors;
													let valid96 = false;
													const _errs450 = errors;
													if (
														typeof data171 !==
														'string'
													) {
														const err260 = {
															instancePath:
																instancePath +
																'/postTypes/' +
																key15
																	.replace(
																		/~/g,
																		'~0'
																	)
																	.replace(
																		/\//g,
																		'~1'
																	) +
																'/template/' +
																i17 +
																'/1/' +
																key20
																	.replace(
																		/~/g,
																		'~0'
																	)
																	.replace(
																		/\//g,
																		'~1'
																	),
															schemaPath:
																'#/properties/postTypes/additionalProperties/anyOf/0/properties/template/items/items/1/additionalProperties/anyOf/0/type',
															keyword: 'type',
															params: {
																type: 'string',
															},
															message:
																'must be string',
														};
														if (vErrors === null) {
															vErrors = [err260];
														} else {
															vErrors.push(
																err260
															);
														}
														errors++;
													}
													var _valid19 =
														_errs450 === errors;
													valid96 =
														valid96 || _valid19;
													if (!valid96) {
														const _errs452 = errors;
														if (
															typeof data171 !==
															'boolean'
														) {
															const err261 = {
																instancePath:
																	instancePath +
																	'/postTypes/' +
																	key15
																		.replace(
																			/~/g,
																			'~0'
																		)
																		.replace(
																			/\//g,
																			'~1'
																		) +
																	'/template/' +
																	i17 +
																	'/1/' +
																	key20
																		.replace(
																			/~/g,
																			'~0'
																		)
																		.replace(
																			/\//g,
																			'~1'
																		),
																schemaPath:
																	'#/properties/postTypes/additionalProperties/anyOf/0/properties/template/items/items/1/additionalProperties/anyOf/1/type',
																keyword: 'type',
																params: {
																	type: 'boolean',
																},
																message:
																	'must be boolean',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err261,
																];
															} else {
																vErrors.push(
																	err261
																);
															}
															errors++;
														}
														var _valid19 =
															_errs452 === errors;
														valid96 =
															valid96 || _valid19;
														if (!valid96) {
															const _errs454 =
																errors;
															if (
																!(
																	typeof data171 ==
																		'number' &&
																	isFinite(
																		data171
																	)
																)
															) {
																const err262 = {
																	instancePath:
																		instancePath +
																		'/postTypes/' +
																		key15
																			.replace(
																				/~/g,
																				'~0'
																			)
																			.replace(
																				/\//g,
																				'~1'
																			) +
																		'/template/' +
																		i17 +
																		'/1/' +
																		key20
																			.replace(
																				/~/g,
																				'~0'
																			)
																			.replace(
																				/\//g,
																				'~1'
																			),
																	schemaPath:
																		'#/properties/postTypes/additionalProperties/anyOf/0/properties/template/items/items/1/additionalProperties/anyOf/2/type',
																	keyword:
																		'type',
																	params: {
																		type: 'number',
																	},
																	message:
																		'must be number',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err262,
																	];
																} else {
																	vErrors.push(
																		err262
																	);
																}
																errors++;
															}
															var _valid19 =
																_errs454 ===
																errors;
															valid96 =
																valid96 ||
																_valid19;
															if (!valid96) {
																const _errs456 =
																	errors;
																if (
																	Array.isArray(
																		data171
																	)
																) {
																	const len18 =
																		data171.length;
																	for (
																		let i18 = 0;
																		i18 <
																		len18;
																		i18++
																	) {
																		if (
																			!validate34(
																				data171[
																					i18
																				],
																				{
																					instancePath:
																						instancePath +
																						'/postTypes/' +
																						key15
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/template/' +
																						i17 +
																						'/1/' +
																						key20
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/' +
																						i18,
																					parentData:
																						data171,
																					parentDataProperty:
																						i18,
																					rootData,
																				}
																			)
																		) {
																			vErrors =
																				vErrors ===
																				null
																					? validate34.errors
																					: vErrors.concat(
																							validate34.errors
																						);
																			errors =
																				vErrors.length;
																		}
																	}
																} else {
																	const err263 =
																		{
																			instancePath:
																				instancePath +
																				'/postTypes/' +
																				key15
																					.replace(
																						/~/g,
																						'~0'
																					)
																					.replace(
																						/\//g,
																						'~1'
																					) +
																				'/template/' +
																				i17 +
																				'/1/' +
																				key20
																					.replace(
																						/~/g,
																						'~0'
																					)
																					.replace(
																						/\//g,
																						'~1'
																					),
																			schemaPath:
																				'#/properties/postTypes/additionalProperties/anyOf/0/properties/template/items/items/1/additionalProperties/anyOf/3/type',
																			keyword:
																				'type',
																			params: {
																				type: 'array',
																			},
																			message:
																				'must be array',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err263,
																			];
																	} else {
																		vErrors.push(
																			err263
																		);
																	}
																	errors++;
																}
																var _valid19 =
																	_errs456 ===
																	errors;
																valid96 =
																	valid96 ||
																	_valid19;
																if (!valid96) {
																	const _errs459 =
																		errors;
																	if (
																		data171 &&
																		typeof data171 ==
																			'object' &&
																		!Array.isArray(
																			data171
																		)
																	) {
																		for (const key21 in data171) {
																			if (
																				!validate34(
																					data171[
																						key21
																					],
																					{
																						instancePath:
																							instancePath +
																							'/postTypes/' +
																							key15
																								.replace(
																									/~/g,
																									'~0'
																								)
																								.replace(
																									/\//g,
																									'~1'
																								) +
																							'/template/' +
																							i17 +
																							'/1/' +
																							key20
																								.replace(
																									/~/g,
																									'~0'
																								)
																								.replace(
																									/\//g,
																									'~1'
																								) +
																							'/' +
																							key21
																								.replace(
																									/~/g,
																									'~0'
																								)
																								.replace(
																									/\//g,
																									'~1'
																								),
																						parentData:
																							data171,
																						parentDataProperty:
																							key21,
																						rootData,
																					}
																				)
																			) {
																				vErrors =
																					vErrors ===
																					null
																						? validate34.errors
																						: vErrors.concat(
																								validate34.errors
																							);
																				errors =
																					vErrors.length;
																			}
																		}
																	} else {
																		const err264 =
																			{
																				instancePath:
																					instancePath +
																					'/postTypes/' +
																					key15
																						.replace(
																							/~/g,
																							'~0'
																						)
																						.replace(
																							/\//g,
																							'~1'
																						) +
																					'/template/' +
																					i17 +
																					'/1/' +
																					key20
																						.replace(
																							/~/g,
																							'~0'
																						)
																						.replace(
																							/\//g,
																							'~1'
																						),
																				schemaPath:
																					'#/properties/postTypes/additionalProperties/anyOf/0/properties/template/items/items/1/additionalProperties/anyOf/4/type',
																				keyword:
																					'type',
																				params: {
																					type: 'object',
																				},
																				message:
																					'must be object',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err264,
																				];
																		} else {
																			vErrors.push(
																				err264
																			);
																		}
																		errors++;
																	}
																	var _valid19 =
																		_errs459 ===
																		errors;
																	valid96 =
																		valid96 ||
																		_valid19;
																}
															}
														}
													}
													if (!valid96) {
														const err265 = {
															instancePath:
																instancePath +
																'/postTypes/' +
																key15
																	.replace(
																		/~/g,
																		'~0'
																	)
																	.replace(
																		/\//g,
																		'~1'
																	) +
																'/template/' +
																i17 +
																'/1/' +
																key20
																	.replace(
																		/~/g,
																		'~0'
																	)
																	.replace(
																		/\//g,
																		'~1'
																	),
															schemaPath:
																'#/properties/postTypes/additionalProperties/anyOf/0/properties/template/items/items/1/additionalProperties/anyOf',
															keyword: 'anyOf',
															params: {},
															message:
																'must match a schema in anyOf',
														};
														if (vErrors === null) {
															vErrors = [err265];
														} else {
															vErrors.push(
																err265
															);
														}
														errors++;
													} else {
														errors = _errs449;
														if (vErrors !== null) {
															if (_errs449) {
																vErrors.length =
																	_errs449;
															} else {
																vErrors = null;
															}
														}
													}
												}
											} else {
												const err266 = {
													instancePath:
														instancePath +
														'/postTypes/' +
														key15
															.replace(/~/g, '~0')
															.replace(
																/\//g,
																'~1'
															) +
														'/template/' +
														i17 +
														'/1',
													schemaPath:
														'#/properties/postTypes/additionalProperties/anyOf/0/properties/template/items/items/1/type',
													keyword: 'type',
													params: { type: 'object' },
													message: 'must be object',
												};
												if (vErrors === null) {
													vErrors = [err266];
												} else {
													vErrors.push(err266);
												}
												errors++;
											}
										}
									} else {
										const err267 = {
											instancePath:
												instancePath +
												'/postTypes/' +
												key15
													.replace(/~/g, '~0')
													.replace(/\//g, '~1') +
												'/template/' +
												i17,
											schemaPath:
												'#/properties/postTypes/additionalProperties/anyOf/0/properties/template/items/type',
											keyword: 'type',
											params: { type: 'array' },
											message: 'must be array',
										};
										if (vErrors === null) {
											vErrors = [err267];
										} else {
											vErrors.push(err267);
										}
										errors++;
									}
								}
							} else {
								const err268 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/template',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/template/type',
									keyword: 'type',
									params: { type: 'array' },
									message: 'must be array',
								};
								if (vErrors === null) {
									vErrors = [err268];
								} else {
									vErrors.push(err268);
								}
								errors++;
							}
						}
						if (data88.template_lock !== undefined) {
							let data174 = data88.template_lock;
							if (
								typeof data174 !== 'string' &&
								typeof data174 !== 'boolean'
							) {
								const err269 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/template_lock',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/template_lock/type',
									keyword: 'type',
									params: {
										type: schema12.properties.postTypes
											.additionalProperties.anyOf[0]
											.properties.template_lock.type,
									},
									message: 'must be string,boolean',
								};
								if (vErrors === null) {
									vErrors = [err269];
								} else {
									vErrors.push(err269);
								}
								errors++;
							}
							if (
								!(
									data174 === 'all' ||
									data174 === 'insert' ||
									data174 === false
								)
							) {
								const err270 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1') +
										'/template_lock',
									schemaPath:
										'#/properties/postTypes/additionalProperties/anyOf/0/properties/template_lock/enum',
									keyword: 'enum',
									params: {
										allowedValues:
											schema12.properties.postTypes
												.additionalProperties.anyOf[0]
												.properties.template_lock.enum,
									},
									message:
										'must be equal to one of the allowed values',
								};
								if (vErrors === null) {
									vErrors = [err270];
								} else {
									vErrors.push(err270);
								}
								errors++;
							}
						}
					} else {
						const err271 = {
							instancePath:
								instancePath +
								'/postTypes/' +
								key15.replace(/~/g, '~0').replace(/\//g, '~1'),
							schemaPath:
								'#/properties/postTypes/additionalProperties/anyOf/0/type',
							keyword: 'type',
							params: { type: 'object' },
							message: 'must be object',
						};
						if (vErrors === null) {
							vErrors = [err271];
						} else {
							vErrors.push(err271);
						}
						errors++;
					}
					var _valid16 = _errs266 === errors;
					valid75 = valid75 || _valid16;
					if (!valid75) {
						const _errs465 = errors;
						if (typeof data88 === 'string') {
							if (!pattern0.test(data88)) {
								const err272 = {
									instancePath:
										instancePath +
										'/postTypes/' +
										key15
											.replace(/~/g, '~0')
											.replace(/\//g, '~1'),
									schemaPath:
										'#/definitions/DataSources.ExecutionContextPath/pattern',
									keyword: 'pattern',
									params: {
										pattern:
											'^(?!.*(?:^|/)\\.\\.(?:/|$))(?:\\./|/).*$',
									},
									message:
										'must match pattern "' +
										'^(?!.*(?:^|/)\\.\\.(?:/|$))(?:\\./|/).*$' +
										'"',
								};
								if (vErrors === null) {
									vErrors = [err272];
								} else {
									vErrors.push(err272);
								}
								errors++;
							}
						} else {
							const err273 = {
								instancePath:
									instancePath +
									'/postTypes/' +
									key15
										.replace(/~/g, '~0')
										.replace(/\//g, '~1'),
								schemaPath:
									'#/definitions/DataSources.ExecutionContextPath/type',
								keyword: 'type',
								params: { type: 'string' },
								message: 'must be string',
							};
							if (vErrors === null) {
								vErrors = [err273];
							} else {
								vErrors.push(err273);
							}
							errors++;
						}
						var _valid16 = _errs465 === errors;
						valid75 = valid75 || _valid16;
					}
					if (!valid75) {
						const err274 = {
							instancePath:
								instancePath +
								'/postTypes/' +
								key15.replace(/~/g, '~0').replace(/\//g, '~1'),
							schemaPath:
								'#/properties/postTypes/additionalProperties/anyOf',
							keyword: 'anyOf',
							params: {},
							message: 'must match a schema in anyOf',
						};
						if (vErrors === null) {
							vErrors = [err274];
						} else {
							vErrors.push(err274);
						}
						errors++;
					} else {
						errors = _errs265;
						if (vErrors !== null) {
							if (_errs265) {
								vErrors.length = _errs265;
							} else {
								vErrors = null;
							}
						}
					}
				}
			} else {
				const err275 = {
					instancePath: instancePath + '/postTypes',
					schemaPath: '#/properties/postTypes/type',
					keyword: 'type',
					params: { type: 'object' },
					message: 'must be object',
				};
				if (vErrors === null) {
					vErrors = [err275];
				} else {
					vErrors.push(err275);
				}
				errors++;
			}
		}
		if (data.fonts !== undefined) {
			let data175 = data.fonts;
			if (
				data175 &&
				typeof data175 == 'object' &&
				!Array.isArray(data175)
			) {
				for (const key22 in data175) {
					let data176 = data175[key22];
					const _errs472 = errors;
					let valid102 = false;
					const _errs473 = errors;
					if (
						!validate40(data176, {
							instancePath:
								instancePath +
								'/fonts/' +
								key22.replace(/~/g, '~0').replace(/\//g, '~1'),
							parentData: data175,
							parentDataProperty: key22,
							rootData,
						})
					) {
						vErrors =
							vErrors === null
								? validate40.errors
								: vErrors.concat(validate40.errors);
						errors = vErrors.length;
					}
					var _valid20 = _errs473 === errors;
					valid102 = valid102 || _valid20;
					if (!valid102) {
						const _errs474 = errors;
						if (
							data176 &&
							typeof data176 == 'object' &&
							!Array.isArray(data176)
						) {
							if (data176.font_families === undefined) {
								const err276 = {
									instancePath:
										instancePath +
										'/fonts/' +
										key22
											.replace(/~/g, '~0')
											.replace(/\//g, '~1'),
									schemaPath:
										'#/properties/fonts/additionalProperties/anyOf/1/required',
									keyword: 'required',
									params: {
										missingProperty: 'font_families',
									},
									message:
										"must have required property '" +
										'font_families' +
										"'",
								};
								if (vErrors === null) {
									vErrors = [err276];
								} else {
									vErrors.push(err276);
								}
								errors++;
							}
							for (const key23 in data176) {
								if (
									!(
										key23 === '$schema' ||
										key23 === 'font_families'
									)
								) {
									const err277 = {
										instancePath:
											instancePath +
											'/fonts/' +
											key22
												.replace(/~/g, '~0')
												.replace(/\//g, '~1'),
										schemaPath:
											'#/properties/fonts/additionalProperties/anyOf/1/additionalProperties',
										keyword: 'additionalProperties',
										params: { additionalProperty: key23 },
										message:
											'must NOT have additional properties',
									};
									if (vErrors === null) {
										vErrors = [err277];
									} else {
										vErrors.push(err277);
									}
									errors++;
								}
							}
							if (data176.$schema !== undefined) {
								if (typeof data176.$schema !== 'string') {
									const err278 = {
										instancePath:
											instancePath +
											'/fonts/' +
											key22
												.replace(/~/g, '~0')
												.replace(/\//g, '~1') +
											'/$schema',
										schemaPath:
											'#/properties/fonts/additionalProperties/anyOf/1/properties/%24schema/type',
										keyword: 'type',
										params: { type: 'string' },
										message: 'must be string',
									};
									if (vErrors === null) {
										vErrors = [err278];
									} else {
										vErrors.push(err278);
									}
									errors++;
								}
							}
							if (data176.font_families !== undefined) {
								let data178 = data176.font_families;
								if (Array.isArray(data178)) {
									const len19 = data178.length;
									for (let i19 = 0; i19 < len19; i19++) {
										let data179 = data178[i19];
										if (
											data179 &&
											typeof data179 == 'object' &&
											!Array.isArray(data179)
										) {
											if (
												data179.font_family_settings ===
												undefined
											) {
												const err279 = {
													instancePath:
														instancePath +
														'/fonts/' +
														key22
															.replace(/~/g, '~0')
															.replace(
																/\//g,
																'~1'
															) +
														'/font_families/' +
														i19,
													schemaPath:
														'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/required',
													keyword: 'required',
													params: {
														missingProperty:
															'font_family_settings',
													},
													message:
														"must have required property '" +
														'font_family_settings' +
														"'",
												};
												if (vErrors === null) {
													vErrors = [err279];
												} else {
													vErrors.push(err279);
												}
												errors++;
											}
											for (const key24 in data179) {
												if (
													!(
														key24 ===
															'font_family_settings' ||
														key24 === 'categories'
													)
												) {
													const err280 = {
														instancePath:
															instancePath +
															'/fonts/' +
															key22
																.replace(
																	/~/g,
																	'~0'
																)
																.replace(
																	/\//g,
																	'~1'
																) +
															'/font_families/' +
															i19,
														schemaPath:
															'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/additionalProperties',
														keyword:
															'additionalProperties',
														params: {
															additionalProperty:
																key24,
														},
														message:
															'must NOT have additional properties',
													};
													if (vErrors === null) {
														vErrors = [err280];
													} else {
														vErrors.push(err280);
													}
													errors++;
												}
											}
											if (
												data179.font_family_settings !==
												undefined
											) {
												let data180 =
													data179.font_family_settings;
												if (
													data180 &&
													typeof data180 ==
														'object' &&
													!Array.isArray(data180)
												) {
													if (
														data180.name ===
														undefined
													) {
														const err281 = {
															instancePath:
																instancePath +
																'/fonts/' +
																key22
																	.replace(
																		/~/g,
																		'~0'
																	)
																	.replace(
																		/\//g,
																		'~1'
																	) +
																'/font_families/' +
																i19 +
																'/font_family_settings',
															schemaPath:
																'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/required',
															keyword: 'required',
															params: {
																missingProperty:
																	'name',
															},
															message:
																"must have required property '" +
																'name' +
																"'",
														};
														if (vErrors === null) {
															vErrors = [err281];
														} else {
															vErrors.push(
																err281
															);
														}
														errors++;
													}
													if (
														data180.slug ===
														undefined
													) {
														const err282 = {
															instancePath:
																instancePath +
																'/fonts/' +
																key22
																	.replace(
																		/~/g,
																		'~0'
																	)
																	.replace(
																		/\//g,
																		'~1'
																	) +
																'/font_families/' +
																i19 +
																'/font_family_settings',
															schemaPath:
																'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/required',
															keyword: 'required',
															params: {
																missingProperty:
																	'slug',
															},
															message:
																"must have required property '" +
																'slug' +
																"'",
														};
														if (vErrors === null) {
															vErrors = [err282];
														} else {
															vErrors.push(
																err282
															);
														}
														errors++;
													}
													if (
														data180.fontFamily ===
														undefined
													) {
														const err283 = {
															instancePath:
																instancePath +
																'/fonts/' +
																key22
																	.replace(
																		/~/g,
																		'~0'
																	)
																	.replace(
																		/\//g,
																		'~1'
																	) +
																'/font_families/' +
																i19 +
																'/font_family_settings',
															schemaPath:
																'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/required',
															keyword: 'required',
															params: {
																missingProperty:
																	'fontFamily',
															},
															message:
																"must have required property '" +
																'fontFamily' +
																"'",
														};
														if (vErrors === null) {
															vErrors = [err283];
														} else {
															vErrors.push(
																err283
															);
														}
														errors++;
													}
													for (const key25 in data180) {
														if (
															!(
																key25 ===
																	'name' ||
																key25 ===
																	'slug' ||
																key25 ===
																	'fontFamily' ||
																key25 ===
																	'preview' ||
																key25 ===
																	'fontFace'
															)
														) {
															const err284 = {
																instancePath:
																	instancePath +
																	'/fonts/' +
																	key22
																		.replace(
																			/~/g,
																			'~0'
																		)
																		.replace(
																			/\//g,
																			'~1'
																		) +
																	'/font_families/' +
																	i19 +
																	'/font_family_settings',
																schemaPath:
																	'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/additionalProperties',
																keyword:
																	'additionalProperties',
																params: {
																	additionalProperty:
																		key25,
																},
																message:
																	'must NOT have additional properties',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err284,
																];
															} else {
																vErrors.push(
																	err284
																);
															}
															errors++;
														}
													}
													if (
														data180.name !==
														undefined
													) {
														if (
															typeof data180.name !==
															'string'
														) {
															const err285 = {
																instancePath:
																	instancePath +
																	'/fonts/' +
																	key22
																		.replace(
																			/~/g,
																			'~0'
																		)
																		.replace(
																			/\//g,
																			'~1'
																		) +
																	'/font_families/' +
																	i19 +
																	'/font_family_settings/name',
																schemaPath:
																	'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/name/type',
																keyword: 'type',
																params: {
																	type: 'string',
																},
																message:
																	'must be string',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err285,
																];
															} else {
																vErrors.push(
																	err285
																);
															}
															errors++;
														}
													}
													if (
														data180.slug !==
														undefined
													) {
														if (
															typeof data180.slug !==
															'string'
														) {
															const err286 = {
																instancePath:
																	instancePath +
																	'/fonts/' +
																	key22
																		.replace(
																			/~/g,
																			'~0'
																		)
																		.replace(
																			/\//g,
																			'~1'
																		) +
																	'/font_families/' +
																	i19 +
																	'/font_family_settings/slug',
																schemaPath:
																	'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/slug/type',
																keyword: 'type',
																params: {
																	type: 'string',
																},
																message:
																	'must be string',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err286,
																];
															} else {
																vErrors.push(
																	err286
																);
															}
															errors++;
														}
													}
													if (
														data180.fontFamily !==
														undefined
													) {
														if (
															typeof data180.fontFamily !==
															'string'
														) {
															const err287 = {
																instancePath:
																	instancePath +
																	'/fonts/' +
																	key22
																		.replace(
																			/~/g,
																			'~0'
																		)
																		.replace(
																			/\//g,
																			'~1'
																		) +
																	'/font_families/' +
																	i19 +
																	'/font_family_settings/fontFamily',
																schemaPath:
																	'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFamily/type',
																keyword: 'type',
																params: {
																	type: 'string',
																},
																message:
																	'must be string',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err287,
																];
															} else {
																vErrors.push(
																	err287
																);
															}
															errors++;
														}
													}
													if (
														data180.preview !==
														undefined
													) {
														if (
															typeof data180.preview !==
															'string'
														) {
															const err288 = {
																instancePath:
																	instancePath +
																	'/fonts/' +
																	key22
																		.replace(
																			/~/g,
																			'~0'
																		)
																		.replace(
																			/\//g,
																			'~1'
																		) +
																	'/font_families/' +
																	i19 +
																	'/font_family_settings/preview',
																schemaPath:
																	'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/preview/type',
																keyword: 'type',
																params: {
																	type: 'string',
																},
																message:
																	'must be string',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err288,
																];
															} else {
																vErrors.push(
																	err288
																);
															}
															errors++;
														}
													}
													if (
														data180.fontFace !==
														undefined
													) {
														let data185 =
															data180.fontFace;
														if (
															Array.isArray(
																data185
															)
														) {
															const len20 =
																data185.length;
															for (
																let i20 = 0;
																i20 < len20;
																i20++
															) {
																let data186 =
																	data185[
																		i20
																	];
																if (
																	data186 &&
																	typeof data186 ==
																		'object' &&
																	!Array.isArray(
																		data186
																	)
																) {
																	if (
																		data186.fontFamily ===
																		undefined
																	) {
																		const err289 =
																			{
																				instancePath:
																					instancePath +
																					'/fonts/' +
																					key22
																						.replace(
																							/~/g,
																							'~0'
																						)
																						.replace(
																							/\//g,
																							'~1'
																						) +
																					'/font_families/' +
																					i19 +
																					'/font_family_settings/fontFace/' +
																					i20,
																				schemaPath:
																					'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/required',
																				keyword:
																					'required',
																				params: {
																					missingProperty:
																						'fontFamily',
																				},
																				message:
																					"must have required property '" +
																					'fontFamily' +
																					"'",
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err289,
																				];
																		} else {
																			vErrors.push(
																				err289
																			);
																		}
																		errors++;
																	}
																	if (
																		data186.src ===
																		undefined
																	) {
																		const err290 =
																			{
																				instancePath:
																					instancePath +
																					'/fonts/' +
																					key22
																						.replace(
																							/~/g,
																							'~0'
																						)
																						.replace(
																							/\//g,
																							'~1'
																						) +
																					'/font_families/' +
																					i19 +
																					'/font_family_settings/fontFace/' +
																					i20,
																				schemaPath:
																					'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/required',
																				keyword:
																					'required',
																				params: {
																					missingProperty:
																						'src',
																				},
																				message:
																					"must have required property '" +
																					'src' +
																					"'",
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err290,
																				];
																		} else {
																			vErrors.push(
																				err290
																			);
																		}
																		errors++;
																	}
																	for (const key26 in data186) {
																		if (
																			!func2.call(
																				schema12
																					.properties
																					.fonts
																					.additionalProperties
																					.anyOf[1]
																					.properties
																					.font_families
																					.items
																					.properties
																					.font_family_settings
																					.properties
																					.fontFace
																					.items
																					.properties,
																				key26
																			)
																		) {
																			const err291 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20,
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/additionalProperties',
																					keyword:
																						'additionalProperties',
																					params: {
																						additionalProperty:
																							key26,
																					},
																					message:
																						'must NOT have additional properties',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err291,
																					];
																			} else {
																				vErrors.push(
																					err291
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data186.preview !==
																		undefined
																	) {
																		if (
																			typeof data186.preview !==
																			'string'
																		) {
																			const err292 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/preview',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/preview/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err292,
																					];
																			} else {
																				vErrors.push(
																					err292
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data186.fontFamily !==
																		undefined
																	) {
																		if (
																			typeof data186.fontFamily !==
																			'string'
																		) {
																			const err293 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/fontFamily',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/fontFamily/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err293,
																					];
																			} else {
																				vErrors.push(
																					err293
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data186.fontStyle !==
																		undefined
																	) {
																		if (
																			typeof data186.fontStyle !==
																			'string'
																		) {
																			const err294 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/fontStyle',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/fontStyle/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err294,
																					];
																			} else {
																				vErrors.push(
																					err294
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data186.fontWeight !==
																		undefined
																	) {
																		let data190 =
																			data186.fontWeight;
																		if (
																			typeof data190 !==
																				'string' &&
																			!(
																				typeof data190 ==
																					'number' &&
																				isFinite(
																					data190
																				)
																			)
																		) {
																			const err295 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/fontWeight',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/fontWeight/type',
																					keyword:
																						'type',
																					params: {
																						type: schema12
																							.properties
																							.fonts
																							.additionalProperties
																							.anyOf[1]
																							.properties
																							.font_families
																							.items
																							.properties
																							.font_family_settings
																							.properties
																							.fontFace
																							.items
																							.properties
																							.fontWeight
																							.type,
																					},
																					message:
																						'must be string,number',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err295,
																					];
																			} else {
																				vErrors.push(
																					err295
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data186.fontDisplay !==
																		undefined
																	) {
																		let data191 =
																			data186.fontDisplay;
																		if (
																			typeof data191 !==
																			'string'
																		) {
																			const err296 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/fontDisplay',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/fontDisplay/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err296,
																					];
																			} else {
																				vErrors.push(
																					err296
																				);
																			}
																			errors++;
																		}
																		if (
																			!(
																				data191 ===
																					'auto' ||
																				data191 ===
																					'block' ||
																				data191 ===
																					'fallback' ||
																				data191 ===
																					'swap' ||
																				data191 ===
																					'optional'
																			)
																		) {
																			const err297 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/fontDisplay',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/fontDisplay/enum',
																					keyword:
																						'enum',
																					params: {
																						allowedValues:
																							schema12
																								.properties
																								.fonts
																								.additionalProperties
																								.anyOf[1]
																								.properties
																								.font_families
																								.items
																								.properties
																								.font_family_settings
																								.properties
																								.fontFace
																								.items
																								.properties
																								.fontDisplay
																								.enum,
																					},
																					message:
																						'must be equal to one of the allowed values',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err297,
																					];
																			} else {
																				vErrors.push(
																					err297
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data186.src !==
																		undefined
																	) {
																		let data192 =
																			data186.src;
																		const _errs511 =
																			errors;
																		let valid111 = false;
																		const _errs512 =
																			errors;
																		if (
																			!validate40(
																				data192,
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/src',
																					parentData:
																						data186,
																					parentDataProperty:
																						'src',
																					rootData,
																				}
																			)
																		) {
																			vErrors =
																				vErrors ===
																				null
																					? validate40.errors
																					: vErrors.concat(
																							validate40.errors
																						);
																			errors =
																				vErrors.length;
																		}
																		var _valid21 =
																			_errs512 ===
																			errors;
																		valid111 =
																			valid111 ||
																			_valid21;
																		if (
																			!valid111
																		) {
																			const _errs513 =
																				errors;
																			if (
																				Array.isArray(
																					data192
																				)
																			) {
																				const len21 =
																					data192.length;
																				for (
																					let i21 = 0;
																					i21 <
																					len21;
																					i21++
																				) {
																					if (
																						!validate40(
																							data192[
																								i21
																							],
																							{
																								instancePath:
																									instancePath +
																									'/fonts/' +
																									key22
																										.replace(
																											/~/g,
																											'~0'
																										)
																										.replace(
																											/\//g,
																											'~1'
																										) +
																									'/font_families/' +
																									i19 +
																									'/font_family_settings/fontFace/' +
																									i20 +
																									'/src/' +
																									i21,
																								parentData:
																									data192,
																								parentDataProperty:
																									i21,
																								rootData,
																							}
																						)
																					) {
																						vErrors =
																							vErrors ===
																							null
																								? validate40.errors
																								: vErrors.concat(
																										validate40.errors
																									);
																						errors =
																							vErrors.length;
																					}
																				}
																			} else {
																				const err298 =
																					{
																						instancePath:
																							instancePath +
																							'/fonts/' +
																							key22
																								.replace(
																									/~/g,
																									'~0'
																								)
																								.replace(
																									/\//g,
																									'~1'
																								) +
																							'/font_families/' +
																							i19 +
																							'/font_family_settings/fontFace/' +
																							i20 +
																							'/src',
																						schemaPath:
																							'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/src/anyOf/1/type',
																						keyword:
																							'type',
																						params: {
																							type: 'array',
																						},
																						message:
																							'must be array',
																					};
																				if (
																					vErrors ===
																					null
																				) {
																					vErrors =
																						[
																							err298,
																						];
																				} else {
																					vErrors.push(
																						err298
																					);
																				}
																				errors++;
																			}
																			var _valid21 =
																				_errs513 ===
																				errors;
																			valid111 =
																				valid111 ||
																				_valid21;
																		}
																		if (
																			!valid111
																		) {
																			const err299 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/src',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/src/anyOf',
																					keyword:
																						'anyOf',
																					params: {},
																					message:
																						'must match a schema in anyOf',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err299,
																					];
																			} else {
																				vErrors.push(
																					err299
																				);
																			}
																			errors++;
																		} else {
																			errors =
																				_errs511;
																			if (
																				vErrors !==
																				null
																			) {
																				if (
																					_errs511
																				) {
																					vErrors.length =
																						_errs511;
																				} else {
																					vErrors =
																						null;
																				}
																			}
																		}
																	}
																	if (
																		data186.fontStretch !==
																		undefined
																	) {
																		if (
																			typeof data186.fontStretch !==
																			'string'
																		) {
																			const err300 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/fontStretch',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/fontStretch/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err300,
																					];
																			} else {
																				vErrors.push(
																					err300
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data186.ascentOverride !==
																		undefined
																	) {
																		if (
																			typeof data186.ascentOverride !==
																			'string'
																		) {
																			const err301 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/ascentOverride',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/ascentOverride/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err301,
																					];
																			} else {
																				vErrors.push(
																					err301
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data186.descentOverride !==
																		undefined
																	) {
																		if (
																			typeof data186.descentOverride !==
																			'string'
																		) {
																			const err302 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/descentOverride',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/descentOverride/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err302,
																					];
																			} else {
																				vErrors.push(
																					err302
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data186.fontVariant !==
																		undefined
																	) {
																		if (
																			typeof data186.fontVariant !==
																			'string'
																		) {
																			const err303 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/fontVariant',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/fontVariant/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err303,
																					];
																			} else {
																				vErrors.push(
																					err303
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data186.fontFeatureSettings !==
																		undefined
																	) {
																		if (
																			typeof data186.fontFeatureSettings !==
																			'string'
																		) {
																			const err304 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/fontFeatureSettings',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/fontFeatureSettings/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err304,
																					];
																			} else {
																				vErrors.push(
																					err304
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data186.fontVariationSettings !==
																		undefined
																	) {
																		if (
																			typeof data186.fontVariationSettings !==
																			'string'
																		) {
																			const err305 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/fontVariationSettings',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/fontVariationSettings/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err305,
																					];
																			} else {
																				vErrors.push(
																					err305
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data186.lineGapOverride !==
																		undefined
																	) {
																		if (
																			typeof data186.lineGapOverride !==
																			'string'
																		) {
																			const err306 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/lineGapOverride',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/lineGapOverride/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err306,
																					];
																			} else {
																				vErrors.push(
																					err306
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data186.sizeAdjust !==
																		undefined
																	) {
																		if (
																			typeof data186.sizeAdjust !==
																			'string'
																		) {
																			const err307 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/sizeAdjust',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/sizeAdjust/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err307,
																					];
																			} else {
																				vErrors.push(
																					err307
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data186.unicodeRange !==
																		undefined
																	) {
																		if (
																			typeof data186.unicodeRange !==
																			'string'
																		) {
																			const err308 =
																				{
																					instancePath:
																						instancePath +
																						'/fonts/' +
																						key22
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/font_families/' +
																						i19 +
																						'/font_family_settings/fontFace/' +
																						i20 +
																						'/unicodeRange',
																					schemaPath:
																						'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/properties/unicodeRange/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err308,
																					];
																			} else {
																				vErrors.push(
																					err308
																				);
																			}
																			errors++;
																		}
																	}
																} else {
																	const err309 =
																		{
																			instancePath:
																				instancePath +
																				'/fonts/' +
																				key22
																					.replace(
																						/~/g,
																						'~0'
																					)
																					.replace(
																						/\//g,
																						'~1'
																					) +
																				'/font_families/' +
																				i19 +
																				'/font_family_settings/fontFace/' +
																				i20,
																			schemaPath:
																				'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/items/type',
																			keyword:
																				'type',
																			params: {
																				type: 'object',
																			},
																			message:
																				'must be object',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err309,
																			];
																	} else {
																		vErrors.push(
																			err309
																		);
																	}
																	errors++;
																}
															}
														} else {
															const err310 = {
																instancePath:
																	instancePath +
																	'/fonts/' +
																	key22
																		.replace(
																			/~/g,
																			'~0'
																		)
																		.replace(
																			/\//g,
																			'~1'
																		) +
																	'/font_families/' +
																	i19 +
																	'/font_family_settings/fontFace',
																schemaPath:
																	'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/properties/fontFace/type',
																keyword: 'type',
																params: {
																	type: 'array',
																},
																message:
																	'must be array',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err310,
																];
															} else {
																vErrors.push(
																	err310
																);
															}
															errors++;
														}
													}
												} else {
													const err311 = {
														instancePath:
															instancePath +
															'/fonts/' +
															key22
																.replace(
																	/~/g,
																	'~0'
																)
																.replace(
																	/\//g,
																	'~1'
																) +
															'/font_families/' +
															i19 +
															'/font_family_settings',
														schemaPath:
															'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/font_family_settings/type',
														keyword: 'type',
														params: {
															type: 'object',
														},
														message:
															'must be object',
													};
													if (vErrors === null) {
														vErrors = [err311];
													} else {
														vErrors.push(err311);
													}
													errors++;
												}
											}
											if (
												data179.categories !== undefined
											) {
												let data203 =
													data179.categories;
												if (Array.isArray(data203)) {
													const len22 =
														data203.length;
													for (
														let i22 = 0;
														i22 < len22;
														i22++
													) {
														if (
															typeof data203[
																i22
															] !== 'string'
														) {
															const err312 = {
																instancePath:
																	instancePath +
																	'/fonts/' +
																	key22
																		.replace(
																			/~/g,
																			'~0'
																		)
																		.replace(
																			/\//g,
																			'~1'
																		) +
																	'/font_families/' +
																	i19 +
																	'/categories/' +
																	i22,
																schemaPath:
																	'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/categories/items/type',
																keyword: 'type',
																params: {
																	type: 'string',
																},
																message:
																	'must be string',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err312,
																];
															} else {
																vErrors.push(
																	err312
																);
															}
															errors++;
														}
													}
												} else {
													const err313 = {
														instancePath:
															instancePath +
															'/fonts/' +
															key22
																.replace(
																	/~/g,
																	'~0'
																)
																.replace(
																	/\//g,
																	'~1'
																) +
															'/font_families/' +
															i19 +
															'/categories',
														schemaPath:
															'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/properties/categories/type',
														keyword: 'type',
														params: {
															type: 'array',
														},
														message:
															'must be array',
													};
													if (vErrors === null) {
														vErrors = [err313];
													} else {
														vErrors.push(err313);
													}
													errors++;
												}
											}
										} else {
											const err314 = {
												instancePath:
													instancePath +
													'/fonts/' +
													key22
														.replace(/~/g, '~0')
														.replace(/\//g, '~1') +
													'/font_families/' +
													i19,
												schemaPath:
													'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/items/type',
												keyword: 'type',
												params: { type: 'object' },
												message: 'must be object',
											};
											if (vErrors === null) {
												vErrors = [err314];
											} else {
												vErrors.push(err314);
											}
											errors++;
										}
									}
								} else {
									const err315 = {
										instancePath:
											instancePath +
											'/fonts/' +
											key22
												.replace(/~/g, '~0')
												.replace(/\//g, '~1') +
											'/font_families',
										schemaPath:
											'#/properties/fonts/additionalProperties/anyOf/1/properties/font_families/type',
										keyword: 'type',
										params: { type: 'array' },
										message: 'must be array',
									};
									if (vErrors === null) {
										vErrors = [err315];
									} else {
										vErrors.push(err315);
									}
									errors++;
								}
							}
						} else {
							const err316 = {
								instancePath:
									instancePath +
									'/fonts/' +
									key22
										.replace(/~/g, '~0')
										.replace(/\//g, '~1'),
								schemaPath:
									'#/properties/fonts/additionalProperties/anyOf/1/type',
								keyword: 'type',
								params: { type: 'object' },
								message: 'must be object',
							};
							if (vErrors === null) {
								vErrors = [err316];
							} else {
								vErrors.push(err316);
							}
							errors++;
						}
						var _valid20 = _errs474 === errors;
						valid102 = valid102 || _valid20;
					}
					if (!valid102) {
						const err317 = {
							instancePath:
								instancePath +
								'/fonts/' +
								key22.replace(/~/g, '~0').replace(/\//g, '~1'),
							schemaPath:
								'#/properties/fonts/additionalProperties/anyOf',
							keyword: 'anyOf',
							params: {},
							message: 'must match a schema in anyOf',
						};
						if (vErrors === null) {
							vErrors = [err317];
						} else {
							vErrors.push(err317);
						}
						errors++;
					} else {
						errors = _errs472;
						if (vErrors !== null) {
							if (_errs472) {
								vErrors.length = _errs472;
							} else {
								vErrors = null;
							}
						}
					}
				}
			} else {
				const err318 = {
					instancePath: instancePath + '/fonts',
					schemaPath: '#/properties/fonts/type',
					keyword: 'type',
					params: { type: 'object' },
					message: 'must be object',
				};
				if (vErrors === null) {
					vErrors = [err318];
				} else {
					vErrors.push(err318);
				}
				errors++;
			}
		}
		if (data.media !== undefined) {
			let data205 = data.media;
			if (Array.isArray(data205)) {
				const len23 = data205.length;
				for (let i23 = 0; i23 < len23; i23++) {
					let data206 = data205[i23];
					const _errs541 = errors;
					let valid118 = false;
					const _errs542 = errors;
					if (
						!validate40(data206, {
							instancePath: instancePath + '/media/' + i23,
							parentData: data205,
							parentDataProperty: i23,
							rootData,
						})
					) {
						vErrors =
							vErrors === null
								? validate40.errors
								: vErrors.concat(validate40.errors);
						errors = vErrors.length;
					}
					var _valid22 = _errs542 === errors;
					valid118 = valid118 || _valid22;
					if (!valid118) {
						const _errs543 = errors;
						if (
							data206 &&
							typeof data206 == 'object' &&
							!Array.isArray(data206)
						) {
							if (data206.source === undefined) {
								const err319 = {
									instancePath:
										instancePath + '/media/' + i23,
									schemaPath:
										'#/properties/media/items/anyOf/1/required',
									keyword: 'required',
									params: { missingProperty: 'source' },
									message:
										"must have required property '" +
										'source' +
										"'",
								};
								if (vErrors === null) {
									vErrors = [err319];
								} else {
									vErrors.push(err319);
								}
								errors++;
							}
							for (const key27 in data206) {
								if (
									!(
										key27 === 'source' ||
										key27 === 'title' ||
										key27 === 'description' ||
										key27 === 'alt' ||
										key27 === 'caption'
									)
								) {
									const err320 = {
										instancePath:
											instancePath + '/media/' + i23,
										schemaPath:
											'#/properties/media/items/anyOf/1/additionalProperties',
										keyword: 'additionalProperties',
										params: { additionalProperty: key27 },
										message:
											'must NOT have additional properties',
									};
									if (vErrors === null) {
										vErrors = [err320];
									} else {
										vErrors.push(err320);
									}
									errors++;
								}
							}
							if (data206.source !== undefined) {
								if (
									!validate40(data206.source, {
										instancePath:
											instancePath +
											'/media/' +
											i23 +
											'/source',
										parentData: data206,
										parentDataProperty: 'source',
										rootData,
									})
								) {
									vErrors =
										vErrors === null
											? validate40.errors
											: vErrors.concat(validate40.errors);
									errors = vErrors.length;
								}
							}
							if (data206.title !== undefined) {
								if (typeof data206.title !== 'string') {
									const err321 = {
										instancePath:
											instancePath +
											'/media/' +
											i23 +
											'/title',
										schemaPath:
											'#/properties/media/items/anyOf/1/properties/title/type',
										keyword: 'type',
										params: { type: 'string' },
										message: 'must be string',
									};
									if (vErrors === null) {
										vErrors = [err321];
									} else {
										vErrors.push(err321);
									}
									errors++;
								}
							}
							if (data206.description !== undefined) {
								if (typeof data206.description !== 'string') {
									const err322 = {
										instancePath:
											instancePath +
											'/media/' +
											i23 +
											'/description',
										schemaPath:
											'#/properties/media/items/anyOf/1/properties/description/type',
										keyword: 'type',
										params: { type: 'string' },
										message: 'must be string',
									};
									if (vErrors === null) {
										vErrors = [err322];
									} else {
										vErrors.push(err322);
									}
									errors++;
								}
							}
							if (data206.alt !== undefined) {
								if (typeof data206.alt !== 'string') {
									const err323 = {
										instancePath:
											instancePath +
											'/media/' +
											i23 +
											'/alt',
										schemaPath:
											'#/properties/media/items/anyOf/1/properties/alt/type',
										keyword: 'type',
										params: { type: 'string' },
										message: 'must be string',
									};
									if (vErrors === null) {
										vErrors = [err323];
									} else {
										vErrors.push(err323);
									}
									errors++;
								}
							}
							if (data206.caption !== undefined) {
								if (typeof data206.caption !== 'string') {
									const err324 = {
										instancePath:
											instancePath +
											'/media/' +
											i23 +
											'/caption',
										schemaPath:
											'#/properties/media/items/anyOf/1/properties/caption/type',
										keyword: 'type',
										params: { type: 'string' },
										message: 'must be string',
									};
									if (vErrors === null) {
										vErrors = [err324];
									} else {
										vErrors.push(err324);
									}
									errors++;
								}
							}
						} else {
							const err325 = {
								instancePath: instancePath + '/media/' + i23,
								schemaPath:
									'#/properties/media/items/anyOf/1/type',
								keyword: 'type',
								params: { type: 'object' },
								message: 'must be object',
							};
							if (vErrors === null) {
								vErrors = [err325];
							} else {
								vErrors.push(err325);
							}
							errors++;
						}
						var _valid22 = _errs543 === errors;
						valid118 = valid118 || _valid22;
					}
					if (!valid118) {
						const err326 = {
							instancePath: instancePath + '/media/' + i23,
							schemaPath: '#/properties/media/items/anyOf',
							keyword: 'anyOf',
							params: {},
							message: 'must match a schema in anyOf',
						};
						if (vErrors === null) {
							vErrors = [err326];
						} else {
							vErrors.push(err326);
						}
						errors++;
					} else {
						errors = _errs541;
						if (vErrors !== null) {
							if (_errs541) {
								vErrors.length = _errs541;
							} else {
								vErrors = null;
							}
						}
					}
				}
			} else {
				const err327 = {
					instancePath: instancePath + '/media',
					schemaPath: '#/properties/media/type',
					keyword: 'type',
					params: { type: 'array' },
					message: 'must be array',
				};
				if (vErrors === null) {
					vErrors = [err327];
				} else {
					vErrors.push(err327);
				}
				errors++;
			}
		}
		if (data.content !== undefined) {
			let data212 = data.content;
			if (Array.isArray(data212)) {
				const len24 = data212.length;
				for (let i24 = 0; i24 < len24; i24++) {
					let data213 = data212[i24];
					if (
						data213 &&
						typeof data213 == 'object' &&
						!Array.isArray(data213)
					) {
						if (data213.type === undefined) {
							const err328 = {
								instancePath: instancePath + '/content/' + i24,
								schemaPath:
									'#/properties/content/items/required',
								keyword: 'required',
								params: { missingProperty: 'type' },
								message:
									"must have required property '" +
									'type' +
									"'",
							};
							if (vErrors === null) {
								vErrors = [err328];
							} else {
								vErrors.push(err328);
							}
							errors++;
						}
						const tag0 = data213.type;
						if (typeof tag0 == 'string') {
							if (tag0 === 'mysql-dump') {
								if (
									data213 &&
									typeof data213 == 'object' &&
									!Array.isArray(data213)
								) {
									if (data213.type === undefined) {
										const err329 = {
											instancePath:
												instancePath +
												'/content/' +
												i24,
											schemaPath:
												'#/properties/content/items/oneOf/0/required',
											keyword: 'required',
											params: { missingProperty: 'type' },
											message:
												"must have required property '" +
												'type' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err329];
										} else {
											vErrors.push(err329);
										}
										errors++;
									}
									if (data213.source === undefined) {
										const err330 = {
											instancePath:
												instancePath +
												'/content/' +
												i24,
											schemaPath:
												'#/properties/content/items/oneOf/0/required',
											keyword: 'required',
											params: {
												missingProperty: 'source',
											},
											message:
												"must have required property '" +
												'source' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err330];
										} else {
											vErrors.push(err330);
										}
										errors++;
									}
									for (const key28 in data213) {
										if (
											!(
												key28 === 'type' ||
												key28 === 'source'
											)
										) {
											const err331 = {
												instancePath:
													instancePath +
													'/content/' +
													i24,
												schemaPath:
													'#/properties/content/items/oneOf/0/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key28,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err331];
											} else {
												vErrors.push(err331);
											}
											errors++;
										}
									}
									if (data213.type !== undefined) {
										let data214 = data213.type;
										if (typeof data214 !== 'string') {
											const err332 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/type',
												schemaPath:
													'#/properties/content/items/oneOf/0/properties/type/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err332];
											} else {
												vErrors.push(err332);
											}
											errors++;
										}
										if ('mysql-dump' !== data214) {
											const err333 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/type',
												schemaPath:
													'#/properties/content/items/oneOf/0/properties/type/const',
												keyword: 'const',
												params: {
													allowedValue: 'mysql-dump',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err333];
											} else {
												vErrors.push(err333);
											}
											errors++;
										}
									}
									if (data213.source !== undefined) {
										let data215 = data213.source;
										const _errs566 = errors;
										let valid125 = false;
										const _errs567 = errors;
										if (
											!validate40(data215, {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/source',
												parentData: data213,
												parentDataProperty: 'source',
												rootData,
											})
										) {
											vErrors =
												vErrors === null
													? validate40.errors
													: vErrors.concat(
															validate40.errors
														);
											errors = vErrors.length;
										}
										var _valid23 = _errs567 === errors;
										valid125 = valid125 || _valid23;
										if (!valid125) {
											const _errs568 = errors;
											if (Array.isArray(data215)) {
												const len25 = data215.length;
												for (
													let i25 = 0;
													i25 < len25;
													i25++
												) {
													if (
														!validate40(
															data215[i25],
															{
																instancePath:
																	instancePath +
																	'/content/' +
																	i24 +
																	'/source/' +
																	i25,
																parentData:
																	data215,
																parentDataProperty:
																	i25,
																rootData,
															}
														)
													) {
														vErrors =
															vErrors === null
																? validate40.errors
																: vErrors.concat(
																		validate40.errors
																	);
														errors = vErrors.length;
													}
												}
											} else {
												const err334 = {
													instancePath:
														instancePath +
														'/content/' +
														i24 +
														'/source',
													schemaPath:
														'#/properties/content/items/oneOf/0/properties/source/anyOf/1/type',
													keyword: 'type',
													params: { type: 'array' },
													message: 'must be array',
												};
												if (vErrors === null) {
													vErrors = [err334];
												} else {
													vErrors.push(err334);
												}
												errors++;
											}
											var _valid23 = _errs568 === errors;
											valid125 = valid125 || _valid23;
										}
										if (!valid125) {
											const err335 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/source',
												schemaPath:
													'#/properties/content/items/oneOf/0/properties/source/anyOf',
												keyword: 'anyOf',
												params: {},
												message:
													'must match a schema in anyOf',
											};
											if (vErrors === null) {
												vErrors = [err335];
											} else {
												vErrors.push(err335);
											}
											errors++;
										} else {
											errors = _errs566;
											if (vErrors !== null) {
												if (_errs566) {
													vErrors.length = _errs566;
												} else {
													vErrors = null;
												}
											}
										}
									}
								} else {
									const err336 = {
										instancePath:
											instancePath + '/content/' + i24,
										schemaPath:
											'#/properties/content/items/oneOf/0/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err336];
									} else {
										vErrors.push(err336);
									}
									errors++;
								}
							} else if (tag0 === 'posts') {
								if (
									data213 &&
									typeof data213 == 'object' &&
									!Array.isArray(data213)
								) {
									if (data213.source === undefined) {
										const err337 = {
											instancePath:
												instancePath +
												'/content/' +
												i24,
											schemaPath:
												'#/properties/content/items/oneOf/1/required',
											keyword: 'required',
											params: {
												missingProperty: 'source',
											},
											message:
												"must have required property '" +
												'source' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err337];
										} else {
											vErrors.push(err337);
										}
										errors++;
									}
									if (data213.type === undefined) {
										const err338 = {
											instancePath:
												instancePath +
												'/content/' +
												i24,
											schemaPath:
												'#/properties/content/items/oneOf/1/required',
											keyword: 'required',
											params: { missingProperty: 'type' },
											message:
												"must have required property '" +
												'type' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err338];
										} else {
											vErrors.push(err338);
										}
										errors++;
									}
									for (const key29 in data213) {
										if (
											!(
												key29 === 'urlsMode' ||
												key29 === 'urlsMap' ||
												key29 === 'type' ||
												key29 === 'source'
											)
										) {
											const err339 = {
												instancePath:
													instancePath +
													'/content/' +
													i24,
												schemaPath:
													'#/properties/content/items/oneOf/1/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key29,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err339];
											} else {
												vErrors.push(err339);
											}
											errors++;
										}
									}
									if (data213.urlsMode !== undefined) {
										let data217 = data213.urlsMode;
										if (typeof data217 !== 'string') {
											const err340 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/urlsMode',
												schemaPath:
													'#/properties/content/items/oneOf/1/properties/urlsMode/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err340];
											} else {
												vErrors.push(err340);
											}
											errors++;
										}
										if (
											!(
												data217 === 'rewrite' ||
												data217 === 'preserve'
											)
										) {
											const err341 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/urlsMode',
												schemaPath:
													'#/properties/content/items/oneOf/1/properties/urlsMode/enum',
												keyword: 'enum',
												params: {
													allowedValues:
														schema12.properties
															.content.items
															.oneOf[1].properties
															.urlsMode.enum,
												},
												message:
													'must be equal to one of the allowed values',
											};
											if (vErrors === null) {
												vErrors = [err341];
											} else {
												vErrors.push(err341);
											}
											errors++;
										}
									}
									if (data213.urlsMap !== undefined) {
										let data218 = data213.urlsMap;
										if (
											data218 &&
											typeof data218 == 'object' &&
											!Array.isArray(data218)
										) {
											for (const key30 in data218) {
												const _errs578 = errors;
												if (typeof key30 === 'string') {
													if (!formats0(key30)) {
														const err342 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/urlsMap',
															schemaPath:
																'#/definitions/DataSources.URLReference/format',
															keyword: 'format',
															params: {
																format: 'whatwg-http-url',
															},
															message:
																'must match format "' +
																'whatwg-http-url' +
																'"',
															propertyName: key30,
														};
														if (vErrors === null) {
															vErrors = [err342];
														} else {
															vErrors.push(
																err342
															);
														}
														errors++;
													}
												} else {
													const err343 = {
														instancePath:
															instancePath +
															'/content/' +
															i24 +
															'/urlsMap',
														schemaPath:
															'#/definitions/DataSources.URLReference/type',
														keyword: 'type',
														params: {
															type: 'string',
														},
														message:
															'must be string',
														propertyName: key30,
													};
													if (vErrors === null) {
														vErrors = [err343];
													} else {
														vErrors.push(err343);
													}
													errors++;
												}
												var valid130 =
													_errs578 === errors;
												if (!valid130) {
													const err344 = {
														instancePath:
															instancePath +
															'/content/' +
															i24 +
															'/urlsMap',
														schemaPath:
															'#/properties/content/items/oneOf/1/properties/urlsMap/propertyNames',
														keyword:
															'propertyNames',
														params: {
															propertyName: key30,
														},
														message:
															'property name must be valid',
													};
													if (vErrors === null) {
														vErrors = [err344];
													} else {
														vErrors.push(err344);
													}
													errors++;
												}
											}
											for (const key31 in data218) {
												let data219 = data218[key31];
												if (
													typeof data219 === 'string'
												) {
													if (!formats0(data219)) {
														const err345 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/urlsMap/' +
																key31
																	.replace(
																		/~/g,
																		'~0'
																	)
																	.replace(
																		/\//g,
																		'~1'
																	),
															schemaPath:
																'#/definitions/DataSources.URLReference/format',
															keyword: 'format',
															params: {
																format: 'whatwg-http-url',
															},
															message:
																'must match format "' +
																'whatwg-http-url' +
																'"',
														};
														if (vErrors === null) {
															vErrors = [err345];
														} else {
															vErrors.push(
																err345
															);
														}
														errors++;
													}
												} else {
													const err346 = {
														instancePath:
															instancePath +
															'/content/' +
															i24 +
															'/urlsMap/' +
															key31
																.replace(
																	/~/g,
																	'~0'
																)
																.replace(
																	/\//g,
																	'~1'
																),
														schemaPath:
															'#/definitions/DataSources.URLReference/type',
														keyword: 'type',
														params: {
															type: 'string',
														},
														message:
															'must be string',
													};
													if (vErrors === null) {
														vErrors = [err346];
													} else {
														vErrors.push(err346);
													}
													errors++;
												}
											}
										} else {
											const err347 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/urlsMap',
												schemaPath:
													'#/properties/content/items/oneOf/1/properties/urlsMap/type',
												keyword: 'type',
												params: { type: 'object' },
												message: 'must be object',
											};
											if (vErrors === null) {
												vErrors = [err347];
											} else {
												vErrors.push(err347);
											}
											errors++;
										}
									}
									if (data213.type !== undefined) {
										let data220 = data213.type;
										if (typeof data220 !== 'string') {
											const err348 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/type',
												schemaPath:
													'#/properties/content/items/oneOf/1/properties/type/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err348];
											} else {
												vErrors.push(err348);
											}
											errors++;
										}
										if ('posts' !== data220) {
											const err349 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/type',
												schemaPath:
													'#/properties/content/items/oneOf/1/properties/type/const',
												keyword: 'const',
												params: {
													allowedValue: 'posts',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err349];
											} else {
												vErrors.push(err349);
											}
											errors++;
										}
									}
									if (data213.source !== undefined) {
										let data221 = data213.source;
										const _errs588 = errors;
										let valid134 = false;
										const _errs589 = errors;
										if (
											!validate40(data221, {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/source',
												parentData: data213,
												parentDataProperty: 'source',
												rootData,
											})
										) {
											vErrors =
												vErrors === null
													? validate40.errors
													: vErrors.concat(
															validate40.errors
														);
											errors = vErrors.length;
										}
										var _valid24 = _errs589 === errors;
										valid134 = valid134 || _valid24;
										if (!valid134) {
											const _errs590 = errors;
											if (
												data221 &&
												typeof data221 == 'object' &&
												!Array.isArray(data221)
											) {
												if (
													data221.post_title ===
													undefined
												) {
													const err350 = {
														instancePath:
															instancePath +
															'/content/' +
															i24 +
															'/source',
														schemaPath:
															'#/properties/content/items/oneOf/1/properties/source/anyOf/1/required',
														keyword: 'required',
														params: {
															missingProperty:
																'post_title',
														},
														message:
															"must have required property '" +
															'post_title' +
															"'",
													};
													if (vErrors === null) {
														vErrors = [err350];
													} else {
														vErrors.push(err350);
													}
													errors++;
												}
												for (const key32 in data221) {
													if (
														!func2.call(
															schema12.properties
																.content.items
																.oneOf[1]
																.properties
																.source.anyOf[1]
																.properties,
															key32
														)
													) {
														const err351 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/additionalProperties',
															keyword:
																'additionalProperties',
															params: {
																additionalProperty:
																	key32,
															},
															message:
																'must NOT have additional properties',
														};
														if (vErrors === null) {
															vErrors = [err351];
														} else {
															vErrors.push(
																err351
															);
														}
														errors++;
													}
												}
												if (
													data221.post_author !==
													undefined
												) {
													let data222 =
														data221.post_author;
													if (
														!(
															typeof data222 ==
																'number' &&
															isFinite(data222)
														)
													) {
														const err352 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/post_author',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_author/type',
															keyword: 'type',
															params: {
																type: 'number',
															},
															message:
																'must be number',
														};
														if (vErrors === null) {
															vErrors = [err352];
														} else {
															vErrors.push(
																err352
															);
														}
														errors++;
													}
												}
												if (
													data221.post_date !==
													undefined
												) {
													if (
														typeof data221.post_date !==
														'string'
													) {
														const err353 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/post_date',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_date/type',
															keyword: 'type',
															params: {
																type: 'string',
															},
															message:
																'must be string',
														};
														if (vErrors === null) {
															vErrors = [err353];
														} else {
															vErrors.push(
																err353
															);
														}
														errors++;
													}
												}
												if (
													data221.post_content !==
													undefined
												) {
													if (
														typeof data221.post_content !==
														'string'
													) {
														const err354 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/post_content',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_content/type',
															keyword: 'type',
															params: {
																type: 'string',
															},
															message:
																'must be string',
														};
														if (vErrors === null) {
															vErrors = [err354];
														} else {
															vErrors.push(
																err354
															);
														}
														errors++;
													}
												}
												if (
													data221.post_title !==
													undefined
												) {
													if (
														typeof data221.post_title !==
														'string'
													) {
														const err355 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/post_title',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_title/type',
															keyword: 'type',
															params: {
																type: 'string',
															},
															message:
																'must be string',
														};
														if (vErrors === null) {
															vErrors = [err355];
														} else {
															vErrors.push(
																err355
															);
														}
														errors++;
													}
												}
												if (
													data221.post_excerpt !==
													undefined
												) {
													if (
														typeof data221.post_excerpt !==
														'string'
													) {
														const err356 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/post_excerpt',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_excerpt/type',
															keyword: 'type',
															params: {
																type: 'string',
															},
															message:
																'must be string',
														};
														if (vErrors === null) {
															vErrors = [err356];
														} else {
															vErrors.push(
																err356
															);
														}
														errors++;
													}
												}
												if (
													data221.post_status !==
													undefined
												) {
													let data227 =
														data221.post_status;
													if (
														typeof data227 !==
														'string'
													) {
														const err357 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/post_status',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_status/type',
															keyword: 'type',
															params: {
																type: 'string',
															},
															message:
																'must be string',
														};
														if (vErrors === null) {
															vErrors = [err357];
														} else {
															vErrors.push(
																err357
															);
														}
														errors++;
													}
													if (
														!(
															data227 ===
																'publish' ||
															data227 ===
																'pending' ||
															data227 ===
																'draft' ||
															data227 ===
																'auto-draft' ||
															data227 ===
																'future' ||
															data227 ===
																'private' ||
															data227 ===
																'inherit' ||
															data227 === 'trash'
														)
													) {
														const err358 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/post_status',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_status/enum',
															keyword: 'enum',
															params: {
																allowedValues:
																	schema12
																		.properties
																		.content
																		.items
																		.oneOf[1]
																		.properties
																		.source
																		.anyOf[1]
																		.properties
																		.post_status
																		.enum,
															},
															message:
																'must be equal to one of the allowed values',
														};
														if (vErrors === null) {
															vErrors = [err358];
														} else {
															vErrors.push(
																err358
															);
														}
														errors++;
													}
												}
												if (
													data221.post_type !==
													undefined
												) {
													if (
														typeof data221.post_type !==
														'string'
													) {
														const err359 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/post_type',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_type/type',
															keyword: 'type',
															params: {
																type: 'string',
															},
															message:
																'must be string',
														};
														if (vErrors === null) {
															vErrors = [err359];
														} else {
															vErrors.push(
																err359
															);
														}
														errors++;
													}
												}
												if (
													data221.comment_status !==
													undefined
												) {
													let data229 =
														data221.comment_status;
													if (
														typeof data229 !==
														'string'
													) {
														const err360 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/comment_status',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/comment_status/type',
															keyword: 'type',
															params: {
																type: 'string',
															},
															message:
																'must be string',
														};
														if (vErrors === null) {
															vErrors = [err360];
														} else {
															vErrors.push(
																err360
															);
														}
														errors++;
													}
													if (
														!(
															data229 ===
																'open' ||
															data229 === 'closed'
														)
													) {
														const err361 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/comment_status',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/comment_status/enum',
															keyword: 'enum',
															params: {
																allowedValues:
																	schema12
																		.properties
																		.content
																		.items
																		.oneOf[1]
																		.properties
																		.source
																		.anyOf[1]
																		.properties
																		.comment_status
																		.enum,
															},
															message:
																'must be equal to one of the allowed values',
														};
														if (vErrors === null) {
															vErrors = [err361];
														} else {
															vErrors.push(
																err361
															);
														}
														errors++;
													}
												}
												if (
													data221.post_password !==
													undefined
												) {
													if (
														typeof data221.post_password !==
														'string'
													) {
														const err362 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/post_password',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_password/type',
															keyword: 'type',
															params: {
																type: 'string',
															},
															message:
																'must be string',
														};
														if (vErrors === null) {
															vErrors = [err362];
														} else {
															vErrors.push(
																err362
															);
														}
														errors++;
													}
												}
												if (
													data221.post_name !==
													undefined
												) {
													if (
														typeof data221.post_name !==
														'string'
													) {
														const err363 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/post_name',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_name/type',
															keyword: 'type',
															params: {
																type: 'string',
															},
															message:
																'must be string',
														};
														if (vErrors === null) {
															vErrors = [err363];
														} else {
															vErrors.push(
																err363
															);
														}
														errors++;
													}
												}
												if (
													data221.post_parent_name !==
													undefined
												) {
													if (
														typeof data221.post_parent_name !==
														'string'
													) {
														const err364 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/post_parent_name',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_parent_name/type',
															keyword: 'type',
															params: {
																type: 'string',
															},
															message:
																'must be string',
														};
														if (vErrors === null) {
															vErrors = [err364];
														} else {
															vErrors.push(
																err364
															);
														}
														errors++;
													}
												}
												if (
													data221.menu_order !==
													undefined
												) {
													let data233 =
														data221.menu_order;
													if (
														!(
															typeof data233 ==
																'number' &&
															isFinite(data233)
														)
													) {
														const err365 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/menu_order',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/menu_order/type',
															keyword: 'type',
															params: {
																type: 'number',
															},
															message:
																'must be number',
														};
														if (vErrors === null) {
															vErrors = [err365];
														} else {
															vErrors.push(
																err365
															);
														}
														errors++;
													}
												}
												if (
													data221.post_mime_type !==
													undefined
												) {
													if (
														typeof data221.post_mime_type !==
														'string'
													) {
														const err366 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/post_mime_type',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_mime_type/type',
															keyword: 'type',
															params: {
																type: 'string',
															},
															message:
																'must be string',
														};
														if (vErrors === null) {
															vErrors = [err366];
														} else {
															vErrors.push(
																err366
															);
														}
														errors++;
													}
												}
												if (
													data221.guid !== undefined
												) {
													if (
														typeof data221.guid !==
														'string'
													) {
														const err367 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/guid',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/guid/type',
															keyword: 'type',
															params: {
																type: 'string',
															},
															message:
																'must be string',
														};
														if (vErrors === null) {
															vErrors = [err367];
														} else {
															vErrors.push(
																err367
															);
														}
														errors++;
													}
												}
												if (
													data221.post_category !==
													undefined
												) {
													let data236 =
														data221.post_category;
													if (
														Array.isArray(data236)
													) {
														const len26 =
															data236.length;
														for (
															let i26 = 0;
															i26 < len26;
															i26++
														) {
															if (
																typeof data236[
																	i26
																] !== 'string'
															) {
																const err368 = {
																	instancePath:
																		instancePath +
																		'/content/' +
																		i24 +
																		'/source/post_category/' +
																		i26,
																	schemaPath:
																		'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_category/items/type',
																	keyword:
																		'type',
																	params: {
																		type: 'string',
																	},
																	message:
																		'must be string',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err368,
																	];
																} else {
																	vErrors.push(
																		err368
																	);
																}
																errors++;
															}
														}
													} else {
														const err369 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/post_category',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_category/type',
															keyword: 'type',
															params: {
																type: 'array',
															},
															message:
																'must be array',
														};
														if (vErrors === null) {
															vErrors = [err369];
														} else {
															vErrors.push(
																err369
															);
														}
														errors++;
													}
												}
												if (
													data221.post_tags !==
													undefined
												) {
													let data238 =
														data221.post_tags;
													if (
														Array.isArray(data238)
													) {
														const len27 =
															data238.length;
														for (
															let i27 = 0;
															i27 < len27;
															i27++
														) {
															if (
																typeof data238[
																	i27
																] !== 'string'
															) {
																const err370 = {
																	instancePath:
																		instancePath +
																		'/content/' +
																		i24 +
																		'/source/post_tags/' +
																		i27,
																	schemaPath:
																		'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_tags/items/type',
																	keyword:
																		'type',
																	params: {
																		type: 'string',
																	},
																	message:
																		'must be string',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err370,
																	];
																} else {
																	vErrors.push(
																		err370
																	);
																}
																errors++;
															}
														}
													} else {
														const err371 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/post_tags',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/post_tags/type',
															keyword: 'type',
															params: {
																type: 'array',
															},
															message:
																'must be array',
														};
														if (vErrors === null) {
															vErrors = [err371];
														} else {
															vErrors.push(
																err371
															);
														}
														errors++;
													}
												}
												if (
													data221.tax_input !==
													undefined
												) {
													let data240 =
														data221.tax_input;
													if (
														data240 &&
														typeof data240 ==
															'object' &&
														!Array.isArray(data240)
													) {
														for (const key33 in data240) {
															let data241 =
																data240[key33];
															if (
																Array.isArray(
																	data241
																)
															) {
																const len28 =
																	data241.length;
																for (
																	let i28 = 0;
																	i28 < len28;
																	i28++
																) {
																	if (
																		typeof data241[
																			i28
																		] !==
																		'string'
																	) {
																		const err372 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/tax_input/' +
																					key33
																						.replace(
																							/~/g,
																							'~0'
																						)
																						.replace(
																							/\//g,
																							'~1'
																						) +
																					'/' +
																					i28,
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/tax_input/additionalProperties/items/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err372,
																				];
																		} else {
																			vErrors.push(
																				err372
																			);
																		}
																		errors++;
																	}
																}
															} else {
																const err373 = {
																	instancePath:
																		instancePath +
																		'/content/' +
																		i24 +
																		'/source/tax_input/' +
																		key33
																			.replace(
																				/~/g,
																				'~0'
																			)
																			.replace(
																				/\//g,
																				'~1'
																			),
																	schemaPath:
																		'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/tax_input/additionalProperties/type',
																	keyword:
																		'type',
																	params: {
																		type: 'array',
																	},
																	message:
																		'must be array',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err373,
																	];
																} else {
																	vErrors.push(
																		err373
																	);
																}
																errors++;
															}
														}
													} else {
														const err374 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/tax_input',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/tax_input/type',
															keyword: 'type',
															params: {
																type: 'object',
															},
															message:
																'must be object',
														};
														if (vErrors === null) {
															vErrors = [err374];
														} else {
															vErrors.push(
																err374
															);
														}
														errors++;
													}
												}
												if (
													data221.meta_input !==
													undefined
												) {
													let data243 =
														data221.meta_input;
													if (
														data243 &&
														typeof data243 ==
															'object' &&
														!Array.isArray(data243)
													) {
														for (const key34 in data243) {
															let data244 =
																data243[key34];
															const _errs640 =
																errors;
															let valid144 = false;
															const _errs641 =
																errors;
															if (
																typeof data244 !==
																'string'
															) {
																const err375 = {
																	instancePath:
																		instancePath +
																		'/content/' +
																		i24 +
																		'/source/meta_input/' +
																		key34
																			.replace(
																				/~/g,
																				'~0'
																			)
																			.replace(
																				/\//g,
																				'~1'
																			),
																	schemaPath:
																		'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/meta_input/additionalProperties/anyOf/0/type',
																	keyword:
																		'type',
																	params: {
																		type: 'string',
																	},
																	message:
																		'must be string',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err375,
																	];
																} else {
																	vErrors.push(
																		err375
																	);
																}
																errors++;
															}
															var _valid25 =
																_errs641 ===
																errors;
															valid144 =
																valid144 ||
																_valid25;
															if (!valid144) {
																const _errs643 =
																	errors;
																if (
																	typeof data244 !==
																	'boolean'
																) {
																	const err376 =
																		{
																			instancePath:
																				instancePath +
																				'/content/' +
																				i24 +
																				'/source/meta_input/' +
																				key34
																					.replace(
																						/~/g,
																						'~0'
																					)
																					.replace(
																						/\//g,
																						'~1'
																					),
																			schemaPath:
																				'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/meta_input/additionalProperties/anyOf/1/type',
																			keyword:
																				'type',
																			params: {
																				type: 'boolean',
																			},
																			message:
																				'must be boolean',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err376,
																			];
																	} else {
																		vErrors.push(
																			err376
																		);
																	}
																	errors++;
																}
																var _valid25 =
																	_errs643 ===
																	errors;
																valid144 =
																	valid144 ||
																	_valid25;
																if (!valid144) {
																	const _errs645 =
																		errors;
																	if (
																		!(
																			typeof data244 ==
																				'number' &&
																			isFinite(
																				data244
																			)
																		)
																	) {
																		const err377 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/meta_input/' +
																					key34
																						.replace(
																							/~/g,
																							'~0'
																						)
																						.replace(
																							/\//g,
																							'~1'
																						),
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/meta_input/additionalProperties/anyOf/2/type',
																				keyword:
																					'type',
																				params: {
																					type: 'number',
																				},
																				message:
																					'must be number',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err377,
																				];
																		} else {
																			vErrors.push(
																				err377
																			);
																		}
																		errors++;
																	}
																	var _valid25 =
																		_errs645 ===
																		errors;
																	valid144 =
																		valid144 ||
																		_valid25;
																	if (
																		!valid144
																	) {
																		const _errs647 =
																			errors;
																		if (
																			Array.isArray(
																				data244
																			)
																		) {
																			const len29 =
																				data244.length;
																			for (
																				let i29 = 0;
																				i29 <
																				len29;
																				i29++
																			) {
																				if (
																					!validate34(
																						data244[
																							i29
																						],
																						{
																							instancePath:
																								instancePath +
																								'/content/' +
																								i24 +
																								'/source/meta_input/' +
																								key34
																									.replace(
																										/~/g,
																										'~0'
																									)
																									.replace(
																										/\//g,
																										'~1'
																									) +
																								'/' +
																								i29,
																							parentData:
																								data244,
																							parentDataProperty:
																								i29,
																							rootData,
																						}
																					)
																				) {
																					vErrors =
																						vErrors ===
																						null
																							? validate34.errors
																							: vErrors.concat(
																									validate34.errors
																								);
																					errors =
																						vErrors.length;
																				}
																			}
																		} else {
																			const err378 =
																				{
																					instancePath:
																						instancePath +
																						'/content/' +
																						i24 +
																						'/source/meta_input/' +
																						key34
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							),
																					schemaPath:
																						'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/meta_input/additionalProperties/anyOf/3/type',
																					keyword:
																						'type',
																					params: {
																						type: 'array',
																					},
																					message:
																						'must be array',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err378,
																					];
																			} else {
																				vErrors.push(
																					err378
																				);
																			}
																			errors++;
																		}
																		var _valid25 =
																			_errs647 ===
																			errors;
																		valid144 =
																			valid144 ||
																			_valid25;
																		if (
																			!valid144
																		) {
																			const _errs650 =
																				errors;
																			if (
																				data244 &&
																				typeof data244 ==
																					'object' &&
																				!Array.isArray(
																					data244
																				)
																			) {
																				for (const key35 in data244) {
																					if (
																						!validate34(
																							data244[
																								key35
																							],
																							{
																								instancePath:
																									instancePath +
																									'/content/' +
																									i24 +
																									'/source/meta_input/' +
																									key34
																										.replace(
																											/~/g,
																											'~0'
																										)
																										.replace(
																											/\//g,
																											'~1'
																										) +
																									'/' +
																									key35
																										.replace(
																											/~/g,
																											'~0'
																										)
																										.replace(
																											/\//g,
																											'~1'
																										),
																								parentData:
																									data244,
																								parentDataProperty:
																									key35,
																								rootData,
																							}
																						)
																					) {
																						vErrors =
																							vErrors ===
																							null
																								? validate34.errors
																								: vErrors.concat(
																										validate34.errors
																									);
																						errors =
																							vErrors.length;
																					}
																				}
																			} else {
																				const err379 =
																					{
																						instancePath:
																							instancePath +
																							'/content/' +
																							i24 +
																							'/source/meta_input/' +
																							key34
																								.replace(
																									/~/g,
																									'~0'
																								)
																								.replace(
																									/\//g,
																									'~1'
																								),
																						schemaPath:
																							'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/meta_input/additionalProperties/anyOf/4/type',
																						keyword:
																							'type',
																						params: {
																							type: 'object',
																						},
																						message:
																							'must be object',
																					};
																				if (
																					vErrors ===
																					null
																				) {
																					vErrors =
																						[
																							err379,
																						];
																				} else {
																					vErrors.push(
																						err379
																					);
																				}
																				errors++;
																			}
																			var _valid25 =
																				_errs650 ===
																				errors;
																			valid144 =
																				valid144 ||
																				_valid25;
																		}
																	}
																}
															}
															if (!valid144) {
																const err380 = {
																	instancePath:
																		instancePath +
																		'/content/' +
																		i24 +
																		'/source/meta_input/' +
																		key34
																			.replace(
																				/~/g,
																				'~0'
																			)
																			.replace(
																				/\//g,
																				'~1'
																			),
																	schemaPath:
																		'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/meta_input/additionalProperties/anyOf',
																	keyword:
																		'anyOf',
																	params: {},
																	message:
																		'must match a schema in anyOf',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err380,
																	];
																} else {
																	vErrors.push(
																		err380
																	);
																}
																errors++;
															} else {
																errors =
																	_errs640;
																if (
																	vErrors !==
																	null
																) {
																	if (
																		_errs640
																	) {
																		vErrors.length =
																			_errs640;
																	} else {
																		vErrors =
																			null;
																	}
																}
															}
														}
													} else {
														const err381 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/meta_input',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/meta_input/type',
															keyword: 'type',
															params: {
																type: 'object',
															},
															message:
																'must be object',
														};
														if (vErrors === null) {
															vErrors = [err381];
														} else {
															vErrors.push(
																err381
															);
														}
														errors++;
													}
												}
												if (
													data221.page_template !==
													undefined
												) {
													if (
														typeof data221.page_template !==
														'string'
													) {
														const err382 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/source/page_template',
															schemaPath:
																'#/properties/content/items/oneOf/1/properties/source/anyOf/1/properties/page_template/type',
															keyword: 'type',
															params: {
																type: 'string',
															},
															message:
																'must be string',
														};
														if (vErrors === null) {
															vErrors = [err382];
														} else {
															vErrors.push(
																err382
															);
														}
														errors++;
													}
												}
											} else {
												const err383 = {
													instancePath:
														instancePath +
														'/content/' +
														i24 +
														'/source',
													schemaPath:
														'#/properties/content/items/oneOf/1/properties/source/anyOf/1/type',
													keyword: 'type',
													params: { type: 'object' },
													message: 'must be object',
												};
												if (vErrors === null) {
													vErrors = [err383];
												} else {
													vErrors.push(err383);
												}
												errors++;
											}
											var _valid24 = _errs590 === errors;
											valid134 = valid134 || _valid24;
											if (!valid134) {
												const _errs656 = errors;
												if (Array.isArray(data221)) {
													const len30 =
														data221.length;
													for (
														let i30 = 0;
														i30 < len30;
														i30++
													) {
														let data248 =
															data221[i30];
														const _errs659 = errors;
														let valid150 = false;
														const _errs660 = errors;
														if (
															!validate40(
																data248,
																{
																	instancePath:
																		instancePath +
																		'/content/' +
																		i24 +
																		'/source/' +
																		i30,
																	parentData:
																		data221,
																	parentDataProperty:
																		i30,
																	rootData,
																}
															)
														) {
															vErrors =
																vErrors === null
																	? validate40.errors
																	: vErrors.concat(
																			validate40.errors
																		);
															errors =
																vErrors.length;
														}
														var _valid26 =
															_errs660 === errors;
														valid150 =
															valid150 ||
															_valid26;
														if (!valid150) {
															const _errs661 =
																errors;
															if (
																data248 &&
																typeof data248 ==
																	'object' &&
																!Array.isArray(
																	data248
																)
															) {
																if (
																	data248.post_title ===
																	undefined
																) {
																	const err384 =
																		{
																			instancePath:
																				instancePath +
																				'/content/' +
																				i24 +
																				'/source/' +
																				i30,
																			schemaPath:
																				'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/required',
																			keyword:
																				'required',
																			params: {
																				missingProperty:
																					'post_title',
																			},
																			message:
																				"must have required property '" +
																				'post_title' +
																				"'",
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err384,
																			];
																	} else {
																		vErrors.push(
																			err384
																		);
																	}
																	errors++;
																}
																for (const key36 in data248) {
																	if (
																		!func2.call(
																			schema12
																				.properties
																				.content
																				.items
																				.oneOf[1]
																				.properties
																				.source
																				.anyOf[2]
																				.items
																				.anyOf[1]
																				.properties,
																			key36
																		)
																	) {
																		const err385 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30,
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/additionalProperties',
																				keyword:
																					'additionalProperties',
																				params: {
																					additionalProperty:
																						key36,
																				},
																				message:
																					'must NOT have additional properties',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err385,
																				];
																		} else {
																			vErrors.push(
																				err385
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.post_author !==
																	undefined
																) {
																	let data249 =
																		data248.post_author;
																	if (
																		!(
																			typeof data249 ==
																				'number' &&
																			isFinite(
																				data249
																			)
																		)
																	) {
																		const err386 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/post_author',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_author/type',
																				keyword:
																					'type',
																				params: {
																					type: 'number',
																				},
																				message:
																					'must be number',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err386,
																				];
																		} else {
																			vErrors.push(
																				err386
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.post_date !==
																	undefined
																) {
																	if (
																		typeof data248.post_date !==
																		'string'
																	) {
																		const err387 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/post_date',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_date/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err387,
																				];
																		} else {
																			vErrors.push(
																				err387
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.post_content !==
																	undefined
																) {
																	if (
																		typeof data248.post_content !==
																		'string'
																	) {
																		const err388 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/post_content',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_content/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err388,
																				];
																		} else {
																			vErrors.push(
																				err388
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.post_title !==
																	undefined
																) {
																	if (
																		typeof data248.post_title !==
																		'string'
																	) {
																		const err389 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/post_title',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_title/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err389,
																				];
																		} else {
																			vErrors.push(
																				err389
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.post_excerpt !==
																	undefined
																) {
																	if (
																		typeof data248.post_excerpt !==
																		'string'
																	) {
																		const err390 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/post_excerpt',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_excerpt/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err390,
																				];
																		} else {
																			vErrors.push(
																				err390
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.post_status !==
																	undefined
																) {
																	let data254 =
																		data248.post_status;
																	if (
																		typeof data254 !==
																		'string'
																	) {
																		const err391 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/post_status',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_status/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err391,
																				];
																		} else {
																			vErrors.push(
																				err391
																			);
																		}
																		errors++;
																	}
																	if (
																		!(
																			data254 ===
																				'publish' ||
																			data254 ===
																				'pending' ||
																			data254 ===
																				'draft' ||
																			data254 ===
																				'auto-draft' ||
																			data254 ===
																				'future' ||
																			data254 ===
																				'private' ||
																			data254 ===
																				'inherit' ||
																			data254 ===
																				'trash'
																		)
																	) {
																		const err392 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/post_status',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_status/enum',
																				keyword:
																					'enum',
																				params: {
																					allowedValues:
																						schema12
																							.properties
																							.content
																							.items
																							.oneOf[1]
																							.properties
																							.source
																							.anyOf[2]
																							.items
																							.anyOf[1]
																							.properties
																							.post_status
																							.enum,
																				},
																				message:
																					'must be equal to one of the allowed values',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err392,
																				];
																		} else {
																			vErrors.push(
																				err392
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.post_type !==
																	undefined
																) {
																	if (
																		typeof data248.post_type !==
																		'string'
																	) {
																		const err393 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/post_type',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_type/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err393,
																				];
																		} else {
																			vErrors.push(
																				err393
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.comment_status !==
																	undefined
																) {
																	let data256 =
																		data248.comment_status;
																	if (
																		typeof data256 !==
																		'string'
																	) {
																		const err394 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/comment_status',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/comment_status/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err394,
																				];
																		} else {
																			vErrors.push(
																				err394
																			);
																		}
																		errors++;
																	}
																	if (
																		!(
																			data256 ===
																				'open' ||
																			data256 ===
																				'closed'
																		)
																	) {
																		const err395 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/comment_status',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/comment_status/enum',
																				keyword:
																					'enum',
																				params: {
																					allowedValues:
																						schema12
																							.properties
																							.content
																							.items
																							.oneOf[1]
																							.properties
																							.source
																							.anyOf[2]
																							.items
																							.anyOf[1]
																							.properties
																							.comment_status
																							.enum,
																				},
																				message:
																					'must be equal to one of the allowed values',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err395,
																				];
																		} else {
																			vErrors.push(
																				err395
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.post_password !==
																	undefined
																) {
																	if (
																		typeof data248.post_password !==
																		'string'
																	) {
																		const err396 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/post_password',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_password/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err396,
																				];
																		} else {
																			vErrors.push(
																				err396
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.post_name !==
																	undefined
																) {
																	if (
																		typeof data248.post_name !==
																		'string'
																	) {
																		const err397 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/post_name',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_name/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err397,
																				];
																		} else {
																			vErrors.push(
																				err397
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.post_parent_name !==
																	undefined
																) {
																	if (
																		typeof data248.post_parent_name !==
																		'string'
																	) {
																		const err398 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/post_parent_name',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_parent_name/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err398,
																				];
																		} else {
																			vErrors.push(
																				err398
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.menu_order !==
																	undefined
																) {
																	let data260 =
																		data248.menu_order;
																	if (
																		!(
																			typeof data260 ==
																				'number' &&
																			isFinite(
																				data260
																			)
																		)
																	) {
																		const err399 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/menu_order',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/menu_order/type',
																				keyword:
																					'type',
																				params: {
																					type: 'number',
																				},
																				message:
																					'must be number',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err399,
																				];
																		} else {
																			vErrors.push(
																				err399
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.post_mime_type !==
																	undefined
																) {
																	if (
																		typeof data248.post_mime_type !==
																		'string'
																	) {
																		const err400 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/post_mime_type',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_mime_type/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err400,
																				];
																		} else {
																			vErrors.push(
																				err400
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.guid !==
																	undefined
																) {
																	if (
																		typeof data248.guid !==
																		'string'
																	) {
																		const err401 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/guid',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/guid/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err401,
																				];
																		} else {
																			vErrors.push(
																				err401
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.post_category !==
																	undefined
																) {
																	let data263 =
																		data248.post_category;
																	if (
																		Array.isArray(
																			data263
																		)
																	) {
																		const len31 =
																			data263.length;
																		for (
																			let i31 = 0;
																			i31 <
																			len31;
																			i31++
																		) {
																			if (
																				typeof data263[
																					i31
																				] !==
																				'string'
																			) {
																				const err402 =
																					{
																						instancePath:
																							instancePath +
																							'/content/' +
																							i24 +
																							'/source/' +
																							i30 +
																							'/post_category/' +
																							i31,
																						schemaPath:
																							'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_category/items/type',
																						keyword:
																							'type',
																						params: {
																							type: 'string',
																						},
																						message:
																							'must be string',
																					};
																				if (
																					vErrors ===
																					null
																				) {
																					vErrors =
																						[
																							err402,
																						];
																				} else {
																					vErrors.push(
																						err402
																					);
																				}
																				errors++;
																			}
																		}
																	} else {
																		const err403 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/post_category',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_category/type',
																				keyword:
																					'type',
																				params: {
																					type: 'array',
																				},
																				message:
																					'must be array',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err403,
																				];
																		} else {
																			vErrors.push(
																				err403
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.post_tags !==
																	undefined
																) {
																	let data265 =
																		data248.post_tags;
																	if (
																		Array.isArray(
																			data265
																		)
																	) {
																		const len32 =
																			data265.length;
																		for (
																			let i32 = 0;
																			i32 <
																			len32;
																			i32++
																		) {
																			if (
																				typeof data265[
																					i32
																				] !==
																				'string'
																			) {
																				const err404 =
																					{
																						instancePath:
																							instancePath +
																							'/content/' +
																							i24 +
																							'/source/' +
																							i30 +
																							'/post_tags/' +
																							i32,
																						schemaPath:
																							'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_tags/items/type',
																						keyword:
																							'type',
																						params: {
																							type: 'string',
																						},
																						message:
																							'must be string',
																					};
																				if (
																					vErrors ===
																					null
																				) {
																					vErrors =
																						[
																							err404,
																						];
																				} else {
																					vErrors.push(
																						err404
																					);
																				}
																				errors++;
																			}
																		}
																	} else {
																		const err405 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/post_tags',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_tags/type',
																				keyword:
																					'type',
																				params: {
																					type: 'array',
																				},
																				message:
																					'must be array',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err405,
																				];
																		} else {
																			vErrors.push(
																				err405
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.tax_input !==
																	undefined
																) {
																	let data267 =
																		data248.tax_input;
																	if (
																		data267 &&
																		typeof data267 ==
																			'object' &&
																		!Array.isArray(
																			data267
																		)
																	) {
																		for (const key37 in data267) {
																			let data268 =
																				data267[
																					key37
																				];
																			if (
																				Array.isArray(
																					data268
																				)
																			) {
																				const len33 =
																					data268.length;
																				for (
																					let i33 = 0;
																					i33 <
																					len33;
																					i33++
																				) {
																					if (
																						typeof data268[
																							i33
																						] !==
																						'string'
																					) {
																						const err406 =
																							{
																								instancePath:
																									instancePath +
																									'/content/' +
																									i24 +
																									'/source/' +
																									i30 +
																									'/tax_input/' +
																									key37
																										.replace(
																											/~/g,
																											'~0'
																										)
																										.replace(
																											/\//g,
																											'~1'
																										) +
																									'/' +
																									i33,
																								schemaPath:
																									'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/tax_input/additionalProperties/items/type',
																								keyword:
																									'type',
																								params: {
																									type: 'string',
																								},
																								message:
																									'must be string',
																							};
																						if (
																							vErrors ===
																							null
																						) {
																							vErrors =
																								[
																									err406,
																								];
																						} else {
																							vErrors.push(
																								err406
																							);
																						}
																						errors++;
																					}
																				}
																			} else {
																				const err407 =
																					{
																						instancePath:
																							instancePath +
																							'/content/' +
																							i24 +
																							'/source/' +
																							i30 +
																							'/tax_input/' +
																							key37
																								.replace(
																									/~/g,
																									'~0'
																								)
																								.replace(
																									/\//g,
																									'~1'
																								),
																						schemaPath:
																							'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/tax_input/additionalProperties/type',
																						keyword:
																							'type',
																						params: {
																							type: 'array',
																						},
																						message:
																							'must be array',
																					};
																				if (
																					vErrors ===
																					null
																				) {
																					vErrors =
																						[
																							err407,
																						];
																				} else {
																					vErrors.push(
																						err407
																					);
																				}
																				errors++;
																			}
																		}
																	} else {
																		const err408 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/tax_input',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/tax_input/type',
																				keyword:
																					'type',
																				params: {
																					type: 'object',
																				},
																				message:
																					'must be object',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err408,
																				];
																		} else {
																			vErrors.push(
																				err408
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.meta_input !==
																	undefined
																) {
																	let data270 =
																		data248.meta_input;
																	if (
																		data270 &&
																		typeof data270 ==
																			'object' &&
																		!Array.isArray(
																			data270
																		)
																	) {
																		for (const key38 in data270) {
																			let data271 =
																				data270[
																					key38
																				];
																			const _errs711 =
																				errors;
																			let valid160 = false;
																			const _errs712 =
																				errors;
																			if (
																				typeof data271 !==
																				'string'
																			) {
																				const err409 =
																					{
																						instancePath:
																							instancePath +
																							'/content/' +
																							i24 +
																							'/source/' +
																							i30 +
																							'/meta_input/' +
																							key38
																								.replace(
																									/~/g,
																									'~0'
																								)
																								.replace(
																									/\//g,
																									'~1'
																								),
																						schemaPath:
																							'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/meta_input/additionalProperties/anyOf/0/type',
																						keyword:
																							'type',
																						params: {
																							type: 'string',
																						},
																						message:
																							'must be string',
																					};
																				if (
																					vErrors ===
																					null
																				) {
																					vErrors =
																						[
																							err409,
																						];
																				} else {
																					vErrors.push(
																						err409
																					);
																				}
																				errors++;
																			}
																			var _valid27 =
																				_errs712 ===
																				errors;
																			valid160 =
																				valid160 ||
																				_valid27;
																			if (
																				!valid160
																			) {
																				const _errs714 =
																					errors;
																				if (
																					typeof data271 !==
																					'boolean'
																				) {
																					const err410 =
																						{
																							instancePath:
																								instancePath +
																								'/content/' +
																								i24 +
																								'/source/' +
																								i30 +
																								'/meta_input/' +
																								key38
																									.replace(
																										/~/g,
																										'~0'
																									)
																									.replace(
																										/\//g,
																										'~1'
																									),
																							schemaPath:
																								'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/meta_input/additionalProperties/anyOf/1/type',
																							keyword:
																								'type',
																							params: {
																								type: 'boolean',
																							},
																							message:
																								'must be boolean',
																						};
																					if (
																						vErrors ===
																						null
																					) {
																						vErrors =
																							[
																								err410,
																							];
																					} else {
																						vErrors.push(
																							err410
																						);
																					}
																					errors++;
																				}
																				var _valid27 =
																					_errs714 ===
																					errors;
																				valid160 =
																					valid160 ||
																					_valid27;
																				if (
																					!valid160
																				) {
																					const _errs716 =
																						errors;
																					if (
																						!(
																							typeof data271 ==
																								'number' &&
																							isFinite(
																								data271
																							)
																						)
																					) {
																						const err411 =
																							{
																								instancePath:
																									instancePath +
																									'/content/' +
																									i24 +
																									'/source/' +
																									i30 +
																									'/meta_input/' +
																									key38
																										.replace(
																											/~/g,
																											'~0'
																										)
																										.replace(
																											/\//g,
																											'~1'
																										),
																								schemaPath:
																									'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/meta_input/additionalProperties/anyOf/2/type',
																								keyword:
																									'type',
																								params: {
																									type: 'number',
																								},
																								message:
																									'must be number',
																							};
																						if (
																							vErrors ===
																							null
																						) {
																							vErrors =
																								[
																									err411,
																								];
																						} else {
																							vErrors.push(
																								err411
																							);
																						}
																						errors++;
																					}
																					var _valid27 =
																						_errs716 ===
																						errors;
																					valid160 =
																						valid160 ||
																						_valid27;
																					if (
																						!valid160
																					) {
																						const _errs718 =
																							errors;
																						if (
																							Array.isArray(
																								data271
																							)
																						) {
																							const len34 =
																								data271.length;
																							for (
																								let i34 = 0;
																								i34 <
																								len34;
																								i34++
																							) {
																								if (
																									!validate34(
																										data271[
																											i34
																										],
																										{
																											instancePath:
																												instancePath +
																												'/content/' +
																												i24 +
																												'/source/' +
																												i30 +
																												'/meta_input/' +
																												key38
																													.replace(
																														/~/g,
																														'~0'
																													)
																													.replace(
																														/\//g,
																														'~1'
																													) +
																												'/' +
																												i34,
																											parentData:
																												data271,
																											parentDataProperty:
																												i34,
																											rootData,
																										}
																									)
																								) {
																									vErrors =
																										vErrors ===
																										null
																											? validate34.errors
																											: vErrors.concat(
																													validate34.errors
																												);
																									errors =
																										vErrors.length;
																								}
																							}
																						} else {
																							const err412 =
																								{
																									instancePath:
																										instancePath +
																										'/content/' +
																										i24 +
																										'/source/' +
																										i30 +
																										'/meta_input/' +
																										key38
																											.replace(
																												/~/g,
																												'~0'
																											)
																											.replace(
																												/\//g,
																												'~1'
																											),
																									schemaPath:
																										'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/meta_input/additionalProperties/anyOf/3/type',
																									keyword:
																										'type',
																									params: {
																										type: 'array',
																									},
																									message:
																										'must be array',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err412,
																									];
																							} else {
																								vErrors.push(
																									err412
																								);
																							}
																							errors++;
																						}
																						var _valid27 =
																							_errs718 ===
																							errors;
																						valid160 =
																							valid160 ||
																							_valid27;
																						if (
																							!valid160
																						) {
																							const _errs721 =
																								errors;
																							if (
																								data271 &&
																								typeof data271 ==
																									'object' &&
																								!Array.isArray(
																									data271
																								)
																							) {
																								for (const key39 in data271) {
																									if (
																										!validate34(
																											data271[
																												key39
																											],
																											{
																												instancePath:
																													instancePath +
																													'/content/' +
																													i24 +
																													'/source/' +
																													i30 +
																													'/meta_input/' +
																													key38
																														.replace(
																															/~/g,
																															'~0'
																														)
																														.replace(
																															/\//g,
																															'~1'
																														) +
																													'/' +
																													key39
																														.replace(
																															/~/g,
																															'~0'
																														)
																														.replace(
																															/\//g,
																															'~1'
																														),
																												parentData:
																													data271,
																												parentDataProperty:
																													key39,
																												rootData,
																											}
																										)
																									) {
																										vErrors =
																											vErrors ===
																											null
																												? validate34.errors
																												: vErrors.concat(
																														validate34.errors
																													);
																										errors =
																											vErrors.length;
																									}
																								}
																							} else {
																								const err413 =
																									{
																										instancePath:
																											instancePath +
																											'/content/' +
																											i24 +
																											'/source/' +
																											i30 +
																											'/meta_input/' +
																											key38
																												.replace(
																													/~/g,
																													'~0'
																												)
																												.replace(
																													/\//g,
																													'~1'
																												),
																										schemaPath:
																											'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/meta_input/additionalProperties/anyOf/4/type',
																										keyword:
																											'type',
																										params: {
																											type: 'object',
																										},
																										message:
																											'must be object',
																									};
																								if (
																									vErrors ===
																									null
																								) {
																									vErrors =
																										[
																											err413,
																										];
																								} else {
																									vErrors.push(
																										err413
																									);
																								}
																								errors++;
																							}
																							var _valid27 =
																								_errs721 ===
																								errors;
																							valid160 =
																								valid160 ||
																								_valid27;
																						}
																					}
																				}
																			}
																			if (
																				!valid160
																			) {
																				const err414 =
																					{
																						instancePath:
																							instancePath +
																							'/content/' +
																							i24 +
																							'/source/' +
																							i30 +
																							'/meta_input/' +
																							key38
																								.replace(
																									/~/g,
																									'~0'
																								)
																								.replace(
																									/\//g,
																									'~1'
																								),
																						schemaPath:
																							'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/meta_input/additionalProperties/anyOf',
																						keyword:
																							'anyOf',
																						params: {},
																						message:
																							'must match a schema in anyOf',
																					};
																				if (
																					vErrors ===
																					null
																				) {
																					vErrors =
																						[
																							err414,
																						];
																				} else {
																					vErrors.push(
																						err414
																					);
																				}
																				errors++;
																			} else {
																				errors =
																					_errs711;
																				if (
																					vErrors !==
																					null
																				) {
																					if (
																						_errs711
																					) {
																						vErrors.length =
																							_errs711;
																					} else {
																						vErrors =
																							null;
																					}
																				}
																			}
																		}
																	} else {
																		const err415 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/meta_input',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/meta_input/type',
																				keyword:
																					'type',
																				params: {
																					type: 'object',
																				},
																				message:
																					'must be object',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err415,
																				];
																		} else {
																			vErrors.push(
																				err415
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data248.page_template !==
																	undefined
																) {
																	if (
																		typeof data248.page_template !==
																		'string'
																	) {
																		const err416 =
																			{
																				instancePath:
																					instancePath +
																					'/content/' +
																					i24 +
																					'/source/' +
																					i30 +
																					'/page_template',
																				schemaPath:
																					'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/properties/page_template/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err416,
																				];
																		} else {
																			vErrors.push(
																				err416
																			);
																		}
																		errors++;
																	}
																}
															} else {
																const err417 = {
																	instancePath:
																		instancePath +
																		'/content/' +
																		i24 +
																		'/source/' +
																		i30,
																	schemaPath:
																		'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf/1/type',
																	keyword:
																		'type',
																	params: {
																		type: 'object',
																	},
																	message:
																		'must be object',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err417,
																	];
																} else {
																	vErrors.push(
																		err417
																	);
																}
																errors++;
															}
															var _valid26 =
																_errs661 ===
																errors;
															valid150 =
																valid150 ||
																_valid26;
														}
														if (!valid150) {
															const err418 = {
																instancePath:
																	instancePath +
																	'/content/' +
																	i24 +
																	'/source/' +
																	i30,
																schemaPath:
																	'#/properties/content/items/oneOf/1/properties/source/anyOf/2/items/anyOf',
																keyword:
																	'anyOf',
																params: {},
																message:
																	'must match a schema in anyOf',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err418,
																];
															} else {
																vErrors.push(
																	err418
																);
															}
															errors++;
														} else {
															errors = _errs659;
															if (
																vErrors !== null
															) {
																if (_errs659) {
																	vErrors.length =
																		_errs659;
																} else {
																	vErrors =
																		null;
																}
															}
														}
													}
												} else {
													const err419 = {
														instancePath:
															instancePath +
															'/content/' +
															i24 +
															'/source',
														schemaPath:
															'#/properties/content/items/oneOf/1/properties/source/anyOf/2/type',
														keyword: 'type',
														params: {
															type: 'array',
														},
														message:
															'must be array',
													};
													if (vErrors === null) {
														vErrors = [err419];
													} else {
														vErrors.push(err419);
													}
													errors++;
												}
												var _valid24 =
													_errs656 === errors;
												valid134 = valid134 || _valid24;
											}
										}
										if (!valid134) {
											const err420 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/source',
												schemaPath:
													'#/properties/content/items/oneOf/1/properties/source/anyOf',
												keyword: 'anyOf',
												params: {},
												message:
													'must match a schema in anyOf',
											};
											if (vErrors === null) {
												vErrors = [err420];
											} else {
												vErrors.push(err420);
											}
											errors++;
										} else {
											errors = _errs588;
											if (vErrors !== null) {
												if (_errs588) {
													vErrors.length = _errs588;
												} else {
													vErrors = null;
												}
											}
										}
									}
								} else {
									const err421 = {
										instancePath:
											instancePath + '/content/' + i24,
										schemaPath:
											'#/properties/content/items/oneOf/1/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err421];
									} else {
										vErrors.push(err421);
									}
									errors++;
								}
							} else if (tag0 === 'wxr') {
								const _errs729 = errors;
								let valid165 = false;
								let passing0 = null;
								const _errs730 = errors;
								if (
									data213 &&
									typeof data213 == 'object' &&
									!Array.isArray(data213)
								) {
									if (data213.authorsMap === undefined) {
										const err422 = {
											instancePath:
												instancePath +
												'/content/' +
												i24,
											schemaPath:
												'#/properties/content/items/oneOf/2/oneOf/0/required',
											keyword: 'required',
											params: {
												missingProperty: 'authorsMap',
											},
											message:
												"must have required property '" +
												'authorsMap' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err422];
										} else {
											vErrors.push(err422);
										}
										errors++;
									}
									if (data213.authorsMode === undefined) {
										const err423 = {
											instancePath:
												instancePath +
												'/content/' +
												i24,
											schemaPath:
												'#/properties/content/items/oneOf/2/oneOf/0/required',
											keyword: 'required',
											params: {
												missingProperty: 'authorsMode',
											},
											message:
												"must have required property '" +
												'authorsMode' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err423];
										} else {
											vErrors.push(err423);
										}
										errors++;
									}
									if (data213.source === undefined) {
										const err424 = {
											instancePath:
												instancePath +
												'/content/' +
												i24,
											schemaPath:
												'#/properties/content/items/oneOf/2/oneOf/0/required',
											keyword: 'required',
											params: {
												missingProperty: 'source',
											},
											message:
												"must have required property '" +
												'source' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err424];
										} else {
											vErrors.push(err424);
										}
										errors++;
									}
									if (data213.type === undefined) {
										const err425 = {
											instancePath:
												instancePath +
												'/content/' +
												i24,
											schemaPath:
												'#/properties/content/items/oneOf/2/oneOf/0/required',
											keyword: 'required',
											params: { missingProperty: 'type' },
											message:
												"must have required property '" +
												'type' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err425];
										} else {
											vErrors.push(err425);
										}
										errors++;
									}
									for (const key40 in data213) {
										if (
											!func2.call(
												schema12.properties.content
													.items.oneOf[2].oneOf[0]
													.properties,
												key40
											)
										) {
											const err426 = {
												instancePath:
													instancePath +
													'/content/' +
													i24,
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/0/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key40,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err426];
											} else {
												vErrors.push(err426);
											}
											errors++;
										}
									}
									if (data213.authorsMode !== undefined) {
										let data275 = data213.authorsMode;
										if (typeof data275 !== 'string') {
											const err427 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/authorsMode',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/0/properties/authorsMode/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err427];
											} else {
												vErrors.push(err427);
											}
											errors++;
										}
										if ('map' !== data275) {
											const err428 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/authorsMode',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/0/properties/authorsMode/const',
												keyword: 'const',
												params: { allowedValue: 'map' },
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err428];
											} else {
												vErrors.push(err428);
											}
											errors++;
										}
									}
									if (data213.authorsMap !== undefined) {
										let data276 = data213.authorsMap;
										if (
											data276 &&
											typeof data276 == 'object' &&
											!Array.isArray(data276)
										) {
											for (const key41 in data276) {
												if (
													typeof data276[key41] !==
													'string'
												) {
													const err429 = {
														instancePath:
															instancePath +
															'/content/' +
															i24 +
															'/authorsMap/' +
															key41
																.replace(
																	/~/g,
																	'~0'
																)
																.replace(
																	/\//g,
																	'~1'
																),
														schemaPath:
															'#/properties/content/items/oneOf/2/oneOf/0/properties/authorsMap/additionalProperties/type',
														keyword: 'type',
														params: {
															type: 'string',
														},
														message:
															'must be string',
													};
													if (vErrors === null) {
														vErrors = [err429];
													} else {
														vErrors.push(err429);
													}
													errors++;
												}
											}
										} else {
											const err430 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/authorsMap',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/0/properties/authorsMap/type',
												keyword: 'type',
												params: { type: 'object' },
												message: 'must be object',
											};
											if (vErrors === null) {
												vErrors = [err430];
											} else {
												vErrors.push(err430);
											}
											errors++;
										}
									}
									if (data213.urlsMode !== undefined) {
										let data278 = data213.urlsMode;
										if (typeof data278 !== 'string') {
											const err431 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/urlsMode',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/0/properties/urlsMode/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err431];
											} else {
												vErrors.push(err431);
											}
											errors++;
										}
										if (
											!(
												data278 === 'rewrite' ||
												data278 === 'preserve'
											)
										) {
											const err432 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/urlsMode',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/0/properties/urlsMode/enum',
												keyword: 'enum',
												params: {
													allowedValues:
														schema12.properties
															.content.items
															.oneOf[2].oneOf[0]
															.properties.urlsMode
															.enum,
												},
												message:
													'must be equal to one of the allowed values',
											};
											if (vErrors === null) {
												vErrors = [err432];
											} else {
												vErrors.push(err432);
											}
											errors++;
										}
									}
									if (data213.urlsMap !== undefined) {
										let data279 = data213.urlsMap;
										if (
											data279 &&
											typeof data279 == 'object' &&
											!Array.isArray(data279)
										) {
											for (const key42 in data279) {
												const _errs744 = errors;
												if (typeof key42 === 'string') {
													if (!formats0(key42)) {
														const err433 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/urlsMap',
															schemaPath:
																'#/definitions/DataSources.URLReference/format',
															keyword: 'format',
															params: {
																format: 'whatwg-http-url',
															},
															message:
																'must match format "' +
																'whatwg-http-url' +
																'"',
															propertyName: key42,
														};
														if (vErrors === null) {
															vErrors = [err433];
														} else {
															vErrors.push(
																err433
															);
														}
														errors++;
													}
												} else {
													const err434 = {
														instancePath:
															instancePath +
															'/content/' +
															i24 +
															'/urlsMap',
														schemaPath:
															'#/definitions/DataSources.URLReference/type',
														keyword: 'type',
														params: {
															type: 'string',
														},
														message:
															'must be string',
														propertyName: key42,
													};
													if (vErrors === null) {
														vErrors = [err434];
													} else {
														vErrors.push(err434);
													}
													errors++;
												}
												var valid168 =
													_errs744 === errors;
												if (!valid168) {
													const err435 = {
														instancePath:
															instancePath +
															'/content/' +
															i24 +
															'/urlsMap',
														schemaPath:
															'#/properties/content/items/oneOf/2/oneOf/0/properties/urlsMap/propertyNames',
														keyword:
															'propertyNames',
														params: {
															propertyName: key42,
														},
														message:
															'property name must be valid',
													};
													if (vErrors === null) {
														vErrors = [err435];
													} else {
														vErrors.push(err435);
													}
													errors++;
												}
											}
											for (const key43 in data279) {
												let data280 = data279[key43];
												if (
													typeof data280 === 'string'
												) {
													if (!formats0(data280)) {
														const err436 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/urlsMap/' +
																key43
																	.replace(
																		/~/g,
																		'~0'
																	)
																	.replace(
																		/\//g,
																		'~1'
																	),
															schemaPath:
																'#/definitions/DataSources.URLReference/format',
															keyword: 'format',
															params: {
																format: 'whatwg-http-url',
															},
															message:
																'must match format "' +
																'whatwg-http-url' +
																'"',
														};
														if (vErrors === null) {
															vErrors = [err436];
														} else {
															vErrors.push(
																err436
															);
														}
														errors++;
													}
												} else {
													const err437 = {
														instancePath:
															instancePath +
															'/content/' +
															i24 +
															'/urlsMap/' +
															key43
																.replace(
																	/~/g,
																	'~0'
																)
																.replace(
																	/\//g,
																	'~1'
																),
														schemaPath:
															'#/definitions/DataSources.URLReference/type',
														keyword: 'type',
														params: {
															type: 'string',
														},
														message:
															'must be string',
													};
													if (vErrors === null) {
														vErrors = [err437];
													} else {
														vErrors.push(err437);
													}
													errors++;
												}
											}
										} else {
											const err438 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/urlsMap',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/0/properties/urlsMap/type',
												keyword: 'type',
												params: { type: 'object' },
												message: 'must be object',
											};
											if (vErrors === null) {
												vErrors = [err438];
											} else {
												vErrors.push(err438);
											}
											errors++;
										}
									}
									if (data213.type !== undefined) {
										let data281 = data213.type;
										if (typeof data281 !== 'string') {
											const err439 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/type',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/0/properties/type/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err439];
											} else {
												vErrors.push(err439);
											}
											errors++;
										}
										if ('wxr' !== data281) {
											const err440 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/type',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/0/properties/type/const',
												keyword: 'const',
												params: { allowedValue: 'wxr' },
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err440];
											} else {
												vErrors.push(err440);
											}
											errors++;
										}
									}
									if (data213.source !== undefined) {
										let data282 = data213.source;
										const _errs754 = errors;
										let valid172 = false;
										const _errs755 = errors;
										if (
											!validate40(data282, {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/source',
												parentData: data213,
												parentDataProperty: 'source',
												rootData,
											})
										) {
											vErrors =
												vErrors === null
													? validate40.errors
													: vErrors.concat(
															validate40.errors
														);
											errors = vErrors.length;
										}
										var _valid29 = _errs755 === errors;
										valid172 = valid172 || _valid29;
										if (!valid172) {
											const _errs756 = errors;
											if (Array.isArray(data282)) {
												const len35 = data282.length;
												for (
													let i35 = 0;
													i35 < len35;
													i35++
												) {
													if (
														!validate40(
															data282[i35],
															{
																instancePath:
																	instancePath +
																	'/content/' +
																	i24 +
																	'/source/' +
																	i35,
																parentData:
																	data282,
																parentDataProperty:
																	i35,
																rootData,
															}
														)
													) {
														vErrors =
															vErrors === null
																? validate40.errors
																: vErrors.concat(
																		validate40.errors
																	);
														errors = vErrors.length;
													}
												}
											} else {
												const err441 = {
													instancePath:
														instancePath +
														'/content/' +
														i24 +
														'/source',
													schemaPath:
														'#/properties/content/items/oneOf/2/oneOf/0/properties/source/anyOf/1/type',
													keyword: 'type',
													params: { type: 'array' },
													message: 'must be array',
												};
												if (vErrors === null) {
													vErrors = [err441];
												} else {
													vErrors.push(err441);
												}
												errors++;
											}
											var _valid29 = _errs756 === errors;
											valid172 = valid172 || _valid29;
										}
										if (!valid172) {
											const err442 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/source',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/0/properties/source/anyOf',
												keyword: 'anyOf',
												params: {},
												message:
													'must match a schema in anyOf',
											};
											if (vErrors === null) {
												vErrors = [err442];
											} else {
												vErrors.push(err442);
											}
											errors++;
										} else {
											errors = _errs754;
											if (vErrors !== null) {
												if (_errs754) {
													vErrors.length = _errs754;
												} else {
													vErrors = null;
												}
											}
										}
									}
									if (data213.staticAssets !== undefined) {
										let data284 = data213.staticAssets;
										if (typeof data284 !== 'string') {
											const err443 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/staticAssets',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/0/properties/staticAssets/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err443];
											} else {
												vErrors.push(err443);
											}
											errors++;
										}
										if (
											!(
												data284 === 'fetch' ||
												data284 === 'hotlink'
											)
										) {
											const err444 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/staticAssets',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/0/properties/staticAssets/enum',
												keyword: 'enum',
												params: {
													allowedValues:
														schema12.properties
															.content.items
															.oneOf[2].oneOf[0]
															.properties
															.staticAssets.enum,
												},
												message:
													'must be equal to one of the allowed values',
											};
											if (vErrors === null) {
												vErrors = [err444];
											} else {
												vErrors.push(err444);
											}
											errors++;
										}
									}
									if (
										data213.defaultAuthorUsername !==
										undefined
									) {
										if (
											typeof data213.defaultAuthorUsername !==
											'string'
										) {
											const err445 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/defaultAuthorUsername',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/0/properties/defaultAuthorUsername/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err445];
											} else {
												vErrors.push(err445);
											}
											errors++;
										}
									}
									if (data213.importUsers !== undefined) {
										if (
											typeof data213.importUsers !==
											'boolean'
										) {
											const err446 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/importUsers',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/0/properties/importUsers/type',
												keyword: 'type',
												params: { type: 'boolean' },
												message: 'must be boolean',
											};
											if (vErrors === null) {
												vErrors = [err446];
											} else {
												vErrors.push(err446);
											}
											errors++;
										}
									}
									if (data213.importComments !== undefined) {
										if (
											typeof data213.importComments !==
											'boolean'
										) {
											const err447 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/importComments',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/0/properties/importComments/type',
												keyword: 'type',
												params: { type: 'boolean' },
												message: 'must be boolean',
											};
											if (vErrors === null) {
												vErrors = [err447];
											} else {
												vErrors.push(err447);
											}
											errors++;
										}
									}
								} else {
									const err448 = {
										instancePath:
											instancePath + '/content/' + i24,
										schemaPath:
											'#/properties/content/items/oneOf/2/oneOf/0/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err448];
									} else {
										vErrors.push(err448);
									}
									errors++;
								}
								var _valid28 = _errs730 === errors;
								if (_valid28) {
									valid165 = true;
									passing0 = 0;
								}
								const _errs767 = errors;
								if (
									data213 &&
									typeof data213 == 'object' &&
									!Array.isArray(data213)
								) {
									if (data213.source === undefined) {
										const err449 = {
											instancePath:
												instancePath +
												'/content/' +
												i24,
											schemaPath:
												'#/properties/content/items/oneOf/2/oneOf/1/required',
											keyword: 'required',
											params: {
												missingProperty: 'source',
											},
											message:
												"must have required property '" +
												'source' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err449];
										} else {
											vErrors.push(err449);
										}
										errors++;
									}
									if (data213.type === undefined) {
										const err450 = {
											instancePath:
												instancePath +
												'/content/' +
												i24,
											schemaPath:
												'#/properties/content/items/oneOf/2/oneOf/1/required',
											keyword: 'required',
											params: { missingProperty: 'type' },
											message:
												"must have required property '" +
												'type' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err450];
										} else {
											vErrors.push(err450);
										}
										errors++;
									}
									for (const key44 in data213) {
										if (
											!func2.call(
												schema12.properties.content
													.items.oneOf[2].oneOf[1]
													.properties,
												key44
											)
										) {
											const err451 = {
												instancePath:
													instancePath +
													'/content/' +
													i24,
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/1/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key44,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err451];
											} else {
												vErrors.push(err451);
											}
											errors++;
										}
									}
									if (data213.authorsMode !== undefined) {
										let data288 = data213.authorsMode;
										if (typeof data288 !== 'string') {
											const err452 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/authorsMode',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/1/properties/authorsMode/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err452];
											} else {
												vErrors.push(err452);
											}
											errors++;
										}
										if (
											!(
												data288 === 'create' ||
												data288 === 'default-author'
											)
										) {
											const err453 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/authorsMode',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/1/properties/authorsMode/enum',
												keyword: 'enum',
												params: {
													allowedValues:
														schema12.properties
															.content.items
															.oneOf[2].oneOf[1]
															.properties
															.authorsMode.enum,
												},
												message:
													'must be equal to one of the allowed values',
											};
											if (vErrors === null) {
												vErrors = [err453];
											} else {
												vErrors.push(err453);
											}
											errors++;
										}
									}
									if (data213.authorsMap !== undefined) {
										let data289 = data213.authorsMap;
										if (
											data289 &&
											typeof data289 == 'object' &&
											!Array.isArray(data289)
										) {
											for (const key45 in data289) {
												if (
													typeof data289[key45] !==
													'string'
												) {
													const err454 = {
														instancePath:
															instancePath +
															'/content/' +
															i24 +
															'/authorsMap/' +
															key45
																.replace(
																	/~/g,
																	'~0'
																)
																.replace(
																	/\//g,
																	'~1'
																),
														schemaPath:
															'#/properties/content/items/oneOf/2/oneOf/1/properties/authorsMap/additionalProperties/type',
														keyword: 'type',
														params: {
															type: 'string',
														},
														message:
															'must be string',
													};
													if (vErrors === null) {
														vErrors = [err454];
													} else {
														vErrors.push(err454);
													}
													errors++;
												}
											}
										} else {
											const err455 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/authorsMap',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/1/properties/authorsMap/type',
												keyword: 'type',
												params: { type: 'object' },
												message: 'must be object',
											};
											if (vErrors === null) {
												vErrors = [err455];
											} else {
												vErrors.push(err455);
											}
											errors++;
										}
									}
									if (data213.urlsMode !== undefined) {
										let data291 = data213.urlsMode;
										if (typeof data291 !== 'string') {
											const err456 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/urlsMode',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/1/properties/urlsMode/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err456];
											} else {
												vErrors.push(err456);
											}
											errors++;
										}
										if (
											!(
												data291 === 'rewrite' ||
												data291 === 'preserve'
											)
										) {
											const err457 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/urlsMode',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/1/properties/urlsMode/enum',
												keyword: 'enum',
												params: {
													allowedValues:
														schema12.properties
															.content.items
															.oneOf[2].oneOf[1]
															.properties.urlsMode
															.enum,
												},
												message:
													'must be equal to one of the allowed values',
											};
											if (vErrors === null) {
												vErrors = [err457];
											} else {
												vErrors.push(err457);
											}
											errors++;
										}
									}
									if (data213.urlsMap !== undefined) {
										let data292 = data213.urlsMap;
										if (
											data292 &&
											typeof data292 == 'object' &&
											!Array.isArray(data292)
										) {
											for (const key46 in data292) {
												const _errs781 = errors;
												if (typeof key46 === 'string') {
													if (!formats0(key46)) {
														const err458 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/urlsMap',
															schemaPath:
																'#/definitions/DataSources.URLReference/format',
															keyword: 'format',
															params: {
																format: 'whatwg-http-url',
															},
															message:
																'must match format "' +
																'whatwg-http-url' +
																'"',
															propertyName: key46,
														};
														if (vErrors === null) {
															vErrors = [err458];
														} else {
															vErrors.push(
																err458
															);
														}
														errors++;
													}
												} else {
													const err459 = {
														instancePath:
															instancePath +
															'/content/' +
															i24 +
															'/urlsMap',
														schemaPath:
															'#/definitions/DataSources.URLReference/type',
														keyword: 'type',
														params: {
															type: 'string',
														},
														message:
															'must be string',
														propertyName: key46,
													};
													if (vErrors === null) {
														vErrors = [err459];
													} else {
														vErrors.push(err459);
													}
													errors++;
												}
												var valid177 =
													_errs781 === errors;
												if (!valid177) {
													const err460 = {
														instancePath:
															instancePath +
															'/content/' +
															i24 +
															'/urlsMap',
														schemaPath:
															'#/properties/content/items/oneOf/2/oneOf/1/properties/urlsMap/propertyNames',
														keyword:
															'propertyNames',
														params: {
															propertyName: key46,
														},
														message:
															'property name must be valid',
													};
													if (vErrors === null) {
														vErrors = [err460];
													} else {
														vErrors.push(err460);
													}
													errors++;
												}
											}
											for (const key47 in data292) {
												let data293 = data292[key47];
												if (
													typeof data293 === 'string'
												) {
													if (!formats0(data293)) {
														const err461 = {
															instancePath:
																instancePath +
																'/content/' +
																i24 +
																'/urlsMap/' +
																key47
																	.replace(
																		/~/g,
																		'~0'
																	)
																	.replace(
																		/\//g,
																		'~1'
																	),
															schemaPath:
																'#/definitions/DataSources.URLReference/format',
															keyword: 'format',
															params: {
																format: 'whatwg-http-url',
															},
															message:
																'must match format "' +
																'whatwg-http-url' +
																'"',
														};
														if (vErrors === null) {
															vErrors = [err461];
														} else {
															vErrors.push(
																err461
															);
														}
														errors++;
													}
												} else {
													const err462 = {
														instancePath:
															instancePath +
															'/content/' +
															i24 +
															'/urlsMap/' +
															key47
																.replace(
																	/~/g,
																	'~0'
																)
																.replace(
																	/\//g,
																	'~1'
																),
														schemaPath:
															'#/definitions/DataSources.URLReference/type',
														keyword: 'type',
														params: {
															type: 'string',
														},
														message:
															'must be string',
													};
													if (vErrors === null) {
														vErrors = [err462];
													} else {
														vErrors.push(err462);
													}
													errors++;
												}
											}
										} else {
											const err463 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/urlsMap',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/1/properties/urlsMap/type',
												keyword: 'type',
												params: { type: 'object' },
												message: 'must be object',
											};
											if (vErrors === null) {
												vErrors = [err463];
											} else {
												vErrors.push(err463);
											}
											errors++;
										}
									}
									if (data213.type !== undefined) {
										let data294 = data213.type;
										if (typeof data294 !== 'string') {
											const err464 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/type',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/1/properties/type/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err464];
											} else {
												vErrors.push(err464);
											}
											errors++;
										}
										if ('wxr' !== data294) {
											const err465 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/type',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/1/properties/type/const',
												keyword: 'const',
												params: { allowedValue: 'wxr' },
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err465];
											} else {
												vErrors.push(err465);
											}
											errors++;
										}
									}
									if (data213.source !== undefined) {
										let data295 = data213.source;
										const _errs791 = errors;
										let valid181 = false;
										const _errs792 = errors;
										if (
											!validate40(data295, {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/source',
												parentData: data213,
												parentDataProperty: 'source',
												rootData,
											})
										) {
											vErrors =
												vErrors === null
													? validate40.errors
													: vErrors.concat(
															validate40.errors
														);
											errors = vErrors.length;
										}
										var _valid30 = _errs792 === errors;
										valid181 = valid181 || _valid30;
										if (!valid181) {
											const _errs793 = errors;
											if (Array.isArray(data295)) {
												const len36 = data295.length;
												for (
													let i36 = 0;
													i36 < len36;
													i36++
												) {
													if (
														!validate40(
															data295[i36],
															{
																instancePath:
																	instancePath +
																	'/content/' +
																	i24 +
																	'/source/' +
																	i36,
																parentData:
																	data295,
																parentDataProperty:
																	i36,
																rootData,
															}
														)
													) {
														vErrors =
															vErrors === null
																? validate40.errors
																: vErrors.concat(
																		validate40.errors
																	);
														errors = vErrors.length;
													}
												}
											} else {
												const err466 = {
													instancePath:
														instancePath +
														'/content/' +
														i24 +
														'/source',
													schemaPath:
														'#/properties/content/items/oneOf/2/oneOf/1/properties/source/anyOf/1/type',
													keyword: 'type',
													params: { type: 'array' },
													message: 'must be array',
												};
												if (vErrors === null) {
													vErrors = [err466];
												} else {
													vErrors.push(err466);
												}
												errors++;
											}
											var _valid30 = _errs793 === errors;
											valid181 = valid181 || _valid30;
										}
										if (!valid181) {
											const err467 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/source',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/1/properties/source/anyOf',
												keyword: 'anyOf',
												params: {},
												message:
													'must match a schema in anyOf',
											};
											if (vErrors === null) {
												vErrors = [err467];
											} else {
												vErrors.push(err467);
											}
											errors++;
										} else {
											errors = _errs791;
											if (vErrors !== null) {
												if (_errs791) {
													vErrors.length = _errs791;
												} else {
													vErrors = null;
												}
											}
										}
									}
									if (data213.staticAssets !== undefined) {
										let data297 = data213.staticAssets;
										if (typeof data297 !== 'string') {
											const err468 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/staticAssets',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/1/properties/staticAssets/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err468];
											} else {
												vErrors.push(err468);
											}
											errors++;
										}
										if (
											!(
												data297 === 'fetch' ||
												data297 === 'hotlink'
											)
										) {
											const err469 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/staticAssets',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/1/properties/staticAssets/enum',
												keyword: 'enum',
												params: {
													allowedValues:
														schema12.properties
															.content.items
															.oneOf[2].oneOf[1]
															.properties
															.staticAssets.enum,
												},
												message:
													'must be equal to one of the allowed values',
											};
											if (vErrors === null) {
												vErrors = [err469];
											} else {
												vErrors.push(err469);
											}
											errors++;
										}
									}
									if (
										data213.defaultAuthorUsername !==
										undefined
									) {
										if (
											typeof data213.defaultAuthorUsername !==
											'string'
										) {
											const err470 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/defaultAuthorUsername',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/1/properties/defaultAuthorUsername/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err470];
											} else {
												vErrors.push(err470);
											}
											errors++;
										}
									}
									if (data213.importUsers !== undefined) {
										if (
											typeof data213.importUsers !==
											'boolean'
										) {
											const err471 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/importUsers',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/1/properties/importUsers/type',
												keyword: 'type',
												params: { type: 'boolean' },
												message: 'must be boolean',
											};
											if (vErrors === null) {
												vErrors = [err471];
											} else {
												vErrors.push(err471);
											}
											errors++;
										}
									}
									if (data213.importComments !== undefined) {
										if (
											typeof data213.importComments !==
											'boolean'
										) {
											const err472 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/importComments',
												schemaPath:
													'#/properties/content/items/oneOf/2/oneOf/1/properties/importComments/type',
												keyword: 'type',
												params: { type: 'boolean' },
												message: 'must be boolean',
											};
											if (vErrors === null) {
												vErrors = [err472];
											} else {
												vErrors.push(err472);
											}
											errors++;
										}
									}
								} else {
									const err473 = {
										instancePath:
											instancePath + '/content/' + i24,
										schemaPath:
											'#/properties/content/items/oneOf/2/oneOf/1/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err473];
									} else {
										vErrors.push(err473);
									}
									errors++;
								}
								var _valid28 = _errs767 === errors;
								if (_valid28 && valid165) {
									valid165 = false;
									passing0 = [passing0, 1];
								} else {
									if (_valid28) {
										valid165 = true;
										passing0 = 1;
									}
								}
								if (!valid165) {
									const err474 = {
										instancePath:
											instancePath + '/content/' + i24,
										schemaPath:
											'#/properties/content/items/oneOf/2/oneOf',
										keyword: 'oneOf',
										params: { passingSchemas: passing0 },
										message:
											'must match exactly one schema in oneOf',
									};
									if (vErrors === null) {
										vErrors = [err474];
									} else {
										vErrors.push(err474);
									}
									errors++;
								} else {
									errors = _errs729;
									if (vErrors !== null) {
										if (_errs729) {
											vErrors.length = _errs729;
										} else {
											vErrors = null;
										}
									}
								}
								if (
									data213 &&
									typeof data213 == 'object' &&
									!Array.isArray(data213)
								) {
									if (data213.type === undefined) {
										const err475 = {
											instancePath:
												instancePath +
												'/content/' +
												i24,
											schemaPath:
												'#/properties/content/items/oneOf/2/required',
											keyword: 'required',
											params: { missingProperty: 'type' },
											message:
												"must have required property '" +
												'type' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err475];
										} else {
											vErrors.push(err475);
										}
										errors++;
									}
									if (data213.type !== undefined) {
										let data301 = data213.type;
										if (typeof data301 !== 'string') {
											const err476 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/type',
												schemaPath:
													'#/properties/content/items/oneOf/2/properties/type/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err476];
											} else {
												vErrors.push(err476);
											}
											errors++;
										}
										if ('wxr' !== data301) {
											const err477 = {
												instancePath:
													instancePath +
													'/content/' +
													i24 +
													'/type',
												schemaPath:
													'#/properties/content/items/oneOf/2/properties/type/const',
												keyword: 'const',
												params: { allowedValue: 'wxr' },
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err477];
											} else {
												vErrors.push(err477);
											}
											errors++;
										}
									}
								} else {
									const err478 = {
										instancePath:
											instancePath + '/content/' + i24,
										schemaPath:
											'#/properties/content/items/oneOf/2/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err478];
									} else {
										vErrors.push(err478);
									}
									errors++;
								}
							} else {
								const err479 = {
									instancePath:
										instancePath + '/content/' + i24,
									schemaPath:
										'#/properties/content/items/discriminator',
									keyword: 'discriminator',
									params: {
										error: 'mapping',
										tag: 'type',
										tagValue: tag0,
									},
									message:
										'value of tag "type" must be in oneOf',
								};
								if (vErrors === null) {
									vErrors = [err479];
								} else {
									vErrors.push(err479);
								}
								errors++;
							}
						} else {
							const err480 = {
								instancePath: instancePath + '/content/' + i24,
								schemaPath:
									'#/properties/content/items/discriminator',
								keyword: 'discriminator',
								params: {
									error: 'tag',
									tag: 'type',
									tagValue: tag0,
								},
								message: 'tag "type" must be string',
							};
							if (vErrors === null) {
								vErrors = [err480];
							} else {
								vErrors.push(err480);
							}
							errors++;
						}
					} else {
						const err481 = {
							instancePath: instancePath + '/content/' + i24,
							schemaPath: '#/properties/content/items/type',
							keyword: 'type',
							params: { type: 'object' },
							message: 'must be object',
						};
						if (vErrors === null) {
							vErrors = [err481];
						} else {
							vErrors.push(err481);
						}
						errors++;
					}
				}
			} else {
				const err482 = {
					instancePath: instancePath + '/content',
					schemaPath: '#/properties/content/type',
					keyword: 'type',
					params: { type: 'array' },
					message: 'must be array',
				};
				if (vErrors === null) {
					vErrors = [err482];
				} else {
					vErrors.push(err482);
				}
				errors++;
			}
		}
		if (data.users !== undefined) {
			let data302 = data.users;
			if (Array.isArray(data302)) {
				const len37 = data302.length;
				for (let i37 = 0; i37 < len37; i37++) {
					let data303 = data302[i37];
					if (
						data303 &&
						typeof data303 == 'object' &&
						!Array.isArray(data303)
					) {
						if (data303.username === undefined) {
							const err483 = {
								instancePath: instancePath + '/users/' + i37,
								schemaPath: '#/properties/users/items/required',
								keyword: 'required',
								params: { missingProperty: 'username' },
								message:
									"must have required property '" +
									'username' +
									"'",
							};
							if (vErrors === null) {
								vErrors = [err483];
							} else {
								vErrors.push(err483);
							}
							errors++;
						}
						if (data303.email === undefined) {
							const err484 = {
								instancePath: instancePath + '/users/' + i37,
								schemaPath: '#/properties/users/items/required',
								keyword: 'required',
								params: { missingProperty: 'email' },
								message:
									"must have required property '" +
									'email' +
									"'",
							};
							if (vErrors === null) {
								vErrors = [err484];
							} else {
								vErrors.push(err484);
							}
							errors++;
						}
						if (data303.role === undefined) {
							const err485 = {
								instancePath: instancePath + '/users/' + i37,
								schemaPath: '#/properties/users/items/required',
								keyword: 'required',
								params: { missingProperty: 'role' },
								message:
									"must have required property '" +
									'role' +
									"'",
							};
							if (vErrors === null) {
								vErrors = [err485];
							} else {
								vErrors.push(err485);
							}
							errors++;
						}
						if (data303.meta === undefined) {
							const err486 = {
								instancePath: instancePath + '/users/' + i37,
								schemaPath: '#/properties/users/items/required',
								keyword: 'required',
								params: { missingProperty: 'meta' },
								message:
									"must have required property '" +
									'meta' +
									"'",
							};
							if (vErrors === null) {
								vErrors = [err486];
							} else {
								vErrors.push(err486);
							}
							errors++;
						}
						for (const key48 in data303) {
							if (
								!(
									key48 === 'username' ||
									key48 === 'email' ||
									key48 === 'role' ||
									key48 === 'meta'
								)
							) {
								const err487 = {
									instancePath:
										instancePath + '/users/' + i37,
									schemaPath:
										'#/properties/users/items/additionalProperties',
									keyword: 'additionalProperties',
									params: { additionalProperty: key48 },
									message:
										'must NOT have additional properties',
								};
								if (vErrors === null) {
									vErrors = [err487];
								} else {
									vErrors.push(err487);
								}
								errors++;
							}
						}
						if (data303.username !== undefined) {
							if (typeof data303.username !== 'string') {
								const err488 = {
									instancePath:
										instancePath +
										'/users/' +
										i37 +
										'/username',
									schemaPath:
										'#/properties/users/items/properties/username/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err488];
								} else {
									vErrors.push(err488);
								}
								errors++;
							}
						}
						if (data303.email !== undefined) {
							if (typeof data303.email !== 'string') {
								const err489 = {
									instancePath:
										instancePath +
										'/users/' +
										i37 +
										'/email',
									schemaPath:
										'#/properties/users/items/properties/email/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err489];
								} else {
									vErrors.push(err489);
								}
								errors++;
							}
						}
						if (data303.role !== undefined) {
							if (typeof data303.role !== 'string') {
								const err490 = {
									instancePath:
										instancePath +
										'/users/' +
										i37 +
										'/role',
									schemaPath:
										'#/properties/users/items/properties/role/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err490];
								} else {
									vErrors.push(err490);
								}
								errors++;
							}
						}
						if (data303.meta !== undefined) {
							let data307 = data303.meta;
							if (
								data307 &&
								typeof data307 == 'object' &&
								!Array.isArray(data307)
							) {
								for (const key49 in data307) {
									if (typeof data307[key49] !== 'string') {
										const err491 = {
											instancePath:
												instancePath +
												'/users/' +
												i37 +
												'/meta/' +
												key49
													.replace(/~/g, '~0')
													.replace(/\//g, '~1'),
											schemaPath:
												'#/properties/users/items/properties/meta/additionalProperties/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err491];
										} else {
											vErrors.push(err491);
										}
										errors++;
									}
								}
							} else {
								const err492 = {
									instancePath:
										instancePath +
										'/users/' +
										i37 +
										'/meta',
									schemaPath:
										'#/properties/users/items/properties/meta/type',
									keyword: 'type',
									params: { type: 'object' },
									message: 'must be object',
								};
								if (vErrors === null) {
									vErrors = [err492];
								} else {
									vErrors.push(err492);
								}
								errors++;
							}
						}
					} else {
						const err493 = {
							instancePath: instancePath + '/users/' + i37,
							schemaPath: '#/properties/users/items/type',
							keyword: 'type',
							params: { type: 'object' },
							message: 'must be object',
						};
						if (vErrors === null) {
							vErrors = [err493];
						} else {
							vErrors.push(err493);
						}
						errors++;
					}
				}
			} else {
				const err494 = {
					instancePath: instancePath + '/users',
					schemaPath: '#/properties/users/type',
					keyword: 'type',
					params: { type: 'array' },
					message: 'must be array',
				};
				if (vErrors === null) {
					vErrors = [err494];
				} else {
					vErrors.push(err494);
				}
				errors++;
			}
		}
		if (data.roles !== undefined) {
			let data309 = data.roles;
			if (Array.isArray(data309)) {
				const len38 = data309.length;
				for (let i38 = 0; i38 < len38; i38++) {
					let data310 = data309[i38];
					if (
						data310 &&
						typeof data310 == 'object' &&
						!Array.isArray(data310)
					) {
						if (data310.name === undefined) {
							const err495 = {
								instancePath: instancePath + '/roles/' + i38,
								schemaPath: '#/properties/roles/items/required',
								keyword: 'required',
								params: { missingProperty: 'name' },
								message:
									"must have required property '" +
									'name' +
									"'",
							};
							if (vErrors === null) {
								vErrors = [err495];
							} else {
								vErrors.push(err495);
							}
							errors++;
						}
						if (data310.capabilities === undefined) {
							const err496 = {
								instancePath: instancePath + '/roles/' + i38,
								schemaPath: '#/properties/roles/items/required',
								keyword: 'required',
								params: { missingProperty: 'capabilities' },
								message:
									"must have required property '" +
									'capabilities' +
									"'",
							};
							if (vErrors === null) {
								vErrors = [err496];
							} else {
								vErrors.push(err496);
							}
							errors++;
						}
						for (const key50 in data310) {
							if (
								!(key50 === 'name' || key50 === 'capabilities')
							) {
								const err497 = {
									instancePath:
										instancePath + '/roles/' + i38,
									schemaPath:
										'#/properties/roles/items/additionalProperties',
									keyword: 'additionalProperties',
									params: { additionalProperty: key50 },
									message:
										'must NOT have additional properties',
								};
								if (vErrors === null) {
									vErrors = [err497];
								} else {
									vErrors.push(err497);
								}
								errors++;
							}
						}
						if (data310.name !== undefined) {
							if (typeof data310.name !== 'string') {
								const err498 = {
									instancePath:
										instancePath +
										'/roles/' +
										i38 +
										'/name',
									schemaPath:
										'#/properties/roles/items/properties/name/type',
									keyword: 'type',
									params: { type: 'string' },
									message: 'must be string',
								};
								if (vErrors === null) {
									vErrors = [err498];
								} else {
									vErrors.push(err498);
								}
								errors++;
							}
						}
						if (data310.capabilities !== undefined) {
							let data312 = data310.capabilities;
							if (
								data312 &&
								typeof data312 == 'object' &&
								!Array.isArray(data312)
							) {
								for (const key51 in data312) {
									if (typeof data312[key51] !== 'string') {
										const err499 = {
											instancePath:
												instancePath +
												'/roles/' +
												i38 +
												'/capabilities/' +
												key51
													.replace(/~/g, '~0')
													.replace(/\//g, '~1'),
											schemaPath:
												'#/properties/roles/items/properties/capabilities/additionalProperties/type',
											keyword: 'type',
											params: { type: 'string' },
											message: 'must be string',
										};
										if (vErrors === null) {
											vErrors = [err499];
										} else {
											vErrors.push(err499);
										}
										errors++;
									}
								}
							} else {
								const err500 = {
									instancePath:
										instancePath +
										'/roles/' +
										i38 +
										'/capabilities',
									schemaPath:
										'#/properties/roles/items/properties/capabilities/type',
									keyword: 'type',
									params: { type: 'object' },
									message: 'must be object',
								};
								if (vErrors === null) {
									vErrors = [err500];
								} else {
									vErrors.push(err500);
								}
								errors++;
							}
						}
					} else {
						const err501 = {
							instancePath: instancePath + '/roles/' + i38,
							schemaPath: '#/properties/roles/items/type',
							keyword: 'type',
							params: { type: 'object' },
							message: 'must be object',
						};
						if (vErrors === null) {
							vErrors = [err501];
						} else {
							vErrors.push(err501);
						}
						errors++;
					}
				}
			} else {
				const err502 = {
					instancePath: instancePath + '/roles',
					schemaPath: '#/properties/roles/type',
					keyword: 'type',
					params: { type: 'array' },
					message: 'must be array',
				};
				if (vErrors === null) {
					vErrors = [err502];
				} else {
					vErrors.push(err502);
				}
				errors++;
			}
		}
		if (data.additionalStepsAfterExecution !== undefined) {
			let data314 = data.additionalStepsAfterExecution;
			if (Array.isArray(data314)) {
				const len39 = data314.length;
				for (let i39 = 0; i39 < len39; i39++) {
					let data315 = data314[i39];
					if (
						data315 &&
						typeof data315 == 'object' &&
						!Array.isArray(data315)
					) {
						if (data315.step === undefined) {
							const err503 = {
								instancePath:
									instancePath +
									'/additionalStepsAfterExecution/' +
									i39,
								schemaPath:
									'#/properties/additionalStepsAfterExecution/items/required',
								keyword: 'required',
								params: { missingProperty: 'step' },
								message:
									"must have required property '" +
									'step' +
									"'",
							};
							if (vErrors === null) {
								vErrors = [err503];
							} else {
								vErrors.push(err503);
							}
							errors++;
						}
						const tag1 = data315.step;
						if (typeof tag1 == 'string') {
							if (tag1 === 'activatePlugin') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err504 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/0/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err504];
										} else {
											vErrors.push(err504);
										}
										errors++;
									}
									if (data315.pluginPath === undefined) {
										const err505 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/0/required',
											keyword: 'required',
											params: {
												missingProperty: 'pluginPath',
											},
											message:
												"must have required property '" +
												'pluginPath' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err505];
										} else {
											vErrors.push(err505);
										}
										errors++;
									}
									for (const key52 in data315) {
										if (
											!(
												key52 === 'step' ||
												key52 === 'pluginPath' ||
												key52 === 'humanReadableName'
											)
										) {
											const err506 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/0/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key52,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err506];
											} else {
												vErrors.push(err506);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data316 = data315.step;
										if (typeof data316 !== 'string') {
											const err507 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/0/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err507];
											} else {
												vErrors.push(err507);
											}
											errors++;
										}
										if ('activatePlugin' !== data316) {
											const err508 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/0/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue:
														'activatePlugin',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err508];
											} else {
												vErrors.push(err508);
											}
											errors++;
										}
									}
									if (data315.pluginPath !== undefined) {
										if (
											typeof data315.pluginPath !==
											'string'
										) {
											const err509 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/pluginPath',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/0/properties/pluginPath/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err509];
											} else {
												vErrors.push(err509);
											}
											errors++;
										}
									}
									if (
										data315.humanReadableName !== undefined
									) {
										if (
											typeof data315.humanReadableName !==
											'string'
										) {
											const err510 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/humanReadableName',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/0/properties/humanReadableName/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err510];
											} else {
												vErrors.push(err510);
											}
											errors++;
										}
									}
								} else {
									const err511 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/0/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err511];
									} else {
										vErrors.push(err511);
									}
									errors++;
								}
							} else if (tag1 === 'activateTheme') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err512 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/1/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err512];
										} else {
											vErrors.push(err512);
										}
										errors++;
									}
									if (
										data315.themeDirectoryName === undefined
									) {
										const err513 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/1/required',
											keyword: 'required',
											params: {
												missingProperty:
													'themeDirectoryName',
											},
											message:
												"must have required property '" +
												'themeDirectoryName' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err513];
										} else {
											vErrors.push(err513);
										}
										errors++;
									}
									for (const key53 in data315) {
										if (
											!(
												key53 === 'step' ||
												key53 ===
													'themeDirectoryName' ||
												key53 === 'humanReadableName'
											)
										) {
											const err514 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/1/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key53,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err514];
											} else {
												vErrors.push(err514);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data319 = data315.step;
										if (typeof data319 !== 'string') {
											const err515 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/1/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err515];
											} else {
												vErrors.push(err515);
											}
											errors++;
										}
										if ('activateTheme' !== data319) {
											const err516 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/1/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue:
														'activateTheme',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err516];
											} else {
												vErrors.push(err516);
											}
											errors++;
										}
									}
									if (
										data315.themeDirectoryName !== undefined
									) {
										if (
											typeof data315.themeDirectoryName !==
											'string'
										) {
											const err517 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/themeDirectoryName',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/1/properties/themeDirectoryName/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err517];
											} else {
												vErrors.push(err517);
											}
											errors++;
										}
									}
									if (
										data315.humanReadableName !== undefined
									) {
										if (
											typeof data315.humanReadableName !==
											'string'
										) {
											const err518 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/humanReadableName',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/1/properties/humanReadableName/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err518];
											} else {
												vErrors.push(err518);
											}
											errors++;
										}
									}
								} else {
									const err519 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/1/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err519];
									} else {
										vErrors.push(err519);
									}
									errors++;
								}
							} else if (tag1 === 'cp') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err520 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/2/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err520];
										} else {
											vErrors.push(err520);
										}
										errors++;
									}
									if (data315.fromPath === undefined) {
										const err521 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/2/required',
											keyword: 'required',
											params: {
												missingProperty: 'fromPath',
											},
											message:
												"must have required property '" +
												'fromPath' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err521];
										} else {
											vErrors.push(err521);
										}
										errors++;
									}
									if (data315.toPath === undefined) {
										const err522 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/2/required',
											keyword: 'required',
											params: {
												missingProperty: 'toPath',
											},
											message:
												"must have required property '" +
												'toPath' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err522];
										} else {
											vErrors.push(err522);
										}
										errors++;
									}
									for (const key54 in data315) {
										if (
											!(
												key54 === 'step' ||
												key54 === 'fromPath' ||
												key54 === 'toPath'
											)
										) {
											const err523 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/2/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key54,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err523];
											} else {
												vErrors.push(err523);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data322 = data315.step;
										if (typeof data322 !== 'string') {
											const err524 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/2/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err524];
											} else {
												vErrors.push(err524);
											}
											errors++;
										}
										if ('cp' !== data322) {
											const err525 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/2/properties/step/const',
												keyword: 'const',
												params: { allowedValue: 'cp' },
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err525];
											} else {
												vErrors.push(err525);
											}
											errors++;
										}
									}
									if (data315.fromPath !== undefined) {
										if (
											typeof data315.fromPath !== 'string'
										) {
											const err526 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/fromPath',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/2/properties/fromPath/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err526];
											} else {
												vErrors.push(err526);
											}
											errors++;
										}
									}
									if (data315.toPath !== undefined) {
										if (
											typeof data315.toPath !== 'string'
										) {
											const err527 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/toPath',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/2/properties/toPath/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err527];
											} else {
												vErrors.push(err527);
											}
											errors++;
										}
									}
								} else {
									const err528 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/2/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err528];
									} else {
										vErrors.push(err528);
									}
									errors++;
								}
							} else if (tag1 === 'defineConstants') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err529 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/3/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err529];
										} else {
											vErrors.push(err529);
										}
										errors++;
									}
									if (data315.constants === undefined) {
										const err530 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/3/required',
											keyword: 'required',
											params: {
												missingProperty: 'constants',
											},
											message:
												"must have required property '" +
												'constants' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err530];
										} else {
											vErrors.push(err530);
										}
										errors++;
									}
									for (const key55 in data315) {
										if (
											!(
												key55 === 'step' ||
												key55 === 'constants'
											)
										) {
											const err531 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/3/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key55,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err531];
											} else {
												vErrors.push(err531);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data325 = data315.step;
										if (typeof data325 !== 'string') {
											const err532 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/3/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err532];
											} else {
												vErrors.push(err532);
											}
											errors++;
										}
										if ('defineConstants' !== data325) {
											const err533 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/3/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue:
														'defineConstants',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err533];
											} else {
												vErrors.push(err533);
											}
											errors++;
										}
									}
									if (data315.constants !== undefined) {
										let data326 = data315.constants;
										if (
											data326 &&
											typeof data326 == 'object' &&
											!Array.isArray(data326)
										) {
											for (const key56 in data326) {
												if (
													!(
														key56 === 'WP_DEBUG' ||
														key56 ===
															'WP_DEBUG_LOG' ||
														key56 ===
															'WP_DEBUG_DISPLAY' ||
														key56 === 'SCRIPT_DEBUG'
													)
												) {
													let data327 =
														data326[key56];
													if (
														typeof data327 !==
															'boolean' &&
														typeof data327 !==
															'string' &&
														!(
															typeof data327 ==
																'number' &&
															isFinite(data327)
														)
													) {
														const err534 = {
															instancePath:
																instancePath +
																'/additionalStepsAfterExecution/' +
																i39 +
																'/constants/' +
																key56
																	.replace(
																		/~/g,
																		'~0'
																	)
																	.replace(
																		/\//g,
																		'~1'
																	),
															schemaPath:
																'#/properties/additionalStepsAfterExecution/items/oneOf/3/properties/constants/additionalProperties/type',
															keyword: 'type',
															params: {
																type: schema12
																	.properties
																	.additionalStepsAfterExecution
																	.items
																	.oneOf[3]
																	.properties
																	.constants
																	.additionalProperties
																	.type,
															},
															message:
																'must be boolean,string,number',
														};
														if (vErrors === null) {
															vErrors = [err534];
														} else {
															vErrors.push(
																err534
															);
														}
														errors++;
													}
												}
											}
											if (
												data326.WP_DEBUG !== undefined
											) {
												if (
													typeof data326.WP_DEBUG !==
													'boolean'
												) {
													const err535 = {
														instancePath:
															instancePath +
															'/additionalStepsAfterExecution/' +
															i39 +
															'/constants/WP_DEBUG',
														schemaPath:
															'#/properties/additionalStepsAfterExecution/items/oneOf/3/properties/constants/properties/WP_DEBUG/type',
														keyword: 'type',
														params: {
															type: 'boolean',
														},
														message:
															'must be boolean',
													};
													if (vErrors === null) {
														vErrors = [err535];
													} else {
														vErrors.push(err535);
													}
													errors++;
												}
											}
											if (
												data326.WP_DEBUG_LOG !==
												undefined
											) {
												if (
													typeof data326.WP_DEBUG_LOG !==
													'boolean'
												) {
													const err536 = {
														instancePath:
															instancePath +
															'/additionalStepsAfterExecution/' +
															i39 +
															'/constants/WP_DEBUG_LOG',
														schemaPath:
															'#/properties/additionalStepsAfterExecution/items/oneOf/3/properties/constants/properties/WP_DEBUG_LOG/type',
														keyword: 'type',
														params: {
															type: 'boolean',
														},
														message:
															'must be boolean',
													};
													if (vErrors === null) {
														vErrors = [err536];
													} else {
														vErrors.push(err536);
													}
													errors++;
												}
											}
											if (
												data326.WP_DEBUG_DISPLAY !==
												undefined
											) {
												if (
													typeof data326.WP_DEBUG_DISPLAY !==
													'boolean'
												) {
													const err537 = {
														instancePath:
															instancePath +
															'/additionalStepsAfterExecution/' +
															i39 +
															'/constants/WP_DEBUG_DISPLAY',
														schemaPath:
															'#/properties/additionalStepsAfterExecution/items/oneOf/3/properties/constants/properties/WP_DEBUG_DISPLAY/type',
														keyword: 'type',
														params: {
															type: 'boolean',
														},
														message:
															'must be boolean',
													};
													if (vErrors === null) {
														vErrors = [err537];
													} else {
														vErrors.push(err537);
													}
													errors++;
												}
											}
											if (
												data326.SCRIPT_DEBUG !==
												undefined
											) {
												if (
													typeof data326.SCRIPT_DEBUG !==
													'boolean'
												) {
													const err538 = {
														instancePath:
															instancePath +
															'/additionalStepsAfterExecution/' +
															i39 +
															'/constants/SCRIPT_DEBUG',
														schemaPath:
															'#/properties/additionalStepsAfterExecution/items/oneOf/3/properties/constants/properties/SCRIPT_DEBUG/type',
														keyword: 'type',
														params: {
															type: 'boolean',
														},
														message:
															'must be boolean',
													};
													if (vErrors === null) {
														vErrors = [err538];
													} else {
														vErrors.push(err538);
													}
													errors++;
												}
											}
										} else {
											const err539 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/constants',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/3/properties/constants/type',
												keyword: 'type',
												params: { type: 'object' },
												message: 'must be object',
											};
											if (vErrors === null) {
												vErrors = [err539];
											} else {
												vErrors.push(err539);
											}
											errors++;
										}
									}
								} else {
									const err540 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/3/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err540];
									} else {
										vErrors.push(err540);
									}
									errors++;
								}
							} else if (tag1 === 'enableMultisite') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err541 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/4/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err541];
										} else {
											vErrors.push(err541);
										}
										errors++;
									}
									for (const key57 in data315) {
										if (!(key57 === 'step')) {
											const err542 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/4/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key57,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err542];
											} else {
												vErrors.push(err542);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data332 = data315.step;
										if (typeof data332 !== 'string') {
											const err543 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/4/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err543];
											} else {
												vErrors.push(err543);
											}
											errors++;
										}
										if ('enableMultisite' !== data332) {
											const err544 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/4/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue:
														'enableMultisite',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err544];
											} else {
												vErrors.push(err544);
											}
											errors++;
										}
									}
								} else {
									const err545 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/4/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err545];
									} else {
										vErrors.push(err545);
									}
									errors++;
								}
							} else if (tag1 === 'importContent') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err546 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/5/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err546];
										} else {
											vErrors.push(err546);
										}
										errors++;
									}
									if (data315.content === undefined) {
										const err547 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/5/required',
											keyword: 'required',
											params: {
												missingProperty: 'content',
											},
											message:
												"must have required property '" +
												'content' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err547];
										} else {
											vErrors.push(err547);
										}
										errors++;
									}
									for (const key58 in data315) {
										if (
											!(
												key58 === 'step' ||
												key58 === 'content'
											)
										) {
											const err548 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/5/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key58,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err548];
											} else {
												vErrors.push(err548);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data333 = data315.step;
										if (typeof data333 !== 'string') {
											const err549 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err549];
											} else {
												vErrors.push(err549);
											}
											errors++;
										}
										if ('importContent' !== data333) {
											const err550 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue:
														'importContent',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err550];
											} else {
												vErrors.push(err550);
											}
											errors++;
										}
									}
									if (data315.content !== undefined) {
										let data334 = data315.content;
										if (Array.isArray(data334)) {
											const len40 = data334.length;
											for (
												let i40 = 0;
												i40 < len40;
												i40++
											) {
												let data335 = data334[i40];
												const _errs897 = errors;
												let valid212 = false;
												const _errs898 = errors;
												if (
													data335 &&
													typeof data335 ==
														'object' &&
													!Array.isArray(data335)
												) {
													if (
														data335.type ===
														undefined
													) {
														const err551 = {
															instancePath:
																instancePath +
																'/additionalStepsAfterExecution/' +
																i39 +
																'/content/' +
																i40,
															schemaPath:
																'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/0/required',
															keyword: 'required',
															params: {
																missingProperty:
																	'type',
															},
															message:
																"must have required property '" +
																'type' +
																"'",
														};
														if (vErrors === null) {
															vErrors = [err551];
														} else {
															vErrors.push(
																err551
															);
														}
														errors++;
													}
													if (
														data335.source ===
														undefined
													) {
														const err552 = {
															instancePath:
																instancePath +
																'/additionalStepsAfterExecution/' +
																i39 +
																'/content/' +
																i40,
															schemaPath:
																'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/0/required',
															keyword: 'required',
															params: {
																missingProperty:
																	'source',
															},
															message:
																"must have required property '" +
																'source' +
																"'",
														};
														if (vErrors === null) {
															vErrors = [err552];
														} else {
															vErrors.push(
																err552
															);
														}
														errors++;
													}
													for (const key59 in data335) {
														if (
															!(
																key59 ===
																	'type' ||
																key59 ===
																	'source'
															)
														) {
															const err553 = {
																instancePath:
																	instancePath +
																	'/additionalStepsAfterExecution/' +
																	i39 +
																	'/content/' +
																	i40,
																schemaPath:
																	'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/0/additionalProperties',
																keyword:
																	'additionalProperties',
																params: {
																	additionalProperty:
																		key59,
																},
																message:
																	'must NOT have additional properties',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err553,
																];
															} else {
																vErrors.push(
																	err553
																);
															}
															errors++;
														}
													}
													if (
														data335.type !==
														undefined
													) {
														let data336 =
															data335.type;
														if (
															typeof data336 !==
															'string'
														) {
															const err554 = {
																instancePath:
																	instancePath +
																	'/additionalStepsAfterExecution/' +
																	i39 +
																	'/content/' +
																	i40 +
																	'/type',
																schemaPath:
																	'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/0/properties/type/type',
																keyword: 'type',
																params: {
																	type: 'string',
																},
																message:
																	'must be string',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err554,
																];
															} else {
																vErrors.push(
																	err554
																);
															}
															errors++;
														}
														if (
															'mysql-dump' !==
															data336
														) {
															const err555 = {
																instancePath:
																	instancePath +
																	'/additionalStepsAfterExecution/' +
																	i39 +
																	'/content/' +
																	i40 +
																	'/type',
																schemaPath:
																	'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/0/properties/type/const',
																keyword:
																	'const',
																params: {
																	allowedValue:
																		'mysql-dump',
																},
																message:
																	'must be equal to constant',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err555,
																];
															} else {
																vErrors.push(
																	err555
																);
															}
															errors++;
														}
													}
													if (
														data335.source !==
														undefined
													) {
														let data337 =
															data335.source;
														const _errs904 = errors;
														let valid214 = false;
														const _errs905 = errors;
														if (
															!validate40(
																data337,
																{
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/content/' +
																		i40 +
																		'/source',
																	parentData:
																		data335,
																	parentDataProperty:
																		'source',
																	rootData,
																}
															)
														) {
															vErrors =
																vErrors === null
																	? validate40.errors
																	: vErrors.concat(
																			validate40.errors
																		);
															errors =
																vErrors.length;
														}
														var _valid32 =
															_errs905 === errors;
														valid214 =
															valid214 ||
															_valid32;
														if (!valid214) {
															const _errs906 =
																errors;
															if (
																Array.isArray(
																	data337
																)
															) {
																const len41 =
																	data337.length;
																for (
																	let i41 = 0;
																	i41 < len41;
																	i41++
																) {
																	if (
																		!validate40(
																			data337[
																				i41
																			],
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/source/' +
																					i41,
																				parentData:
																					data337,
																				parentDataProperty:
																					i41,
																				rootData,
																			}
																		)
																	) {
																		vErrors =
																			vErrors ===
																			null
																				? validate40.errors
																				: vErrors.concat(
																						validate40.errors
																					);
																		errors =
																			vErrors.length;
																	}
																}
															} else {
																const err556 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/content/' +
																		i40 +
																		'/source',
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/0/properties/source/anyOf/1/type',
																	keyword:
																		'type',
																	params: {
																		type: 'array',
																	},
																	message:
																		'must be array',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err556,
																	];
																} else {
																	vErrors.push(
																		err556
																	);
																}
																errors++;
															}
															var _valid32 =
																_errs906 ===
																errors;
															valid214 =
																valid214 ||
																_valid32;
														}
														if (!valid214) {
															const err557 = {
																instancePath:
																	instancePath +
																	'/additionalStepsAfterExecution/' +
																	i39 +
																	'/content/' +
																	i40 +
																	'/source',
																schemaPath:
																	'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/0/properties/source/anyOf',
																keyword:
																	'anyOf',
																params: {},
																message:
																	'must match a schema in anyOf',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err557,
																];
															} else {
																vErrors.push(
																	err557
																);
															}
															errors++;
														} else {
															errors = _errs904;
															if (
																vErrors !== null
															) {
																if (_errs904) {
																	vErrors.length =
																		_errs904;
																} else {
																	vErrors =
																		null;
																}
															}
														}
													}
												} else {
													const err558 = {
														instancePath:
															instancePath +
															'/additionalStepsAfterExecution/' +
															i39 +
															'/content/' +
															i40,
														schemaPath:
															'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/0/type',
														keyword: 'type',
														params: {
															type: 'object',
														},
														message:
															'must be object',
													};
													if (vErrors === null) {
														vErrors = [err558];
													} else {
														vErrors.push(err558);
													}
													errors++;
												}
												var _valid31 =
													_errs898 === errors;
												valid212 = valid212 || _valid31;
												if (!valid212) {
													const _errs909 = errors;
													if (
														data335 &&
														typeof data335 ==
															'object' &&
														!Array.isArray(data335)
													) {
														if (
															data335.source ===
															undefined
														) {
															const err559 = {
																instancePath:
																	instancePath +
																	'/additionalStepsAfterExecution/' +
																	i39 +
																	'/content/' +
																	i40,
																schemaPath:
																	'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/required',
																keyword:
																	'required',
																params: {
																	missingProperty:
																		'source',
																},
																message:
																	"must have required property '" +
																	'source' +
																	"'",
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err559,
																];
															} else {
																vErrors.push(
																	err559
																);
															}
															errors++;
														}
														if (
															data335.type ===
															undefined
														) {
															const err560 = {
																instancePath:
																	instancePath +
																	'/additionalStepsAfterExecution/' +
																	i39 +
																	'/content/' +
																	i40,
																schemaPath:
																	'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/required',
																keyword:
																	'required',
																params: {
																	missingProperty:
																		'type',
																},
																message:
																	"must have required property '" +
																	'type' +
																	"'",
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err560,
																];
															} else {
																vErrors.push(
																	err560
																);
															}
															errors++;
														}
														for (const key60 in data335) {
															if (
																!(
																	key60 ===
																		'urlsMode' ||
																	key60 ===
																		'urlsMap' ||
																	key60 ===
																		'type' ||
																	key60 ===
																		'source'
																)
															) {
																const err561 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/content/' +
																		i40,
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/additionalProperties',
																	keyword:
																		'additionalProperties',
																	params: {
																		additionalProperty:
																			key60,
																	},
																	message:
																		'must NOT have additional properties',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err561,
																	];
																} else {
																	vErrors.push(
																		err561
																	);
																}
																errors++;
															}
														}
														if (
															data335.urlsMode !==
															undefined
														) {
															let data339 =
																data335.urlsMode;
															if (
																typeof data339 !==
																'string'
															) {
																const err562 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/content/' +
																		i40 +
																		'/urlsMode',
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/urlsMode/type',
																	keyword:
																		'type',
																	params: {
																		type: 'string',
																	},
																	message:
																		'must be string',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err562,
																	];
																} else {
																	vErrors.push(
																		err562
																	);
																}
																errors++;
															}
															if (
																!(
																	data339 ===
																		'rewrite' ||
																	data339 ===
																		'preserve'
																)
															) {
																const err563 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/content/' +
																		i40 +
																		'/urlsMode',
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/urlsMode/enum',
																	keyword:
																		'enum',
																	params: {
																		allowedValues:
																			schema12
																				.properties
																				.additionalStepsAfterExecution
																				.items
																				.oneOf[5]
																				.properties
																				.content
																				.items
																				.anyOf[1]
																				.properties
																				.urlsMode
																				.enum,
																	},
																	message:
																		'must be equal to one of the allowed values',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err563,
																	];
																} else {
																	vErrors.push(
																		err563
																	);
																}
																errors++;
															}
														}
														if (
															data335.urlsMap !==
															undefined
														) {
															let data340 =
																data335.urlsMap;
															if (
																data340 &&
																typeof data340 ==
																	'object' &&
																!Array.isArray(
																	data340
																)
															) {
																for (const key61 in data340) {
																	const _errs916 =
																		errors;
																	if (
																		typeof key61 ===
																		'string'
																	) {
																		if (
																			!formats0(
																				key61
																			)
																		) {
																			const err564 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/urlsMap',
																					schemaPath:
																						'#/definitions/DataSources.URLReference/format',
																					keyword:
																						'format',
																					params: {
																						format: 'whatwg-http-url',
																					},
																					message:
																						'must match format "' +
																						'whatwg-http-url' +
																						'"',
																					propertyName:
																						key61,
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err564,
																					];
																			} else {
																				vErrors.push(
																					err564
																				);
																			}
																			errors++;
																		}
																	} else {
																		const err565 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/urlsMap',
																				schemaPath:
																					'#/definitions/DataSources.URLReference/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																				propertyName:
																					key61,
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err565,
																				];
																		} else {
																			vErrors.push(
																				err565
																			);
																		}
																		errors++;
																	}
																	var valid218 =
																		_errs916 ===
																		errors;
																	if (
																		!valid218
																	) {
																		const err566 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/urlsMap',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/urlsMap/propertyNames',
																				keyword:
																					'propertyNames',
																				params: {
																					propertyName:
																						key61,
																				},
																				message:
																					'property name must be valid',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err566,
																				];
																		} else {
																			vErrors.push(
																				err566
																			);
																		}
																		errors++;
																	}
																}
																for (const key62 in data340) {
																	let data341 =
																		data340[
																			key62
																		];
																	if (
																		typeof data341 ===
																		'string'
																	) {
																		if (
																			!formats0(
																				data341
																			)
																		) {
																			const err567 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/urlsMap/' +
																						key62
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							),
																					schemaPath:
																						'#/definitions/DataSources.URLReference/format',
																					keyword:
																						'format',
																					params: {
																						format: 'whatwg-http-url',
																					},
																					message:
																						'must match format "' +
																						'whatwg-http-url' +
																						'"',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err567,
																					];
																			} else {
																				vErrors.push(
																					err567
																				);
																			}
																			errors++;
																		}
																	} else {
																		const err568 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/urlsMap/' +
																					key62
																						.replace(
																							/~/g,
																							'~0'
																						)
																						.replace(
																							/\//g,
																							'~1'
																						),
																				schemaPath:
																					'#/definitions/DataSources.URLReference/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err568,
																				];
																		} else {
																			vErrors.push(
																				err568
																			);
																		}
																		errors++;
																	}
																}
															} else {
																const err569 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/content/' +
																		i40 +
																		'/urlsMap',
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/urlsMap/type',
																	keyword:
																		'type',
																	params: {
																		type: 'object',
																	},
																	message:
																		'must be object',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err569,
																	];
																} else {
																	vErrors.push(
																		err569
																	);
																}
																errors++;
															}
														}
														if (
															data335.type !==
															undefined
														) {
															let data342 =
																data335.type;
															if (
																typeof data342 !==
																'string'
															) {
																const err570 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/content/' +
																		i40 +
																		'/type',
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/type/type',
																	keyword:
																		'type',
																	params: {
																		type: 'string',
																	},
																	message:
																		'must be string',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err570,
																	];
																} else {
																	vErrors.push(
																		err570
																	);
																}
																errors++;
															}
															if (
																'posts' !==
																data342
															) {
																const err571 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/content/' +
																		i40 +
																		'/type',
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/type/const',
																	keyword:
																		'const',
																	params: {
																		allowedValue:
																			'posts',
																	},
																	message:
																		'must be equal to constant',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err571,
																	];
																} else {
																	vErrors.push(
																		err571
																	);
																}
																errors++;
															}
														}
														if (
															data335.source !==
															undefined
														) {
															let data343 =
																data335.source;
															const _errs926 =
																errors;
															let valid222 = false;
															const _errs927 =
																errors;
															if (
																!validate40(
																	data343,
																	{
																		instancePath:
																			instancePath +
																			'/additionalStepsAfterExecution/' +
																			i39 +
																			'/content/' +
																			i40 +
																			'/source',
																		parentData:
																			data335,
																		parentDataProperty:
																			'source',
																		rootData,
																	}
																)
															) {
																vErrors =
																	vErrors ===
																	null
																		? validate40.errors
																		: vErrors.concat(
																				validate40.errors
																			);
																errors =
																	vErrors.length;
															}
															var _valid33 =
																_errs927 ===
																errors;
															valid222 =
																valid222 ||
																_valid33;
															if (!valid222) {
																const _errs928 =
																	errors;
																if (
																	data343 &&
																	typeof data343 ==
																		'object' &&
																	!Array.isArray(
																		data343
																	)
																) {
																	if (
																		data343.post_title ===
																		undefined
																	) {
																		const err572 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/source',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/required',
																				keyword:
																					'required',
																				params: {
																					missingProperty:
																						'post_title',
																				},
																				message:
																					"must have required property '" +
																					'post_title' +
																					"'",
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err572,
																				];
																		} else {
																			vErrors.push(
																				err572
																			);
																		}
																		errors++;
																	}
																	for (const key63 in data343) {
																		if (
																			!func2.call(
																				schema12
																					.properties
																					.additionalStepsAfterExecution
																					.items
																					.oneOf[5]
																					.properties
																					.content
																					.items
																					.anyOf[1]
																					.properties
																					.source
																					.anyOf[1]
																					.properties,
																				key63
																			)
																		) {
																			const err573 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/additionalProperties',
																					keyword:
																						'additionalProperties',
																					params: {
																						additionalProperty:
																							key63,
																					},
																					message:
																						'must NOT have additional properties',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err573,
																					];
																			} else {
																				vErrors.push(
																					err573
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.post_author !==
																		undefined
																	) {
																		let data344 =
																			data343.post_author;
																		if (
																			!(
																				typeof data344 ==
																					'number' &&
																				isFinite(
																					data344
																				)
																			)
																		) {
																			const err574 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/post_author',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_author/type',
																					keyword:
																						'type',
																					params: {
																						type: 'number',
																					},
																					message:
																						'must be number',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err574,
																					];
																			} else {
																				vErrors.push(
																					err574
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.post_date !==
																		undefined
																	) {
																		if (
																			typeof data343.post_date !==
																			'string'
																		) {
																			const err575 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/post_date',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_date/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err575,
																					];
																			} else {
																				vErrors.push(
																					err575
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.post_content !==
																		undefined
																	) {
																		if (
																			typeof data343.post_content !==
																			'string'
																		) {
																			const err576 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/post_content',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_content/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err576,
																					];
																			} else {
																				vErrors.push(
																					err576
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.post_title !==
																		undefined
																	) {
																		if (
																			typeof data343.post_title !==
																			'string'
																		) {
																			const err577 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/post_title',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_title/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err577,
																					];
																			} else {
																				vErrors.push(
																					err577
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.post_excerpt !==
																		undefined
																	) {
																		if (
																			typeof data343.post_excerpt !==
																			'string'
																		) {
																			const err578 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/post_excerpt',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_excerpt/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err578,
																					];
																			} else {
																				vErrors.push(
																					err578
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.post_status !==
																		undefined
																	) {
																		let data349 =
																			data343.post_status;
																		if (
																			typeof data349 !==
																			'string'
																		) {
																			const err579 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/post_status',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_status/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err579,
																					];
																			} else {
																				vErrors.push(
																					err579
																				);
																			}
																			errors++;
																		}
																		if (
																			!(
																				data349 ===
																					'publish' ||
																				data349 ===
																					'pending' ||
																				data349 ===
																					'draft' ||
																				data349 ===
																					'auto-draft' ||
																				data349 ===
																					'future' ||
																				data349 ===
																					'private' ||
																				data349 ===
																					'inherit' ||
																				data349 ===
																					'trash'
																			)
																		) {
																			const err580 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/post_status',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_status/enum',
																					keyword:
																						'enum',
																					params: {
																						allowedValues:
																							schema12
																								.properties
																								.additionalStepsAfterExecution
																								.items
																								.oneOf[5]
																								.properties
																								.content
																								.items
																								.anyOf[1]
																								.properties
																								.source
																								.anyOf[1]
																								.properties
																								.post_status
																								.enum,
																					},
																					message:
																						'must be equal to one of the allowed values',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err580,
																					];
																			} else {
																				vErrors.push(
																					err580
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.post_type !==
																		undefined
																	) {
																		if (
																			typeof data343.post_type !==
																			'string'
																		) {
																			const err581 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/post_type',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_type/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err581,
																					];
																			} else {
																				vErrors.push(
																					err581
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.comment_status !==
																		undefined
																	) {
																		let data351 =
																			data343.comment_status;
																		if (
																			typeof data351 !==
																			'string'
																		) {
																			const err582 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/comment_status',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/comment_status/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err582,
																					];
																			} else {
																				vErrors.push(
																					err582
																				);
																			}
																			errors++;
																		}
																		if (
																			!(
																				data351 ===
																					'open' ||
																				data351 ===
																					'closed'
																			)
																		) {
																			const err583 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/comment_status',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/comment_status/enum',
																					keyword:
																						'enum',
																					params: {
																						allowedValues:
																							schema12
																								.properties
																								.additionalStepsAfterExecution
																								.items
																								.oneOf[5]
																								.properties
																								.content
																								.items
																								.anyOf[1]
																								.properties
																								.source
																								.anyOf[1]
																								.properties
																								.comment_status
																								.enum,
																					},
																					message:
																						'must be equal to one of the allowed values',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err583,
																					];
																			} else {
																				vErrors.push(
																					err583
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.post_password !==
																		undefined
																	) {
																		if (
																			typeof data343.post_password !==
																			'string'
																		) {
																			const err584 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/post_password',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_password/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err584,
																					];
																			} else {
																				vErrors.push(
																					err584
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.post_name !==
																		undefined
																	) {
																		if (
																			typeof data343.post_name !==
																			'string'
																		) {
																			const err585 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/post_name',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_name/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err585,
																					];
																			} else {
																				vErrors.push(
																					err585
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.post_parent_name !==
																		undefined
																	) {
																		if (
																			typeof data343.post_parent_name !==
																			'string'
																		) {
																			const err586 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/post_parent_name',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_parent_name/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err586,
																					];
																			} else {
																				vErrors.push(
																					err586
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.menu_order !==
																		undefined
																	) {
																		let data355 =
																			data343.menu_order;
																		if (
																			!(
																				typeof data355 ==
																					'number' &&
																				isFinite(
																					data355
																				)
																			)
																		) {
																			const err587 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/menu_order',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/menu_order/type',
																					keyword:
																						'type',
																					params: {
																						type: 'number',
																					},
																					message:
																						'must be number',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err587,
																					];
																			} else {
																				vErrors.push(
																					err587
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.post_mime_type !==
																		undefined
																	) {
																		if (
																			typeof data343.post_mime_type !==
																			'string'
																		) {
																			const err588 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/post_mime_type',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_mime_type/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err588,
																					];
																			} else {
																				vErrors.push(
																					err588
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.guid !==
																		undefined
																	) {
																		if (
																			typeof data343.guid !==
																			'string'
																		) {
																			const err589 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/guid',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/guid/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err589,
																					];
																			} else {
																				vErrors.push(
																					err589
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.post_category !==
																		undefined
																	) {
																		let data358 =
																			data343.post_category;
																		if (
																			Array.isArray(
																				data358
																			)
																		) {
																			const len42 =
																				data358.length;
																			for (
																				let i42 = 0;
																				i42 <
																				len42;
																				i42++
																			) {
																				if (
																					typeof data358[
																						i42
																					] !==
																					'string'
																				) {
																					const err590 =
																						{
																							instancePath:
																								instancePath +
																								'/additionalStepsAfterExecution/' +
																								i39 +
																								'/content/' +
																								i40 +
																								'/source/post_category/' +
																								i42,
																							schemaPath:
																								'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_category/items/type',
																							keyword:
																								'type',
																							params: {
																								type: 'string',
																							},
																							message:
																								'must be string',
																						};
																					if (
																						vErrors ===
																						null
																					) {
																						vErrors =
																							[
																								err590,
																							];
																					} else {
																						vErrors.push(
																							err590
																						);
																					}
																					errors++;
																				}
																			}
																		} else {
																			const err591 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/post_category',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_category/type',
																					keyword:
																						'type',
																					params: {
																						type: 'array',
																					},
																					message:
																						'must be array',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err591,
																					];
																			} else {
																				vErrors.push(
																					err591
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.post_tags !==
																		undefined
																	) {
																		let data360 =
																			data343.post_tags;
																		if (
																			Array.isArray(
																				data360
																			)
																		) {
																			const len43 =
																				data360.length;
																			for (
																				let i43 = 0;
																				i43 <
																				len43;
																				i43++
																			) {
																				if (
																					typeof data360[
																						i43
																					] !==
																					'string'
																				) {
																					const err592 =
																						{
																							instancePath:
																								instancePath +
																								'/additionalStepsAfterExecution/' +
																								i39 +
																								'/content/' +
																								i40 +
																								'/source/post_tags/' +
																								i43,
																							schemaPath:
																								'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_tags/items/type',
																							keyword:
																								'type',
																							params: {
																								type: 'string',
																							},
																							message:
																								'must be string',
																						};
																					if (
																						vErrors ===
																						null
																					) {
																						vErrors =
																							[
																								err592,
																							];
																					} else {
																						vErrors.push(
																							err592
																						);
																					}
																					errors++;
																				}
																			}
																		} else {
																			const err593 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/post_tags',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/post_tags/type',
																					keyword:
																						'type',
																					params: {
																						type: 'array',
																					},
																					message:
																						'must be array',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err593,
																					];
																			} else {
																				vErrors.push(
																					err593
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.tax_input !==
																		undefined
																	) {
																		let data362 =
																			data343.tax_input;
																		if (
																			data362 &&
																			typeof data362 ==
																				'object' &&
																			!Array.isArray(
																				data362
																			)
																		) {
																			for (const key64 in data362) {
																				let data363 =
																					data362[
																						key64
																					];
																				if (
																					Array.isArray(
																						data363
																					)
																				) {
																					const len44 =
																						data363.length;
																					for (
																						let i44 = 0;
																						i44 <
																						len44;
																						i44++
																					) {
																						if (
																							typeof data363[
																								i44
																							] !==
																							'string'
																						) {
																							const err594 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/tax_input/' +
																										key64
																											.replace(
																												/~/g,
																												'~0'
																											)
																											.replace(
																												/\//g,
																												'~1'
																											) +
																										'/' +
																										i44,
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/tax_input/additionalProperties/items/type',
																									keyword:
																										'type',
																									params: {
																										type: 'string',
																									},
																									message:
																										'must be string',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err594,
																									];
																							} else {
																								vErrors.push(
																									err594
																								);
																							}
																							errors++;
																						}
																					}
																				} else {
																					const err595 =
																						{
																							instancePath:
																								instancePath +
																								'/additionalStepsAfterExecution/' +
																								i39 +
																								'/content/' +
																								i40 +
																								'/source/tax_input/' +
																								key64
																									.replace(
																										/~/g,
																										'~0'
																									)
																									.replace(
																										/\//g,
																										'~1'
																									),
																							schemaPath:
																								'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/tax_input/additionalProperties/type',
																							keyword:
																								'type',
																							params: {
																								type: 'array',
																							},
																							message:
																								'must be array',
																						};
																					if (
																						vErrors ===
																						null
																					) {
																						vErrors =
																							[
																								err595,
																							];
																					} else {
																						vErrors.push(
																							err595
																						);
																					}
																					errors++;
																				}
																			}
																		} else {
																			const err596 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/tax_input',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/tax_input/type',
																					keyword:
																						'type',
																					params: {
																						type: 'object',
																					},
																					message:
																						'must be object',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err596,
																					];
																			} else {
																				vErrors.push(
																					err596
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.meta_input !==
																		undefined
																	) {
																		let data365 =
																			data343.meta_input;
																		if (
																			data365 &&
																			typeof data365 ==
																				'object' &&
																			!Array.isArray(
																				data365
																			)
																		) {
																			for (const key65 in data365) {
																				let data366 =
																					data365[
																						key65
																					];
																				const _errs978 =
																					errors;
																				let valid232 = false;
																				const _errs979 =
																					errors;
																				if (
																					typeof data366 !==
																					'string'
																				) {
																					const err597 =
																						{
																							instancePath:
																								instancePath +
																								'/additionalStepsAfterExecution/' +
																								i39 +
																								'/content/' +
																								i40 +
																								'/source/meta_input/' +
																								key65
																									.replace(
																										/~/g,
																										'~0'
																									)
																									.replace(
																										/\//g,
																										'~1'
																									),
																							schemaPath:
																								'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/meta_input/additionalProperties/anyOf/0/type',
																							keyword:
																								'type',
																							params: {
																								type: 'string',
																							},
																							message:
																								'must be string',
																						};
																					if (
																						vErrors ===
																						null
																					) {
																						vErrors =
																							[
																								err597,
																							];
																					} else {
																						vErrors.push(
																							err597
																						);
																					}
																					errors++;
																				}
																				var _valid34 =
																					_errs979 ===
																					errors;
																				valid232 =
																					valid232 ||
																					_valid34;
																				if (
																					!valid232
																				) {
																					const _errs981 =
																						errors;
																					if (
																						typeof data366 !==
																						'boolean'
																					) {
																						const err598 =
																							{
																								instancePath:
																									instancePath +
																									'/additionalStepsAfterExecution/' +
																									i39 +
																									'/content/' +
																									i40 +
																									'/source/meta_input/' +
																									key65
																										.replace(
																											/~/g,
																											'~0'
																										)
																										.replace(
																											/\//g,
																											'~1'
																										),
																								schemaPath:
																									'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/meta_input/additionalProperties/anyOf/1/type',
																								keyword:
																									'type',
																								params: {
																									type: 'boolean',
																								},
																								message:
																									'must be boolean',
																							};
																						if (
																							vErrors ===
																							null
																						) {
																							vErrors =
																								[
																									err598,
																								];
																						} else {
																							vErrors.push(
																								err598
																							);
																						}
																						errors++;
																					}
																					var _valid34 =
																						_errs981 ===
																						errors;
																					valid232 =
																						valid232 ||
																						_valid34;
																					if (
																						!valid232
																					) {
																						const _errs983 =
																							errors;
																						if (
																							!(
																								typeof data366 ==
																									'number' &&
																								isFinite(
																									data366
																								)
																							)
																						) {
																							const err599 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/meta_input/' +
																										key65
																											.replace(
																												/~/g,
																												'~0'
																											)
																											.replace(
																												/\//g,
																												'~1'
																											),
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/meta_input/additionalProperties/anyOf/2/type',
																									keyword:
																										'type',
																									params: {
																										type: 'number',
																									},
																									message:
																										'must be number',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err599,
																									];
																							} else {
																								vErrors.push(
																									err599
																								);
																							}
																							errors++;
																						}
																						var _valid34 =
																							_errs983 ===
																							errors;
																						valid232 =
																							valid232 ||
																							_valid34;
																						if (
																							!valid232
																						) {
																							const _errs985 =
																								errors;
																							if (
																								Array.isArray(
																									data366
																								)
																							) {
																								const len45 =
																									data366.length;
																								for (
																									let i45 = 0;
																									i45 <
																									len45;
																									i45++
																								) {
																									if (
																										!validate34(
																											data366[
																												i45
																											],
																											{
																												instancePath:
																													instancePath +
																													'/additionalStepsAfterExecution/' +
																													i39 +
																													'/content/' +
																													i40 +
																													'/source/meta_input/' +
																													key65
																														.replace(
																															/~/g,
																															'~0'
																														)
																														.replace(
																															/\//g,
																															'~1'
																														) +
																													'/' +
																													i45,
																												parentData:
																													data366,
																												parentDataProperty:
																													i45,
																												rootData,
																											}
																										)
																									) {
																										vErrors =
																											vErrors ===
																											null
																												? validate34.errors
																												: vErrors.concat(
																														validate34.errors
																													);
																										errors =
																											vErrors.length;
																									}
																								}
																							} else {
																								const err600 =
																									{
																										instancePath:
																											instancePath +
																											'/additionalStepsAfterExecution/' +
																											i39 +
																											'/content/' +
																											i40 +
																											'/source/meta_input/' +
																											key65
																												.replace(
																													/~/g,
																													'~0'
																												)
																												.replace(
																													/\//g,
																													'~1'
																												),
																										schemaPath:
																											'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/meta_input/additionalProperties/anyOf/3/type',
																										keyword:
																											'type',
																										params: {
																											type: 'array',
																										},
																										message:
																											'must be array',
																									};
																								if (
																									vErrors ===
																									null
																								) {
																									vErrors =
																										[
																											err600,
																										];
																								} else {
																									vErrors.push(
																										err600
																									);
																								}
																								errors++;
																							}
																							var _valid34 =
																								_errs985 ===
																								errors;
																							valid232 =
																								valid232 ||
																								_valid34;
																							if (
																								!valid232
																							) {
																								const _errs988 =
																									errors;
																								if (
																									data366 &&
																									typeof data366 ==
																										'object' &&
																									!Array.isArray(
																										data366
																									)
																								) {
																									for (const key66 in data366) {
																										if (
																											!validate34(
																												data366[
																													key66
																												],
																												{
																													instancePath:
																														instancePath +
																														'/additionalStepsAfterExecution/' +
																														i39 +
																														'/content/' +
																														i40 +
																														'/source/meta_input/' +
																														key65
																															.replace(
																																/~/g,
																																'~0'
																															)
																															.replace(
																																/\//g,
																																'~1'
																															) +
																														'/' +
																														key66
																															.replace(
																																/~/g,
																																'~0'
																															)
																															.replace(
																																/\//g,
																																'~1'
																															),
																													parentData:
																														data366,
																													parentDataProperty:
																														key66,
																													rootData,
																												}
																											)
																										) {
																											vErrors =
																												vErrors ===
																												null
																													? validate34.errors
																													: vErrors.concat(
																															validate34.errors
																														);
																											errors =
																												vErrors.length;
																										}
																									}
																								} else {
																									const err601 =
																										{
																											instancePath:
																												instancePath +
																												'/additionalStepsAfterExecution/' +
																												i39 +
																												'/content/' +
																												i40 +
																												'/source/meta_input/' +
																												key65
																													.replace(
																														/~/g,
																														'~0'
																													)
																													.replace(
																														/\//g,
																														'~1'
																													),
																											schemaPath:
																												'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/meta_input/additionalProperties/anyOf/4/type',
																											keyword:
																												'type',
																											params: {
																												type: 'object',
																											},
																											message:
																												'must be object',
																										};
																									if (
																										vErrors ===
																										null
																									) {
																										vErrors =
																											[
																												err601,
																											];
																									} else {
																										vErrors.push(
																											err601
																										);
																									}
																									errors++;
																								}
																								var _valid34 =
																									_errs988 ===
																									errors;
																								valid232 =
																									valid232 ||
																									_valid34;
																							}
																						}
																					}
																				}
																				if (
																					!valid232
																				) {
																					const err602 =
																						{
																							instancePath:
																								instancePath +
																								'/additionalStepsAfterExecution/' +
																								i39 +
																								'/content/' +
																								i40 +
																								'/source/meta_input/' +
																								key65
																									.replace(
																										/~/g,
																										'~0'
																									)
																									.replace(
																										/\//g,
																										'~1'
																									),
																							schemaPath:
																								'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/meta_input/additionalProperties/anyOf',
																							keyword:
																								'anyOf',
																							params: {},
																							message:
																								'must match a schema in anyOf',
																						};
																					if (
																						vErrors ===
																						null
																					) {
																						vErrors =
																							[
																								err602,
																							];
																					} else {
																						vErrors.push(
																							err602
																						);
																					}
																					errors++;
																				} else {
																					errors =
																						_errs978;
																					if (
																						vErrors !==
																						null
																					) {
																						if (
																							_errs978
																						) {
																							vErrors.length =
																								_errs978;
																						} else {
																							vErrors =
																								null;
																						}
																					}
																				}
																			}
																		} else {
																			const err603 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/meta_input',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/meta_input/type',
																					keyword:
																						'type',
																					params: {
																						type: 'object',
																					},
																					message:
																						'must be object',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err603,
																					];
																			} else {
																				vErrors.push(
																					err603
																				);
																			}
																			errors++;
																		}
																	}
																	if (
																		data343.page_template !==
																		undefined
																	) {
																		if (
																			typeof data343.page_template !==
																			'string'
																		) {
																			const err604 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source/page_template',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/properties/page_template/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err604,
																					];
																			} else {
																				vErrors.push(
																					err604
																				);
																			}
																			errors++;
																		}
																	}
																} else {
																	const err605 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/source',
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/1/type',
																			keyword:
																				'type',
																			params: {
																				type: 'object',
																			},
																			message:
																				'must be object',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err605,
																			];
																	} else {
																		vErrors.push(
																			err605
																		);
																	}
																	errors++;
																}
																var _valid33 =
																	_errs928 ===
																	errors;
																valid222 =
																	valid222 ||
																	_valid33;
																if (!valid222) {
																	const _errs994 =
																		errors;
																	if (
																		Array.isArray(
																			data343
																		)
																	) {
																		const len46 =
																			data343.length;
																		for (
																			let i46 = 0;
																			i46 <
																			len46;
																			i46++
																		) {
																			let data370 =
																				data343[
																					i46
																				];
																			const _errs997 =
																				errors;
																			let valid238 = false;
																			const _errs998 =
																				errors;
																			if (
																				!validate40(
																					data370,
																					{
																						instancePath:
																							instancePath +
																							'/additionalStepsAfterExecution/' +
																							i39 +
																							'/content/' +
																							i40 +
																							'/source/' +
																							i46,
																						parentData:
																							data343,
																						parentDataProperty:
																							i46,
																						rootData,
																					}
																				)
																			) {
																				vErrors =
																					vErrors ===
																					null
																						? validate40.errors
																						: vErrors.concat(
																								validate40.errors
																							);
																				errors =
																					vErrors.length;
																			}
																			var _valid35 =
																				_errs998 ===
																				errors;
																			valid238 =
																				valid238 ||
																				_valid35;
																			if (
																				!valid238
																			) {
																				const _errs999 =
																					errors;
																				if (
																					data370 &&
																					typeof data370 ==
																						'object' &&
																					!Array.isArray(
																						data370
																					)
																				) {
																					if (
																						data370.post_title ===
																						undefined
																					) {
																						const err606 =
																							{
																								instancePath:
																									instancePath +
																									'/additionalStepsAfterExecution/' +
																									i39 +
																									'/content/' +
																									i40 +
																									'/source/' +
																									i46,
																								schemaPath:
																									'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/required',
																								keyword:
																									'required',
																								params: {
																									missingProperty:
																										'post_title',
																								},
																								message:
																									"must have required property '" +
																									'post_title' +
																									"'",
																							};
																						if (
																							vErrors ===
																							null
																						) {
																							vErrors =
																								[
																									err606,
																								];
																						} else {
																							vErrors.push(
																								err606
																							);
																						}
																						errors++;
																					}
																					for (const key67 in data370) {
																						if (
																							!func2.call(
																								schema12
																									.properties
																									.additionalStepsAfterExecution
																									.items
																									.oneOf[5]
																									.properties
																									.content
																									.items
																									.anyOf[1]
																									.properties
																									.source
																									.anyOf[2]
																									.items
																									.anyOf[1]
																									.properties,
																								key67
																							)
																						) {
																							const err607 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46,
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/additionalProperties',
																									keyword:
																										'additionalProperties',
																									params: {
																										additionalProperty:
																											key67,
																									},
																									message:
																										'must NOT have additional properties',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err607,
																									];
																							} else {
																								vErrors.push(
																									err607
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.post_author !==
																						undefined
																					) {
																						let data371 =
																							data370.post_author;
																						if (
																							!(
																								typeof data371 ==
																									'number' &&
																								isFinite(
																									data371
																								)
																							)
																						) {
																							const err608 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/post_author',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_author/type',
																									keyword:
																										'type',
																									params: {
																										type: 'number',
																									},
																									message:
																										'must be number',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err608,
																									];
																							} else {
																								vErrors.push(
																									err608
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.post_date !==
																						undefined
																					) {
																						if (
																							typeof data370.post_date !==
																							'string'
																						) {
																							const err609 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/post_date',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_date/type',
																									keyword:
																										'type',
																									params: {
																										type: 'string',
																									},
																									message:
																										'must be string',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err609,
																									];
																							} else {
																								vErrors.push(
																									err609
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.post_content !==
																						undefined
																					) {
																						if (
																							typeof data370.post_content !==
																							'string'
																						) {
																							const err610 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/post_content',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_content/type',
																									keyword:
																										'type',
																									params: {
																										type: 'string',
																									},
																									message:
																										'must be string',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err610,
																									];
																							} else {
																								vErrors.push(
																									err610
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.post_title !==
																						undefined
																					) {
																						if (
																							typeof data370.post_title !==
																							'string'
																						) {
																							const err611 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/post_title',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_title/type',
																									keyword:
																										'type',
																									params: {
																										type: 'string',
																									},
																									message:
																										'must be string',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err611,
																									];
																							} else {
																								vErrors.push(
																									err611
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.post_excerpt !==
																						undefined
																					) {
																						if (
																							typeof data370.post_excerpt !==
																							'string'
																						) {
																							const err612 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/post_excerpt',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_excerpt/type',
																									keyword:
																										'type',
																									params: {
																										type: 'string',
																									},
																									message:
																										'must be string',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err612,
																									];
																							} else {
																								vErrors.push(
																									err612
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.post_status !==
																						undefined
																					) {
																						let data376 =
																							data370.post_status;
																						if (
																							typeof data376 !==
																							'string'
																						) {
																							const err613 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/post_status',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_status/type',
																									keyword:
																										'type',
																									params: {
																										type: 'string',
																									},
																									message:
																										'must be string',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err613,
																									];
																							} else {
																								vErrors.push(
																									err613
																								);
																							}
																							errors++;
																						}
																						if (
																							!(
																								data376 ===
																									'publish' ||
																								data376 ===
																									'pending' ||
																								data376 ===
																									'draft' ||
																								data376 ===
																									'auto-draft' ||
																								data376 ===
																									'future' ||
																								data376 ===
																									'private' ||
																								data376 ===
																									'inherit' ||
																								data376 ===
																									'trash'
																							)
																						) {
																							const err614 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/post_status',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_status/enum',
																									keyword:
																										'enum',
																									params: {
																										allowedValues:
																											schema12
																												.properties
																												.additionalStepsAfterExecution
																												.items
																												.oneOf[5]
																												.properties
																												.content
																												.items
																												.anyOf[1]
																												.properties
																												.source
																												.anyOf[2]
																												.items
																												.anyOf[1]
																												.properties
																												.post_status
																												.enum,
																									},
																									message:
																										'must be equal to one of the allowed values',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err614,
																									];
																							} else {
																								vErrors.push(
																									err614
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.post_type !==
																						undefined
																					) {
																						if (
																							typeof data370.post_type !==
																							'string'
																						) {
																							const err615 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/post_type',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_type/type',
																									keyword:
																										'type',
																									params: {
																										type: 'string',
																									},
																									message:
																										'must be string',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err615,
																									];
																							} else {
																								vErrors.push(
																									err615
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.comment_status !==
																						undefined
																					) {
																						let data378 =
																							data370.comment_status;
																						if (
																							typeof data378 !==
																							'string'
																						) {
																							const err616 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/comment_status',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/comment_status/type',
																									keyword:
																										'type',
																									params: {
																										type: 'string',
																									},
																									message:
																										'must be string',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err616,
																									];
																							} else {
																								vErrors.push(
																									err616
																								);
																							}
																							errors++;
																						}
																						if (
																							!(
																								data378 ===
																									'open' ||
																								data378 ===
																									'closed'
																							)
																						) {
																							const err617 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/comment_status',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/comment_status/enum',
																									keyword:
																										'enum',
																									params: {
																										allowedValues:
																											schema12
																												.properties
																												.additionalStepsAfterExecution
																												.items
																												.oneOf[5]
																												.properties
																												.content
																												.items
																												.anyOf[1]
																												.properties
																												.source
																												.anyOf[2]
																												.items
																												.anyOf[1]
																												.properties
																												.comment_status
																												.enum,
																									},
																									message:
																										'must be equal to one of the allowed values',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err617,
																									];
																							} else {
																								vErrors.push(
																									err617
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.post_password !==
																						undefined
																					) {
																						if (
																							typeof data370.post_password !==
																							'string'
																						) {
																							const err618 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/post_password',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_password/type',
																									keyword:
																										'type',
																									params: {
																										type: 'string',
																									},
																									message:
																										'must be string',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err618,
																									];
																							} else {
																								vErrors.push(
																									err618
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.post_name !==
																						undefined
																					) {
																						if (
																							typeof data370.post_name !==
																							'string'
																						) {
																							const err619 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/post_name',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_name/type',
																									keyword:
																										'type',
																									params: {
																										type: 'string',
																									},
																									message:
																										'must be string',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err619,
																									];
																							} else {
																								vErrors.push(
																									err619
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.post_parent_name !==
																						undefined
																					) {
																						if (
																							typeof data370.post_parent_name !==
																							'string'
																						) {
																							const err620 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/post_parent_name',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_parent_name/type',
																									keyword:
																										'type',
																									params: {
																										type: 'string',
																									},
																									message:
																										'must be string',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err620,
																									];
																							} else {
																								vErrors.push(
																									err620
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.menu_order !==
																						undefined
																					) {
																						let data382 =
																							data370.menu_order;
																						if (
																							!(
																								typeof data382 ==
																									'number' &&
																								isFinite(
																									data382
																								)
																							)
																						) {
																							const err621 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/menu_order',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/menu_order/type',
																									keyword:
																										'type',
																									params: {
																										type: 'number',
																									},
																									message:
																										'must be number',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err621,
																									];
																							} else {
																								vErrors.push(
																									err621
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.post_mime_type !==
																						undefined
																					) {
																						if (
																							typeof data370.post_mime_type !==
																							'string'
																						) {
																							const err622 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/post_mime_type',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_mime_type/type',
																									keyword:
																										'type',
																									params: {
																										type: 'string',
																									},
																									message:
																										'must be string',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err622,
																									];
																							} else {
																								vErrors.push(
																									err622
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.guid !==
																						undefined
																					) {
																						if (
																							typeof data370.guid !==
																							'string'
																						) {
																							const err623 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/guid',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/guid/type',
																									keyword:
																										'type',
																									params: {
																										type: 'string',
																									},
																									message:
																										'must be string',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err623,
																									];
																							} else {
																								vErrors.push(
																									err623
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.post_category !==
																						undefined
																					) {
																						let data385 =
																							data370.post_category;
																						if (
																							Array.isArray(
																								data385
																							)
																						) {
																							const len47 =
																								data385.length;
																							for (
																								let i47 = 0;
																								i47 <
																								len47;
																								i47++
																							) {
																								if (
																									typeof data385[
																										i47
																									] !==
																									'string'
																								) {
																									const err624 =
																										{
																											instancePath:
																												instancePath +
																												'/additionalStepsAfterExecution/' +
																												i39 +
																												'/content/' +
																												i40 +
																												'/source/' +
																												i46 +
																												'/post_category/' +
																												i47,
																											schemaPath:
																												'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_category/items/type',
																											keyword:
																												'type',
																											params: {
																												type: 'string',
																											},
																											message:
																												'must be string',
																										};
																									if (
																										vErrors ===
																										null
																									) {
																										vErrors =
																											[
																												err624,
																											];
																									} else {
																										vErrors.push(
																											err624
																										);
																									}
																									errors++;
																								}
																							}
																						} else {
																							const err625 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/post_category',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_category/type',
																									keyword:
																										'type',
																									params: {
																										type: 'array',
																									},
																									message:
																										'must be array',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err625,
																									];
																							} else {
																								vErrors.push(
																									err625
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.post_tags !==
																						undefined
																					) {
																						let data387 =
																							data370.post_tags;
																						if (
																							Array.isArray(
																								data387
																							)
																						) {
																							const len48 =
																								data387.length;
																							for (
																								let i48 = 0;
																								i48 <
																								len48;
																								i48++
																							) {
																								if (
																									typeof data387[
																										i48
																									] !==
																									'string'
																								) {
																									const err626 =
																										{
																											instancePath:
																												instancePath +
																												'/additionalStepsAfterExecution/' +
																												i39 +
																												'/content/' +
																												i40 +
																												'/source/' +
																												i46 +
																												'/post_tags/' +
																												i48,
																											schemaPath:
																												'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_tags/items/type',
																											keyword:
																												'type',
																											params: {
																												type: 'string',
																											},
																											message:
																												'must be string',
																										};
																									if (
																										vErrors ===
																										null
																									) {
																										vErrors =
																											[
																												err626,
																											];
																									} else {
																										vErrors.push(
																											err626
																										);
																									}
																									errors++;
																								}
																							}
																						} else {
																							const err627 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/post_tags',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/post_tags/type',
																									keyword:
																										'type',
																									params: {
																										type: 'array',
																									},
																									message:
																										'must be array',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err627,
																									];
																							} else {
																								vErrors.push(
																									err627
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.tax_input !==
																						undefined
																					) {
																						let data389 =
																							data370.tax_input;
																						if (
																							data389 &&
																							typeof data389 ==
																								'object' &&
																							!Array.isArray(
																								data389
																							)
																						) {
																							for (const key68 in data389) {
																								let data390 =
																									data389[
																										key68
																									];
																								if (
																									Array.isArray(
																										data390
																									)
																								) {
																									const len49 =
																										data390.length;
																									for (
																										let i49 = 0;
																										i49 <
																										len49;
																										i49++
																									) {
																										if (
																											typeof data390[
																												i49
																											] !==
																											'string'
																										) {
																											const err628 =
																												{
																													instancePath:
																														instancePath +
																														'/additionalStepsAfterExecution/' +
																														i39 +
																														'/content/' +
																														i40 +
																														'/source/' +
																														i46 +
																														'/tax_input/' +
																														key68
																															.replace(
																																/~/g,
																																'~0'
																															)
																															.replace(
																																/\//g,
																																'~1'
																															) +
																														'/' +
																														i49,
																													schemaPath:
																														'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/tax_input/additionalProperties/items/type',
																													keyword:
																														'type',
																													params: {
																														type: 'string',
																													},
																													message:
																														'must be string',
																												};
																											if (
																												vErrors ===
																												null
																											) {
																												vErrors =
																													[
																														err628,
																													];
																											} else {
																												vErrors.push(
																													err628
																												);
																											}
																											errors++;
																										}
																									}
																								} else {
																									const err629 =
																										{
																											instancePath:
																												instancePath +
																												'/additionalStepsAfterExecution/' +
																												i39 +
																												'/content/' +
																												i40 +
																												'/source/' +
																												i46 +
																												'/tax_input/' +
																												key68
																													.replace(
																														/~/g,
																														'~0'
																													)
																													.replace(
																														/\//g,
																														'~1'
																													),
																											schemaPath:
																												'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/tax_input/additionalProperties/type',
																											keyword:
																												'type',
																											params: {
																												type: 'array',
																											},
																											message:
																												'must be array',
																										};
																									if (
																										vErrors ===
																										null
																									) {
																										vErrors =
																											[
																												err629,
																											];
																									} else {
																										vErrors.push(
																											err629
																										);
																									}
																									errors++;
																								}
																							}
																						} else {
																							const err630 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/tax_input',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/tax_input/type',
																									keyword:
																										'type',
																									params: {
																										type: 'object',
																									},
																									message:
																										'must be object',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err630,
																									];
																							} else {
																								vErrors.push(
																									err630
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.meta_input !==
																						undefined
																					) {
																						let data392 =
																							data370.meta_input;
																						if (
																							data392 &&
																							typeof data392 ==
																								'object' &&
																							!Array.isArray(
																								data392
																							)
																						) {
																							for (const key69 in data392) {
																								let data393 =
																									data392[
																										key69
																									];
																								const _errs1049 =
																									errors;
																								let valid248 = false;
																								const _errs1050 =
																									errors;
																								if (
																									typeof data393 !==
																									'string'
																								) {
																									const err631 =
																										{
																											instancePath:
																												instancePath +
																												'/additionalStepsAfterExecution/' +
																												i39 +
																												'/content/' +
																												i40 +
																												'/source/' +
																												i46 +
																												'/meta_input/' +
																												key69
																													.replace(
																														/~/g,
																														'~0'
																													)
																													.replace(
																														/\//g,
																														'~1'
																													),
																											schemaPath:
																												'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/meta_input/additionalProperties/anyOf/0/type',
																											keyword:
																												'type',
																											params: {
																												type: 'string',
																											},
																											message:
																												'must be string',
																										};
																									if (
																										vErrors ===
																										null
																									) {
																										vErrors =
																											[
																												err631,
																											];
																									} else {
																										vErrors.push(
																											err631
																										);
																									}
																									errors++;
																								}
																								var _valid36 =
																									_errs1050 ===
																									errors;
																								valid248 =
																									valid248 ||
																									_valid36;
																								if (
																									!valid248
																								) {
																									const _errs1052 =
																										errors;
																									if (
																										typeof data393 !==
																										'boolean'
																									) {
																										const err632 =
																											{
																												instancePath:
																													instancePath +
																													'/additionalStepsAfterExecution/' +
																													i39 +
																													'/content/' +
																													i40 +
																													'/source/' +
																													i46 +
																													'/meta_input/' +
																													key69
																														.replace(
																															/~/g,
																															'~0'
																														)
																														.replace(
																															/\//g,
																															'~1'
																														),
																												schemaPath:
																													'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/meta_input/additionalProperties/anyOf/1/type',
																												keyword:
																													'type',
																												params: {
																													type: 'boolean',
																												},
																												message:
																													'must be boolean',
																											};
																										if (
																											vErrors ===
																											null
																										) {
																											vErrors =
																												[
																													err632,
																												];
																										} else {
																											vErrors.push(
																												err632
																											);
																										}
																										errors++;
																									}
																									var _valid36 =
																										_errs1052 ===
																										errors;
																									valid248 =
																										valid248 ||
																										_valid36;
																									if (
																										!valid248
																									) {
																										const _errs1054 =
																											errors;
																										if (
																											!(
																												typeof data393 ==
																													'number' &&
																												isFinite(
																													data393
																												)
																											)
																										) {
																											const err633 =
																												{
																													instancePath:
																														instancePath +
																														'/additionalStepsAfterExecution/' +
																														i39 +
																														'/content/' +
																														i40 +
																														'/source/' +
																														i46 +
																														'/meta_input/' +
																														key69
																															.replace(
																																/~/g,
																																'~0'
																															)
																															.replace(
																																/\//g,
																																'~1'
																															),
																													schemaPath:
																														'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/meta_input/additionalProperties/anyOf/2/type',
																													keyword:
																														'type',
																													params: {
																														type: 'number',
																													},
																													message:
																														'must be number',
																												};
																											if (
																												vErrors ===
																												null
																											) {
																												vErrors =
																													[
																														err633,
																													];
																											} else {
																												vErrors.push(
																													err633
																												);
																											}
																											errors++;
																										}
																										var _valid36 =
																											_errs1054 ===
																											errors;
																										valid248 =
																											valid248 ||
																											_valid36;
																										if (
																											!valid248
																										) {
																											const _errs1056 =
																												errors;
																											if (
																												Array.isArray(
																													data393
																												)
																											) {
																												const len50 =
																													data393.length;
																												for (
																													let i50 = 0;
																													i50 <
																													len50;
																													i50++
																												) {
																													if (
																														!validate34(
																															data393[
																																i50
																															],
																															{
																																instancePath:
																																	instancePath +
																																	'/additionalStepsAfterExecution/' +
																																	i39 +
																																	'/content/' +
																																	i40 +
																																	'/source/' +
																																	i46 +
																																	'/meta_input/' +
																																	key69
																																		.replace(
																																			/~/g,
																																			'~0'
																																		)
																																		.replace(
																																			/\//g,
																																			'~1'
																																		) +
																																	'/' +
																																	i50,
																																parentData:
																																	data393,
																																parentDataProperty:
																																	i50,
																																rootData,
																															}
																														)
																													) {
																														vErrors =
																															vErrors ===
																															null
																																? validate34.errors
																																: vErrors.concat(
																																		validate34.errors
																																	);
																														errors =
																															vErrors.length;
																													}
																												}
																											} else {
																												const err634 =
																													{
																														instancePath:
																															instancePath +
																															'/additionalStepsAfterExecution/' +
																															i39 +
																															'/content/' +
																															i40 +
																															'/source/' +
																															i46 +
																															'/meta_input/' +
																															key69
																																.replace(
																																	/~/g,
																																	'~0'
																																)
																																.replace(
																																	/\//g,
																																	'~1'
																																),
																														schemaPath:
																															'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/meta_input/additionalProperties/anyOf/3/type',
																														keyword:
																															'type',
																														params: {
																															type: 'array',
																														},
																														message:
																															'must be array',
																													};
																												if (
																													vErrors ===
																													null
																												) {
																													vErrors =
																														[
																															err634,
																														];
																												} else {
																													vErrors.push(
																														err634
																													);
																												}
																												errors++;
																											}
																											var _valid36 =
																												_errs1056 ===
																												errors;
																											valid248 =
																												valid248 ||
																												_valid36;
																											if (
																												!valid248
																											) {
																												const _errs1059 =
																													errors;
																												if (
																													data393 &&
																													typeof data393 ==
																														'object' &&
																													!Array.isArray(
																														data393
																													)
																												) {
																													for (const key70 in data393) {
																														if (
																															!validate34(
																																data393[
																																	key70
																																],
																																{
																																	instancePath:
																																		instancePath +
																																		'/additionalStepsAfterExecution/' +
																																		i39 +
																																		'/content/' +
																																		i40 +
																																		'/source/' +
																																		i46 +
																																		'/meta_input/' +
																																		key69
																																			.replace(
																																				/~/g,
																																				'~0'
																																			)
																																			.replace(
																																				/\//g,
																																				'~1'
																																			) +
																																		'/' +
																																		key70
																																			.replace(
																																				/~/g,
																																				'~0'
																																			)
																																			.replace(
																																				/\//g,
																																				'~1'
																																			),
																																	parentData:
																																		data393,
																																	parentDataProperty:
																																		key70,
																																	rootData,
																																}
																															)
																														) {
																															vErrors =
																																vErrors ===
																																null
																																	? validate34.errors
																																	: vErrors.concat(
																																			validate34.errors
																																		);
																															errors =
																																vErrors.length;
																														}
																													}
																												} else {
																													const err635 =
																														{
																															instancePath:
																																instancePath +
																																'/additionalStepsAfterExecution/' +
																																i39 +
																																'/content/' +
																																i40 +
																																'/source/' +
																																i46 +
																																'/meta_input/' +
																																key69
																																	.replace(
																																		/~/g,
																																		'~0'
																																	)
																																	.replace(
																																		/\//g,
																																		'~1'
																																	),
																															schemaPath:
																																'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/meta_input/additionalProperties/anyOf/4/type',
																															keyword:
																																'type',
																															params: {
																																type: 'object',
																															},
																															message:
																																'must be object',
																														};
																													if (
																														vErrors ===
																														null
																													) {
																														vErrors =
																															[
																																err635,
																															];
																													} else {
																														vErrors.push(
																															err635
																														);
																													}
																													errors++;
																												}
																												var _valid36 =
																													_errs1059 ===
																													errors;
																												valid248 =
																													valid248 ||
																													_valid36;
																											}
																										}
																									}
																								}
																								if (
																									!valid248
																								) {
																									const err636 =
																										{
																											instancePath:
																												instancePath +
																												'/additionalStepsAfterExecution/' +
																												i39 +
																												'/content/' +
																												i40 +
																												'/source/' +
																												i46 +
																												'/meta_input/' +
																												key69
																													.replace(
																														/~/g,
																														'~0'
																													)
																													.replace(
																														/\//g,
																														'~1'
																													),
																											schemaPath:
																												'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/meta_input/additionalProperties/anyOf',
																											keyword:
																												'anyOf',
																											params: {},
																											message:
																												'must match a schema in anyOf',
																										};
																									if (
																										vErrors ===
																										null
																									) {
																										vErrors =
																											[
																												err636,
																											];
																									} else {
																										vErrors.push(
																											err636
																										);
																									}
																									errors++;
																								} else {
																									errors =
																										_errs1049;
																									if (
																										vErrors !==
																										null
																									) {
																										if (
																											_errs1049
																										) {
																											vErrors.length =
																												_errs1049;
																										} else {
																											vErrors =
																												null;
																										}
																									}
																								}
																							}
																						} else {
																							const err637 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/meta_input',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/meta_input/type',
																									keyword:
																										'type',
																									params: {
																										type: 'object',
																									},
																									message:
																										'must be object',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err637,
																									];
																							} else {
																								vErrors.push(
																									err637
																								);
																							}
																							errors++;
																						}
																					}
																					if (
																						data370.page_template !==
																						undefined
																					) {
																						if (
																							typeof data370.page_template !==
																							'string'
																						) {
																							const err638 =
																								{
																									instancePath:
																										instancePath +
																										'/additionalStepsAfterExecution/' +
																										i39 +
																										'/content/' +
																										i40 +
																										'/source/' +
																										i46 +
																										'/page_template',
																									schemaPath:
																										'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/properties/page_template/type',
																									keyword:
																										'type',
																									params: {
																										type: 'string',
																									},
																									message:
																										'must be string',
																								};
																							if (
																								vErrors ===
																								null
																							) {
																								vErrors =
																									[
																										err638,
																									];
																							} else {
																								vErrors.push(
																									err638
																								);
																							}
																							errors++;
																						}
																					}
																				} else {
																					const err639 =
																						{
																							instancePath:
																								instancePath +
																								'/additionalStepsAfterExecution/' +
																								i39 +
																								'/content/' +
																								i40 +
																								'/source/' +
																								i46,
																							schemaPath:
																								'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf/1/type',
																							keyword:
																								'type',
																							params: {
																								type: 'object',
																							},
																							message:
																								'must be object',
																						};
																					if (
																						vErrors ===
																						null
																					) {
																						vErrors =
																							[
																								err639,
																							];
																					} else {
																						vErrors.push(
																							err639
																						);
																					}
																					errors++;
																				}
																				var _valid35 =
																					_errs999 ===
																					errors;
																				valid238 =
																					valid238 ||
																					_valid35;
																			}
																			if (
																				!valid238
																			) {
																				const err640 =
																					{
																						instancePath:
																							instancePath +
																							'/additionalStepsAfterExecution/' +
																							i39 +
																							'/content/' +
																							i40 +
																							'/source/' +
																							i46,
																						schemaPath:
																							'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/items/anyOf',
																						keyword:
																							'anyOf',
																						params: {},
																						message:
																							'must match a schema in anyOf',
																					};
																				if (
																					vErrors ===
																					null
																				) {
																					vErrors =
																						[
																							err640,
																						];
																				} else {
																					vErrors.push(
																						err640
																					);
																				}
																				errors++;
																			} else {
																				errors =
																					_errs997;
																				if (
																					vErrors !==
																					null
																				) {
																					if (
																						_errs997
																					) {
																						vErrors.length =
																							_errs997;
																					} else {
																						vErrors =
																							null;
																					}
																				}
																			}
																		}
																	} else {
																		const err641 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/source',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf/2/type',
																				keyword:
																					'type',
																				params: {
																					type: 'array',
																				},
																				message:
																					'must be array',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err641,
																				];
																		} else {
																			vErrors.push(
																				err641
																			);
																		}
																		errors++;
																	}
																	var _valid33 =
																		_errs994 ===
																		errors;
																	valid222 =
																		valid222 ||
																		_valid33;
																}
															}
															if (!valid222) {
																const err642 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/content/' +
																		i40 +
																		'/source',
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/properties/source/anyOf',
																	keyword:
																		'anyOf',
																	params: {},
																	message:
																		'must match a schema in anyOf',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err642,
																	];
																} else {
																	vErrors.push(
																		err642
																	);
																}
																errors++;
															} else {
																errors =
																	_errs926;
																if (
																	vErrors !==
																	null
																) {
																	if (
																		_errs926
																	) {
																		vErrors.length =
																			_errs926;
																	} else {
																		vErrors =
																			null;
																	}
																}
															}
														}
													} else {
														const err643 = {
															instancePath:
																instancePath +
																'/additionalStepsAfterExecution/' +
																i39 +
																'/content/' +
																i40,
															schemaPath:
																'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/1/type',
															keyword: 'type',
															params: {
																type: 'object',
															},
															message:
																'must be object',
														};
														if (vErrors === null) {
															vErrors = [err643];
														} else {
															vErrors.push(
																err643
															);
														}
														errors++;
													}
													var _valid31 =
														_errs909 === errors;
													valid212 =
														valid212 || _valid31;
													if (!valid212) {
														const _errs1065 =
															errors;
														if (
															data335 &&
															typeof data335 ==
																'object' &&
															!Array.isArray(
																data335
															)
														) {
															if (
																data335.authorsMap ===
																undefined
															) {
																const err644 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/content/' +
																		i40,
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/required',
																	keyword:
																		'required',
																	params: {
																		missingProperty:
																			'authorsMap',
																	},
																	message:
																		"must have required property '" +
																		'authorsMap' +
																		"'",
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err644,
																	];
																} else {
																	vErrors.push(
																		err644
																	);
																}
																errors++;
															}
															if (
																data335.authorsMode ===
																undefined
															) {
																const err645 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/content/' +
																		i40,
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/required',
																	keyword:
																		'required',
																	params: {
																		missingProperty:
																			'authorsMode',
																	},
																	message:
																		"must have required property '" +
																		'authorsMode' +
																		"'",
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err645,
																	];
																} else {
																	vErrors.push(
																		err645
																	);
																}
																errors++;
															}
															if (
																data335.source ===
																undefined
															) {
																const err646 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/content/' +
																		i40,
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/required',
																	keyword:
																		'required',
																	params: {
																		missingProperty:
																			'source',
																	},
																	message:
																		"must have required property '" +
																		'source' +
																		"'",
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err646,
																	];
																} else {
																	vErrors.push(
																		err646
																	);
																}
																errors++;
															}
															if (
																data335.type ===
																undefined
															) {
																const err647 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/content/' +
																		i40,
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/required',
																	keyword:
																		'required',
																	params: {
																		missingProperty:
																			'type',
																	},
																	message:
																		"must have required property '" +
																		'type' +
																		"'",
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err647,
																	];
																} else {
																	vErrors.push(
																		err647
																	);
																}
																errors++;
															}
															for (const key71 in data335) {
																if (
																	!func2.call(
																		schema12
																			.properties
																			.additionalStepsAfterExecution
																			.items
																			.oneOf[5]
																			.properties
																			.content
																			.items
																			.anyOf[2]
																			.properties,
																		key71
																	)
																) {
																	const err648 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40,
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/additionalProperties',
																			keyword:
																				'additionalProperties',
																			params: {
																				additionalProperty:
																					key71,
																			},
																			message:
																				'must NOT have additional properties',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err648,
																			];
																	} else {
																		vErrors.push(
																			err648
																		);
																	}
																	errors++;
																}
															}
															if (
																data335.authorsMode !==
																undefined
															) {
																let data397 =
																	data335.authorsMode;
																if (
																	typeof data397 !==
																	'string'
																) {
																	const err649 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/authorsMode',
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/authorsMode/type',
																			keyword:
																				'type',
																			params: {
																				type: 'string',
																			},
																			message:
																				'must be string',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err649,
																			];
																	} else {
																		vErrors.push(
																			err649
																		);
																	}
																	errors++;
																}
																if (
																	'map' !==
																	data397
																) {
																	const err650 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/authorsMode',
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/authorsMode/const',
																			keyword:
																				'const',
																			params: {
																				allowedValue:
																					'map',
																			},
																			message:
																				'must be equal to constant',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err650,
																			];
																	} else {
																		vErrors.push(
																			err650
																		);
																	}
																	errors++;
																}
															}
															if (
																data335.authorsMap !==
																undefined
															) {
																let data398 =
																	data335.authorsMap;
																if (
																	data398 &&
																	typeof data398 ==
																		'object' &&
																	!Array.isArray(
																		data398
																	)
																) {
																	for (const key72 in data398) {
																		if (
																			typeof data398[
																				key72
																			] !==
																			'string'
																		) {
																			const err651 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/authorsMap/' +
																						key72
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							),
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/authorsMap/additionalProperties/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err651,
																					];
																			} else {
																				vErrors.push(
																					err651
																				);
																			}
																			errors++;
																		}
																	}
																} else {
																	const err652 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/authorsMap',
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/authorsMap/type',
																			keyword:
																				'type',
																			params: {
																				type: 'object',
																			},
																			message:
																				'must be object',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err652,
																			];
																	} else {
																		vErrors.push(
																			err652
																		);
																	}
																	errors++;
																}
															}
															if (
																data335.urlsMode !==
																undefined
															) {
																let data400 =
																	data335.urlsMode;
																if (
																	typeof data400 !==
																	'string'
																) {
																	const err653 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/urlsMode',
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/urlsMode/type',
																			keyword:
																				'type',
																			params: {
																				type: 'string',
																			},
																			message:
																				'must be string',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err653,
																			];
																	} else {
																		vErrors.push(
																			err653
																		);
																	}
																	errors++;
																}
																if (
																	!(
																		data400 ===
																			'rewrite' ||
																		data400 ===
																			'preserve'
																	)
																) {
																	const err654 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/urlsMode',
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/urlsMode/enum',
																			keyword:
																				'enum',
																			params: {
																				allowedValues:
																					schema12
																						.properties
																						.additionalStepsAfterExecution
																						.items
																						.oneOf[5]
																						.properties
																						.content
																						.items
																						.anyOf[2]
																						.properties
																						.urlsMode
																						.enum,
																			},
																			message:
																				'must be equal to one of the allowed values',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err654,
																			];
																	} else {
																		vErrors.push(
																			err654
																		);
																	}
																	errors++;
																}
															}
															if (
																data335.urlsMap !==
																undefined
															) {
																let data401 =
																	data335.urlsMap;
																if (
																	data401 &&
																	typeof data401 ==
																		'object' &&
																	!Array.isArray(
																		data401
																	)
																) {
																	for (const key73 in data401) {
																		const _errs1079 =
																			errors;
																		if (
																			typeof key73 ===
																			'string'
																		) {
																			if (
																				!formats0(
																					key73
																				)
																			) {
																				const err655 =
																					{
																						instancePath:
																							instancePath +
																							'/additionalStepsAfterExecution/' +
																							i39 +
																							'/content/' +
																							i40 +
																							'/urlsMap',
																						schemaPath:
																							'#/definitions/DataSources.URLReference/format',
																						keyword:
																							'format',
																						params: {
																							format: 'whatwg-http-url',
																						},
																						message:
																							'must match format "' +
																							'whatwg-http-url' +
																							'"',
																						propertyName:
																							key73,
																					};
																				if (
																					vErrors ===
																					null
																				) {
																					vErrors =
																						[
																							err655,
																						];
																				} else {
																					vErrors.push(
																						err655
																					);
																				}
																				errors++;
																			}
																		} else {
																			const err656 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/urlsMap',
																					schemaPath:
																						'#/definitions/DataSources.URLReference/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																					propertyName:
																						key73,
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err656,
																					];
																			} else {
																				vErrors.push(
																					err656
																				);
																			}
																			errors++;
																		}
																		var valid254 =
																			_errs1079 ===
																			errors;
																		if (
																			!valid254
																		) {
																			const err657 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/urlsMap',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/urlsMap/propertyNames',
																					keyword:
																						'propertyNames',
																					params: {
																						propertyName:
																							key73,
																					},
																					message:
																						'property name must be valid',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err657,
																					];
																			} else {
																				vErrors.push(
																					err657
																				);
																			}
																			errors++;
																		}
																	}
																	for (const key74 in data401) {
																		let data402 =
																			data401[
																				key74
																			];
																		if (
																			typeof data402 ===
																			'string'
																		) {
																			if (
																				!formats0(
																					data402
																				)
																			) {
																				const err658 =
																					{
																						instancePath:
																							instancePath +
																							'/additionalStepsAfterExecution/' +
																							i39 +
																							'/content/' +
																							i40 +
																							'/urlsMap/' +
																							key74
																								.replace(
																									/~/g,
																									'~0'
																								)
																								.replace(
																									/\//g,
																									'~1'
																								),
																						schemaPath:
																							'#/definitions/DataSources.URLReference/format',
																						keyword:
																							'format',
																						params: {
																							format: 'whatwg-http-url',
																						},
																						message:
																							'must match format "' +
																							'whatwg-http-url' +
																							'"',
																					};
																				if (
																					vErrors ===
																					null
																				) {
																					vErrors =
																						[
																							err658,
																						];
																				} else {
																					vErrors.push(
																						err658
																					);
																				}
																				errors++;
																			}
																		} else {
																			const err659 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/urlsMap/' +
																						key74
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							),
																					schemaPath:
																						'#/definitions/DataSources.URLReference/type',
																					keyword:
																						'type',
																					params: {
																						type: 'string',
																					},
																					message:
																						'must be string',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err659,
																					];
																			} else {
																				vErrors.push(
																					err659
																				);
																			}
																			errors++;
																		}
																	}
																} else {
																	const err660 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/urlsMap',
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/urlsMap/type',
																			keyword:
																				'type',
																			params: {
																				type: 'object',
																			},
																			message:
																				'must be object',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err660,
																			];
																	} else {
																		vErrors.push(
																			err660
																		);
																	}
																	errors++;
																}
															}
															if (
																data335.type !==
																undefined
															) {
																let data403 =
																	data335.type;
																if (
																	typeof data403 !==
																	'string'
																) {
																	const err661 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/type',
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/type/type',
																			keyword:
																				'type',
																			params: {
																				type: 'string',
																			},
																			message:
																				'must be string',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err661,
																			];
																	} else {
																		vErrors.push(
																			err661
																		);
																	}
																	errors++;
																}
																if (
																	'wxr' !==
																	data403
																) {
																	const err662 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/type',
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/type/const',
																			keyword:
																				'const',
																			params: {
																				allowedValue:
																					'wxr',
																			},
																			message:
																				'must be equal to constant',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err662,
																			];
																	} else {
																		vErrors.push(
																			err662
																		);
																	}
																	errors++;
																}
															}
															if (
																data335.source !==
																undefined
															) {
																let data404 =
																	data335.source;
																const _errs1089 =
																	errors;
																let valid258 = false;
																const _errs1090 =
																	errors;
																if (
																	!validate40(
																		data404,
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/source',
																			parentData:
																				data335,
																			parentDataProperty:
																				'source',
																			rootData,
																		}
																	)
																) {
																	vErrors =
																		vErrors ===
																		null
																			? validate40.errors
																			: vErrors.concat(
																					validate40.errors
																				);
																	errors =
																		vErrors.length;
																}
																var _valid37 =
																	_errs1090 ===
																	errors;
																valid258 =
																	valid258 ||
																	_valid37;
																if (!valid258) {
																	const _errs1091 =
																		errors;
																	if (
																		Array.isArray(
																			data404
																		)
																	) {
																		const len51 =
																			data404.length;
																		for (
																			let i51 = 0;
																			i51 <
																			len51;
																			i51++
																		) {
																			if (
																				!validate40(
																					data404[
																						i51
																					],
																					{
																						instancePath:
																							instancePath +
																							'/additionalStepsAfterExecution/' +
																							i39 +
																							'/content/' +
																							i40 +
																							'/source/' +
																							i51,
																						parentData:
																							data404,
																						parentDataProperty:
																							i51,
																						rootData,
																					}
																				)
																			) {
																				vErrors =
																					vErrors ===
																					null
																						? validate40.errors
																						: vErrors.concat(
																								validate40.errors
																							);
																				errors =
																					vErrors.length;
																			}
																		}
																	} else {
																		const err663 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/source',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/source/anyOf/1/type',
																				keyword:
																					'type',
																				params: {
																					type: 'array',
																				},
																				message:
																					'must be array',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err663,
																				];
																		} else {
																			vErrors.push(
																				err663
																			);
																		}
																		errors++;
																	}
																	var _valid37 =
																		_errs1091 ===
																		errors;
																	valid258 =
																		valid258 ||
																		_valid37;
																}
																if (!valid258) {
																	const err664 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/source',
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/source/anyOf',
																			keyword:
																				'anyOf',
																			params: {},
																			message:
																				'must match a schema in anyOf',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err664,
																			];
																	} else {
																		vErrors.push(
																			err664
																		);
																	}
																	errors++;
																} else {
																	errors =
																		_errs1089;
																	if (
																		vErrors !==
																		null
																	) {
																		if (
																			_errs1089
																		) {
																			vErrors.length =
																				_errs1089;
																		} else {
																			vErrors =
																				null;
																		}
																	}
																}
															}
															if (
																data335.staticAssets !==
																undefined
															) {
																let data406 =
																	data335.staticAssets;
																if (
																	typeof data406 !==
																	'string'
																) {
																	const err665 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/staticAssets',
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/staticAssets/type',
																			keyword:
																				'type',
																			params: {
																				type: 'string',
																			},
																			message:
																				'must be string',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err665,
																			];
																	} else {
																		vErrors.push(
																			err665
																		);
																	}
																	errors++;
																}
																if (
																	!(
																		data406 ===
																			'fetch' ||
																		data406 ===
																			'hotlink'
																	)
																) {
																	const err666 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/staticAssets',
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/staticAssets/enum',
																			keyword:
																				'enum',
																			params: {
																				allowedValues:
																					schema12
																						.properties
																						.additionalStepsAfterExecution
																						.items
																						.oneOf[5]
																						.properties
																						.content
																						.items
																						.anyOf[2]
																						.properties
																						.staticAssets
																						.enum,
																			},
																			message:
																				'must be equal to one of the allowed values',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err666,
																			];
																	} else {
																		vErrors.push(
																			err666
																		);
																	}
																	errors++;
																}
															}
															if (
																data335.defaultAuthorUsername !==
																undefined
															) {
																if (
																	typeof data335.defaultAuthorUsername !==
																	'string'
																) {
																	const err667 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/defaultAuthorUsername',
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/defaultAuthorUsername/type',
																			keyword:
																				'type',
																			params: {
																				type: 'string',
																			},
																			message:
																				'must be string',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err667,
																			];
																	} else {
																		vErrors.push(
																			err667
																		);
																	}
																	errors++;
																}
															}
															if (
																data335.importUsers !==
																undefined
															) {
																if (
																	typeof data335.importUsers !==
																	'boolean'
																) {
																	const err668 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/importUsers',
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/importUsers/type',
																			keyword:
																				'type',
																			params: {
																				type: 'boolean',
																			},
																			message:
																				'must be boolean',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err668,
																			];
																	} else {
																		vErrors.push(
																			err668
																		);
																	}
																	errors++;
																}
															}
															if (
																data335.importComments !==
																undefined
															) {
																if (
																	typeof data335.importComments !==
																	'boolean'
																) {
																	const err669 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40 +
																				'/importComments',
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/properties/importComments/type',
																			keyword:
																				'type',
																			params: {
																				type: 'boolean',
																			},
																			message:
																				'must be boolean',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err669,
																			];
																	} else {
																		vErrors.push(
																			err669
																		);
																	}
																	errors++;
																}
															}
														} else {
															const err670 = {
																instancePath:
																	instancePath +
																	'/additionalStepsAfterExecution/' +
																	i39 +
																	'/content/' +
																	i40,
																schemaPath:
																	'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/2/type',
																keyword: 'type',
																params: {
																	type: 'object',
																},
																message:
																	'must be object',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err670,
																];
															} else {
																vErrors.push(
																	err670
																);
															}
															errors++;
														}
														var _valid31 =
															_errs1065 ===
															errors;
														valid212 =
															valid212 ||
															_valid31;
														if (!valid212) {
															const _errs1102 =
																errors;
															if (
																data335 &&
																typeof data335 ==
																	'object' &&
																!Array.isArray(
																	data335
																)
															) {
																if (
																	data335.source ===
																	undefined
																) {
																	const err671 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40,
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/required',
																			keyword:
																				'required',
																			params: {
																				missingProperty:
																					'source',
																			},
																			message:
																				"must have required property '" +
																				'source' +
																				"'",
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err671,
																			];
																	} else {
																		vErrors.push(
																			err671
																		);
																	}
																	errors++;
																}
																if (
																	data335.type ===
																	undefined
																) {
																	const err672 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/content/' +
																				i40,
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/required',
																			keyword:
																				'required',
																			params: {
																				missingProperty:
																					'type',
																			},
																			message:
																				"must have required property '" +
																				'type' +
																				"'",
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err672,
																			];
																	} else {
																		vErrors.push(
																			err672
																		);
																	}
																	errors++;
																}
																for (const key75 in data335) {
																	if (
																		!func2.call(
																			schema12
																				.properties
																				.additionalStepsAfterExecution
																				.items
																				.oneOf[5]
																				.properties
																				.content
																				.items
																				.anyOf[3]
																				.properties,
																			key75
																		)
																	) {
																		const err673 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40,
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/additionalProperties',
																				keyword:
																					'additionalProperties',
																				params: {
																					additionalProperty:
																						key75,
																				},
																				message:
																					'must NOT have additional properties',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err673,
																				];
																		} else {
																			vErrors.push(
																				err673
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data335.authorsMode !==
																	undefined
																) {
																	let data410 =
																		data335.authorsMode;
																	if (
																		typeof data410 !==
																		'string'
																	) {
																		const err674 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/authorsMode',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/authorsMode/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err674,
																				];
																		} else {
																			vErrors.push(
																				err674
																			);
																		}
																		errors++;
																	}
																	if (
																		!(
																			data410 ===
																				'create' ||
																			data410 ===
																				'default-author'
																		)
																	) {
																		const err675 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/authorsMode',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/authorsMode/enum',
																				keyword:
																					'enum',
																				params: {
																					allowedValues:
																						schema12
																							.properties
																							.additionalStepsAfterExecution
																							.items
																							.oneOf[5]
																							.properties
																							.content
																							.items
																							.anyOf[3]
																							.properties
																							.authorsMode
																							.enum,
																				},
																				message:
																					'must be equal to one of the allowed values',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err675,
																				];
																		} else {
																			vErrors.push(
																				err675
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data335.authorsMap !==
																	undefined
																) {
																	let data411 =
																		data335.authorsMap;
																	if (
																		data411 &&
																		typeof data411 ==
																			'object' &&
																		!Array.isArray(
																			data411
																		)
																	) {
																		for (const key76 in data411) {
																			if (
																				typeof data411[
																					key76
																				] !==
																				'string'
																			) {
																				const err676 =
																					{
																						instancePath:
																							instancePath +
																							'/additionalStepsAfterExecution/' +
																							i39 +
																							'/content/' +
																							i40 +
																							'/authorsMap/' +
																							key76
																								.replace(
																									/~/g,
																									'~0'
																								)
																								.replace(
																									/\//g,
																									'~1'
																								),
																						schemaPath:
																							'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/authorsMap/additionalProperties/type',
																						keyword:
																							'type',
																						params: {
																							type: 'string',
																						},
																						message:
																							'must be string',
																					};
																				if (
																					vErrors ===
																					null
																				) {
																					vErrors =
																						[
																							err676,
																						];
																				} else {
																					vErrors.push(
																						err676
																					);
																				}
																				errors++;
																			}
																		}
																	} else {
																		const err677 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/authorsMap',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/authorsMap/type',
																				keyword:
																					'type',
																				params: {
																					type: 'object',
																				},
																				message:
																					'must be object',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err677,
																				];
																		} else {
																			vErrors.push(
																				err677
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data335.urlsMode !==
																	undefined
																) {
																	let data413 =
																		data335.urlsMode;
																	if (
																		typeof data413 !==
																		'string'
																	) {
																		const err678 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/urlsMode',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/urlsMode/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err678,
																				];
																		} else {
																			vErrors.push(
																				err678
																			);
																		}
																		errors++;
																	}
																	if (
																		!(
																			data413 ===
																				'rewrite' ||
																			data413 ===
																				'preserve'
																		)
																	) {
																		const err679 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/urlsMode',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/urlsMode/enum',
																				keyword:
																					'enum',
																				params: {
																					allowedValues:
																						schema12
																							.properties
																							.additionalStepsAfterExecution
																							.items
																							.oneOf[5]
																							.properties
																							.content
																							.items
																							.anyOf[3]
																							.properties
																							.urlsMode
																							.enum,
																				},
																				message:
																					'must be equal to one of the allowed values',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err679,
																				];
																		} else {
																			vErrors.push(
																				err679
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data335.urlsMap !==
																	undefined
																) {
																	let data414 =
																		data335.urlsMap;
																	if (
																		data414 &&
																		typeof data414 ==
																			'object' &&
																		!Array.isArray(
																			data414
																		)
																	) {
																		for (const key77 in data414) {
																			const _errs1116 =
																				errors;
																			if (
																				typeof key77 ===
																				'string'
																			) {
																				if (
																					!formats0(
																						key77
																					)
																				) {
																					const err680 =
																						{
																							instancePath:
																								instancePath +
																								'/additionalStepsAfterExecution/' +
																								i39 +
																								'/content/' +
																								i40 +
																								'/urlsMap',
																							schemaPath:
																								'#/definitions/DataSources.URLReference/format',
																							keyword:
																								'format',
																							params: {
																								format: 'whatwg-http-url',
																							},
																							message:
																								'must match format "' +
																								'whatwg-http-url' +
																								'"',
																							propertyName:
																								key77,
																						};
																					if (
																						vErrors ===
																						null
																					) {
																						vErrors =
																							[
																								err680,
																							];
																					} else {
																						vErrors.push(
																							err680
																						);
																					}
																					errors++;
																				}
																			} else {
																				const err681 =
																					{
																						instancePath:
																							instancePath +
																							'/additionalStepsAfterExecution/' +
																							i39 +
																							'/content/' +
																							i40 +
																							'/urlsMap',
																						schemaPath:
																							'#/definitions/DataSources.URLReference/type',
																						keyword:
																							'type',
																						params: {
																							type: 'string',
																						},
																						message:
																							'must be string',
																						propertyName:
																							key77,
																					};
																				if (
																					vErrors ===
																					null
																				) {
																					vErrors =
																						[
																							err681,
																						];
																				} else {
																					vErrors.push(
																						err681
																					);
																				}
																				errors++;
																			}
																			var valid263 =
																				_errs1116 ===
																				errors;
																			if (
																				!valid263
																			) {
																				const err682 =
																					{
																						instancePath:
																							instancePath +
																							'/additionalStepsAfterExecution/' +
																							i39 +
																							'/content/' +
																							i40 +
																							'/urlsMap',
																						schemaPath:
																							'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/urlsMap/propertyNames',
																						keyword:
																							'propertyNames',
																						params: {
																							propertyName:
																								key77,
																						},
																						message:
																							'property name must be valid',
																					};
																				if (
																					vErrors ===
																					null
																				) {
																					vErrors =
																						[
																							err682,
																						];
																				} else {
																					vErrors.push(
																						err682
																					);
																				}
																				errors++;
																			}
																		}
																		for (const key78 in data414) {
																			let data415 =
																				data414[
																					key78
																				];
																			if (
																				typeof data415 ===
																				'string'
																			) {
																				if (
																					!formats0(
																						data415
																					)
																				) {
																					const err683 =
																						{
																							instancePath:
																								instancePath +
																								'/additionalStepsAfterExecution/' +
																								i39 +
																								'/content/' +
																								i40 +
																								'/urlsMap/' +
																								key78
																									.replace(
																										/~/g,
																										'~0'
																									)
																									.replace(
																										/\//g,
																										'~1'
																									),
																							schemaPath:
																								'#/definitions/DataSources.URLReference/format',
																							keyword:
																								'format',
																							params: {
																								format: 'whatwg-http-url',
																							},
																							message:
																								'must match format "' +
																								'whatwg-http-url' +
																								'"',
																						};
																					if (
																						vErrors ===
																						null
																					) {
																						vErrors =
																							[
																								err683,
																							];
																					} else {
																						vErrors.push(
																							err683
																						);
																					}
																					errors++;
																				}
																			} else {
																				const err684 =
																					{
																						instancePath:
																							instancePath +
																							'/additionalStepsAfterExecution/' +
																							i39 +
																							'/content/' +
																							i40 +
																							'/urlsMap/' +
																							key78
																								.replace(
																									/~/g,
																									'~0'
																								)
																								.replace(
																									/\//g,
																									'~1'
																								),
																						schemaPath:
																							'#/definitions/DataSources.URLReference/type',
																						keyword:
																							'type',
																						params: {
																							type: 'string',
																						},
																						message:
																							'must be string',
																					};
																				if (
																					vErrors ===
																					null
																				) {
																					vErrors =
																						[
																							err684,
																						];
																				} else {
																					vErrors.push(
																						err684
																					);
																				}
																				errors++;
																			}
																		}
																	} else {
																		const err685 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/urlsMap',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/urlsMap/type',
																				keyword:
																					'type',
																				params: {
																					type: 'object',
																				},
																				message:
																					'must be object',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err685,
																				];
																		} else {
																			vErrors.push(
																				err685
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data335.type !==
																	undefined
																) {
																	let data416 =
																		data335.type;
																	if (
																		typeof data416 !==
																		'string'
																	) {
																		const err686 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/type',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/type/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err686,
																				];
																		} else {
																			vErrors.push(
																				err686
																			);
																		}
																		errors++;
																	}
																	if (
																		'wxr' !==
																		data416
																	) {
																		const err687 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/type',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/type/const',
																				keyword:
																					'const',
																				params: {
																					allowedValue:
																						'wxr',
																				},
																				message:
																					'must be equal to constant',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err687,
																				];
																		} else {
																			vErrors.push(
																				err687
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data335.source !==
																	undefined
																) {
																	let data417 =
																		data335.source;
																	const _errs1126 =
																		errors;
																	let valid267 = false;
																	const _errs1127 =
																		errors;
																	if (
																		!validate40(
																			data417,
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/source',
																				parentData:
																					data335,
																				parentDataProperty:
																					'source',
																				rootData,
																			}
																		)
																	) {
																		vErrors =
																			vErrors ===
																			null
																				? validate40.errors
																				: vErrors.concat(
																						validate40.errors
																					);
																		errors =
																			vErrors.length;
																	}
																	var _valid38 =
																		_errs1127 ===
																		errors;
																	valid267 =
																		valid267 ||
																		_valid38;
																	if (
																		!valid267
																	) {
																		const _errs1128 =
																			errors;
																		if (
																			Array.isArray(
																				data417
																			)
																		) {
																			const len52 =
																				data417.length;
																			for (
																				let i52 = 0;
																				i52 <
																				len52;
																				i52++
																			) {
																				if (
																					!validate40(
																						data417[
																							i52
																						],
																						{
																							instancePath:
																								instancePath +
																								'/additionalStepsAfterExecution/' +
																								i39 +
																								'/content/' +
																								i40 +
																								'/source/' +
																								i52,
																							parentData:
																								data417,
																							parentDataProperty:
																								i52,
																							rootData,
																						}
																					)
																				) {
																					vErrors =
																						vErrors ===
																						null
																							? validate40.errors
																							: vErrors.concat(
																									validate40.errors
																								);
																					errors =
																						vErrors.length;
																				}
																			}
																		} else {
																			const err688 =
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/content/' +
																						i40 +
																						'/source',
																					schemaPath:
																						'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/source/anyOf/1/type',
																					keyword:
																						'type',
																					params: {
																						type: 'array',
																					},
																					message:
																						'must be array',
																				};
																			if (
																				vErrors ===
																				null
																			) {
																				vErrors =
																					[
																						err688,
																					];
																			} else {
																				vErrors.push(
																					err688
																				);
																			}
																			errors++;
																		}
																		var _valid38 =
																			_errs1128 ===
																			errors;
																		valid267 =
																			valid267 ||
																			_valid38;
																	}
																	if (
																		!valid267
																	) {
																		const err689 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/source',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/source/anyOf',
																				keyword:
																					'anyOf',
																				params: {},
																				message:
																					'must match a schema in anyOf',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err689,
																				];
																		} else {
																			vErrors.push(
																				err689
																			);
																		}
																		errors++;
																	} else {
																		errors =
																			_errs1126;
																		if (
																			vErrors !==
																			null
																		) {
																			if (
																				_errs1126
																			) {
																				vErrors.length =
																					_errs1126;
																			} else {
																				vErrors =
																					null;
																			}
																		}
																	}
																}
																if (
																	data335.staticAssets !==
																	undefined
																) {
																	let data419 =
																		data335.staticAssets;
																	if (
																		typeof data419 !==
																		'string'
																	) {
																		const err690 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/staticAssets',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/staticAssets/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err690,
																				];
																		} else {
																			vErrors.push(
																				err690
																			);
																		}
																		errors++;
																	}
																	if (
																		!(
																			data419 ===
																				'fetch' ||
																			data419 ===
																				'hotlink'
																		)
																	) {
																		const err691 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/staticAssets',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/staticAssets/enum',
																				keyword:
																					'enum',
																				params: {
																					allowedValues:
																						schema12
																							.properties
																							.additionalStepsAfterExecution
																							.items
																							.oneOf[5]
																							.properties
																							.content
																							.items
																							.anyOf[3]
																							.properties
																							.staticAssets
																							.enum,
																				},
																				message:
																					'must be equal to one of the allowed values',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err691,
																				];
																		} else {
																			vErrors.push(
																				err691
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data335.defaultAuthorUsername !==
																	undefined
																) {
																	if (
																		typeof data335.defaultAuthorUsername !==
																		'string'
																	) {
																		const err692 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/defaultAuthorUsername',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/defaultAuthorUsername/type',
																				keyword:
																					'type',
																				params: {
																					type: 'string',
																				},
																				message:
																					'must be string',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err692,
																				];
																		} else {
																			vErrors.push(
																				err692
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data335.importUsers !==
																	undefined
																) {
																	if (
																		typeof data335.importUsers !==
																		'boolean'
																	) {
																		const err693 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/importUsers',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/importUsers/type',
																				keyword:
																					'type',
																				params: {
																					type: 'boolean',
																				},
																				message:
																					'must be boolean',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err693,
																				];
																		} else {
																			vErrors.push(
																				err693
																			);
																		}
																		errors++;
																	}
																}
																if (
																	data335.importComments !==
																	undefined
																) {
																	if (
																		typeof data335.importComments !==
																		'boolean'
																	) {
																		const err694 =
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/content/' +
																					i40 +
																					'/importComments',
																				schemaPath:
																					'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/properties/importComments/type',
																				keyword:
																					'type',
																				params: {
																					type: 'boolean',
																				},
																				message:
																					'must be boolean',
																			};
																		if (
																			vErrors ===
																			null
																		) {
																			vErrors =
																				[
																					err694,
																				];
																		} else {
																			vErrors.push(
																				err694
																			);
																		}
																		errors++;
																	}
																}
															} else {
																const err695 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/content/' +
																		i40,
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf/3/type',
																	keyword:
																		'type',
																	params: {
																		type: 'object',
																	},
																	message:
																		'must be object',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err695,
																	];
																} else {
																	vErrors.push(
																		err695
																	);
																}
																errors++;
															}
															var _valid31 =
																_errs1102 ===
																errors;
															valid212 =
																valid212 ||
																_valid31;
														}
													}
												}
												if (!valid212) {
													const err696 = {
														instancePath:
															instancePath +
															'/additionalStepsAfterExecution/' +
															i39 +
															'/content/' +
															i40,
														schemaPath:
															'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/items/anyOf',
														keyword: 'anyOf',
														params: {},
														message:
															'must match a schema in anyOf',
													};
													if (vErrors === null) {
														vErrors = [err696];
													} else {
														vErrors.push(err696);
													}
													errors++;
												} else {
													errors = _errs897;
													if (vErrors !== null) {
														if (_errs897) {
															vErrors.length =
																_errs897;
														} else {
															vErrors = null;
														}
													}
												}
											}
										} else {
											const err697 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/content',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/5/properties/content/type',
												keyword: 'type',
												params: { type: 'array' },
												message: 'must be array',
											};
											if (vErrors === null) {
												vErrors = [err697];
											} else {
												vErrors.push(err697);
											}
											errors++;
										}
									}
								} else {
									const err698 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/5/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err698];
									} else {
										vErrors.push(err698);
									}
									errors++;
								}
							} else if (tag1 === 'importMedia') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err699 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/6/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err699];
										} else {
											vErrors.push(err699);
										}
										errors++;
									}
									if (data315.media === undefined) {
										const err700 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/6/required',
											keyword: 'required',
											params: {
												missingProperty: 'media',
											},
											message:
												"must have required property '" +
												'media' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err700];
										} else {
											vErrors.push(err700);
										}
										errors++;
									}
									for (const key79 in data315) {
										if (
											!(
												key79 === 'step' ||
												key79 === 'media'
											)
										) {
											const err701 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/6/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key79,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err701];
											} else {
												vErrors.push(err701);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data423 = data315.step;
										if (typeof data423 !== 'string') {
											const err702 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/6/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err702];
											} else {
												vErrors.push(err702);
											}
											errors++;
										}
										if ('importMedia' !== data423) {
											const err703 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/6/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue: 'importMedia',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err703];
											} else {
												vErrors.push(err703);
											}
											errors++;
										}
									}
									if (data315.media !== undefined) {
										let data424 = data315.media;
										if (Array.isArray(data424)) {
											const len53 = data424.length;
											for (
												let i53 = 0;
												i53 < len53;
												i53++
											) {
												let data425 = data424[i53];
												const _errs1147 = errors;
												let valid274 = false;
												const _errs1148 = errors;
												if (
													!validate40(data425, {
														instancePath:
															instancePath +
															'/additionalStepsAfterExecution/' +
															i39 +
															'/media/' +
															i53,
														parentData: data424,
														parentDataProperty: i53,
														rootData,
													})
												) {
													vErrors =
														vErrors === null
															? validate40.errors
															: vErrors.concat(
																	validate40.errors
																);
													errors = vErrors.length;
												}
												var _valid39 =
													_errs1148 === errors;
												valid274 = valid274 || _valid39;
												if (!valid274) {
													const _errs1149 = errors;
													if (
														data425 &&
														typeof data425 ==
															'object' &&
														!Array.isArray(data425)
													) {
														if (
															data425.source ===
															undefined
														) {
															const err704 = {
																instancePath:
																	instancePath +
																	'/additionalStepsAfterExecution/' +
																	i39 +
																	'/media/' +
																	i53,
																schemaPath:
																	'#/properties/additionalStepsAfterExecution/items/oneOf/6/properties/media/items/anyOf/1/required',
																keyword:
																	'required',
																params: {
																	missingProperty:
																		'source',
																},
																message:
																	"must have required property '" +
																	'source' +
																	"'",
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err704,
																];
															} else {
																vErrors.push(
																	err704
																);
															}
															errors++;
														}
														for (const key80 in data425) {
															if (
																!(
																	key80 ===
																		'source' ||
																	key80 ===
																		'title' ||
																	key80 ===
																		'description' ||
																	key80 ===
																		'alt' ||
																	key80 ===
																		'caption'
																)
															) {
																const err705 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/media/' +
																		i53,
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/6/properties/media/items/anyOf/1/additionalProperties',
																	keyword:
																		'additionalProperties',
																	params: {
																		additionalProperty:
																			key80,
																	},
																	message:
																		'must NOT have additional properties',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err705,
																	];
																} else {
																	vErrors.push(
																		err705
																	);
																}
																errors++;
															}
														}
														if (
															data425.source !==
															undefined
														) {
															if (
																!validate40(
																	data425.source,
																	{
																		instancePath:
																			instancePath +
																			'/additionalStepsAfterExecution/' +
																			i39 +
																			'/media/' +
																			i53 +
																			'/source',
																		parentData:
																			data425,
																		parentDataProperty:
																			'source',
																		rootData,
																	}
																)
															) {
																vErrors =
																	vErrors ===
																	null
																		? validate40.errors
																		: vErrors.concat(
																				validate40.errors
																			);
																errors =
																	vErrors.length;
															}
														}
														if (
															data425.title !==
															undefined
														) {
															if (
																typeof data425.title !==
																'string'
															) {
																const err706 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/media/' +
																		i53 +
																		'/title',
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/6/properties/media/items/anyOf/1/properties/title/type',
																	keyword:
																		'type',
																	params: {
																		type: 'string',
																	},
																	message:
																		'must be string',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err706,
																	];
																} else {
																	vErrors.push(
																		err706
																	);
																}
																errors++;
															}
														}
														if (
															data425.description !==
															undefined
														) {
															if (
																typeof data425.description !==
																'string'
															) {
																const err707 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/media/' +
																		i53 +
																		'/description',
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/6/properties/media/items/anyOf/1/properties/description/type',
																	keyword:
																		'type',
																	params: {
																		type: 'string',
																	},
																	message:
																		'must be string',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err707,
																	];
																} else {
																	vErrors.push(
																		err707
																	);
																}
																errors++;
															}
														}
														if (
															data425.alt !==
															undefined
														) {
															if (
																typeof data425.alt !==
																'string'
															) {
																const err708 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/media/' +
																		i53 +
																		'/alt',
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/6/properties/media/items/anyOf/1/properties/alt/type',
																	keyword:
																		'type',
																	params: {
																		type: 'string',
																	},
																	message:
																		'must be string',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err708,
																	];
																} else {
																	vErrors.push(
																		err708
																	);
																}
																errors++;
															}
														}
														if (
															data425.caption !==
															undefined
														) {
															if (
																typeof data425.caption !==
																'string'
															) {
																const err709 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/media/' +
																		i53 +
																		'/caption',
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/6/properties/media/items/anyOf/1/properties/caption/type',
																	keyword:
																		'type',
																	params: {
																		type: 'string',
																	},
																	message:
																		'must be string',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err709,
																	];
																} else {
																	vErrors.push(
																		err709
																	);
																}
																errors++;
															}
														}
													} else {
														const err710 = {
															instancePath:
																instancePath +
																'/additionalStepsAfterExecution/' +
																i39 +
																'/media/' +
																i53,
															schemaPath:
																'#/properties/additionalStepsAfterExecution/items/oneOf/6/properties/media/items/anyOf/1/type',
															keyword: 'type',
															params: {
																type: 'object',
															},
															message:
																'must be object',
														};
														if (vErrors === null) {
															vErrors = [err710];
														} else {
															vErrors.push(
																err710
															);
														}
														errors++;
													}
													var _valid39 =
														_errs1149 === errors;
													valid274 =
														valid274 || _valid39;
												}
												if (!valid274) {
													const err711 = {
														instancePath:
															instancePath +
															'/additionalStepsAfterExecution/' +
															i39 +
															'/media/' +
															i53,
														schemaPath:
															'#/properties/additionalStepsAfterExecution/items/oneOf/6/properties/media/items/anyOf',
														keyword: 'anyOf',
														params: {},
														message:
															'must match a schema in anyOf',
													};
													if (vErrors === null) {
														vErrors = [err711];
													} else {
														vErrors.push(err711);
													}
													errors++;
												} else {
													errors = _errs1147;
													if (vErrors !== null) {
														if (_errs1147) {
															vErrors.length =
																_errs1147;
														} else {
															vErrors = null;
														}
													}
												}
											}
										} else {
											const err712 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/media',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/6/properties/media/type',
												keyword: 'type',
												params: { type: 'array' },
												message: 'must be array',
											};
											if (vErrors === null) {
												vErrors = [err712];
											} else {
												vErrors.push(err712);
											}
											errors++;
										}
									}
								} else {
									const err713 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/6/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err713];
									} else {
										vErrors.push(err713);
									}
									errors++;
								}
							} else if (tag1 === 'importThemeStarterContent') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err714 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/7/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err714];
										} else {
											vErrors.push(err714);
										}
										errors++;
									}
									for (const key81 in data315) {
										if (
											!(
												key81 === 'step' ||
												key81 === 'themeSlug'
											)
										) {
											const err715 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/7/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key81,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err715];
											} else {
												vErrors.push(err715);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data431 = data315.step;
										if (typeof data431 !== 'string') {
											const err716 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/7/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err716];
											} else {
												vErrors.push(err716);
											}
											errors++;
										}
										if (
											'importThemeStarterContent' !==
											data431
										) {
											const err717 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/7/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue:
														'importThemeStarterContent',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err717];
											} else {
												vErrors.push(err717);
											}
											errors++;
										}
									}
									if (data315.themeSlug !== undefined) {
										if (
											typeof data315.themeSlug !==
											'string'
										) {
											const err718 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/themeSlug',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/7/properties/themeSlug/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err718];
											} else {
												vErrors.push(err718);
											}
											errors++;
										}
									}
								} else {
									const err719 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/7/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err719];
									} else {
										vErrors.push(err719);
									}
									errors++;
								}
							} else if (tag1 === 'installPlugin') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.source === undefined) {
										const err720 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/8/required',
											keyword: 'required',
											params: {
												missingProperty: 'source',
											},
											message:
												"must have required property '" +
												'source' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err720];
										} else {
											vErrors.push(err720);
										}
										errors++;
									}
									if (data315.step === undefined) {
										const err721 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/8/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err721];
										} else {
											vErrors.push(err721);
										}
										errors++;
									}
									for (const key82 in data315) {
										if (
											!(
												key82 === 'source' ||
												key82 === 'active' ||
												key82 === 'activationOptions' ||
												key82 ===
													'targetDirectoryName' ||
												key82 === 'onError' ||
												key82 ===
													'ifAlreadyInstalled' ||
												key82 === 'humanReadableName' ||
												key82 === 'step'
											)
										) {
											const err722 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/8/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key82,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err722];
											} else {
												vErrors.push(err722);
											}
											errors++;
										}
									}
									if (data315.source !== undefined) {
										let data433 = data315.source;
										const _errs1172 = errors;
										let valid280 = false;
										const _errs1173 = errors;
										if (
											!validate12(data433, {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/source',
												parentData: data315,
												parentDataProperty: 'source',
												rootData,
											})
										) {
											vErrors =
												vErrors === null
													? validate12.errors
													: vErrors.concat(
															validate12.errors
														);
											errors = vErrors.length;
										}
										var _valid40 = _errs1173 === errors;
										valid280 = valid280 || _valid40;
										if (!valid280) {
											const _errs1174 = errors;
											if (
												!validate30(data433, {
													instancePath:
														instancePath +
														'/additionalStepsAfterExecution/' +
														i39 +
														'/source',
													parentData: data315,
													parentDataProperty:
														'source',
													rootData,
												})
											) {
												vErrors =
													vErrors === null
														? validate30.errors
														: vErrors.concat(
																validate30.errors
															);
												errors = vErrors.length;
											}
											var _valid40 = _errs1174 === errors;
											valid280 = valid280 || _valid40;
										}
										if (!valid280) {
											const err723 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/source',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/source/anyOf',
												keyword: 'anyOf',
												params: {},
												message:
													'must match a schema in anyOf',
											};
											if (vErrors === null) {
												vErrors = [err723];
											} else {
												vErrors.push(err723);
											}
											errors++;
										} else {
											errors = _errs1172;
											if (vErrors !== null) {
												if (_errs1172) {
													vErrors.length = _errs1172;
												} else {
													vErrors = null;
												}
											}
										}
									}
									if (data315.active !== undefined) {
										if (
											typeof data315.active !== 'boolean'
										) {
											const err724 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/active',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/active/type',
												keyword: 'type',
												params: { type: 'boolean' },
												message: 'must be boolean',
											};
											if (vErrors === null) {
												vErrors = [err724];
											} else {
												vErrors.push(err724);
											}
											errors++;
										}
									}
									if (
										data315.activationOptions !== undefined
									) {
										let data435 = data315.activationOptions;
										if (
											data435 &&
											typeof data435 == 'object' &&
											!Array.isArray(data435)
										) {
											for (const key83 in data435) {
												let data436 = data435[key83];
												const _errs1181 = errors;
												let valid282 = false;
												const _errs1182 = errors;
												if (
													typeof data436 !== 'string'
												) {
													const err725 = {
														instancePath:
															instancePath +
															'/additionalStepsAfterExecution/' +
															i39 +
															'/activationOptions/' +
															key83
																.replace(
																	/~/g,
																	'~0'
																)
																.replace(
																	/\//g,
																	'~1'
																),
														schemaPath:
															'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/activationOptions/additionalProperties/anyOf/0/type',
														keyword: 'type',
														params: {
															type: 'string',
														},
														message:
															'must be string',
													};
													if (vErrors === null) {
														vErrors = [err725];
													} else {
														vErrors.push(err725);
													}
													errors++;
												}
												var _valid41 =
													_errs1182 === errors;
												valid282 = valid282 || _valid41;
												if (!valid282) {
													const _errs1184 = errors;
													if (
														typeof data436 !==
														'boolean'
													) {
														const err726 = {
															instancePath:
																instancePath +
																'/additionalStepsAfterExecution/' +
																i39 +
																'/activationOptions/' +
																key83
																	.replace(
																		/~/g,
																		'~0'
																	)
																	.replace(
																		/\//g,
																		'~1'
																	),
															schemaPath:
																'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/activationOptions/additionalProperties/anyOf/1/type',
															keyword: 'type',
															params: {
																type: 'boolean',
															},
															message:
																'must be boolean',
														};
														if (vErrors === null) {
															vErrors = [err726];
														} else {
															vErrors.push(
																err726
															);
														}
														errors++;
													}
													var _valid41 =
														_errs1184 === errors;
													valid282 =
														valid282 || _valid41;
													if (!valid282) {
														const _errs1186 =
															errors;
														if (
															!(
																typeof data436 ==
																	'number' &&
																isFinite(
																	data436
																)
															)
														) {
															const err727 = {
																instancePath:
																	instancePath +
																	'/additionalStepsAfterExecution/' +
																	i39 +
																	'/activationOptions/' +
																	key83
																		.replace(
																			/~/g,
																			'~0'
																		)
																		.replace(
																			/\//g,
																			'~1'
																		),
																schemaPath:
																	'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/activationOptions/additionalProperties/anyOf/2/type',
																keyword: 'type',
																params: {
																	type: 'number',
																},
																message:
																	'must be number',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err727,
																];
															} else {
																vErrors.push(
																	err727
																);
															}
															errors++;
														}
														var _valid41 =
															_errs1186 ===
															errors;
														valid282 =
															valid282 ||
															_valid41;
														if (!valid282) {
															const _errs1188 =
																errors;
															if (
																Array.isArray(
																	data436
																)
															) {
																const len54 =
																	data436.length;
																for (
																	let i54 = 0;
																	i54 < len54;
																	i54++
																) {
																	if (
																		!validate34(
																			data436[
																				i54
																			],
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/activationOptions/' +
																					key83
																						.replace(
																							/~/g,
																							'~0'
																						)
																						.replace(
																							/\//g,
																							'~1'
																						) +
																					'/' +
																					i54,
																				parentData:
																					data436,
																				parentDataProperty:
																					i54,
																				rootData,
																			}
																		)
																	) {
																		vErrors =
																			vErrors ===
																			null
																				? validate34.errors
																				: vErrors.concat(
																						validate34.errors
																					);
																		errors =
																			vErrors.length;
																	}
																}
															} else {
																const err728 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/activationOptions/' +
																		key83
																			.replace(
																				/~/g,
																				'~0'
																			)
																			.replace(
																				/\//g,
																				'~1'
																			),
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/activationOptions/additionalProperties/anyOf/3/type',
																	keyword:
																		'type',
																	params: {
																		type: 'array',
																	},
																	message:
																		'must be array',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err728,
																	];
																} else {
																	vErrors.push(
																		err728
																	);
																}
																errors++;
															}
															var _valid41 =
																_errs1188 ===
																errors;
															valid282 =
																valid282 ||
																_valid41;
															if (!valid282) {
																const _errs1191 =
																	errors;
																if (
																	data436 &&
																	typeof data436 ==
																		'object' &&
																	!Array.isArray(
																		data436
																	)
																) {
																	for (const key84 in data436) {
																		if (
																			!validate34(
																				data436[
																					key84
																				],
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/activationOptions/' +
																						key83
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/' +
																						key84
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							),
																					parentData:
																						data436,
																					parentDataProperty:
																						key84,
																					rootData,
																				}
																			)
																		) {
																			vErrors =
																				vErrors ===
																				null
																					? validate34.errors
																					: vErrors.concat(
																							validate34.errors
																						);
																			errors =
																				vErrors.length;
																		}
																	}
																} else {
																	const err729 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/activationOptions/' +
																				key83
																					.replace(
																						/~/g,
																						'~0'
																					)
																					.replace(
																						/\//g,
																						'~1'
																					),
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/activationOptions/additionalProperties/anyOf/4/type',
																			keyword:
																				'type',
																			params: {
																				type: 'object',
																			},
																			message:
																				'must be object',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err729,
																			];
																	} else {
																		vErrors.push(
																			err729
																		);
																	}
																	errors++;
																}
																var _valid41 =
																	_errs1191 ===
																	errors;
																valid282 =
																	valid282 ||
																	_valid41;
															}
														}
													}
												}
												if (!valid282) {
													const err730 = {
														instancePath:
															instancePath +
															'/additionalStepsAfterExecution/' +
															i39 +
															'/activationOptions/' +
															key83
																.replace(
																	/~/g,
																	'~0'
																)
																.replace(
																	/\//g,
																	'~1'
																),
														schemaPath:
															'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/activationOptions/additionalProperties/anyOf',
														keyword: 'anyOf',
														params: {},
														message:
															'must match a schema in anyOf',
													};
													if (vErrors === null) {
														vErrors = [err730];
													} else {
														vErrors.push(err730);
													}
													errors++;
												} else {
													errors = _errs1181;
													if (vErrors !== null) {
														if (_errs1181) {
															vErrors.length =
																_errs1181;
														} else {
															vErrors = null;
														}
													}
												}
											}
										} else {
											const err731 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/activationOptions',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/activationOptions/type',
												keyword: 'type',
												params: { type: 'object' },
												message: 'must be object',
											};
											if (vErrors === null) {
												vErrors = [err731];
											} else {
												vErrors.push(err731);
											}
											errors++;
										}
									}
									if (
										data315.targetDirectoryName !==
										undefined
									) {
										let data439 =
											data315.targetDirectoryName;
										if (typeof data439 === 'string') {
											if (!pattern3.test(data439)) {
												const err732 = {
													instancePath:
														instancePath +
														'/additionalStepsAfterExecution/' +
														i39 +
														'/targetDirectoryName',
													schemaPath:
														'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/targetDirectoryName/pattern',
													keyword: 'pattern',
													params: {
														pattern:
															'^(?!(?:\\.|\\.\\.)$)[^/]+$',
													},
													message:
														'must match pattern "' +
														'^(?!(?:\\.|\\.\\.)$)[^/]+$' +
														'"',
												};
												if (vErrors === null) {
													vErrors = [err732];
												} else {
													vErrors.push(err732);
												}
												errors++;
											}
										} else {
											const err733 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/targetDirectoryName',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/targetDirectoryName/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err733];
											} else {
												vErrors.push(err733);
											}
											errors++;
										}
									}
									if (data315.onError !== undefined) {
										let data440 = data315.onError;
										if (typeof data440 !== 'string') {
											const err734 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/onError',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/onError/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err734];
											} else {
												vErrors.push(err734);
											}
											errors++;
										}
										if (
											!(
												data440 === 'skip-plugin' ||
												data440 === 'throw'
											)
										) {
											const err735 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/onError',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/onError/enum',
												keyword: 'enum',
												params: {
													allowedValues:
														schema12.properties
															.additionalStepsAfterExecution
															.items.oneOf[8]
															.properties.onError
															.enum,
												},
												message:
													'must be equal to one of the allowed values',
											};
											if (vErrors === null) {
												vErrors = [err735];
											} else {
												vErrors.push(err735);
											}
											errors++;
										}
									}
									if (
										data315.ifAlreadyInstalled !== undefined
									) {
										let data441 =
											data315.ifAlreadyInstalled;
										if (typeof data441 !== 'string') {
											const err736 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/ifAlreadyInstalled',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/ifAlreadyInstalled/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err736];
											} else {
												vErrors.push(err736);
											}
											errors++;
										}
										if (
											!(
												data441 === 'overwrite' ||
												data441 === 'skip' ||
												data441 === 'error'
											)
										) {
											const err737 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/ifAlreadyInstalled',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/ifAlreadyInstalled/enum',
												keyword: 'enum',
												params: {
													allowedValues:
														schema12.properties
															.additionalStepsAfterExecution
															.items.oneOf[8]
															.properties
															.ifAlreadyInstalled
															.enum,
												},
												message:
													'must be equal to one of the allowed values',
											};
											if (vErrors === null) {
												vErrors = [err737];
											} else {
												vErrors.push(err737);
											}
											errors++;
										}
									}
									if (
										data315.humanReadableName !== undefined
									) {
										if (
											typeof data315.humanReadableName !==
											'string'
										) {
											const err738 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/humanReadableName',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/humanReadableName/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err738];
											} else {
												vErrors.push(err738);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data443 = data315.step;
										if (typeof data443 !== 'string') {
											const err739 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err739];
											} else {
												vErrors.push(err739);
											}
											errors++;
										}
										if ('installPlugin' !== data443) {
											const err740 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/8/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue:
														'installPlugin',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err740];
											} else {
												vErrors.push(err740);
											}
											errors++;
										}
									}
								} else {
									const err741 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/8/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err741];
									} else {
										vErrors.push(err741);
									}
									errors++;
								}
							} else if (tag1 === 'installTheme') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.source === undefined) {
										const err742 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/9/required',
											keyword: 'required',
											params: {
												missingProperty: 'source',
											},
											message:
												"must have required property '" +
												'source' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err742];
										} else {
											vErrors.push(err742);
										}
										errors++;
									}
									if (data315.step === undefined) {
										const err743 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/9/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err743];
										} else {
											vErrors.push(err743);
										}
										errors++;
									}
									for (const key85 in data315) {
										if (
											!(
												key85 === 'source' ||
												key85 ===
													'importStarterContent' ||
												key85 ===
													'targetDirectoryName' ||
												key85 === 'onError' ||
												key85 ===
													'ifAlreadyInstalled' ||
												key85 === 'humanReadableName' ||
												key85 === 'step' ||
												key85 === 'active'
											)
										) {
											const err744 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/9/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key85,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err744];
											} else {
												vErrors.push(err744);
											}
											errors++;
										}
									}
									if (data315.source !== undefined) {
										let data444 = data315.source;
										const _errs1209 = errors;
										let valid288 = false;
										const _errs1210 = errors;
										if (
											!validate20(data444, {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/source',
												parentData: data315,
												parentDataProperty: 'source',
												rootData,
											})
										) {
											vErrors =
												vErrors === null
													? validate20.errors
													: vErrors.concat(
															validate20.errors
														);
											errors = vErrors.length;
										}
										var _valid42 = _errs1210 === errors;
										valid288 = valid288 || _valid42;
										if (!valid288) {
											const _errs1211 = errors;
											if (
												!validate12(data444, {
													instancePath:
														instancePath +
														'/additionalStepsAfterExecution/' +
														i39 +
														'/source',
													parentData: data315,
													parentDataProperty:
														'source',
													rootData,
												})
											) {
												vErrors =
													vErrors === null
														? validate12.errors
														: vErrors.concat(
																validate12.errors
															);
												errors = vErrors.length;
											}
											var _valid42 = _errs1211 === errors;
											valid288 = valid288 || _valid42;
										}
										if (!valid288) {
											const err745 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/source',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/9/properties/source/anyOf',
												keyword: 'anyOf',
												params: {},
												message:
													'must match a schema in anyOf',
											};
											if (vErrors === null) {
												vErrors = [err745];
											} else {
												vErrors.push(err745);
											}
											errors++;
										} else {
											errors = _errs1209;
											if (vErrors !== null) {
												if (_errs1209) {
													vErrors.length = _errs1209;
												} else {
													vErrors = null;
												}
											}
										}
									}
									if (
										data315.importStarterContent !==
										undefined
									) {
										if (
											typeof data315.importStarterContent !==
											'boolean'
										) {
											const err746 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/importStarterContent',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/9/properties/importStarterContent/type',
												keyword: 'type',
												params: { type: 'boolean' },
												message: 'must be boolean',
											};
											if (vErrors === null) {
												vErrors = [err746];
											} else {
												vErrors.push(err746);
											}
											errors++;
										}
									}
									if (
										data315.targetDirectoryName !==
										undefined
									) {
										let data446 =
											data315.targetDirectoryName;
										if (typeof data446 === 'string') {
											if (!pattern3.test(data446)) {
												const err747 = {
													instancePath:
														instancePath +
														'/additionalStepsAfterExecution/' +
														i39 +
														'/targetDirectoryName',
													schemaPath:
														'#/properties/additionalStepsAfterExecution/items/oneOf/9/properties/targetDirectoryName/pattern',
													keyword: 'pattern',
													params: {
														pattern:
															'^(?!(?:\\.|\\.\\.)$)[^/]+$',
													},
													message:
														'must match pattern "' +
														'^(?!(?:\\.|\\.\\.)$)[^/]+$' +
														'"',
												};
												if (vErrors === null) {
													vErrors = [err747];
												} else {
													vErrors.push(err747);
												}
												errors++;
											}
										} else {
											const err748 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/targetDirectoryName',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/9/properties/targetDirectoryName/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err748];
											} else {
												vErrors.push(err748);
											}
											errors++;
										}
									}
									if (data315.onError !== undefined) {
										let data447 = data315.onError;
										if (typeof data447 !== 'string') {
											const err749 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/onError',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/9/properties/onError/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err749];
											} else {
												vErrors.push(err749);
											}
											errors++;
										}
										if (
											!(
												data447 === 'skip-theme' ||
												data447 === 'throw'
											)
										) {
											const err750 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/onError',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/9/properties/onError/enum',
												keyword: 'enum',
												params: {
													allowedValues:
														schema12.properties
															.additionalStepsAfterExecution
															.items.oneOf[9]
															.properties.onError
															.enum,
												},
												message:
													'must be equal to one of the allowed values',
											};
											if (vErrors === null) {
												vErrors = [err750];
											} else {
												vErrors.push(err750);
											}
											errors++;
										}
									}
									if (
										data315.ifAlreadyInstalled !== undefined
									) {
										let data448 =
											data315.ifAlreadyInstalled;
										if (typeof data448 !== 'string') {
											const err751 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/ifAlreadyInstalled',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/9/properties/ifAlreadyInstalled/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err751];
											} else {
												vErrors.push(err751);
											}
											errors++;
										}
										if (
											!(
												data448 === 'overwrite' ||
												data448 === 'skip' ||
												data448 === 'error'
											)
										) {
											const err752 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/ifAlreadyInstalled',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/9/properties/ifAlreadyInstalled/enum',
												keyword: 'enum',
												params: {
													allowedValues:
														schema12.properties
															.additionalStepsAfterExecution
															.items.oneOf[9]
															.properties
															.ifAlreadyInstalled
															.enum,
												},
												message:
													'must be equal to one of the allowed values',
											};
											if (vErrors === null) {
												vErrors = [err752];
											} else {
												vErrors.push(err752);
											}
											errors++;
										}
									}
									if (
										data315.humanReadableName !== undefined
									) {
										if (
											typeof data315.humanReadableName !==
											'string'
										) {
											const err753 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/humanReadableName',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/9/properties/humanReadableName/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err753];
											} else {
												vErrors.push(err753);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data450 = data315.step;
										if (typeof data450 !== 'string') {
											const err754 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/9/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err754];
											} else {
												vErrors.push(err754);
											}
											errors++;
										}
										if ('installTheme' !== data450) {
											const err755 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/9/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue:
														'installTheme',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err755];
											} else {
												vErrors.push(err755);
											}
											errors++;
										}
									}
									if (data315.active !== undefined) {
										if (
											typeof data315.active !== 'boolean'
										) {
											const err756 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/active',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/9/properties/active/type',
												keyword: 'type',
												params: { type: 'boolean' },
												message: 'must be boolean',
											};
											if (vErrors === null) {
												vErrors = [err756];
											} else {
												vErrors.push(err756);
											}
											errors++;
										}
									}
								} else {
									const err757 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/9/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err757];
									} else {
										vErrors.push(err757);
									}
									errors++;
								}
							} else if (tag1 === 'mkdir') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err758 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/10/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err758];
										} else {
											vErrors.push(err758);
										}
										errors++;
									}
									if (data315.path === undefined) {
										const err759 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/10/required',
											keyword: 'required',
											params: { missingProperty: 'path' },
											message:
												"must have required property '" +
												'path' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err759];
										} else {
											vErrors.push(err759);
										}
										errors++;
									}
									for (const key86 in data315) {
										if (
											!(
												key86 === 'step' ||
												key86 === 'path'
											)
										) {
											const err760 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/10/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key86,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err760];
											} else {
												vErrors.push(err760);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data452 = data315.step;
										if (typeof data452 !== 'string') {
											const err761 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/10/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err761];
											} else {
												vErrors.push(err761);
											}
											errors++;
										}
										if ('mkdir' !== data452) {
											const err762 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/10/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue: 'mkdir',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err762];
											} else {
												vErrors.push(err762);
											}
											errors++;
										}
									}
									if (data315.path !== undefined) {
										if (typeof data315.path !== 'string') {
											const err763 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/path',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/10/properties/path/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err763];
											} else {
												vErrors.push(err763);
											}
											errors++;
										}
									}
								} else {
									const err764 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/10/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err764];
									} else {
										vErrors.push(err764);
									}
									errors++;
								}
							} else if (tag1 === 'mv') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err765 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/11/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err765];
										} else {
											vErrors.push(err765);
										}
										errors++;
									}
									if (data315.fromPath === undefined) {
										const err766 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/11/required',
											keyword: 'required',
											params: {
												missingProperty: 'fromPath',
											},
											message:
												"must have required property '" +
												'fromPath' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err766];
										} else {
											vErrors.push(err766);
										}
										errors++;
									}
									if (data315.toPath === undefined) {
										const err767 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/11/required',
											keyword: 'required',
											params: {
												missingProperty: 'toPath',
											},
											message:
												"must have required property '" +
												'toPath' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err767];
										} else {
											vErrors.push(err767);
										}
										errors++;
									}
									for (const key87 in data315) {
										if (
											!(
												key87 === 'step' ||
												key87 === 'fromPath' ||
												key87 === 'toPath'
											)
										) {
											const err768 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/11/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key87,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err768];
											} else {
												vErrors.push(err768);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data454 = data315.step;
										if (typeof data454 !== 'string') {
											const err769 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/11/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err769];
											} else {
												vErrors.push(err769);
											}
											errors++;
										}
										if ('mv' !== data454) {
											const err770 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/11/properties/step/const',
												keyword: 'const',
												params: { allowedValue: 'mv' },
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err770];
											} else {
												vErrors.push(err770);
											}
											errors++;
										}
									}
									if (data315.fromPath !== undefined) {
										if (
											typeof data315.fromPath !== 'string'
										) {
											const err771 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/fromPath',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/11/properties/fromPath/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err771];
											} else {
												vErrors.push(err771);
											}
											errors++;
										}
									}
									if (data315.toPath !== undefined) {
										if (
											typeof data315.toPath !== 'string'
										) {
											const err772 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/toPath',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/11/properties/toPath/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err772];
											} else {
												vErrors.push(err772);
											}
											errors++;
										}
									}
								} else {
									const err773 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/11/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err773];
									} else {
										vErrors.push(err773);
									}
									errors++;
								}
							} else if (tag1 === 'rm') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err774 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/12/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err774];
										} else {
											vErrors.push(err774);
										}
										errors++;
									}
									if (data315.path === undefined) {
										const err775 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/12/required',
											keyword: 'required',
											params: { missingProperty: 'path' },
											message:
												"must have required property '" +
												'path' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err775];
										} else {
											vErrors.push(err775);
										}
										errors++;
									}
									for (const key88 in data315) {
										if (
											!(
												key88 === 'step' ||
												key88 === 'path'
											)
										) {
											const err776 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/12/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key88,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err776];
											} else {
												vErrors.push(err776);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data457 = data315.step;
										if (typeof data457 !== 'string') {
											const err777 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/12/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err777];
											} else {
												vErrors.push(err777);
											}
											errors++;
										}
										if ('rm' !== data457) {
											const err778 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/12/properties/step/const',
												keyword: 'const',
												params: { allowedValue: 'rm' },
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err778];
											} else {
												vErrors.push(err778);
											}
											errors++;
										}
									}
									if (data315.path !== undefined) {
										if (typeof data315.path !== 'string') {
											const err779 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/path',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/12/properties/path/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err779];
											} else {
												vErrors.push(err779);
											}
											errors++;
										}
									}
								} else {
									const err780 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/12/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err780];
									} else {
										vErrors.push(err780);
									}
									errors++;
								}
							} else if (tag1 === 'rmdir') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err781 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/13/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err781];
										} else {
											vErrors.push(err781);
										}
										errors++;
									}
									if (data315.path === undefined) {
										const err782 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/13/required',
											keyword: 'required',
											params: { missingProperty: 'path' },
											message:
												"must have required property '" +
												'path' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err782];
										} else {
											vErrors.push(err782);
										}
										errors++;
									}
									for (const key89 in data315) {
										if (
											!(
												key89 === 'step' ||
												key89 === 'path'
											)
										) {
											const err783 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/13/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key89,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err783];
											} else {
												vErrors.push(err783);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data459 = data315.step;
										if (typeof data459 !== 'string') {
											const err784 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/13/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err784];
											} else {
												vErrors.push(err784);
											}
											errors++;
										}
										if ('rmdir' !== data459) {
											const err785 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/13/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue: 'rmdir',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err785];
											} else {
												vErrors.push(err785);
											}
											errors++;
										}
									}
									if (data315.path !== undefined) {
										if (typeof data315.path !== 'string') {
											const err786 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/path',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/13/properties/path/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err786];
											} else {
												vErrors.push(err786);
											}
											errors++;
										}
									}
								} else {
									const err787 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/13/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err787];
									} else {
										vErrors.push(err787);
									}
									errors++;
								}
							} else if (tag1 === 'resetData') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err788 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/14/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err788];
										} else {
											vErrors.push(err788);
										}
										errors++;
									}
									for (const key90 in data315) {
										if (
											!(
												key90 === 'step' ||
												key90 === 'contentTypes'
											)
										) {
											const err789 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/14/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key90,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err789];
											} else {
												vErrors.push(err789);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data461 = data315.step;
										if (typeof data461 !== 'string') {
											const err790 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/14/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err790];
											} else {
												vErrors.push(err790);
											}
											errors++;
										}
										if ('resetData' !== data461) {
											const err791 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/14/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue: 'resetData',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err791];
											} else {
												vErrors.push(err791);
											}
											errors++;
										}
									}
									if (data315.contentTypes !== undefined) {
										let data462 = data315.contentTypes;
										if (Array.isArray(data462)) {
											const len55 = data462.length;
											for (
												let i55 = 0;
												i55 < len55;
												i55++
											) {
												let data463 = data462[i55];
												if (
													typeof data463 !== 'string'
												) {
													const err792 = {
														instancePath:
															instancePath +
															'/additionalStepsAfterExecution/' +
															i39 +
															'/contentTypes/' +
															i55,
														schemaPath:
															'#/properties/additionalStepsAfterExecution/items/oneOf/14/properties/contentTypes/items/type',
														keyword: 'type',
														params: {
															type: 'string',
														},
														message:
															'must be string',
													};
													if (vErrors === null) {
														vErrors = [err792];
													} else {
														vErrors.push(err792);
													}
													errors++;
												}
												if (
													!(
														data463 === 'posts' ||
														data463 === 'pages' ||
														data463 === 'comments'
													)
												) {
													const err793 = {
														instancePath:
															instancePath +
															'/additionalStepsAfterExecution/' +
															i39 +
															'/contentTypes/' +
															i55,
														schemaPath:
															'#/properties/additionalStepsAfterExecution/items/oneOf/14/properties/contentTypes/items/enum',
														keyword: 'enum',
														params: {
															allowedValues:
																schema12
																	.properties
																	.additionalStepsAfterExecution
																	.items
																	.oneOf[14]
																	.properties
																	.contentTypes
																	.items.enum,
														},
														message:
															'must be equal to one of the allowed values',
													};
													if (vErrors === null) {
														vErrors = [err793];
													} else {
														vErrors.push(err793);
													}
													errors++;
												}
											}
										} else {
											const err794 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/contentTypes',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/14/properties/contentTypes/type',
												keyword: 'type',
												params: { type: 'array' },
												message: 'must be array',
											};
											if (vErrors === null) {
												vErrors = [err794];
											} else {
												vErrors.push(err794);
											}
											errors++;
										}
									}
								} else {
									const err795 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/14/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err795];
									} else {
										vErrors.push(err795);
									}
									errors++;
								}
							} else if (tag1 === 'runPHP') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err796 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/15/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err796];
										} else {
											vErrors.push(err796);
										}
										errors++;
									}
									if (data315.code === undefined) {
										const err797 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/15/required',
											keyword: 'required',
											params: { missingProperty: 'code' },
											message:
												"must have required property '" +
												'code' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err797];
										} else {
											vErrors.push(err797);
										}
										errors++;
									}
									for (const key91 in data315) {
										if (
											!(
												key91 === 'step' ||
												key91 === 'code' ||
												key91 === 'env'
											)
										) {
											const err798 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/15/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key91,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err798];
											} else {
												vErrors.push(err798);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data464 = data315.step;
										if (typeof data464 !== 'string') {
											const err799 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/15/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err799];
											} else {
												vErrors.push(err799);
											}
											errors++;
										}
										if ('runPHP' !== data464) {
											const err800 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/15/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue: 'runPHP',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err800];
											} else {
												vErrors.push(err800);
											}
											errors++;
										}
									}
									if (data315.code !== undefined) {
										if (
											!validate40(data315.code, {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/code',
												parentData: data315,
												parentDataProperty: 'code',
												rootData,
											})
										) {
											vErrors =
												vErrors === null
													? validate40.errors
													: vErrors.concat(
															validate40.errors
														);
											errors = vErrors.length;
										}
									}
									if (data315.env !== undefined) {
										let data466 = data315.env;
										if (
											data466 &&
											typeof data466 == 'object' &&
											!Array.isArray(data466)
										) {
											for (const key92 in data466) {
												if (
													typeof data466[key92] !==
													'string'
												) {
													const err801 = {
														instancePath:
															instancePath +
															'/additionalStepsAfterExecution/' +
															i39 +
															'/env/' +
															key92
																.replace(
																	/~/g,
																	'~0'
																)
																.replace(
																	/\//g,
																	'~1'
																),
														schemaPath:
															'#/properties/additionalStepsAfterExecution/items/oneOf/15/properties/env/additionalProperties/type',
														keyword: 'type',
														params: {
															type: 'string',
														},
														message:
															'must be string',
													};
													if (vErrors === null) {
														vErrors = [err801];
													} else {
														vErrors.push(err801);
													}
													errors++;
												}
											}
										} else {
											const err802 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/env',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/15/properties/env/type',
												keyword: 'type',
												params: { type: 'object' },
												message: 'must be object',
											};
											if (vErrors === null) {
												vErrors = [err802];
											} else {
												vErrors.push(err802);
											}
											errors++;
										}
									}
								} else {
									const err803 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/15/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err803];
									} else {
										vErrors.push(err803);
									}
									errors++;
								}
							} else if (tag1 === 'runSQL') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err804 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/16/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err804];
										} else {
											vErrors.push(err804);
										}
										errors++;
									}
									if (data315.source === undefined) {
										const err805 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/16/required',
											keyword: 'required',
											params: {
												missingProperty: 'source',
											},
											message:
												"must have required property '" +
												'source' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err805];
										} else {
											vErrors.push(err805);
										}
										errors++;
									}
									for (const key93 in data315) {
										if (
											!(
												key93 === 'step' ||
												key93 === 'source'
											)
										) {
											const err806 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/16/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key93,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err806];
											} else {
												vErrors.push(err806);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data468 = data315.step;
										if (typeof data468 !== 'string') {
											const err807 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/16/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err807];
											} else {
												vErrors.push(err807);
											}
											errors++;
										}
										if ('runSQL' !== data468) {
											const err808 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/16/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue: 'runSQL',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err808];
											} else {
												vErrors.push(err808);
											}
											errors++;
										}
									}
									if (data315.source !== undefined) {
										if (
											!validate40(data315.source, {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/source',
												parentData: data315,
												parentDataProperty: 'source',
												rootData,
											})
										) {
											vErrors =
												vErrors === null
													? validate40.errors
													: vErrors.concat(
															validate40.errors
														);
											errors = vErrors.length;
										}
									}
								} else {
									const err809 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/16/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err809];
									} else {
										vErrors.push(err809);
									}
									errors++;
								}
							} else if (tag1 === 'setSiteLanguage') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err810 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/17/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err810];
										} else {
											vErrors.push(err810);
										}
										errors++;
									}
									if (data315.language === undefined) {
										const err811 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/17/required',
											keyword: 'required',
											params: {
												missingProperty: 'language',
											},
											message:
												"must have required property '" +
												'language' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err811];
										} else {
											vErrors.push(err811);
										}
										errors++;
									}
									for (const key94 in data315) {
										if (
											!(
												key94 === 'step' ||
												key94 === 'language'
											)
										) {
											const err812 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/17/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key94,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err812];
											} else {
												vErrors.push(err812);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data470 = data315.step;
										if (typeof data470 !== 'string') {
											const err813 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/17/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err813];
											} else {
												vErrors.push(err813);
											}
											errors++;
										}
										if ('setSiteLanguage' !== data470) {
											const err814 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/17/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue:
														'setSiteLanguage',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err814];
											} else {
												vErrors.push(err814);
											}
											errors++;
										}
									}
									if (data315.language !== undefined) {
										if (
											typeof data315.language !== 'string'
										) {
											const err815 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/language',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/17/properties/language/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err815];
											} else {
												vErrors.push(err815);
											}
											errors++;
										}
									}
								} else {
									const err816 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/17/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err816];
									} else {
										vErrors.push(err816);
									}
									errors++;
								}
							} else if (tag1 === 'setSiteOptions') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err817 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/18/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err817];
										} else {
											vErrors.push(err817);
										}
										errors++;
									}
									if (data315.options === undefined) {
										const err818 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/18/required',
											keyword: 'required',
											params: {
												missingProperty: 'options',
											},
											message:
												"must have required property '" +
												'options' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err818];
										} else {
											vErrors.push(err818);
										}
										errors++;
									}
									for (const key95 in data315) {
										if (
											!(
												key95 === 'step' ||
												key95 === 'options'
											)
										) {
											const err819 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/18/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key95,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err819];
											} else {
												vErrors.push(err819);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data472 = data315.step;
										if (typeof data472 !== 'string') {
											const err820 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/18/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err820];
											} else {
												vErrors.push(err820);
											}
											errors++;
										}
										if ('setSiteOptions' !== data472) {
											const err821 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/18/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue:
														'setSiteOptions',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err821];
											} else {
												vErrors.push(err821);
											}
											errors++;
										}
									}
									if (data315.options !== undefined) {
										let data473 = data315.options;
										if (
											data473 &&
											typeof data473 == 'object' &&
											!Array.isArray(data473)
										) {
											for (const key96 in data473) {
												let data474 = data473[key96];
												const _errs1298 = errors;
												let valid311 = false;
												const _errs1299 = errors;
												if (
													typeof data474 !== 'string'
												) {
													const err822 = {
														instancePath:
															instancePath +
															'/additionalStepsAfterExecution/' +
															i39 +
															'/options/' +
															key96
																.replace(
																	/~/g,
																	'~0'
																)
																.replace(
																	/\//g,
																	'~1'
																),
														schemaPath:
															'#/properties/additionalStepsAfterExecution/items/oneOf/18/properties/options/additionalProperties/anyOf/0/type',
														keyword: 'type',
														params: {
															type: 'string',
														},
														message:
															'must be string',
													};
													if (vErrors === null) {
														vErrors = [err822];
													} else {
														vErrors.push(err822);
													}
													errors++;
												}
												var _valid43 =
													_errs1299 === errors;
												valid311 = valid311 || _valid43;
												if (!valid311) {
													const _errs1301 = errors;
													if (
														typeof data474 !==
														'boolean'
													) {
														const err823 = {
															instancePath:
																instancePath +
																'/additionalStepsAfterExecution/' +
																i39 +
																'/options/' +
																key96
																	.replace(
																		/~/g,
																		'~0'
																	)
																	.replace(
																		/\//g,
																		'~1'
																	),
															schemaPath:
																'#/properties/additionalStepsAfterExecution/items/oneOf/18/properties/options/additionalProperties/anyOf/1/type',
															keyword: 'type',
															params: {
																type: 'boolean',
															},
															message:
																'must be boolean',
														};
														if (vErrors === null) {
															vErrors = [err823];
														} else {
															vErrors.push(
																err823
															);
														}
														errors++;
													}
													var _valid43 =
														_errs1301 === errors;
													valid311 =
														valid311 || _valid43;
													if (!valid311) {
														const _errs1303 =
															errors;
														if (
															!(
																typeof data474 ==
																	'number' &&
																isFinite(
																	data474
																)
															)
														) {
															const err824 = {
																instancePath:
																	instancePath +
																	'/additionalStepsAfterExecution/' +
																	i39 +
																	'/options/' +
																	key96
																		.replace(
																			/~/g,
																			'~0'
																		)
																		.replace(
																			/\//g,
																			'~1'
																		),
																schemaPath:
																	'#/properties/additionalStepsAfterExecution/items/oneOf/18/properties/options/additionalProperties/anyOf/2/type',
																keyword: 'type',
																params: {
																	type: 'number',
																},
																message:
																	'must be number',
															};
															if (
																vErrors === null
															) {
																vErrors = [
																	err824,
																];
															} else {
																vErrors.push(
																	err824
																);
															}
															errors++;
														}
														var _valid43 =
															_errs1303 ===
															errors;
														valid311 =
															valid311 ||
															_valid43;
														if (!valid311) {
															const _errs1305 =
																errors;
															if (
																Array.isArray(
																	data474
																)
															) {
																const len56 =
																	data474.length;
																for (
																	let i56 = 0;
																	i56 < len56;
																	i56++
																) {
																	if (
																		!validate34(
																			data474[
																				i56
																			],
																			{
																				instancePath:
																					instancePath +
																					'/additionalStepsAfterExecution/' +
																					i39 +
																					'/options/' +
																					key96
																						.replace(
																							/~/g,
																							'~0'
																						)
																						.replace(
																							/\//g,
																							'~1'
																						) +
																					'/' +
																					i56,
																				parentData:
																					data474,
																				parentDataProperty:
																					i56,
																				rootData,
																			}
																		)
																	) {
																		vErrors =
																			vErrors ===
																			null
																				? validate34.errors
																				: vErrors.concat(
																						validate34.errors
																					);
																		errors =
																			vErrors.length;
																	}
																}
															} else {
																const err825 = {
																	instancePath:
																		instancePath +
																		'/additionalStepsAfterExecution/' +
																		i39 +
																		'/options/' +
																		key96
																			.replace(
																				/~/g,
																				'~0'
																			)
																			.replace(
																				/\//g,
																				'~1'
																			),
																	schemaPath:
																		'#/properties/additionalStepsAfterExecution/items/oneOf/18/properties/options/additionalProperties/anyOf/3/type',
																	keyword:
																		'type',
																	params: {
																		type: 'array',
																	},
																	message:
																		'must be array',
																};
																if (
																	vErrors ===
																	null
																) {
																	vErrors = [
																		err825,
																	];
																} else {
																	vErrors.push(
																		err825
																	);
																}
																errors++;
															}
															var _valid43 =
																_errs1305 ===
																errors;
															valid311 =
																valid311 ||
																_valid43;
															if (!valid311) {
																const _errs1308 =
																	errors;
																if (
																	data474 &&
																	typeof data474 ==
																		'object' &&
																	!Array.isArray(
																		data474
																	)
																) {
																	for (const key97 in data474) {
																		if (
																			!validate34(
																				data474[
																					key97
																				],
																				{
																					instancePath:
																						instancePath +
																						'/additionalStepsAfterExecution/' +
																						i39 +
																						'/options/' +
																						key96
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							) +
																						'/' +
																						key97
																							.replace(
																								/~/g,
																								'~0'
																							)
																							.replace(
																								/\//g,
																								'~1'
																							),
																					parentData:
																						data474,
																					parentDataProperty:
																						key97,
																					rootData,
																				}
																			)
																		) {
																			vErrors =
																				vErrors ===
																				null
																					? validate34.errors
																					: vErrors.concat(
																							validate34.errors
																						);
																			errors =
																				vErrors.length;
																		}
																	}
																} else {
																	const err826 =
																		{
																			instancePath:
																				instancePath +
																				'/additionalStepsAfterExecution/' +
																				i39 +
																				'/options/' +
																				key96
																					.replace(
																						/~/g,
																						'~0'
																					)
																					.replace(
																						/\//g,
																						'~1'
																					),
																			schemaPath:
																				'#/properties/additionalStepsAfterExecution/items/oneOf/18/properties/options/additionalProperties/anyOf/4/type',
																			keyword:
																				'type',
																			params: {
																				type: 'object',
																			},
																			message:
																				'must be object',
																		};
																	if (
																		vErrors ===
																		null
																	) {
																		vErrors =
																			[
																				err826,
																			];
																	} else {
																		vErrors.push(
																			err826
																		);
																	}
																	errors++;
																}
																var _valid43 =
																	_errs1308 ===
																	errors;
																valid311 =
																	valid311 ||
																	_valid43;
															}
														}
													}
												}
												if (!valid311) {
													const err827 = {
														instancePath:
															instancePath +
															'/additionalStepsAfterExecution/' +
															i39 +
															'/options/' +
															key96
																.replace(
																	/~/g,
																	'~0'
																)
																.replace(
																	/\//g,
																	'~1'
																),
														schemaPath:
															'#/properties/additionalStepsAfterExecution/items/oneOf/18/properties/options/additionalProperties/anyOf',
														keyword: 'anyOf',
														params: {},
														message:
															'must match a schema in anyOf',
													};
													if (vErrors === null) {
														vErrors = [err827];
													} else {
														vErrors.push(err827);
													}
													errors++;
												} else {
													errors = _errs1298;
													if (vErrors !== null) {
														if (_errs1298) {
															vErrors.length =
																_errs1298;
														} else {
															vErrors = null;
														}
													}
												}
											}
										} else {
											const err828 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/options',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/18/properties/options/type',
												keyword: 'type',
												params: { type: 'object' },
												message: 'must be object',
											};
											if (vErrors === null) {
												vErrors = [err828];
											} else {
												vErrors.push(err828);
											}
											errors++;
										}
									}
								} else {
									const err829 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/18/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err829];
									} else {
										vErrors.push(err829);
									}
									errors++;
								}
							} else if (tag1 === 'unzip') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err830 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/19/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err830];
										} else {
											vErrors.push(err830);
										}
										errors++;
									}
									if (data315.zipFile === undefined) {
										const err831 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/19/required',
											keyword: 'required',
											params: {
												missingProperty: 'zipFile',
											},
											message:
												"must have required property '" +
												'zipFile' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err831];
										} else {
											vErrors.push(err831);
										}
										errors++;
									}
									if (data315.extractToPath === undefined) {
										const err832 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/19/required',
											keyword: 'required',
											params: {
												missingProperty:
													'extractToPath',
											},
											message:
												"must have required property '" +
												'extractToPath' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err832];
										} else {
											vErrors.push(err832);
										}
										errors++;
									}
									for (const key98 in data315) {
										if (
											!(
												key98 === 'step' ||
												key98 === 'zipFile' ||
												key98 === 'extractToPath'
											)
										) {
											const err833 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/19/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key98,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err833];
											} else {
												vErrors.push(err833);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data477 = data315.step;
										if (typeof data477 !== 'string') {
											const err834 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/19/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err834];
											} else {
												vErrors.push(err834);
											}
											errors++;
										}
										if ('unzip' !== data477) {
											const err835 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/19/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue: 'unzip',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err835];
											} else {
												vErrors.push(err835);
											}
											errors++;
										}
									}
									if (data315.zipFile !== undefined) {
										if (
											!validate40(data315.zipFile, {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/zipFile',
												parentData: data315,
												parentDataProperty: 'zipFile',
												rootData,
											})
										) {
											vErrors =
												vErrors === null
													? validate40.errors
													: vErrors.concat(
															validate40.errors
														);
											errors = vErrors.length;
										}
									}
									if (data315.extractToPath !== undefined) {
										if (
											typeof data315.extractToPath !==
											'string'
										) {
											const err836 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/extractToPath',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/19/properties/extractToPath/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err836];
											} else {
												vErrors.push(err836);
											}
											errors++;
										}
									}
								} else {
									const err837 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/19/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err837];
									} else {
										vErrors.push(err837);
									}
									errors++;
								}
							} else if (tag1 === 'wp-cli') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err838 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/20/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err838];
										} else {
											vErrors.push(err838);
										}
										errors++;
									}
									if (data315.command === undefined) {
										const err839 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/20/required',
											keyword: 'required',
											params: {
												missingProperty: 'command',
											},
											message:
												"must have required property '" +
												'command' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err839];
										} else {
											vErrors.push(err839);
										}
										errors++;
									}
									for (const key99 in data315) {
										if (
											!(
												key99 === 'step' ||
												key99 === 'command' ||
												key99 === 'wpCliPath'
											)
										) {
											const err840 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/20/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key99,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err840];
											} else {
												vErrors.push(err840);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data480 = data315.step;
										if (typeof data480 !== 'string') {
											const err841 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/20/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err841];
											} else {
												vErrors.push(err841);
											}
											errors++;
										}
										if ('wp-cli' !== data480) {
											const err842 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/20/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue: 'wp-cli',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err842];
											} else {
												vErrors.push(err842);
											}
											errors++;
										}
									}
									if (data315.command !== undefined) {
										if (
											typeof data315.command !== 'string'
										) {
											const err843 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/command',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/20/properties/command/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err843];
											} else {
												vErrors.push(err843);
											}
											errors++;
										}
									}
									if (data315.wpCliPath !== undefined) {
										if (
											typeof data315.wpCliPath !==
											'string'
										) {
											const err844 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/wpCliPath',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/20/properties/wpCliPath/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err844];
											} else {
												vErrors.push(err844);
											}
											errors++;
										}
									}
								} else {
									const err845 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/20/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err845];
									} else {
										vErrors.push(err845);
									}
									errors++;
								}
							} else if (tag1 === 'writeFiles') {
								if (
									data315 &&
									typeof data315 == 'object' &&
									!Array.isArray(data315)
								) {
									if (data315.step === undefined) {
										const err846 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/21/required',
											keyword: 'required',
											params: { missingProperty: 'step' },
											message:
												"must have required property '" +
												'step' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err846];
										} else {
											vErrors.push(err846);
										}
										errors++;
									}
									if (data315.files === undefined) {
										const err847 = {
											instancePath:
												instancePath +
												'/additionalStepsAfterExecution/' +
												i39,
											schemaPath:
												'#/properties/additionalStepsAfterExecution/items/oneOf/21/required',
											keyword: 'required',
											params: {
												missingProperty: 'files',
											},
											message:
												"must have required property '" +
												'files' +
												"'",
										};
										if (vErrors === null) {
											vErrors = [err847];
										} else {
											vErrors.push(err847);
										}
										errors++;
									}
									for (const key100 in data315) {
										if (
											!(
												key100 === 'step' ||
												key100 === 'files'
											)
										) {
											const err848 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39,
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/21/additionalProperties',
												keyword: 'additionalProperties',
												params: {
													additionalProperty: key100,
												},
												message:
													'must NOT have additional properties',
											};
											if (vErrors === null) {
												vErrors = [err848];
											} else {
												vErrors.push(err848);
											}
											errors++;
										}
									}
									if (data315.step !== undefined) {
										let data483 = data315.step;
										if (typeof data483 !== 'string') {
											const err849 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/21/properties/step/type',
												keyword: 'type',
												params: { type: 'string' },
												message: 'must be string',
											};
											if (vErrors === null) {
												vErrors = [err849];
											} else {
												vErrors.push(err849);
											}
											errors++;
										}
										if ('writeFiles' !== data483) {
											const err850 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/step',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/21/properties/step/const',
												keyword: 'const',
												params: {
													allowedValue: 'writeFiles',
												},
												message:
													'must be equal to constant',
											};
											if (vErrors === null) {
												vErrors = [err850];
											} else {
												vErrors.push(err850);
											}
											errors++;
										}
									}
									if (data315.files !== undefined) {
										let data484 = data315.files;
										if (
											data484 &&
											typeof data484 == 'object' &&
											!Array.isArray(data484)
										) {
											for (const key101 in data484) {
												if (
													!validate12(
														data484[key101],
														{
															instancePath:
																instancePath +
																'/additionalStepsAfterExecution/' +
																i39 +
																'/files/' +
																key101
																	.replace(
																		/~/g,
																		'~0'
																	)
																	.replace(
																		/\//g,
																		'~1'
																	),
															parentData: data484,
															parentDataProperty:
																key101,
															rootData,
														}
													)
												) {
													vErrors =
														vErrors === null
															? validate12.errors
															: vErrors.concat(
																	validate12.errors
																);
													errors = vErrors.length;
												}
											}
										} else {
											const err851 = {
												instancePath:
													instancePath +
													'/additionalStepsAfterExecution/' +
													i39 +
													'/files',
												schemaPath:
													'#/properties/additionalStepsAfterExecution/items/oneOf/21/properties/files/type',
												keyword: 'type',
												params: { type: 'object' },
												message: 'must be object',
											};
											if (vErrors === null) {
												vErrors = [err851];
											} else {
												vErrors.push(err851);
											}
											errors++;
										}
									}
								} else {
									const err852 = {
										instancePath:
											instancePath +
											'/additionalStepsAfterExecution/' +
											i39,
										schemaPath:
											'#/properties/additionalStepsAfterExecution/items/oneOf/21/type',
										keyword: 'type',
										params: { type: 'object' },
										message: 'must be object',
									};
									if (vErrors === null) {
										vErrors = [err852];
									} else {
										vErrors.push(err852);
									}
									errors++;
								}
							} else {
								const err853 = {
									instancePath:
										instancePath +
										'/additionalStepsAfterExecution/' +
										i39,
									schemaPath:
										'#/properties/additionalStepsAfterExecution/items/discriminator',
									keyword: 'discriminator',
									params: {
										error: 'mapping',
										tag: 'step',
										tagValue: tag1,
									},
									message:
										'value of tag "step" must be in oneOf',
								};
								if (vErrors === null) {
									vErrors = [err853];
								} else {
									vErrors.push(err853);
								}
								errors++;
							}
						} else {
							const err854 = {
								instancePath:
									instancePath +
									'/additionalStepsAfterExecution/' +
									i39,
								schemaPath:
									'#/properties/additionalStepsAfterExecution/items/discriminator',
								keyword: 'discriminator',
								params: {
									error: 'tag',
									tag: 'step',
									tagValue: tag1,
								},
								message: 'tag "step" must be string',
							};
							if (vErrors === null) {
								vErrors = [err854];
							} else {
								vErrors.push(err854);
							}
							errors++;
						}
					} else {
						const err855 = {
							instancePath:
								instancePath +
								'/additionalStepsAfterExecution/' +
								i39,
							schemaPath:
								'#/properties/additionalStepsAfterExecution/items/type',
							keyword: 'type',
							params: { type: 'object' },
							message: 'must be object',
						};
						if (vErrors === null) {
							vErrors = [err855];
						} else {
							vErrors.push(err855);
						}
						errors++;
					}
				}
			} else {
				const err856 = {
					instancePath:
						instancePath + '/additionalStepsAfterExecution',
					schemaPath:
						'#/properties/additionalStepsAfterExecution/type',
					keyword: 'type',
					params: { type: 'array' },
					message: 'must be array',
				};
				if (vErrors === null) {
					vErrors = [err856];
				} else {
					vErrors.push(err856);
				}
				errors++;
			}
		}
	} else {
		const err857 = {
			instancePath,
			schemaPath: '#/type',
			keyword: 'type',
			params: { type: 'object' },
			message: 'must be object',
		};
		if (vErrors === null) {
			vErrors = [err857];
		} else {
			vErrors.push(err857);
		}
		errors++;
	}
	validate11.errors = vErrors;
	return errors === 0;
}
function validate10(
	data,
	{ instancePath = '', parentData, parentDataProperty, rootData = data } = {}
) {
	let vErrors = null;
	let errors = 0;
	if (
		!validate11(data, {
			instancePath,
			parentData,
			parentDataProperty,
			rootData,
		})
	) {
		vErrors =
			vErrors === null
				? validate11.errors
				: vErrors.concat(validate11.errors);
		errors = vErrors.length;
	}
	validate10.errors = vErrors;
	return errors === 0;
}
